import { useState, useRef, useEffect, useCallback, useMemo } from "react";
import React from "react";
import "mapbox-gl/dist/mapbox-gl.css";
import { DayPicker } from "react-day-picker";
import "react-day-picker/src/style.css";
import { eachDayOfInterval, format } from "date-fns";
import QRCode from "qrcode";
import { supabase, supabasePublic } from "./supabase";
import { t, tLevel, lang } from "./i18n";
import { LEGAL_CONTACT_EMAIL, LEGAL_COUNTRY, LEGAL_PRIVACY_VERSION, LEGAL_TERMS_VERSION } from "./legalPages";
import { getLocalNotifications } from "./localNotifications";
import { reportError } from "./errorReporting";
import kickrKMark from "./assets/kickr_k_mark_cropped.png";
import kickrWordmark from "./assets/kickr_wordmark_cropped.png";

// Single icon family across the whole app — Lucide. One stroke weight (1.8)
// at sizes 14/16/18/20 covers every surface. The previous mix of inline SVG
// (some at stroke 1.8, some at 2, some at 2.5) was a major polish drag.
import {
  Bell, Search, Plus, X, Check, ChevronRight, ChevronLeft, ChevronDown,
  MapPin, Users, Calendar, Clock, Star, Heart, Bookmark, BookmarkCheck,
  Lock, Unlock, Share2, Flag, Settings as SettingsIcon, LogOut, Edit3,
  Camera, Image as ImageIcon, Trash2, Filter, ArrowLeft, Send,
  CheckCircle2, AlertCircle, Sparkles, Compass, Home, User,
  UserCircle2, MessageCircle, Slash, Swords, Handshake,
  CircleDot, Zap, ShieldCheck, SunMedium, CloudSun, CloudRain,
  Tag, SlidersHorizontal, Info,
} from "lucide-react";

// Default icon prop — applied via spread. Lucide accepts size + strokeWidth
// + color so this gives every icon in the app the same weight unless the
// caller deliberately overrides.
const ICON = { strokeWidth: 1.8 };

function VerifiedTick({ size = 12, style = {}, title }) {
  const { C } = useTheme();
  const label = title || t("verifiedBadge");
  const badgeSize = size + 8;
  return (
    <span
      aria-label={label}
      title={label}
      style={{
        width: badgeSize,
        height: badgeSize,
        minWidth: badgeSize,
        borderRadius: "50%",
        background: C.info,
        color: "#fff",
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
        boxShadow: `0 0 0 2px ${C.surface}`,
        ...style,
      }}
    >
      <Check size={size} {...ICON} strokeWidth={2.8} />
    </span>
  );
}

// Lazy-load mapbox JS only when the map is first shown
let mapboxgl = null;
async function loadMapbox() {
  if (mapboxgl) return mapboxgl;
  const mod = await import("mapbox-gl");
  mapboxgl = mod.default;
  return mapboxgl;
}

// ─── Venue busyness tiers ─────────────────────────────────────────────────────
// Tier A = very popular/busy, Tier B = medium, Tier C = quieter
// Based on real-world football density, club activity and location data
const VENUE_TIERS = {
  A: new Set([
    // Oslo outdoor
    "Ekeberg idrettspark","Dælenenga idrettspark","Voldsløkka idrettspark",
    "Frogner stadion","Valle Hovin idrettspark","Løren idrettspark",
    "Jordal idrettspark","Caltexløkka","Tørteberg kunstgress",
    // Oslo cageball
    "Torshov cageball","Voldsløkka cages","Bjølsenparken cages","Lille Bislett",
    // Bærum
    "Nadderud stadion","Sandvika stadion",
    // Bergen
    "Nymark","Krohnsminde","Møhlenpris","Åsane stadion",
    // Trondheim
    "Eberg idrettspark","Lade idrettspark","Strindheim idrettspark",
  ]),
  C: new Set([
    // Oslo peripheral
    "Lusetjern kunstgress","Bjørndal idrettspark","Mortensrud idrettspark",
    "Prinsdal idrettspark","Jesperudjordet","Rustad idrettspark",
    "Mortensrud cageball","Holmlia idrettspark",
    // Bærum outer
    "Rud kunstgress","Rykkinn kunstgress","Lommedalen kunstgress",
    "Emma Hjort","Bjørnegård kunstgress",
    // Bergen peripheral
    "Alvøen idrettspark","Espeland","Hjellestad","Tellevik",
    "Ytre Arna","Ådnamarka","Salhus","Liland",
    // Trondheim outer
    "Flatåsen idrettspark","Kattem idrettspark",
  ]),
};

function getVenueTier(venue) {
  // If the venue has a tier set from Supabase (via admin panel), use it directly
  if (venue?.tier === "A" || venue?.tier === "B" || venue?.tier === "C") return venue.tier;
  // Otherwise fall back to hardcoded knowledge
  const name = venue?.name || "";
  if (VENUE_TIERS.A.has(name)) return "A";
  if (VENUE_TIERS.C.has(name)) return "C";
  return "B";
}

// ─── Norwegian school holidays ────────────────────────────────────────────────
function isNorwegianSchoolHoliday(date) {
  if (!date) return false;
  const d = date instanceof Date ? date : new Date(date + "T12:00");
  const y = d.getFullYear(); const m = d.getMonth() + 1; const day = d.getDate();
  // Common holidays across years
  if (m === 5 && day === 17) return true; // National day
  if (m === 7) return true; // Summer (July always off)
  if (m === 12 && day >= 23) return true; // Christmas break
  if (m === 1 && day <= 3) return true; // New Year break

  if (y === 2026) {
    if (m === 2 && day >= 16 && day <= 20) return true; // Winter break
    if (m === 4 && day >= 1 && day <= 13) return true; // Easter
    if (m === 6 && day >= 20) return true; // Summer start
    if (m === 8 && day <= 16) return true; // Summer end
    if (m === 10 && day >= 5 && day <= 9) return true; // Autumn break
  }
  if (y === 2027) {
    if (m === 2 && day >= 15 && day <= 19) return true;
    if (m === 3 && day >= 22 && day <= 29) return true; // Easter
    if (m === 6 && day >= 19) return true;
    if (m === 8 && day <= 16) return true;
    if (m === 10 && day >= 4 && day <= 8) return true;
  }
  if (y === 2028) {
    if (m === 2 && day >= 21 && day <= 25) return true;
    if (m === 4 && day >= 10 && day <= 17) return true; // Easter
    if (m === 6 && day >= 19) return true;
    if (m === 8 && day <= 16) return true;
    if (m === 10 && day >= 2 && day <= 6) return true;
  }
  return false;
}

// ─── Pitch confidence 0-100 (higher = more likely free) ──────────────────────
// Busyness time table: rows = hour slots [8,10,12,14,16,18,20],
// cols = day of week Mon=0…Sun=6. Values 1(quiet)–5(very busy).
const BUSY_TABLE = [
//  Mon Tue Wed Thu Fri Sat Sun
  [  1,  1,  1,  1,  1,  1,  1 ], // 08h
  [  2,  2,  2,  2,  2,  2,  2 ], // 10h
  [  2,  2,  2,  2,  2,  2,  2 ], // 12h
  [  3,  3,  3,  3,  3,  2,  2 ], // 14h
  [  5,  5,  5,  5,  4,  4,  3 ], // 16h
  [  5,  5,  5,  5,  4,  4,  3 ], // 18h
  [  4,  4,  4,  4,  3,  2,  2 ], // 20h
];
const BUSY_SLOTS  = [8, 10, 12, 14, 16, 18, 20];
// Busyness 1-5 → confidence (free likelihood) 0-100
const BUSY_TO_CONF = { 1: 88, 2: 72, 3: 52, 4: 28, 5: 10 };
const STANDARD_FORMATS = ["3v3", "5v5", "7v7", "9v9", "11v11"];
const CUSTOM_FORMAT_SIDES = [2, 4, 6, 8, 10, 12];
const GROUP_TAG_OPTIONS = [
  "Casual",
  "Student",
  "Exchange students",
  "Beginner friendly",
  "Intermediate",
  "Competitive",
  "Women",
  "University",
  "Evenings",
  "Weekends",
  "5v5",
  "7v7",
  "11v11",
];
const ENABLE_GROUP_CHALLENGES = true;
const CHAT_NOTIFICATION_META_KEY = "__kickrChatNotification";
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
const USERNAME_RE = /^[a-z0-9_]{3,20}$/;
const MENTION_RE = /@([a-z0-9_]{3,20})\b/gi;
const COMMUNITY_NOTIFICATION_TYPES = new Set([
  "community_join_request",
  "community_join_approved",
  "community_challenge",
  "community_challenge_decision",
  "community_report_submitted",
  "group_join_request",
  "group_join_approved",
  "group_challenge",
  "group_challenge_decision",
]);

function encodeChatNotificationSub(meta = {}) {
  return JSON.stringify({ [CHAT_NOTIFICATION_META_KEY]: true, ...meta });
}

function parseChatNotificationSub(sub) {
  if (!sub || typeof sub !== "string") return null;
  try {
    const parsed = JSON.parse(sub);
    return parsed?.[CHAT_NOTIFICATION_META_KEY] ? parsed : null;
  } catch {
    return null;
  }
}

function isLaunchQaNotification(n) {
  const text = String(n?.text || "");
  const sub = String(n?.sub || "");
  return /^Strix /i.test(text) ||
    /^Strix /i.test(sub) ||
    /^Auto-cancel anon validation /i.test(text) ||
    /^Auto-cancel anon validation /i.test(sub) ||
    /^validation self notification$/i.test(text);
}

function getNotifSubLabel(n) {
  const meta = parseChatNotificationSub(n?.sub);
  if (meta) return meta.preview || "";
  const sub = typeof n?.sub === "string" ? n.sub : "";
  if (!sub) return "";
  if (n?.type === "availability_group" || n?.type === "availability_match") {
    try {
      const data = JSON.parse(sub);
      const date = data?.date || "";
      const hour = data?.hour || "";
      const count = Array.isArray(data?.playerIds) ? data.playerIds.length : 0;
      return [count ? `${count} player${count === 1 ? "" : "s"} available` : "Players available", [date, hour].filter(Boolean).join(" · ")].filter(Boolean).join(" · ");
    } catch {
      return sub.startsWith("{") ? "" : sub;
    }
  }
  if (COMMUNITY_NOTIFICATION_TYPES.has(n?.type) && UUID_RE.test(sub)) return "";
  if ((sub.startsWith("{") && sub.endsWith("}")) || UUID_RE.test(sub)) return "";
  return sub;
}

function normalizeUsernameInput(value = "") {
  return String(value || "")
    .trim()
    .toLowerCase()
    .replace(/^@+/, "")
    .replace(/[^a-z0-9_]/g, "")
    .slice(0, 20);
}

function getUsernameError(username) {
  if (!username) return t("usernameRequired");
  if (!USERNAME_RE.test(username)) return t("usernameInvalid");
  return "";
}

function isUsernameConflictError(error) {
  const msg = `${error?.code || ""} ${error?.message || ""} ${error?.details || ""}`.toLowerCase();
  return msg.includes("23505") || msg.includes("profiles_username") || msg.includes("username_unique");
}

function getLegalAcceptancePayload() {
  return {
    terms_accepted_at: new Date().toISOString(),
    terms_version: LEGAL_TERMS_VERSION,
    privacy_version: LEGAL_PRIVACY_VERSION,
    legal_country: LEGAL_COUNTRY,
  };
}

function hasAcceptedCurrentLegal(profile) {
  return Boolean(
    profile?.terms_accepted_at &&
    profile?.terms_version === LEGAL_TERMS_VERSION &&
    profile?.privacy_version === LEGAL_PRIVACY_VERSION
  );
}

function isLegalSchemaMissingError(error) {
  const msg = `${error?.code || ""} ${error?.message || ""} ${error?.details || ""} ${error?.hint || ""}`.toLowerCase();
  return msg.includes("terms_accepted_at") ||
    msg.includes("terms_version") ||
    msg.includes("privacy_version") ||
    msg.includes("legal_country");
}

function formatLegalAcceptedDate(value) {
  if (!value) return t("legalNotAccepted");
  try {
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return t("legalAccepted");
    return new Intl.DateTimeFormat(lang === "nb" ? "nb-NO" : "en-GB", { day: "numeric", month: "short", year: "numeric" }).format(date);
  } catch {
    return t("legalAccepted");
  }
}

async function checkUsernameAvailable(username, currentUserId) {
  const normalized = normalizeUsernameInput(username);
  const { data, error } = await supabase.rpc("is_username_available", { candidate: normalized });
  if (!error && typeof data === "boolean") return data;

  const fallback = await supabasePublic
    .from("profiles_public")
    .select("user_id")
    .eq("username", normalized)
    .neq("user_id", currentUserId)
    .limit(1);
  if (fallback.error) throw (error || fallback.error);
  return (fallback.data || []).length === 0;
}

function fallbackMentionHandle(value = "") {
  const normalized = normalizeUsernameInput(value);
  return USERNAME_RE.test(normalized) ? normalized : "";
}

function mentionHandleForProfile(profile = {}) {
  return normalizeUsernameInput(profile.username || "") || fallbackMentionHandle(profile.name || "");
}

function extractMentionHandles(text = "") {
  return [...new Set(
    [...String(text || "").matchAll(MENTION_RE)]
      .map(match => normalizeUsernameInput(match[1]))
      .filter(Boolean)
  )];
}

function mentionedProfilesFromText(text = "", candidates = [], currentUserId = null) {
  const handles = new Set(extractMentionHandles(text));
  if (!handles.size) return [];
  const seen = new Set();
  return (candidates || []).filter(profile => {
    const id = profile?.id || profile?.user_id;
    if (!id || id === currentUserId || seen.has(id)) return false;
    const handle = mentionHandleForProfile(profile);
    if (!handle || !handles.has(handle)) return false;
    seen.add(id);
    return true;
  });
}

function getActiveMentionQuery(text = "") {
  const match = String(text || "").match(/(?:^|\s)@([a-z0-9_]{0,20})$/i);
  return match ? normalizeUsernameInput(match[1]) : null;
}

function insertMentionHandle(text = "", profile = {}) {
  const handle = mentionHandleForProfile(profile);
  if (!handle) return text;
  const value = String(text || "");
  const replacement = match => `${match.startsWith(" ") ? " " : ""}@${handle} `;
  return value.match(/(?:^|\s)@([a-z0-9_]{0,20})$/i)
    ? value.replace(/(?:^|\s)@([a-z0-9_]{0,20})$/i, replacement)
    : `${value}${value && !/\s$/.test(value) ? " " : ""}@${handle} `;
}

function MentionText({ text, own = false }) {
  const { C } = useTheme();
  const value = String(text || "");
  const parts = [];
  let lastIndex = 0;
  for (const match of value.matchAll(MENTION_RE)) {
    if (match.index > lastIndex) parts.push({ text: value.slice(lastIndex, match.index), mention: false });
    parts.push({ text: match[0], mention: true });
    lastIndex = match.index + match[0].length;
  }
  if (lastIndex < value.length) parts.push({ text: value.slice(lastIndex), mention: false });
  if (!parts.length) return value;
  return parts.map((part, index) => part.mention
    ? (
      <span key={`${part.text}-${index}`} style={{ fontWeight: 900, color: own ? C.accentText : C.accent }}>
        {part.text}
      </span>
    )
    : <React.Fragment key={`${part.text}-${index}`}>{part.text}</React.Fragment>);
}

function MentionSuggestions({ draft, candidates = [], onPick, max = 5 }) {
  const { C, T, SPACE, RADIUS, SHADOW } = useTheme();
  const query = getActiveMentionQuery(draft);
  const options = useMemo(() => {
    if (query == null) return [];
    const seen = new Set();
    return (candidates || [])
      .filter(profile => {
        const id = profile?.id || profile?.user_id;
        if (!id || seen.has(id)) return false;
        const handle = mentionHandleForProfile(profile);
        if (!handle) return false;
        const name = String(profile?.name || "").toLowerCase();
        const matches = !query || handle.includes(query) || name.includes(query);
        if (matches) seen.add(id);
        return matches;
      })
      .slice(0, max);
  }, [candidates, max, query]);
  if (!options.length) return null;
  return (
    <div style={{ padding: `0 ${SPACE[4]}px ${SPACE[2]}px`, background: C.bg }}>
      <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: RADIUS.md, boxShadow: SHADOW.sm, overflow: "hidden" }}>
        {options.map((profile, index) => {
          const handle = mentionHandleForProfile(profile);
          return (
            <button
              key={profile.id || profile.user_id}
              type="button"
              onMouseDown={e => { e.preventDefault(); onPick?.(profile); }}
              style={{
                width: "100%",
                minHeight: 44,
                padding: `7px ${SPACE[3]}px`,
                border: "none",
                borderBottom: index < options.length - 1 ? `1px solid ${C.border}` : "none",
                background: "transparent",
                color: C.ink,
                display: "flex",
                alignItems: "center",
                gap: SPACE[2],
                cursor: "pointer",
                textAlign: "left",
                fontFamily: "inherit",
              }}
            >
              <Avatar initials={communityInitials(profile.name || handle).slice(0, 2)} avatarUrl={profile.avatarUrl || profile.avatar_url} size={30} />
              <span style={{ flex: 1, minWidth: 0 }}>
                <span style={{ ...T.h3, display: "block", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{profile.name || handle}</span>
                <span style={{ ...T.small, color: C.inkFaint }}>@{handle}</span>
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function getLegacyCommunityNameFromChatNotifText(text = "") {
  const marker = " posted in ";
  const idx = String(text).lastIndexOf(marker);
  return idx >= 0 ? String(text).slice(idx + marker.length).trim() : "";
}

function getLegacyFriendNameFromChatNotifText(text = "") {
  const marker = " sent you a message";
  const value = String(text);
  return value.endsWith(marker) ? value.slice(0, -marker.length).trim() : "";
}

function parseFormatSide(format = "5v5") {
  return parseInt(String(format).split("v")[0], 10) || 5;
}

function nearestCustomFormatSide(side = 4) {
  const safeSide = Math.min(12, Math.max(2, Number(side) || 4));
  return CUSTOM_FORMAT_SIDES.reduce((closest, current) => (
    Math.abs(current - safeSide) < Math.abs(closest - safeSide) ? current : closest
  ), CUSTOM_FORMAT_SIDES[1]);
}

function getVenueCapacitySide(venue) {
  const size = String(venue?.size || "").toLowerCase();
  if (/11|full/.test(size)) return 11;
  if (/9/.test(size)) return 9;
  if (/8/.test(size)) return 8;
  if (/7/.test(size)) return 7;
  if (/6/.test(size)) return 6;
  if (/5/.test(size)) return 5;
  if (/4/.test(size)) return 4;
  if (/3/.test(size)) return 3;
  return venue?.type === "Outdoor" ? 7 : 5;
}

function isVenueGoodForFormat(venue, format = "5v5") {
  if (!venue) return false;
  const side = parseFormatSide(format);
  const capacity = getVenueCapacitySide(venue);
  if (side >= 11) return venue.type === "Outdoor" && capacity >= 11;
  if (side >= 7) return venue.type === "Outdoor" && capacity >= side;
  if (side <= 4) return capacity >= 3;
  return capacity >= 5;
}

function getPitchConfidence(venue, dateStr, hourStr, format = "5v5") {
  const hour    = parseInt((hourStr || "18:00").split(":")[0]);
  const date    = dateStr ? new Date(dateStr + "T12:00") : new Date();
  const isHoliday = isNorwegianSchoolHoliday(date);
  const side = parseFormatSide(format);

  // Day-of-week index Mon=0…Sun=6 (JS getDay: 0=Sun)
  let dow = date.getDay() === 0 ? 6 : date.getDay() - 1;
  // Holidays behave like Saturday (mixed demand, not peak commuter)
  if (isHoliday && dow < 5) dow = 5;

  // Nearest hour slot
  const slotIdx = BUSY_SLOTS.reduce((best, s, i) =>
    Math.abs(s - hour) < Math.abs(BUSY_SLOTS[best] - hour) ? i : best, 0);

  const busyScore = BUSY_TABLE[slotIdx][dow]; // 1-5
  let conf = BUSY_TO_CONF[busyScore] ?? 50;

  // Tier adjustment: tier A = busiest (no boost), B = +14, C = +28
  const tier = getVenueTier(venue);
  if (tier === "B") conf = Math.min(100, conf + 14);
  if (tier === "C") conf = Math.min(100, conf + 28);

  // Format pressure: small-sided games can adapt to a cage or a half-pitch,
  // while 7v7+ and especially 11v11 need a specific surface and size.
  if (side <= 4) conf += venue?.type === "Cageball" ? 14 : 8;
  if (side === 5 && venue?.type === "Cageball") conf += 4;
  if (side >= 7) conf -= 10;
  if (side >= 7 && venue?.type !== "Outdoor") conf -= 28;
  if (side >= 11) conf -= isVenueGoodForFormat(venue, format) ? 16 : 48;

  // Peak hours matter more for large formats because a full pitch is harder to
  // salvage. For 3v3/4v4 the same hour is still workable with a backup cage.
  if (busyScore >= 4 && side <= 4) conf += 8;
  if (busyScore >= 4 && side >= 7) conf -= 10;
  if (busyScore >= 4 && side >= 11) conf -= 12;

  return Math.round(Math.min(100, Math.max(0, conf)));
}

function getPitchRisk(venue, dateStr, hourStr, format = "5v5") {
  if (!venue || !dateStr || !hourStr) return null;
  const side = parseFormatSide(format);
  const score = getPitchConfidence(venue, dateStr, hourStr, format);
  const peak = score < 55;
  const badFit = !isVenueGoodForFormat(venue, format);
  const level = score < 35 || badFit ? "high" : score < 55 ? "medium" : "low";
  if (level === "low") {
    return {
      score,
      level,
      title: "Good chance this pitch works",
      body: side <= 4 ? "Small-sided games are flexible if one pitch is busy." : "Still worth checking the pitch before kickoff.",
    };
  }
  if (badFit && side >= 11) {
    return {
      score,
      level: "high",
      title: "11v11 needs a full outdoor pitch",
      body: "This format is hard to run here. Pick an 11er pitch or add a strong backup before publishing.",
    };
  }
  if (side >= 11 && peak) {
    return {
      score,
      level: "high",
      title: "High risk during peak hours",
      body: "Full pitches are usually hardest to secure after work. Add a backup pitch or move earlier/later.",
    };
  }
  if (side >= 7 && peak) {
    return {
      score,
      level,
      title: "This time can be busy",
      body: "Bigger formats need more space. A backup pitch will make the game feel safer for players.",
    };
  }
  return {
    score,
    level,
    title: "This time can be busy",
    body: "Add a backup pitch so players know where to go if the first pitch is taken.",
  };
}

function confidenceLabel(score) {
  // Three semantic colours: brand-green (free), amber (might be busy),
  // red (busy). Pulls from PALETTE so the swatches stay in lockstep with
  // the rest of the app.
  if (score >= 65) return { label: "Likely free",   color: PALETTE.brand500, bg: PALETTE.brand50 };
  if (score >= 40) return { label: "Might be busy", color: PALETTE.amber600, bg: PALETTE.amber50 };
  return                  { label: "Likely busy",   color: PALETTE.red600,   bg: PALETTE.red50   };
}

// ─── Design tokens — single source of truth ──────────────────────────────────
// The whole app should pull from these. The biggest "indie-app" tell is
// drift: 15 different borderRadius values, 17 fontSize values, half-baked
// padding combos. Pro apps (Airbnb, Linear, FotMob) lock down a tiny
// vocabulary and use it religiously. These five tokens are that vocabulary
// for kickr.

// 4-px spacing grid. Reach for a number: SPACE[1] = 4, SPACE[3] = 12, etc.
// Anything outside this is style drift.
const SPACE  = { 1: 4, 2: 8, 3: 12, 4: 16, 5: 24, 6: 32, 7: 48, 8: 64 };

// Three radii cover the whole app. Inputs and small chips use sm; cards and
// surfaces use md; bottom sheets and modals use lg; pills and avatars use
// pill. Stop reaching for 10, 13, 18 — they read as "designed at 2am".
const RADIUS = { sm: 8, md: 12, lg: 16, xl: 20, pill: 999 };

// Type scale — eight steps, deliberate weights. Body is 15 (was 13/14
// site-wide); the previous size felt like Settings detail text, not a
// product surface. Letter spacing stays at 0 so headings do not get the
// over-styled "template app" look on mobile screens.
const TYPE = {
  display: { fontSize: 30, fontWeight: 800, letterSpacing: 0, lineHeight: 1.1 },
  h1:      { fontSize: 22, fontWeight: 700, letterSpacing: 0, lineHeight: 1.2 },
  h2:      { fontSize: 17, fontWeight: 600, letterSpacing: 0, lineHeight: 1.3 },
  h3:      { fontSize: 15, fontWeight: 600, letterSpacing: 0, lineHeight: 1.35 },
  body:    { fontSize: 15, fontWeight: 400, lineHeight: 1.5 },
  caption: { fontSize: 13, fontWeight: 400, lineHeight: 1.4 },
  micro:   { fontSize: 11, fontWeight: 600, letterSpacing: "0.04em", textTransform: "uppercase" },
};

// Three shadow steps — sm for resting cards, md for hovered/elevated cards
// and floating buttons, lg for modals and bottom sheets. Combine with NO
// border for a clean Airbnb/Linear feel. Use border XOR shadow, never both.
// Built from rgba(15,22,35,…) — same ink colour used everywhere else.
const SHADOW = {
  none: "none",
  sm: "0 1px 2px rgba(15, 22, 35, 0.04), 0 1px 3px rgba(15, 22, 35, 0.06)",
  md: "0 4px 8px rgba(15, 22, 35, 0.05), 0 8px 24px rgba(15, 22, 35, 0.08)",
  lg: "0 12px 28px rgba(15, 22, 35, 0.10), 0 24px 56px rgba(15, 22, 35, 0.12)",
};

// ─── Color palette — single source of truth for every colour in the app ─────
//
// The design-token system in the section above had a hole: components were
// still reaching for raw hex values (#22c55e, #4ade80, #ef4444) instead of
// going through the theme. An audit found 47 distinct hex values across the
// file, with three different greens and four different reds doing the same
// job. This palette is the fix.
//
// Three layers:
//
//   1. PALETTE — raw colour values. Never reference these directly from
//      components. They exist so LIGHT_C / DARK_C can pick from a fixed
//      vocabulary instead of inventing values.
//   2. LIGHT_C / DARK_C — semantic colour roles (ink, surface, accent…)
//      derived from PALETTE. Components consume these via useTheme().
//   3. ALPHA — the four allowed translucent ink overlays. Replaces the
//      ~13 different rgba(0,0,0,X) values that were drifting across the
//      file (X ∈ {0.06, 0.08, 0.15, 0.18, 0.25, 0.3, 0.4, 0.45, 0.5, 0.55,
//      0.6, 0.75…}). Pick one of four. That's it.
const PALETTE = {
  // Neutral grays — the spine of the app. 9 steps from white to near-black.
  // Derived from Tailwind slate but tuned slightly cooler at the high end.
  white:   "#ffffff",
  gray50:  "#f6faf7",   // page background (very faint green tint, kickr's identity)
  gray100: "#f1f5f3",   // surface tint
  gray200: "#e2e8eb",   // borders, dividers
  gray300: "#cbd5d8",   // disabled text, dashed borders
  gray400: "#94a3b8",   // ink faint
  gray500: "#64748b",   // ink mid
  gray700: "#334155",   // body text on light surfaces
  gray900: "#0f1623",   // ink, primary text
  black:   "#000000",   // pure black — used only for the few "ink button" CTAs

  // Brand green. ONE value for "kickr". The 50/600/900 stops let us tint
  // surfaces/pressed states without inventing new greens.
  brand50:  "#f0fdf4",
  brand500: "#22c55e",  // the kickr green
  brand600: "#16a34a",  // pressed
  brand900: "#0a3a1a",

  // Semantic — one value per intent. Was 4 reds, 4 ambers, 4 blues.
  red50:    "#fef2f2",
  red500:   "#ef4444",
  red600:   "#dc2626",
  amber50:  "#fffbeb",
  amber500: "#f59e0b",
  amber600: "#d97706",
  blue50:   "#eff6ff",
  blue500:  "#3b82f6",
  blue600:  "#2563eb",
  violet50: "#f5f3ff",
  violet500:"#8b5cf6",
  violet600:"#7c3aed",

  // Dark-mode neutrals. Tuned with cool navy undertone (slate-blue, not
  // pure gray) — matches the existing brand identity in the previous theme.
  navy900: "#0d1526",   // dark bg
  navy800: "#121e35",   // dark surface
  navy700: "#1e3050",   // dark border
  navy500: "#7a94c0",   // dark ink mid
  navy300: "#3d5478",   // dark ink faint
  navy100: "#e8f0ff",   // dark ink (high contrast)
};

// Translucent ink overlays. Use ONLY these four for anything that needs to
// dim the underlying content. Hard-coding "rgba(0,0,0,0.42)" is drift.
const ALPHA = {
  inkXS:   "rgba(15, 22, 35, 0.04)",   // hover tint, faint dividers
  inkSM:   "rgba(15, 22, 35, 0.08)",   // resting card shadow inner
  inkMD:   "rgba(15, 22, 35, 0.18)",   // sheet backdrop, hovered chip
  inkLG:   "rgba(15, 22, 35, 0.45)",   // photo gradient bottom, modal scrim
  inkXL:   "rgba(15, 22, 35, 0.78)",   // pill-on-photo background

  whiteSM: "rgba(255, 255, 255, 0.20)",  // glass on dark photo
  whiteMD: "rgba(255, 255, 255, 0.45)",
};

const LIGHT_C = {
  ink: PALETTE.gray900, inkMid: PALETTE.gray500, inkFaint: PALETTE.gray400,
  accent: PALETTE.brand500, accentText: PALETTE.white, accentBg: PALETTE.brand50, accentPressed: PALETTE.brand600,
  bg: PALETTE.gray50, surface: PALETTE.white, border: PALETTE.gray200,
  danger: PALETTE.red600, success: PALETTE.brand600, warn: PALETTE.amber600, info: PALETTE.blue600,
  mapStyle: "mapbox://styles/mapbox/streets-v12",
  isDark: false,
};

const DARK_C = {
  ink: PALETTE.navy100, inkMid: PALETTE.navy500, inkFaint: PALETTE.navy300,
  accent: PALETTE.brand500, accentText: PALETTE.white, accentBg: PALETTE.brand900, accentPressed: PALETTE.brand600,
  bg: PALETTE.navy900, surface: PALETTE.navy800, border: PALETTE.navy700,
  danger: PALETTE.red500, success: PALETTE.brand500, warn: PALETTE.amber500, info: PALETTE.blue500,
  mapStyle: "mapbox://styles/mapbox/dark-v11",
  isDark: true,
};

const makeTheme = (C) => ({
  C,
  // Re-exported from the module-level tokens so consumers can grab everything
  // through useTheme() without a second import.
  SPACE, RADIUS, SHADOW, TYPE, PALETTE, ALPHA,
  T: {
    // T mirrors TYPE but pre-applies semantic colors so most call sites just
    // spread `T.h1` / `T.body` and don't have to think about colour. Use TYPE
    // directly when you need a non-default colour (e.g. white-on-cover).
    display: { ...TYPE.display, color: C.ink },
    h1:      { ...TYPE.h1, color: C.ink },
    h2:      { ...TYPE.h2, color: C.ink },
    h3:      { ...TYPE.h3, color: C.ink },
    body:    { ...TYPE.body, color: C.inkMid },
    caption: { ...TYPE.caption, color: C.inkFaint },
    micro:   { ...TYPE.micro, color: C.inkMid },
    // Legacy aliases so existing code keeps compiling. Migrate over time.
    small:   { ...TYPE.caption, color: C.inkFaint },
    label:   { ...TYPE.micro, color: C.inkMid },
    mono:    { fontSize: 13, fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace", color: C.inkMid },
  },
  // Buttons — RADIUS.sm (8) reads more confident than 10 once the type scale
  // bumps up to 15px body. Padding lands on the 4-px grid (12 vertical, 20 horizontal).
  btnPrimary: {
    background: C.accent, color: C.accentText, border: "none",
    borderRadius: RADIUS.sm, padding: `${SPACE[3]}px ${SPACE[5]}px`,
    fontSize: 15, fontWeight: 600, cursor: "pointer",
    letterSpacing: 0, transition: "opacity 0.15s, transform 0.15s",
  },
  btnSecondary: {
    background: "transparent", color: C.ink,
    border: `1px solid ${C.border}`, borderRadius: RADIUS.sm,
    padding: `${SPACE[3] - 1}px ${SPACE[5]}px`, fontSize: 15, fontWeight: 500,
    cursor: "pointer", transition: "background 0.15s",
  },
  inputBase: {
    width: "100%", padding: `${SPACE[3] - 1}px ${SPACE[4] - 2}px`, borderRadius: RADIUS.sm,
    border: `1px solid ${C.border}`, fontSize: 15,
    color: C.ink, background: C.surface, outline: "none",
    boxSizing: "border-box", fontFamily: "inherit",
    transition: "border-color 0.15s, box-shadow 0.15s",
  },
  // Cards default to shadow-only — borders are reserved for inputs/chips. The
  // border XOR shadow rule is what separates "Airbnb card" from "admin panel
  // card". When a card needs to feel grouped on the page, lean on SHADOW.sm.
  card:        { background: C.surface, borderRadius: RADIUS.md, boxShadow: SHADOW.sm },
  cardBorder:  { background: C.surface, borderRadius: RADIUS.md, border: `1px solid ${C.border}` },
  // Semantic surface colours — every status background, every danger banner
  // pulls from these instead of inventing one. Dark variants tinted toward
  // navy900 instead of pure black so they sit on the dark surface naturally.
  dangerBg:     C.isDark ? "#3a0f10" : PALETTE.red50,
  dangerBorder: C.isDark ? "#7a2020" : "#fecaca",
  warnBg:       C.isDark ? "#3a1f0a" : PALETTE.amber50,
  warnBorder:   C.isDark ? "#7a4f10" : "#fde68a",
  successBg:    C.isDark ? PALETTE.brand900 : PALETTE.brand50,
  successBorder:C.isDark ? "#1a5a2a" : "#bbf7d0",
  infoBg:       C.isDark ? "#0f1a3a" : PALETTE.blue50,
  infoBorder:   C.isDark ? "#1f3a7a" : "#bfdbfe",
  // Legacy alias — old code reads `infoNavyBg`; new code should use `infoBg`.
  infoNavyBg:   C.isDark ? "#0f1a3a" : PALETTE.blue50,

  // Skill levels — neutral sequence: brand-green (starter) → blue (steady)
  // → violet (advanced). Red/amber are reserved for *state* (filling, full,
  // cancelled) so the user never confuses skill with urgency. Each row uses
  // PALETTE values so the three families stay in lockstep when one is tweaked.
  levelColor: {
    "Beginner":     { bg: C.isDark ? PALETTE.brand900 : PALETTE.brand50,  text: PALETTE.brand600,  dot: PALETTE.brand500  },
    "Intermediate": { bg: C.isDark ? "#0b2540"        : PALETTE.blue50,   text: PALETTE.blue600,   dot: PALETTE.blue500   },
    "Advanced":     { bg: C.isDark ? "#1f1442"        : PALETTE.violet50, text: PALETTE.violet600, dot: PALETTE.violet500 },
    "All levels":   { bg: C.accentBg, text: C.accent, dot: C.accent },
  },

  // Domain-specific colour tokens. Anywhere you'd otherwise reach for a raw
  // hex like "#f59e0b" because "Team B is amber", use this instead.
  teams: {
    A: { fg: C.accent,        bg: C.accentBg },                                        // brand green — host's team
    B: { fg: PALETTE.amber600, bg: C.isDark ? "#3a1f0a" : PALETTE.amber50 },           // amber — opponent
  },
  // Cageball pitches get a violet badge to differentiate from regular pitches.
  // Same violet as Advanced skill — coincidence, but it works (both are "stronger").
  pitchType: {
    cage:  { fg: PALETTE.violet600, bg: C.isDark ? "#1f1442" : PALETTE.violet50 },
    field: { fg: C.success,         bg: C.isDark ? PALETTE.brand900 : PALETTE.brand50 },
  },
});

const ThemeCtx = React.createContext(makeTheme(LIGHT_C));
const useTheme = () => React.useContext(ThemeCtx);

function uiSegmentShell({ C, RADIUS, SHADOW }, opts = {}) {
  const {
    display = "inline-flex",
    gridTemplateColumns,
    width,
    flex,
    border = false,
  } = opts;
  return {
    display,
    ...(gridTemplateColumns ? { gridTemplateColumns } : {}),
    ...(width ? { width } : {}),
    ...(flex != null ? { flex } : {}),
    alignItems: "center",
    gap: 3,
    padding: 3,
    borderRadius: RADIUS.pill,
    background: C.surface,
    border: border ? `1px solid ${C.border}` : "none",
    boxShadow: SHADOW.sm,
    flexShrink: 0,
  };
}

function uiSegmentButton({ C, SPACE, RADIUS }, active, opts = {}) {
  const {
    flex,
    minWidth = 52,
    minHeight = 34,
    padding = `0 ${SPACE[3]}px`,
    fontSize = 13,
    activeColor = C.accent,
    activeWeight = 850,
    inactiveWeight = 650,
  } = opts;
  return {
    ...(flex != null ? { flex } : {}),
    minWidth,
    minHeight,
    padding,
    border: "none",
    borderRadius: RADIUS.pill,
    background: active ? C.accentBg : "transparent",
    color: active ? activeColor : C.inkMid,
    cursor: "pointer",
    fontSize,
    fontWeight: active ? activeWeight : inactiveWeight,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    gap: SPACE[2],
    boxShadow: active ? `inset 0 0 0 1px ${C.accent}10` : "none",
    transition: "background 0.15s, color 0.15s, box-shadow 0.15s",
  };
}

function uiIconControl({ C, RADIUS, SHADOW }, active = false, opts = {}) {
  const { size = 42 } = opts;
  return {
    position: "relative",
    width: size,
    height: size,
    padding: 0,
    borderRadius: RADIUS.pill,
    border: active ? `1px solid ${C.accent}33` : "none",
    background: active ? C.accentBg : C.surface,
    color: active ? C.accent : C.inkMid,
    boxShadow: SHADOW.sm,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    flexShrink: 0,
    transition: "background 0.15s, color 0.15s, transform 0.12s, box-shadow 0.15s",
  };
}

function uiFloatingBadge({ C, RADIUS }, background) {
  return {
    position: "absolute",
    top: -5,
    right: -4,
    minWidth: 17,
    height: 17,
    padding: "0 5px",
    borderRadius: RADIUS.pill,
    background: C.accent,
    color: C.accentText,
    fontSize: 10,
    fontWeight: 900,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    boxShadow: `0 0 0 2px ${background || C.bg}`,
  };
}

// Global C is used by the ErrorBoundary class component which cannot call hooks
let C = LIGHT_C;

// ─── Local notification helpers ───────────────────────────────────────────────
// Derive a stable 31-bit integer ID from a game UUID + slot (0=3h, 1=1h, 2=ball)
function _gameNotifId(gameId, slot) {
  let h = 5381;
  for (let i = 0; i < gameId.length; i++) h = ((h << 5) + h + gameId.charCodeAt(i)) & 0x7FFFFFFF;
  return (h * 3 + slot) & 0x7FFFFFFF;
}

// User-controlled notification preferences (written by the Settings screen).
// Single source of truth so the scheduler + list filter + future code all agree.
function getNotifPrefs() {
  try {
    const raw = JSON.parse(localStorage.getItem("kickr_notif_prefs") || "null");
    if (raw && typeof raw === "object") return { reminders: true, friends: true, chat: true, ...raw };
  } catch { /* corrupt JSON — fall through to defaults */ }
  return { reminders: true, friends: true, chat: true };
}

async function scheduleGameReminders(game, isBallHolder = false) {
  if (!game?.date || !game?.hour || !game?.id) return;
  // Respect the user's "Reminders" toggle — without this the setting was cosmetic.
  if (!getNotifPrefs().reminders) return;
  try {
    const LocalNotifications = await getLocalNotifications();
    const perm = await LocalNotifications.requestPermissions();
    if (perm.display !== "granted") return;

    // Cancel existing reminders first to avoid duplicates when re-scheduling
    await LocalNotifications.cancel({ notifications: [0, 1, 2, 3].map(s => ({ id: _gameNotifId(game.id, s) })) });

    const kickoff = new Date(`${game.date}T${game.hour}`).getTime();
    const now = Date.now();
    const notifs = [];
    const where = game.location || game.venue || "";

    const t3 = kickoff - 3 * 3600 * 1000;
    if (t3 > now) notifs.push({
      id: _gameNotifId(game.id, 0),
      title: game.title,
      body: where ? `Kicks off in 3 hours at ${where}` : "Kicks off in 3 hours",
      schedule: { at: new Date(t3), allowWhileIdle: true },
      sound: "default",
    });

    const t1 = kickoff - 3600 * 1000;
    if (t1 > now) notifs.push({
      id: _gameNotifId(game.id, 1),
      title: isBallHolder ? `${game.title} — don't forget the ball` : game.title,
      body: isBallHolder
        ? `Kicks off in 1 hour at ${where}. You're bringing the ball!`
        : `Kicks off in 1 hour${where ? ` at ${where}` : ""}`,
      schedule: { at: new Date(t1), allowWhileIdle: true },
      sound: "default",
    });

    if (isBallHolder) {
      const t2 = kickoff - 2 * 3600 * 1000;
      if (t2 > now) notifs.push({
        id: _gameNotifId(game.id, 2),
        title: "Don't forget the ball",
        body: `You're bringing the ball to ${game.title} in 2 hours`,
        schedule: { at: new Date(t2), allowWhileIdle: true },
        sound: "default",
      });
    }

    // Post-game review notification at the final whistle (kickoff + duration)
    const durationMs = (parseInt(game.duration) || 60) * 60 * 1000;
    const tReview = kickoff + durationMs;
    if (tReview > now) notifs.push({
      id: _gameNotifId(game.id, 3),
      title: `How was ${game.title}?`,
      body: "Rate the pitch, report no-shows and leave a review.",
      schedule: { at: new Date(tReview), allowWhileIdle: true },
      sound: "default",
    });

    if (notifs.length) await LocalNotifications.schedule({ notifications: notifs });
  } catch { /* plugin not available in web or not yet installed */ }
}

async function cancelGameReminders(gameId) {
  if (!gameId) return;
  try {
    const LocalNotifications = await getLocalNotifications();
    await LocalNotifications.cancel({ notifications: [0, 1, 2, 3].map(s => ({ id: _gameNotifId(gameId, s) })) });
  } catch { /* not available */ }
}

// Cancels every pending reminder this app has scheduled. Used when the user
// toggles the "Reminders" preference off so the change takes effect immediately
// instead of only blocking future schedules.
async function cancelAllScheduledReminders() {
  try {
    const LocalNotifications = await getLocalNotifications();
    const pending = await LocalNotifications.getPending?.();
    const notifications = pending?.notifications || [];
    if (notifications.length) {
      await LocalNotifications.cancel({ notifications: notifications.map(n => ({ id: n.id })) });
    }
  } catch { /* not available */ }
}
  
// ─── Config ───────────────────────────────────────────────────────────────────
const MAPBOX_TOKEN = import.meta.env.VITE_MAPBOX_TOKEN;

const VENUES_FALLBACK = [
  // ── Oslo – Outdoor pitches ─────────────────────────────────────────────────
  { id: 1,  name: "Dælenenga idrettspark",    address: "Seilduksgata 30, 0553 Oslo",                lat: 59.9255, lng: 10.7602, type: "Outdoor", size: "11er",     area: "Grünerløkka",       price: "Free", access: "Public when free" },
  { id: 2,  name: "Caltexløkka",              address: "Finnmarkgata 27, 0563 Oslo",                lat: 59.9163, lng: 10.7781, type: "Outdoor", size: "7er",      area: "Grünerløkka",       price: "Free", access: "Public when free" },
  { id: 3,  name: "Voldsløkka idrettspark",   address: "Stavangergata 40, 0467 Oslo",               lat: 59.9430, lng: 10.7567, type: "Outdoor", size: "11er/7er", area: "Sagene",            price: "Free", access: "Public when free" },
  { id: 4,  name: "Tørteberg kunstgress",     address: "Apalveien 8, 0371 Oslo",                    lat: 59.9366, lng: 10.7163, type: "Outdoor", size: "11er",     area: "Majorstuen",        price: "Free", access: "Public when free" },
  { id: 5,  name: "Frogner stadion",          address: "Middelthuns gate 26, 0368 Oslo",            lat: 59.9276, lng: 10.7096, type: "Outdoor", size: "11er",     area: "Frogner",           price: "Free", access: "Public when free" },
  { id: 6,  name: "Ullern idrettspark",       address: "Myntfunnveien 3, 0382 Oslo",                lat: 59.9256, lng: 10.6558, type: "Outdoor", size: "11er/7er", area: "Ullern",            price: "Free", access: "Public when free" },
  { id: 7,  name: "Grefsen stadion",          address: "Neptunveien 8, 0493 Oslo",                  lat: 59.9593, lng: 10.7814, type: "Outdoor", size: "11er/7er", area: "Nordre Aker",       price: "Free", access: "Public when free" },
  { id: 8,  name: "Korsvoll idrettspark",     address: "Langåsveien 18, 0880 Oslo",                 lat: 59.9613, lng: 10.7592, type: "Outdoor", size: "11er/7er", area: "Nordre Aker",       price: "Free", access: "Public when free" },
  { id: 9,  name: "Kringsjå idrettspark",     address: "Olav M. Troviks vei 13, 0864 Oslo",         lat: 59.9638, lng: 10.7349, type: "Outdoor", size: "11er/7er", area: "Nordre Aker",       price: "Free", access: "Public when free" },
  { id: 10, name: "Linderud idrettspark",     address: "Statsråd Mathiesens vei 27, 0594 Oslo",     lat: 59.9401, lng: 10.8338, type: "Outdoor", size: "11er",     area: "Bjerke",            price: "Free", access: "Public when free" },
  { id: 11, name: "Kalbakken idrettspark",    address: "Gårdsveien 6, 0952 Oslo",                   lat: 59.9548, lng: 10.8670, type: "Outdoor", size: "11er",     area: "Grorud",            price: "Free", access: "Public when free" },
  { id: 12, name: "Grorud idrettspark",       address: "Grorudveien 7, 0962 Oslo",                  lat: 59.9581, lng: 10.8820, type: "Outdoor", size: "11er",     area: "Grorud",            price: "Free", access: "Public when free" },
  { id: 13, name: "Ekeberg idrettspark",      address: "Ekebergsletta, 1181 Oslo",                  lat: 59.8942, lng: 10.7802, type: "Outdoor", size: "11er",     area: "Nordstrand",        price: "Free", access: "Public when free" },
  { id: 14, name: "KFUM Arena",               address: "Ekebergveien 109, 1178 Oslo",               lat: 59.8886, lng: 10.7821, type: "Outdoor", size: "11er/7er", area: "Nordstrand",        price: "Free", access: "Limited access" },
  { id: 15, name: "Nordstrand idrettspark",   address: "Oberst Rodes vei 79, 1165 Oslo",            lat: 59.8641, lng: 10.8089, type: "Outdoor", size: "11er",     area: "Nordstrand",        price: "Free", access: "Public when free" },
  { id: 16, name: "Lusetjern kunstgress",     address: "Lusetjernveien 37, 1253 Oslo",              lat: 59.8312, lng: 10.7879, type: "Outdoor", size: "11er",     area: "Holmlia",           price: "Free", access: "Public when free" },
  { id: 17, name: "Trasop idrettspark",       address: "Trasoppterrassen 10, 0672 Oslo",            lat: 59.9049, lng: 10.8459, type: "Outdoor", size: "11er/7er", area: "Østensjø",          price: "Free", access: "Public when free" },
  { id: 18, name: "Valle Hovin idrettspark",  address: "Innspurten 16, 0663 Oslo",                  lat: 59.9184, lng: 10.8057, type: "Outdoor", size: "11er/7er", area: "Helsfyr",           price: "Free", access: "Public when free" },
  { id: 19, name: "Løren idrettspark",        address: "Spireaveien 3, 0580 Oslo",                  lat: 59.9362, lng: 10.8050, type: "Outdoor", size: "11er/7er", area: "Løren",             price: "Free", access: "Public when free" },
  { id: 20, name: "Stovner stadion",          address: "Karl Fossums vei 30, 0985 Oslo",            lat: 59.9553, lng: 10.9211, type: "Outdoor", size: "11er",     area: "Stovner",           price: "Free", access: "Public when free" },
  { id: 38, name: "Hasle kunstgressbane",     address: "Haralds vei 1, 0576 Oslo",                  lat: 59.9244, lng: 10.7947, type: "Outdoor", size: "7er",      area: "Bjerke",            price: "Free", access: "Public when free" },
  { id: 39, name: "Heming idrettspark",       address: "Gulleråsveien 5, 0779 Oslo",                lat: 59.9541, lng: 10.6996, type: "Outdoor", size: "11er/7er", area: "Vestre Aker",       price: "Free", access: "Public when free" },
  { id: 40, name: "Holmlia idrettspark",      address: "Nordåsveien 1, 1251 Oslo",                  lat: 59.8370, lng: 10.7900, type: "Outdoor", size: "7er",      area: "Holmlia",           price: "Free", access: "Public when free" },
  { id: 41, name: "Myrerfeltet",              address: "Sandakerveien 24C, 0473 Oslo",              lat: 59.9500, lng: 10.7688, type: "Outdoor", size: "7er",      area: "Sagene",            price: "Free", access: "Public when free" },
  { id: 42, name: "Nordre Åsen idrettspark",  address: "Kjelsåsveien 7, 0488 Oslo",                 lat: 59.9428, lng: 10.7849, type: "Outdoor", size: "11er/7er", area: "Nordre Aker",       price: "Free", access: "Public when free" },
  { id: 43, name: "Røa idrettspark",          address: "Kristian Auberts vei 20, 0760 Oslo",        lat: 59.9472, lng: 10.6436, type: "Outdoor", size: "11er/7er", area: "Røa",               price: "Free", access: "Public when free" },
  { id: 44, name: "Årvoll idrettspark",       address: "Bård Skolemesters vei 1, 0590 Oslo",        lat: 59.9447, lng: 10.8142, type: "Outdoor", size: "11er/7er", area: "Alna",              price: "Free", access: "Public when free" },
  { id: 45, name: "Gressbanen Ready",         address: "Stasjonsveien 24, 0773 Oslo",               lat: 59.9515, lng: 10.6757, type: "Outdoor", size: "11er/7er", area: "Holmenkollen",      price: "Free", access: "Public when free" },
  { id: 50, name: "Jarmyra kunstgress",       address: "Sandakerveien 99, 0484 Oslo",               lat: 59.9444, lng: 10.7724, type: "Outdoor", size: "7er",      area: "Sagene",            price: "Free", access: "Public when free" },
  { id: 51, name: "Abildsø idrettspark",      address: "Smedbergveien 25, 1187 Oslo",               lat: 59.8851, lng: 10.8220, type: "Outdoor", size: "11er",     area: "Nordstrand",        price: "Free", access: "Public when free" },
  { id: 52, name: "Bjørndal idrettspark",     address: "Seterbråtveien 4, 1271 Oslo",               lat: 59.8265, lng: 10.8372, type: "Outdoor", size: "11er/9er", area: "Søndre Nordstrand", price: "Free", access: "Public when free" },
  { id: 53, name: "Disen idrettspark",        address: "Kildeveien, 0591 Oslo",                     lat: 59.9438, lng: 10.7937, type: "Outdoor", size: "11er",     area: "Alna",              price: "Free", access: "Public when free" },
  { id: 54, name: "Ellingsrud idrettspark",   address: "Gamle Strømsvei 109, 1061 Oslo",            lat: 59.9305, lng: 10.9234, type: "Outdoor", size: "11er",     area: "Alna",              price: "Free", access: "Public when free" },
  { id: 55, name: "Furuset idrettspark",      address: "Strømsveien 303, 1081 Oslo",                lat: 59.9411, lng: 10.8964, type: "Outdoor", size: "11er",     area: "Alna",              price: "Free", access: "Public when free" },
  { id: 56, name: "Hallager idrettspark",     address: "Herregårdsveien 40, 1168 Oslo",             lat: 59.8419, lng: 10.7818, type: "Outdoor", size: "11er",     area: "Nordstrand",        price: "Free", access: "Public when free" },
  { id: 57, name: "Haraløkka idrettspark",    address: "Bølerlia, 0688 Oslo",                       lat: 59.8831, lng: 10.8584, type: "Outdoor", size: "11er",     area: "Østensjø",          price: "Free", access: "Public when free" },
  { id: 58, name: "Jesperudjordet",           address: "Jesperudjordet, 0985 Oslo",                 lat: 59.9627, lng: 10.9289, type: "Outdoor", size: "11er",     area: "Stovner",           price: "Free", access: "Public when free" },
  { id: 59, name: "Jordal idrettspark",       address: "Jordalgata 1, 0657 Oslo",                   lat: 59.9081, lng: 10.7790, type: "Outdoor", size: "11er",     area: "Grünerløkka",       price: "Free", access: "Public when free" },
  { id: 60, name: "Lambertseter idrettspark", address: "Feltspatveien, 1155 Oslo",                  lat: 59.8734, lng: 10.8104, type: "Outdoor", size: "11er",     area: "Nordstrand",        price: "Free", access: "Public when free" },
  { id: 61, name: "Mortensrud idrettspark",   address: "Lofsrudveien, 1281 Oslo",                   lat: 59.8498, lng: 10.8276, type: "Outdoor", size: "11er",     area: "Søndre Nordstrand", price: "Free", access: "Public when free" },
  { id: 62, name: "Prinsdal idrettspark",     address: "Øvre Prinsdals vei 62, 1263 Oslo",          lat: 59.8331, lng: 10.8079, type: "Outdoor", size: "11er",     area: "Søndre Nordstrand", price: "Free", access: "Public when free" },
  { id: 63, name: "Rustad idrettspark",       address: "Paal Bergs vei 135, 0692 Oslo",             lat: 59.8730, lng: 10.8375, type: "Outdoor", size: "11er",     area: "Østensjø",          price: "Free", access: "Public when free" },
  { id: 64, name: "Veitvet idrettspark",      address: "Veitvetveien 8, 0596 Oslo",                 lat: 59.9440, lng: 10.8478, type: "Outdoor", size: "11er",     area: "Bjerke",            price: "Free", access: "Public when free" },
  // ── Bærum – Outdoor pitches ────────────────────────────────────────────────
  { id: 21, name: "Nadderud stadion",         address: "Gamle Ringeriksvei 59, 1357 Bekkestua",     lat: 59.9214, lng: 10.5802, type: "Outdoor", size: "11er",     area: "Bekkestua",         price: "Free", access: "Public when free" },
  { id: 22, name: "Sandvika stadion",         address: "Kadettangen 14, 1337 Sandvika",             lat: 59.8887, lng: 10.5281, type: "Outdoor", size: "11er/7er", area: "Sandvika",          price: "Free", access: "Public when free" },
  { id: 23, name: "Rud kunstgress",           address: "Hauger skolevei 36, 1351 Rud",              lat: 59.9099, lng: 10.4983, type: "Outdoor", size: "11er",     area: "Rud",               price: "Free", access: "Public when free" },
  { id: 24, name: "Haslum idrettspark",       address: "Gjønnesjordet 36, 1357 Bekkestua",          lat: 59.9152, lng: 10.5653, type: "Outdoor", size: "11er/7er", area: "Haslum",            price: "Free", access: "Public when free" },
  { id: 25, name: "Hoslebanen",               address: "Bispeveien 69, 1362 Hosle",                 lat: 59.9343, lng: 10.5879, type: "Outdoor", size: "11er/7er", area: "Hosle",             price: "Free", access: "Public when free" },
  { id: 26, name: "Rykkinn kunstgress",       address: "Leirdueveien 73, 1349 Rykkinn",             lat: 59.9339, lng: 10.4701, type: "Outdoor", size: "11er",     area: "Rykkinn",           price: "Free", access: "Public when free" },
  { id: 27, name: "Lommedalen kunstgress",    address: "Lommedalsveien 221, 1350 Lommedalen",       lat: 59.9685, lng: 10.4690, type: "Outdoor", size: "11er",     area: "Lommedalen",        price: "Free", access: "Public when free" },
  { id: 46, name: "Fossum kunstgress",        address: "Ankerveien 245, 1359 Eiksmarka",            lat: 59.9593, lng: 10.6132, type: "Outdoor", size: "11er/7er", area: "Bærum",             price: "Free", access: "Public when free" },
  { id: 65, name: "Bjørnegård kunstgress",    address: "Nedre Ås vei 39, 1341 Slependen",           lat: 59.8933, lng: 10.5109, type: "Outdoor", size: "11er",     area: "Bærum",             price: "Free", access: "Public when free" },
  { id: 66, name: "Eikeli kunstgress",        address: "Bispeveien 10, 1362 Hosle",                 lat: 59.9362, lng: 10.6016, type: "Outdoor", size: "11er/7er", area: "Bærum",             price: "Free", access: "Public when free" },
  { id: 67, name: "Emma Hjort",               address: "Tokes vei 50, 1336 Sandvika",               lat: 59.8966, lng: 10.4867, type: "Outdoor", size: "9er",      area: "Sandvika",          price: "Free", access: "Public when free" },
  { id: 68, name: "Hundsund kunstgress",      address: "Snarøyveien 83, 1367 Snarøya",              lat: 59.8882, lng: 10.6198, type: "Outdoor", size: "11er",     area: "Bærum",             price: "Free", access: "Public when free" },
  { id: 69, name: "Jarmyra idrettspark",      address: "Bærumsveien 126, 1358 Jar",                 lat: 59.9247, lng: 10.6126, type: "Outdoor", size: "11er",     area: "Bærum",             price: "Free", access: "Public when free" },
  { id: 70, name: "Storøya kunstgress",       address: "Forneburingen 300, 1360 Fornebu",           lat: 59.8940, lng: 10.6100, type: "Outdoor", size: "11er",     area: "Fornebu",           price: "Free", access: "Public when free" },
  // ── Cageball / ballbinge ───────────────────────────────────────────────────
  { id: 28, name: "Torshov cageball",         address: "Torshovdalen, 0854 Oslo",                   lat: 59.9350, lng: 10.7777, type: "Cageball", size: "5v5",     area: "Sagene",            price: "Free", access: "Always open" },
  { id: 29, name: "Voldsløkka cages",         address: "Stavangergata 40, 0467 Oslo",               lat: 59.9430, lng: 10.7567, type: "Cageball", size: "3v3/5v5", area: "Sagene",            price: "Free", access: "Always open" },
  { id: 30, name: "Bjølsenparken cages",      address: "Grimstadgata 30J, 0464 Oslo",               lat: 59.9388, lng: 10.7552, type: "Cageball", size: "3v3/7er", area: "Sagene",            price: "Free", access: "Always open" },
  { id: 31, name: "Ekeberg cages",            address: "Ekebergsletta, 1181 Oslo",                  lat: 59.8942, lng: 10.7802, type: "Cageball", size: "3v3",     area: "Nordstrand",        price: "Free", access: "Always open" },
  { id: 32, name: "Linderud cageball",        address: "Statsråd Mathiesens vei 27, 0594 Oslo",     lat: 59.9401, lng: 10.8338, type: "Cageball", size: "5v5",     area: "Bjerke",            price: "Free", access: "Always open" },
  { id: 33, name: "Stovner cageball",         address: "Karl Fossums vei 30, 0985 Oslo",            lat: 59.9617, lng: 10.9289, type: "Cageball", size: "5v5",     area: "Stovner",           price: "Free", access: "Always open" },
  { id: 34, name: "Furuset cageball",         address: "Strømsveien 303, 1081 Oslo",                lat: 59.9338, lng: 10.8731, type: "Cageball", size: "5v5",     area: "Alna",              price: "Free", access: "Always open" },
  { id: 35, name: "Mortensrud cageball",      address: "Lofsrudveien, 1281 Oslo",                   lat: 59.8498, lng: 10.8276, type: "Cageball", size: "5v5",     area: "Søndre Nordstrand", price: "Free", access: "Always open" },
  { id: 36, name: "Røa mini pitch",           address: "Kristian Auberts vei 20, 0760 Oslo",        lat: 59.9430, lng: 10.6351, type: "Cageball", size: "5v5",     area: "Røa",               price: "Free", access: "Always open" },
  { id: 37, name: "Nadderud cageball",        address: "Gamle Ringeriksvei 59, 1357 Bekkestua",     lat: 59.9214, lng: 10.5802, type: "Cageball", size: "5v5",     area: "Bærum",             price: "Free", access: "Always open" },
  { id: 47, name: "Muselunden ballbinge",     address: "Muselunden, 0582 Oslo",                     lat: 59.9050, lng: 10.8442, type: "Cageball", size: "5v5",     area: "Alna",              price: "Free", access: "Always open" },
  { id: 48, name: "Greibanen ballbinge",      address: "Greibanen, 0956 Oslo",                      lat: 59.9578, lng: 10.8616, type: "Cageball", size: "5v5",     area: "Grorud",            price: "Free", access: "Always open" },
  { id: 49, name: "Lille Bislett",            address: "Dalsbergstien 17, 0170 Oslo",               lat: 59.9234, lng: 10.7340, type: "Cageball", size: "5v5",     area: "Frogner",           price: "Free", access: "Always open" },
  { id: 71, name: "Ferd kunstgress",          address: "Bygdøyveien 20, 0287 Oslo",                  lat: 59.9179, lng: 10.6839, type: "Outdoor", size: "7er",      area: "Bygdøy",            price: "Free", access: "Public when free" },
  // ── Bergen — OSM-verified coordinates ────────────────────────────────────
  { id: 100, name: "Alvøen idrettspark",    address: "Alvøen, Bergen",                        lat: 60.3632, lng: 5.1983, type: "Outdoor", size: "11er/7er", area: "Laksevåg",      price: "Free", access: "Public when free", city: "Bergen" },
  { id: 101, name: "Arna Idrettspark",      address: "Stølsvegen, Arna, Bergen",              lat: 60.4257, lng: 5.4717, type: "Outdoor", size: "11er",     area: "Arna",          price: "Free", access: "Public when free", city: "Bergen" },
  { id: 102, name: "Breimyra",              address: "Breimyra, Bergen",                      lat: 60.4622, lng: 5.3653, type: "Outdoor", size: "11er",     area: "Åsane",         price: "Free", access: "Public when free", city: "Bergen" },
  { id: 103, name: "Bønes",                 address: "Bønes, Bergen",                         lat: 60.3322, lng: 5.3079, type: "Outdoor", size: "9er",      area: "Fyllingsdalen", price: "Free", access: "Public when free", city: "Bergen" },
  { id: 104, name: "Eidsvåg",               address: "Eidsvåg, Bergen",                       lat: 60.4411, lng: 5.3021, type: "Outdoor", size: "11er",     area: "Åsane",         price: "Free", access: "Public when free", city: "Bergen" },
  { id: 105, name: "Espeland",              address: "Espeland, Bergen",                      lat: 60.3810, lng: 5.4636, type: "Outdoor", size: "11er",     area: "Arna",          price: "Free", access: "Public when free", city: "Bergen" },
  { id: 106, name: "Fjellsdalen",           address: "Fjellsdalen, Bergen",                   lat: 60.3349, lng: 5.2945, type: "Outdoor", size: "11er",     area: "Fyllingsdalen", price: "Free", access: "Public when free", city: "Bergen" },
  { id: 107, name: "Flaktveit stadion",     address: "Flaktveit, Bergen",                     lat: 60.4570, lng: 5.3681, type: "Outdoor", size: "11er/7er", area: "Åsane",         price: "Free", access: "Public when free", city: "Bergen" },
  { id: 108, name: "Fyllingsdalen stadion", address: "Hjalmar Brantings vei, Bergen",         lat: 60.3480, lng: 5.2877, type: "Outdoor", size: "11er/7er", area: "Fyllingsdalen", price: "Free", access: "Public when free", city: "Bergen" },
  { id: 109, name: "Garnes",                address: "Garneshøyden, Arna, Bergen",             lat: 60.4468, lng: 5.4702, type: "Outdoor", size: "11er",     area: "Arna",          price: "Free", access: "Public when free", city: "Bergen" },
  { id: 110, name: "Gravdal",               address: "Gravdal, Bergen",                       lat: 60.3916, lng: 5.2554, type: "Outdoor", size: "11er",     area: "Laksevåg",      price: "Free", access: "Public when free", city: "Bergen" },
  { id: 111, name: "Gyldenpris",            address: "Gyldenprisveien, Bergen",               lat: 60.3813, lng: 5.3165, type: "Outdoor", size: "7er",      area: "Gyldenpris",    price: "Free", access: "Public when free", city: "Bergen" },
  { id: 112, name: "Hjellestad",            address: "Grendatunvegen, Bergen",                lat: 60.2598, lng: 5.2461, type: "Outdoor", size: "11er",     area: "Ytrebygda",     price: "Free", access: "Public when free", city: "Bergen" },
  { id: 113, name: "Kalandseid",            address: "Kalandseid, Fana, Bergen",              lat: 60.2794, lng: 5.4044, type: "Outdoor", size: "11er/7er", area: "Fana",          price: "Free", access: "Public when free", city: "Bergen" },
  { id: 114, name: "Kjøkkelvik",            address: "Kjøkkelvik, Bergen",                    lat: 60.3889, lng: 5.2271, type: "Outdoor", size: "11er",     area: "Laksevåg",      price: "Free", access: "Public when free", city: "Bergen" },
  { id: 115, name: "Kongsmyren",            address: "Kongsmyren, Bergen",                    lat: 60.3368, lng: 5.2095, type: "Outdoor", size: "11er",     area: "Laksevåg",      price: "Free", access: "Public when free", city: "Bergen" },
  { id: 116, name: "Kringlebotn",           address: "Kringlebotn, Bergen",                   lat: 60.3322, lng: 5.3704, type: "Outdoor", size: "11er",     area: "Fana",          price: "Free", access: "Public when free", city: "Bergen" },
  { id: 117, name: "Krohnsminde",           address: "Krohnsminde, Bergen",                   lat: 60.3757, lng: 5.3415, type: "Outdoor", size: "11er/7er", area: "Møllendal",     price: "Free", access: "Public when free", city: "Bergen" },
  { id: 118, name: "Landås",                address: "Landåsbanen, Bergen",                   lat: 60.3615, lng: 5.3720, type: "Outdoor", size: "11er",     area: "Landås",        price: "Free", access: "Public when free", city: "Bergen" },
  { id: 119, name: "Leikvang",              address: "Leikvang, Bergen",                      lat: 60.4920, lng: 5.3899, type: "Outdoor", size: "11er",     area: "Åsane",         price: "Free", access: "Public when free", city: "Bergen" },
  { id: 120, name: "Liland",                address: "Liland, Ytrebygda, Bergen",             lat: 60.2876, lng: 5.2428, type: "Outdoor", size: "11er",     area: "Ytrebygda",     price: "Free", access: "Public when free", city: "Bergen" },
  { id: 121, name: "Marikollen",            address: "Marikollen, Bergen",                    lat: 60.4852, lng: 5.2629, type: "Outdoor", size: "11er",     area: "Åsane",         price: "Free", access: "Public when free", city: "Bergen" },
  { id: 122, name: "Møhlenpris",            address: "Møhlenpris, Bergen",                    lat: 60.3833, lng: 5.3250, type: "Outdoor", size: "11er",     area: "Møhlenpris",    price: "Free", access: "Public when free", city: "Bergen" },
  { id: 123, name: "Nesttun",               address: "Nesttun idrettsplass, Bergen",          lat: 60.3163, lng: 5.3544, type: "Outdoor", size: "11er/7er", area: "Fana",          price: "Free", access: "Public when free", city: "Bergen" },
  { id: 124, name: "Nygårdsmyren",          address: "Nygårdsmyren, Bergen",                  lat: 60.3914, lng: 5.2768, type: "Outdoor", size: "11er",     area: "Laksevåg",      price: "Free", access: "Public when free", city: "Bergen" },
  { id: 125, name: "Nymark",                address: "Fredrik Stangsvei 14, Bergen",          lat: 60.3691, lng: 5.3543, type: "Outdoor", size: "11er/7er", area: "Nymark",        price: "Free", access: "Public when free", city: "Bergen" },
  { id: 126, name: "Olsvik",                address: "Olsvik, Bergen",                        lat: 60.3803, lng: 5.2160, type: "Outdoor", size: "11er",     area: "Laksevåg",      price: "Free", access: "Public when free", city: "Bergen" },
  { id: 127, name: "Paradis",               address: "Paradis, Fana, Bergen",                 lat: 60.3361, lng: 5.3469, type: "Outdoor", size: "11er",     area: "Fana",          price: "Free", access: "Public when free", city: "Bergen" },
  { id: 128, name: "Rautjern",              address: "Bendixens vei, Minde, Bergen",          lat: 60.3607, lng: 5.3536, type: "Outdoor", size: "11er",     area: "Årstad",        price: "Free", access: "Public when free", city: "Bergen" },
  { id: 129, name: "Rolland",               address: "Rolland, Åsane, Bergen",                lat: 60.4627, lng: 5.3312, type: "Outdoor", size: "11er",     area: "Åsane",         price: "Free", access: "Public when free", city: "Bergen" },
  { id: 130, name: "Salhus",                address: "Salhus, Bergen",                        lat: 60.5065, lng: 5.2678, type: "Outdoor", size: "11er",     area: "Åsane",         price: "Free", access: "Public when free", city: "Bergen" },
  { id: 131, name: "Sandsli",               address: "Sandslivegen, Bergen",                  lat: 60.2943, lng: 5.2814, type: "Outdoor", size: "11er/7er", area: "Ytrebygda",     price: "Free", access: "Public when free", city: "Bergen" },
  { id: 132, name: "Slettebakken",          address: "Slettebakken, Bergen",                  lat: 60.3561, lng: 5.3610, type: "Outdoor", size: "11er",     area: "Fana",          price: "Free", access: "Public when free", city: "Bergen" },
  { id: 133, name: "Slåtthaug",             address: "Slåtthaug, Bergen",                     lat: 60.3102, lng: 5.3589, type: "Outdoor", size: "11er",     area: "Fana",          price: "Free", access: "Public when free", city: "Bergen" },
  { id: 134, name: "Stavollen",             address: "Stavollen, Bergen",                     lat: 60.2797, lng: 5.3136, type: "Outdoor", size: "11er",     area: "Fana",          price: "Free", access: "Public when free", city: "Bergen" },
  { id: 135, name: "Stemmemyren",           address: "Stemmemyren, Bergen",                   lat: 60.4258, lng: 5.3054, type: "Outdoor", size: "11er",     area: "Åsane",         price: "Free", access: "Public when free", city: "Bergen" },
  { id: 136, name: "Sædalen",               address: "Sædalen, Bergen",                       lat: 60.3490, lng: 5.3924, type: "Outdoor", size: "11er",     area: "Fana",          price: "Free", access: "Public when free", city: "Bergen" },
  { id: 137, name: "Søvik",                 address: "Søviktjernet, Bergen",                  lat: 60.3084, lng: 5.2913, type: "Outdoor", size: "11er",     area: "Fyllingsdalen", price: "Free", access: "Public when free", city: "Bergen" },
  { id: 138, name: "Vadmyra",               address: "Vadmyra, Bergen",                       lat: 60.3615, lng: 5.2306, type: "Outdoor", size: "11er",     area: "Laksevåg",      price: "Free", access: "Public when free", city: "Bergen" },
  { id: 139, name: "Varden Amfi",           address: "Allestadveien 12, Fyllingsdalen",       lat: 60.3415, lng: 5.2685, type: "Outdoor", size: "11er",     area: "Fyllingsdalen", price: "Free", access: "Public when free", city: "Bergen" },
  { id: 140, name: "Ytre Arna",             address: "Ytre Arna, Bergen",                     lat: 60.4587, lng: 5.4391, type: "Outdoor", size: "11er",     area: "Arna",          price: "Free", access: "Public when free", city: "Bergen" },
  { id: 141, name: "Åsane stadion",         address: "Tertnesveien, Åsane, Bergen",           lat: 60.4573, lng: 5.3092, type: "Outdoor", size: "11er",     area: "Åsane",         price: "Free", access: "Public when free", city: "Bergen" },
  { id: 142, name: "Åstveit",               address: "Tertnesveien, Åsane, Bergen",           lat: 60.4495, lng: 5.3218, type: "Outdoor", size: "11er",     area: "Åsane",         price: "Free", access: "Public when free", city: "Bergen" },
  { id: 143, name: "Petedalsmyra",          address: "Petedalsmyra, Bergen",                  lat: 60.2985, lng: 5.2905, type: "Outdoor", size: "9er",      area: "Ytrebygda",     price: "Free", access: "Public when free", city: "Bergen" },
  { id: 144, name: "Nordahl Grieg vgs",     address: "Nordahl Grieg vgs, Bergen",             lat: 60.2994, lng: 5.3235, type: "Outdoor", size: "7er",      area: "Ytrebygda",     price: "Free", access: "Public when free", city: "Bergen" },
  { id: 145, name: "Ådnamarka",             address: "Ådnamarka skole, Arna, Bergen",         lat: 60.4107, lng: 5.4769, type: "Outdoor", size: "11er",     area: "Arna",          price: "Free", access: "Public when free", city: "Bergen" },
  { id: 146, name: "Tellevik",              address: "Tellevik, Bergen",                      lat: 60.5146, lng: 5.2917, type: "Outdoor", size: "11er",     area: "Åsane",         price: "Free", access: "Public when free", city: "Bergen" },
  // ── Trondheim ─────────────────────────────────────────────────────────────
  { id: 150, name: "Eberg idrettspark",         address: "Ebergvegen 22, 7050 Trondheim",       lat: 63.4166, lng: 10.4325, type: "Outdoor", size: "11er", area: "Moholt",         price: "Free", access: "Public when free", city: "Trondheim" },
  { id: 151, name: "Lade idrettspark",          address: "Håkon Magnussons gate, 7041 Trondheim", lat: 63.4449, lng: 10.4508, type: "Outdoor", size: "11er", area: "Lade",         price: "Free", access: "Public when free", city: "Trondheim" },
  { id: 152, name: "Dalgård idrettspark",       address: "Dalgårdsvegen 9, 7024 Trondheim",     lat: 63.3963, lng: 10.3460, type: "Outdoor", size: "11er", area: "Byåsen",         price: "Free", access: "Public when free", city: "Trondheim" },
  { id: 153, name: "Saupstad idrettspark",      address: "Saupstadringen 13, 7078 Trondheim",   lat: 63.3653, lng: 10.3526, type: "Outdoor", size: "11er", area: "Saupstad",       price: "Free", access: "Public when free", city: "Trondheim" },
  { id: 154, name: "Flatåsen idrettspark",      address: "Flatåsen, 7079 Trondheim",            lat: 63.3765, lng: 10.3383, type: "Outdoor", size: "11er", area: "Flatåsen",       price: "Free", access: "Public when free", city: "Trondheim" },
  { id: 155, name: "Kattem idrettspark",        address: "Øvre Flatåsveg 51, 7079 Trondheim",   lat: 63.3465, lng: 10.3338, type: "Outdoor", size: "11er", area: "Kattem",         price: "Free", access: "Public when free", city: "Trondheim" },
  { id: 156, name: "Heimdal kunstgress",        address: "Blisterhaugvegen 17, 7080 Heimdal",   lat: 63.3664, lng: 10.3526, type: "Outdoor", size: "7er",  area: "Heimdal",        price: "Free", access: "Public when free", city: "Trondheim" },
  { id: 157, name: "Charlottenlund idrettspark",address: "Yrkesskolevegen 16, 7058 Trondheim",  lat: 63.4270, lng: 10.4946, type: "Outdoor", size: "11er", area: "Charlottenlund", price: "Free", access: "Public when free", city: "Trondheim" },
  { id: 158, name: "Strindheim idrettspark",    address: "Leangenvegen 12, 7044 Trondheim",     lat: 63.4269, lng: 10.4534, type: "Outdoor", size: "11er", area: "Strindheim",     price: "Free", access: "Public when free", city: "Trondheim" },
  { id: 159, name: "Nardo kunstgress",          address: "Hans Baucks veg 15, 7033 Trondheim",  lat: 63.4075, lng: 10.4197, type: "Outdoor", size: "7er",  area: "Nardo",          price: "Free", access: "Public when free", city: "Trondheim" },
];

const VENUE_TYPE_OPTIONS = ["Outdoor", "Cageball", "Indoor"];
const VENUE_TIER_OPTIONS = ["", "A", "B", "C"];
const emptyAdminVenueForm = () => ({
  name: "",
  address: "",
  lat: "",
  lng: "",
  type: "Outdoor",
  size: "",
  area: "",
  price: "Free",
  access: "Public when free",
  city: "Oslo",
  tier: "",
  details: "",
});
const textOrNull = (value) => {
  const next = String(value ?? "").trim();
  return next || null;
};
const normaliseVenueRow = (row) => {
  if (!row) return null;
  const lat = Number(row.lat);
  const lng = Number(row.lng);
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
  return {
    ...row,
    id: row.id != null ? String(row.id) : `${lat.toFixed(6)},${lng.toFixed(6)}`,
    name: row.name || row.address || "Venue",
    address: row.address || "",
    lat,
    lng,
    type: row.type || "Outdoor",
    size: row.size || "",
    area: row.area || "",
    price: row.price || "Free",
    access: row.access || "",
    city: row.city || "Oslo",
    tier: explicitVenueTier(row),
    details: row.details || "",
  };
};
const explicitVenueTier = (venue) => (
  venue?.tier === "A" || venue?.tier === "B" || venue?.tier === "C" ? venue.tier : ""
);
const effectiveVenueTier = (venue) => explicitVenueTier(venue) || getVenueTier(venue);
const sortVenueRows = (rows = []) => [...rows].sort((a, b) => {
  const city = String(a.city || "").localeCompare(String(b.city || ""));
  if (city) return city;
  return String(a.name || "").localeCompare(String(b.name || ""));
});
const normaliseVenueList = (rows = []) => sortVenueRows((rows || []).map(normaliseVenueRow).filter(Boolean));
const venueFormFromRow = (row = {}) => ({
  ...emptyAdminVenueForm(),
  name: row.name || "",
  address: row.address || "",
  lat: row.lat ?? "",
  lng: row.lng ?? "",
  type: row.type || "Outdoor",
  size: row.size || "",
  area: row.area || "",
  price: row.price || "Free",
  access: row.access || "Public when free",
  city: row.city || "Oslo",
  tier: explicitVenueTier(row),
  details: row.details || "",
});
const venuePayloadFromForm = (form) => ({
  name: textOrNull(form.name),
  address: textOrNull(form.address),
  lat: Number(form.lat),
  lng: Number(form.lng),
  type: textOrNull(form.type) || "Outdoor",
  size: textOrNull(form.size),
  area: textOrNull(form.area),
  price: textOrNull(form.price) || "Free",
  access: textOrNull(form.access),
  city: textOrNull(form.city) || "Oslo",
  tier: textOrNull(form.tier),
  details: textOrNull(form.details),
});
const venueSeedPayloadFromRow = (row) => ({
  id: String(row.id),
  ...venuePayloadFromForm(venueFormFromRow(row)),
});
const makeAdminVenueId = () => String(Math.floor(Date.now() / 1000) + Math.floor(Math.random() * 1000));

const FORMAT_SPOTS = { "3v3": 6, "4v4": 8, "5v5": 10, "6v6": 12, "7v7": 14, "8v8": 16, "9v9": 18, "11v11": 22 };
const MIN_PLAYERS_BY_FORMAT = {
  "3v3": [4, 6],
  "5v5": [8, 10],
  "6v6": [10, 12],
  "7v7": [12, 14],
  "8v8": [14, 16],
  "9v9": [16, 18],
  "11v11": [18, 20, 22],
};
const minPlayerOptionsForFormat = (format) => {
  const max = FORMAT_SPOTS[format] || parseInt(String(format).split("v")[0]) * 2 || 10;
  return (MIN_PLAYERS_BY_FORMAT[format] || [Math.max(2, max - 2), max]).filter(n => n <= max);
};
const defaultMinPlayersForFormat = (format) => minPlayerOptionsForFormat(format).at(-1) || 10;

// ─── Helpers ──────────────────────────────────────────────────────────────────
const getDistance = (lat1, lng1, lat2, lng2) => {
  const R = 6371, dLat = (lat2-lat1)*Math.PI/180, dLng = (lng2-lng1)*Math.PI/180;
  const a = Math.sin(dLat/2)**2 + Math.cos(lat1*Math.PI/180)*Math.cos(lat2*Math.PI/180)*Math.sin(dLng/2)**2;
  return R*2*Math.atan2(Math.sqrt(a),Math.sqrt(1-a));
};
const fmtDist = km => km < 1 ? `${Math.round(km*1000)} m` : `${km.toFixed(1)} km`;
const isFilling = g => g.spotsFilled/g.spotsTotal >= 0.8 && g.spotsFilled < g.spotsTotal;
const gameStartMs = (g) => {
  if (!g?.date) return Number.POSITIVE_INFINITY;
  const ms = new Date(`${g.date}T${g.hour || "00:00"}`).getTime();
  return Number.isFinite(ms) ? ms : Number.POSITIVE_INFINITY;
};
const getGameEndTime = (g) => {
  if (g?.endTime) return g.endTime;
  if (!g?.date || !g?.hour) return null;
  return new Date(`${g.date}T${g.hour}`).getTime() + (parseInt(g.duration) || 60) * 60000;
};
const isPastGame = (g) => {
  const end = getGameEndTime(g);
  return !!end && end <= Date.now();
};
const getGameVisibility = (g) => String(g?.visibility || (g?.is_private ? "private" : "public")).toLowerCase();
const getConfirmedPlayerCount = (g) => (g?.game_players || []).filter(p => !p.status || p.status === "confirmed").length;
const getGameSpotsTotal = (g) => Number(g?.spots_total ?? g?.spotsTotal ?? FORMAT_SPOTS[g?.format] ?? 0) || 0;
const getDefaultDiscoverHiddenReason = (g, now = Date.now()) => {
  if (!g) return "Missing data";
  if (g.status === "cancelled") return "Cancelled";
  const end = getGameEndTime(g);
  if (end && end <= now) return "Finished";
  const visibility = getGameVisibility(g);
  if (visibility === "private") return "Private";
  if (visibility === "community") return "Group-only";
  const spotsTotal = getGameSpotsTotal(g);
  if (spotsTotal > 0 && getConfirmedPlayerCount(g) >= spotsTotal) return "Full";
  return "";
};
const AUTO_CANCEL_BEFORE_MS = 3 * 60 * 60 * 1000;
const AUTO_CANCEL_REASON = "Auto-cancelled: minimum players not reached 3 hours before kickoff";
const URGENT_GAME_WINDOW_MS = 24 * 60 * 60 * 1000;
const playersNeededToConfirm = (g) => Math.max(0, (Number(g?.minPlayers) || Number(g?.spotsTotal) || 0) - (Number(g?.spotsFilled) || 0));
const shouldAutoCancelGame = (g, now = Date.now()) => {
  const start = gameStartMs(g);
  return g?.status !== "cancelled"
    && playersNeededToConfirm(g) > 0
    && Number.isFinite(start)
    && start > now
    && start - now <= AUTO_CANCEL_BEFORE_MS;
};
const isUrgentPlayerNeededGame = (g, now = Date.now()) => {
  const start = gameStartMs(g);
  return g?.status !== "cancelled"
    && playersNeededToConfirm(g) > 0
    && Number.isFinite(start)
    && start > now
    && start - now <= URGENT_GAME_WINDOW_MS;
};
const parseLocalDateParts = (dateStr) => {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(String(dateStr || ""))) return null;
  const [year, month, day] = dateStr.split("-").map(Number);
  if (!year || !month || !day) return null;
  return { year, month, day };
};
const formatLocalDate = (date) => `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
const isFutureGameDateTime = (dateStr, hourStr) => {
  if (!dateStr || !hourStr) return false;
  const ms = new Date(`${dateStr}T${hourStr}`).getTime();
  return Number.isFinite(ms) && ms > Date.now();
};
const getNearestFutureSameWeekdayDate = (sourceDateStr, hourStr = "18:00") => {
  const sourceParts = parseLocalDateParts(sourceDateStr);
  const now = new Date();
  const targetWeekday = sourceParts
    ? new Date(sourceParts.year, sourceParts.month - 1, sourceParts.day).getDay()
    : now.getDay();
  const [hour = 18, minute = 0] = String(hourStr || "18:00").slice(0, 5).split(":").map(Number);
  const candidate = new Date(now);
  candidate.setHours(Number.isFinite(hour) ? hour : 18, Number.isFinite(minute) ? minute : 0, 0, 0);
  const daysUntilTarget = (targetWeekday - candidate.getDay() + 7) % 7;
  candidate.setDate(candidate.getDate() + daysUntilTarget);
  if (candidate.getTime() <= now.getTime()) candidate.setDate(candidate.getDate() + 7);
  return formatLocalDate(candidate);
};
function smartGameScore(g, { joined = false, waitlisted = false, host = false } = {}) {
  if (!g) return -9999;
  if (isPastGame(g)) return -9000;
  const spotsTotal = Math.max(1, g.spotsTotal || 1);
  const spotsFilled = Math.max(0, g.spotsFilled || 0);
  const spotsLeft = Math.max(0, spotsTotal - spotsFilled);
  const fillRatio = Math.min(1, spotsFilled / spotsTotal);
  const minPlayers = Math.max(1, g.minPlayers || spotsTotal);
  const needToStart = Math.max(0, minPlayers - spotsFilled);
  const hoursUntilStart = (gameStartMs(g) - Date.now()) / 36e5;

  let score = 0;
  if (g.distance != null && Number.isFinite(g.distance)) score += Math.max(0, 42 - g.distance * 7);
  if (hoursUntilStart >= 0) score += Math.max(0, 26 - Math.min(hoursUntilStart, 72) * 0.35);
  if (spotsLeft > 0) score += 10;
  score += fillRatio * 18;
  if (needToStart === 0) score += 14;
  else score += Math.max(0, 12 - needToStart * 3);
  if (joined) score += 5;
  if (waitlisted) score += 2;
  if (host) score -= 2;
  if (spotsLeft === 0) score -= 45;
  if (g.status === "cancelled") score -= 200;
  return score;
}
const FILTER_DATE_MAX_DAYS = 30;
const FILTER_DATE_DEFAULT_RANGE = [0, FILTER_DATE_MAX_DAYS];
const FILTER_RADIUS_MIN_KM = 1;
const FILTER_RADIUS_MAX_KM = 25;
const DAY_MS = 24 * 60 * 60 * 1000;
const normaliseFilterDateRange = (range) => {
  const rawStart = Array.isArray(range) ? Number(range[0]) : 0;
  const rawEnd = Array.isArray(range) ? Number(range[1]) : FILTER_DATE_MAX_DAYS;
  const start = Math.max(0, Math.min(FILTER_DATE_MAX_DAYS, Number.isFinite(rawStart) ? Math.round(rawStart) : 0));
  const end = Math.max(0, Math.min(FILTER_DATE_MAX_DAYS, Number.isFinite(rawEnd) ? Math.round(rawEnd) : FILTER_DATE_MAX_DAYS));
  return [Math.min(start, end), Math.max(start, end)];
};
const isDefaultFilterDateRange = (range) => {
  const [start, end] = normaliseFilterDateRange(range);
  return start === FILTER_DATE_DEFAULT_RANGE[0] && end === FILTER_DATE_DEFAULT_RANGE[1];
};
const getUpcomingWeekendRange = () => {
  const day = new Date().getDay();
  if (day === 0) return [0, 0];
  if (day === 6) return [0, 1];
  return [6 - day, 7 - day];
};
const getDateOffset = (dateString) => {
  if (!dateString) return null;
  const [y, mo, d] = dateString.split("-").map(Number);
  if (!y || !mo || !d) return null;
  const today = new Date(); today.setHours(0, 0, 0, 0);
  return Math.round((new Date(y, mo - 1, d).getTime() - today.getTime()) / DAY_MS);
};
const formatUtcCalendarDate = d => d.toISOString().replace(/[-:]/g, "").replace(/\.\d{3}/, "");
const buildGameCalendarEvent = (game, backupAddress = "") => {
  const start = new Date(`${game.date}T${game.hour}`);
  if (Number.isNaN(start.getTime())) return null;
  const durationMinutes = parseInt(game.duration, 10) || 60;
  const end = new Date(start.getTime() + durationMinutes * 60000);
  const title = game.title || "Kickr game";
  const location = backupAddress
    ? `${game.location || ""} (backup: ${backupAddress})`
    : game.location || "";
  const gameUrl = typeof window !== "undefined" ? `${window.location.origin}?game=${game.id}` : "";
  const details = [
    `${game.format || "Football"} at ${game.location || "the pitch"}.`,
    backupAddress ? `Backup pitch: ${backupAddress}.` : "",
    game.host ? `Host: ${game.host}.` : "",
    game.description || "",
    gameUrl ? `Open in Kickr: ${gameUrl}` : "",
  ].filter(Boolean).join("\n");
  const dates = `${formatUtcCalendarDate(start)}/${formatUtcCalendarDate(end)}`;
  const googleUrl = `https://calendar.google.com/calendar/render?${new URLSearchParams({
    action: "TEMPLATE",
    text: title,
    dates,
    details,
    location,
  }).toString()}`;
  return { googleUrl };
};
const formatNotifTime = (value) => {
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const then = new Date(d); then.setHours(0, 0, 0, 0);
  const time = d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  return then.getTime() === today.getTime()
    ? time
    : `${d.toLocaleDateString("en-GB", { day: "numeric", month: "short" })} · ${time}`;
};
const fmtCountdown = (date, hour) => {
  if (!date || !hour) return null;
  const start = new Date(`${date}T${hour}`);
  const now = new Date();
  const diff = start - now;
  if (Math.abs(diff) < 60000) return t("startingNow");
  if (diff < 0) {
    const m = Math.abs(Math.round(diff / 60000));
    if (m < 60) return t("startedMAgo", { m });
    const h = Math.floor(m / 60);
    if (h >= 24) return t("startedDAgo", { d: Math.floor(h / 24) });
    return t("startedHAgo", { h });
  }
  const m = Math.round(diff / 60000);
  if (m < 60) return t("startsInM", { m });
  const h = Math.floor(m / 60), rem = m % 60;
  if (h >= 24) {
    const days = Math.floor(h / 24);
    return days === 1 ? t("startsInDay", { d: days }) : t("startsInDays", { d: days });
  }
  return rem > 0 ? t("startsInHM", { h, m: rem }) : (lang === "nb" ? `Starter om ${h}t` : `Starts in ${h}h`);
};

// ─── Shared components ────────────────────────────────────────────────────────

// Full-screen blocking gate for banned or suspended users. The DB-side RLS
// (is_active_user) already prevents these users from writing, but the app
// also needs to surface WHY they can't act. Banned users see a permanent
// removal screen with an appeal contact; suspended users see a countdown.
//
// Renders nothing when the profile is healthy. The parent should mount this
// after profile load — non-blocking when profile not yet loaded so login
// flows finish cleanly.
function AccountStatusGate({ profile, onSignOut }) {
  const { C, T, btnPrimary, btnSecondary, SPACE, RADIUS, SHADOW, TYPE } = useTheme();
  if (!profile) return null;
  const isBanned = profile.banned === true;
  const isSuspended = profile.suspended_until && new Date(profile.suspended_until) > new Date();
  if (!isBanned && !isSuspended) return null;

  const reason = isBanned ? profile.banned_reason : null; // suspension reason lives on user_suspensions
  const endsAt = isSuspended ? new Date(profile.suspended_until) : null;
  const fmtEnds = endsAt ? endsAt.toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" }) : null;

  return (
    <div style={{
      position: "fixed", inset: 0, zIndex: 1000,
      background: C.bg, color: C.ink,
      display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
      padding: SPACE[5], textAlign: "center",
    }}>
      <div style={{
        maxWidth: 420, width: "100%",
        background: C.surface, borderRadius: RADIUS.lg, boxShadow: SHADOW.md,
        padding: SPACE[6],
      }}>
        <div style={{
          width: 56, height: 56, borderRadius: "50%",
          background: isBanned ? "rgba(239,68,68,0.12)" : "rgba(245,158,11,0.12)",
          color: isBanned ? C.danger : C.warn,
          display: "flex", alignItems: "center", justifyContent: "center",
          margin: `0 auto ${SPACE[4]}px`,
        }}>
          {isBanned
            ? <Slash size={26} {...ICON} />
            : <Clock size={26} {...ICON} />}
        </div>
        <div style={{ ...TYPE.h1, color: C.ink, marginBottom: SPACE[2] }}>
          {isBanned ? "Account removed" : "Account suspended"}
        </div>
        <div style={{ ...TYPE.body, color: C.inkMid, marginBottom: SPACE[4] }}>
          {isBanned
            ? "Your kickr account has been removed and you can no longer use the app."
            : <>Your account is suspended until <b style={{ color: C.ink }}>{fmtEnds}</b>.</>}
        </div>
        {reason && (
          <div style={{
            ...TYPE.caption, color: C.inkMid,
            background: C.bg, borderRadius: RADIUS.sm,
            padding: `${SPACE[3]}px ${SPACE[4] - 2}px`,
            marginBottom: SPACE[4], textAlign: "left",
            whiteSpace: "pre-wrap",
          }}>
            <b style={{ display: "block", color: C.ink, marginBottom: 2 }}>Reason</b>
            {reason}
          </div>
        )}
        <a
          href={`mailto:${LEGAL_CONTACT_EMAIL}?subject=${encodeURIComponent(isBanned ? "Account appeal" : "Suspension question")}`}
          style={{ ...btnPrimary, padding: `${SPACE[3]}px ${SPACE[5]}px`, borderRadius: RADIUS.pill, textDecoration: "none", display: "inline-block", marginBottom: SPACE[3] }}
        >
          Email support
        </a>
        <div>
          <button onClick={onSignOut} style={{ ...btnSecondary, padding: `${SPACE[2] + 1}px ${SPACE[4]}px`, borderRadius: RADIUS.pill, fontSize: 13 }}>
            Sign out
          </button>
        </div>
      </div>
    </div>
  );
}

function Avatar({ initials, avatarUrl, size = 34, accent = false }) {
  const { C } = useTheme();
  if (avatarUrl) {
    return <img src={avatarUrl} alt={initials} style={{ width: size, height: size, borderRadius: "50%", objectFit: "cover", flexShrink: 0 }} />;
  }
  return (
    <div style={{ width: size, height: size, borderRadius: "50%", flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center", fontSize: size * 0.35, fontWeight: 600, background: accent ? C.accent : C.surface, color: accent ? C.accentText : C.inkMid, letterSpacing: "0.02em" }}>
      {initials}
    </div>
  );
}

// ── Venue review breakdown (TGTG-style multi-axis bars) ─────────────────────
// Reads `venue_review_stats` (a Supabase view aggregating venue_reviews) and
// renders a compact overall + 3-axis (surface/availability/lighting) summary.
// The view may not exist on dev databases — failures are silenced and the
// component renders nothing in that case.
function VenueReviewBar({ label, value }) {
  const { C } = useTheme();
  const pct = Math.max(0, Math.min(100, ((value || 0) / 5) * 100));
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
      <div style={{ width: 92, fontSize: 12, color: C.inkMid }}>{label}</div>
      <div style={{ flex: 1, height: 6, borderRadius: 3, background: C.surface, overflow: "hidden" }}>
        <div style={{ width: `${pct}%`, height: "100%", background: C.accent, borderRadius: 3 }} />
      </div>
      <div style={{ width: 30, fontSize: 12, fontWeight: 600, color: C.ink, textAlign: "right" }}>{(value || 0).toFixed(1)}</div>
    </div>
  );
}

function VenueReviewBreakdown({ venueId }) {
  const { C, T } = useTheme();
  const [stats, setStats] = React.useState(null);

  React.useEffect(() => {
    if (!venueId) return;
    let cancelled = false;
    supabase.from("venue_review_stats").select("review_count, overall, surface, availability, lighting").eq("venue_id", venueId).maybeSingle()
      .then(({ data }) => { if (!cancelled && data) setStats(data); })
      .catch(() => { /* view missing — fail silently */ });
    return () => { cancelled = true; };
  }, [venueId]);

  if (!stats || !stats.review_count) return null;

  return (
    <div style={{ marginBottom: 14, padding: "12px 14px", borderRadius: 12, background: C.surface, border: `1px solid ${C.border}` }}>
      <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", marginBottom: 10 }}>
        <div>
          <div style={{ ...T.label }}>{t("overallExperience")}</div>
          <div style={{ fontSize: 22, fontWeight: 700, color: C.ink, marginTop: 2 }}>{(stats.overall || 0).toFixed(1)}<span style={{ fontSize: 12, fontWeight: 500, color: C.inkFaint, marginLeft: 4 }}>/ 5</span></div>
        </div>
        <div style={{ fontSize: 11, color: C.inkFaint }}>{t("basedOnReviews", { n: stats.review_count, s: stats.review_count === 1 ? "" : "s" })}</div>
      </div>
      <VenueReviewBar label={t("surface")} value={stats.surface} />
      <VenueReviewBar label={t("availability")} value={stats.availability} />
      <VenueReviewBar label={t("lighting")} value={stats.lighting} />
    </div>
  );
}

function VenueAvailabilityChart({ venue, gameFormat = "5v5" }) {
  const { C } = useTheme();
  const todayDow = React.useMemo(() => {
    const d = new Date().getDay();
    return d === 0 ? 6 : d - 1; // Mon=0 … Sun=6
  }, []);
  const [dayIdx, setDayIdx]       = React.useState(todayDow);
  const [fbData, setFbData]       = React.useState({}); // "dow_slot" → {count, occupied}
  const touchStartX               = React.useRef(null);

  // Fetch real feedback from Supabase for this venue
  React.useEffect(() => {
    if (!venue?.lat || !venue?.lng) return;
    let cancelled = false;
    supabase
      .from("pitch_feedback")
      .select("date, hour, was_occupied")
      .eq("venue_lat", parseFloat(venue.lat))
      .eq("venue_lng", parseFloat(venue.lng))
      .then(({ data }) => {
        if (cancelled || !data?.length) return;
        const agg = {};
        const SLOTS = [8,10,12,14,16,18,20,22];
        data.forEach(({ date, hour, was_occupied }) => {
          if (!date || !hour) return;
          const d   = new Date(date + "T12:00");
          const dow = d.getDay() === 0 ? 6 : d.getDay() - 1;
          const h   = parseInt((hour || "").split(":")[0]) || 0;
          const slot = SLOTS.reduce((a, b) => Math.abs(b - h) < Math.abs(a - h) ? b : a, 8);
          const k   = `${dow}_${slot}`;
          if (!agg[k]) agg[k] = { count: 0, occupied: 0 };
          agg[k].count++;
          if (was_occupied) agg[k].occupied++;
        });
        setFbData(agg);
      });
    return () => { cancelled = true; };
  }, [venue?.lat, venue?.lng]);

  const days      = ["Monday","Tuesday","Wednesday","Thursday","Friday","Saturday","Sunday"];
  const daysShort = ["Mon","Tue","Wed","Thu","Fri","Sat","Sun"];
  const hours     = [8, 10, 12, 14, 16, 18, 20, 22];

  function dateForDayIdx(idx) {
    const today = new Date();
    let diff = idx - todayDow;
    if (diff < 0) diff += 7;
    const d = new Date(today);
    d.setDate(today.getDate() + diff);
    return format(d, "yyyy-MM-dd");
  }

  const prev = () => setDayIdx(i => (i + 6) % 7);
  const next = () => setDayIdx(i => (i + 1) % 7);

  // Returns busyness 0-100 (higher = busier) + whether it's from real data
  function getBusyness(dow, h, dateStr) {
    const k  = `${dow}_${h}`;
    const fb = fbData[k];
    if (fb && fb.count >= 3) {
      return { busy: Math.round((fb.occupied / fb.count) * 100), real: true };
    }
    // Invert confidence (confidence = "how free") to get busyness
    return { busy: 100 - getPitchConfidence(venue, dateStr, `${h}:00`, gameFormat), real: false };
  }

  function barColor(busy) {
    if (busy >= 60) return PALETTE.red500;
    if (busy >= 35) return PALETTE.amber500;
    return PALETTE.brand500;
  }

  const dateStr = dateForDayIdx(dayIdx);
  const bars = hours.map(h => ({ h, ...getBusyness(dayIdx, h, dateStr) }));
  const hasRealData = Object.values(fbData).some(v => v.count >= 3);

  return (
    <div style={{ marginBottom: 16 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
        <div style={{ fontSize: 11, fontWeight: 600, color: C.inkMid, textTransform: "uppercase", letterSpacing: "0.05em" }}>
          Busyness
        </div>
        {hasRealData && (
          <div style={{ fontSize: 9, color: C.accent, fontWeight: 600, display: "flex", alignItems: "center", gap: 3 }}>
            <div style={{ width: 5, height: 5, borderRadius: "50%", background: C.accent }} />
            Based on player reports
          </div>
        )}
      </div>

      {/* Carousel card */}
      <div
        style={{ background: C.surface, borderRadius: 14, border: `1px solid ${C.border}`, overflow: "hidden" }}
        onTouchStart={e => { touchStartX.current = e.touches[0].clientX; }}
        onTouchEnd={e => {
          if (touchStartX.current === null) return;
          const dx = touchStartX.current - e.changedTouches[0].clientX;
          if (Math.abs(dx) > 36) dx > 0 ? next() : prev();
          touchStartX.current = null;
        }}
      >
        {/* Header: prev · day name · next */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "14px 16px 4px" }}>
          <button onClick={prev} style={{ width: 30, height: 30, borderRadius: "50%", border: `1px solid ${C.border}`, background: C.bg, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, color: C.inkMid, fontSize: 16, lineHeight: 1, padding: 0 }}>‹</button>
          <div style={{ textAlign: "center", lineHeight: 1.2 }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: C.ink }}>{days[dayIdx]}</div>
            {dayIdx === todayDow && <div style={{ fontSize: 10, fontWeight: 600, color: C.accent, marginTop: 2 }}>Today</div>}
          </div>
          <button onClick={next} style={{ width: 30, height: 30, borderRadius: "50%", border: `1px solid ${C.border}`, background: C.bg, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, color: C.inkMid, fontSize: 16, lineHeight: 1, padding: 0 }}>›</button>
        </div>

        {/* Bar chart — taller = busier */}
        <div style={{ padding: "14px 16px 10px" }}>
          <div style={{ display: "flex", alignItems: "flex-end", gap: 5, height: 72 }}>
            {bars.map(({ h, busy, real }) => {
              const color  = barColor(busy);
              const barH   = Math.max(4, Math.round((busy / 100) * 64));
              return (
                <div key={h} style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", gap: 3 }}>
                  <div style={{ width: "100%", height: barH, borderRadius: "4px 4px 2px 2px", background: color, opacity: real ? 1 : 0.7, transition: "height 0.28s cubic-bezier(.4,0,.2,1)" }} />
                  <div style={{ fontSize: 8, color: C.inkFaint, whiteSpace: "nowrap" }}>{h}h</div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Dot pagination */}
        <div style={{ display: "flex", gap: 5, justifyContent: "center", paddingBottom: 14 }}>
          {daysShort.map((_, i) => (
            <button key={i} onClick={() => setDayIdx(i)} style={{ width: i === dayIdx ? 18 : 6, height: 6, borderRadius: 3, border: "none", cursor: "pointer", padding: 0, background: i === dayIdx ? C.ink : C.border, transition: "all 0.2s ease" }} />
          ))}
        </div>
      </div>

      {/* Legend */}
      <div style={{ display: "flex", gap: 10, marginTop: 8, justifyContent: "flex-end" }}>
        {[["Quiet", PALETTE.brand500], ["Moderate", PALETTE.amber500], ["Busy", PALETTE.red500]].map(([label, color]) => (
          <div key={label} style={{ display: "flex", alignItems: "center", gap: 4 }}>
            <div style={{ width: 8, height: 8, borderRadius: 2, background: color, opacity: 0.85 }} />
            <span style={{ fontSize: 9, color: C.inkFaint }}>{label}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function LevelPill({ level, small }) {
  const { levelColor } = useTheme();
  const lc = levelColor[level] || levelColor["All levels"];
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 6, background: lc.bg, color: lc.text, padding: small ? "3px 10px" : "4px 12px", borderRadius: 999, fontSize: small ? 12 : 13, fontWeight: 600, letterSpacing: 0 }}>
      <span style={{ width: 6, height: 6, borderRadius: "50%", background: lc.dot, flexShrink: 0 }} />
      {tLevel(level)}
    </span>
  );
}

function Label({ children }) {
  const { T } = useTheme();
  return <div style={{ ...T.label, marginBottom: 6 }}>{children}</div>;
}

function BrandMark({ size = 28, withTile = true }) {
  const { C, RADIUS } = useTheme();
  const content = (
    <img
      src={kickrKMark}
      alt=""
      aria-hidden="true"
      style={{ display: "block", width: size, height: size, objectFit: "contain" }}
    />
  );
  if (!withTile) return content;
  return (
    <span style={{
      width: size + 10, height: size + 10, borderRadius: RADIUS.sm,
      background: C.surface, border: `1px solid ${C.accent}55`,
      display: "inline-flex", alignItems: "center", justifyContent: "center",
      boxShadow: "0 1px 3px rgba(15,22,35,0.08)", flexShrink: 0,
    }}>
      {content}
    </span>
  );
}

function AvailabilityAvatarStack({ players = [], total = 0, size = 24 }) {
  const { C, SHADOW } = useTheme();
  const shown = players.slice(0, 3);
  const remaining = Math.max(0, total - shown.length);
  return (
    <span style={{ display: "inline-flex", alignItems: "center", flexShrink: 0 }}>
      {shown.map((player, index) => {
        const profile = player.profiles_public || player.profiles || player;
        const name = profile?.name || "Player";
        return (
          <span key={player.user_id || player.id || index} style={{ marginLeft: index === 0 ? 0 : -7, position: "relative", zIndex: shown.length - index }}>
            <Avatar initials={name.split(" ").map(w => w[0]).join("").slice(0, 2).toUpperCase()} avatarUrl={profile?.avatar_url} size={size} />
          </span>
        );
      })}
      {remaining > 0 && (
        <span style={{ marginLeft: shown.length ? -7 : 0, width: size, height: size, borderRadius: "50%", background: C.accent, color: C.accentText, border: `2px solid ${C.bg}`, display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: Math.max(9, size - 14), fontWeight: 850, boxShadow: SHADOW.sm, position: "relative", zIndex: 0 }}>
          +{remaining}
        </span>
      )}
    </span>
  );
}

function FootballIcon({ size = 18, color = "currentColor", strokeWidth = 1.8, style, ...props }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden="true" style={style} {...props}>
      <circle cx="12" cy="12" r="9" stroke={color} strokeWidth={strokeWidth} />
      <path d="M12 7.4 15.9 10.2 14.4 14.9H9.6L8.1 10.2 12 7.4Z" stroke={color} strokeWidth={strokeWidth} strokeLinejoin="round" />
      <path d="m8.1 10.2-4 .4M15.9 10.2l4 .4M9.6 14.9 7.2 18M14.4 14.9l2.4 3.1M12 7.4V3.2" stroke={color} strokeWidth={strokeWidth} strokeLinecap="round" />
    </svg>
  );
}

function KickrWordmark({ onClick }) {
  const { C } = useTheme();
  return (
    <button
      onClick={onClick}
      aria-label="kickr home"
      style={{
        display: "inline-flex", alignItems: "center",
        background: "transparent", border: "none", padding: 0, minHeight: 32,
        color: C.ink, cursor: onClick ? "pointer" : "default",
      }}
    >
      <img
        src={kickrWordmark}
        alt="kickr"
        style={{ display: "block", width: 86, height: 28, objectFit: "contain", objectPosition: "left center" }}
      />
    </button>
  );
}

function KickrLoader({ label = "Loading kickr" }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }} role="status" aria-label={label}>
      <div style={{ width: 64, height: 64, position: "relative" }}>
        <img src={kickrKMark} alt="" style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "contain", opacity: 0.16, filter: "grayscale(1)" }} />
        <div style={{ position: "absolute", inset: 0, overflow: "hidden", animation: "kickrFill 1.35s ease-in-out infinite" }}>
          <img src={kickrKMark} alt="" style={{ width: "100%", height: "100%", objectFit: "contain", display: "block" }} />
        </div>
      </div>
      <style>{`@keyframes kickrFill{0%{clip-path:inset(100% 0 0 0)}58%{clip-path:inset(0 0 0 0)}100%{clip-path:inset(0 0 0 0)}}`}</style>
    </div>
  );
}

function LegalAgreementControl({ checked, onChange }) {
  const { C, T, SPACE, RADIUS } = useTheme();
  const linkStyle = {
    color: C.accent,
    fontWeight: 850,
    textDecoration: "none",
  };
  const stop = (e) => e.stopPropagation();
  return (
    <label
      style={{
        display: "flex",
        alignItems: "flex-start",
        gap: SPACE[3],
        padding: SPACE[3],
        borderRadius: RADIUS.md,
        border: `1px solid ${checked ? `${C.accent}66` : C.border}`,
        background: checked ? C.accentBg : C.surface,
        color: C.ink,
        cursor: "pointer",
      }}
    >
      <input
        type="checkbox"
        checked={checked}
        onChange={e => onChange(e.target.checked)}
        style={{ width: 18, height: 18, margin: 1, accentColor: C.accent, flexShrink: 0 }}
      />
      <span style={{ ...T.small, lineHeight: 1.45, color: C.inkMid }}>
        {t("legalAgreeStart")}{" "}
        <a href="/terms/" target="_blank" rel="noreferrer" onClick={stop} style={linkStyle}>{t("termsOfService")}</a>
        {" "}{t("legalAnd")}{" "}
        <a href="/privacy/" target="_blank" rel="noreferrer" onClick={stop} style={linkStyle}>{t("privacyPolicy")}</a>.
      </span>
    </label>
  );
}

// ─── Welcome Flow (first-time onboarding + profile setup) ────────────────────
function WelcomeFlow({ user, profile, requireUsername = true, requireLegalAcceptance = false, onDone }) {
  const { C, T, TYPE, btnPrimary, inputBase, card, levelColor, dangerBg, dangerBorder, SPACE, RADIUS, SHADOW } = useTheme();

  const fallbackName = profile?.name || user?.user_metadata?.name || user?.email?.split("@")[0] || "";
  const fallbackUsername = normalizeUsernameInput(profile?.username || user?.user_metadata?.user_name || user?.email?.split("@")[0] || "");
  const [form, setForm] = useState({ name: fallbackName, username: fallbackUsername, level: profile?.level || "Intermediate", position: profile?.position || "" });
  const [legalAccepted, setLegalAccepted] = useState(() => hasAcceptedCurrentLegal(profile));
  const [avatarUrl, setAvatarUrl] = useState(null);
  const [avatarUploading, setAvatarUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const fileInputRef = useRef(null);

  const handleAvatarChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setAvatarUploading(true);
    try {
      const ext = file.name.split(".").pop();
      const path = `${user.id}.${ext}`;
      const { error: upErr } = await supabase.storage.from("Avatar").upload(path, file, { upsert: true });
      if (upErr) throw upErr;
      const { data } = supabase.storage.from("Avatar").getPublicUrl(path);
      setAvatarUrl(data.publicUrl);
    } catch (e) {
      // Avatar is optional — tell the user it failed but let onboarding continue
      console.error("[onboarding.avatarUpload] failed", e);
      reportError(e, { where: "onboarding.avatarUpload" });
      setError(t("avatarUploadFailed"));
    }
    finally { setAvatarUploading(false); }
  };

  const handleSave = async () => {
    const username = normalizeUsernameInput(form.username);
    const usernameError = requireUsername ? getUsernameError(username) : "";
    if (!form.name.trim() || usernameError) {
      setError(usernameError || "Add your name to continue");
      return;
    }
    if (requireLegalAcceptance && !legalAccepted) {
      setError(t("legalAgreementRequired"));
      return;
    }
    setSaving(true); setError(null);
    try {
      if (requireUsername) {
        const available = await checkUsernameAvailable(username, user.id);
        if (!available) {
          setError(t("usernameTaken"));
          return;
        }
      }
      const fullPayload = { id: user.id, name: form.name.trim(), level: form.level, position: form.position, avatar_url: avatarUrl || profile?.avatar_url || null };
      const minPayload  = { id: user.id, name: form.name.trim(), level: form.level, position: form.position };
      if (requireUsername) {
        fullPayload.username = username;
        minPayload.username = username;
      }
      let acceptedLegalPayload = null;
      if (requireLegalAcceptance) {
        acceptedLegalPayload = getLegalAcceptancePayload();
        Object.assign(fullPayload, acceptedLegalPayload);
        Object.assign(minPayload, acceptedLegalPayload);
      }
      let data, dbErr;
      ({ data, error: dbErr } = await supabase.from("profiles").upsert(fullPayload).select().maybeSingle());
      if (dbErr && requireLegalAcceptance && isLegalSchemaMissingError(dbErr)) {
        throw new Error(t("legalSchemaMissing"));
      }
      if (dbErr?.message?.includes("avatar_url") || dbErr?.message?.includes("schema cache")) {
        // avatar_url column not in schema — retry without it
        ({ data, error: dbErr } = await supabase.from("profiles").upsert(minPayload).select().maybeSingle());
      }
      if (dbErr && requireLegalAcceptance && isLegalSchemaMissingError(dbErr)) {
        throw new Error(t("legalSchemaMissing"));
      }
      if (dbErr) throw dbErr;
      localStorage.setItem(`kickr_onboarded_${user.id}`, "1");
      localStorage.removeItem("kickr_onboarded");
      onDone(data || { id: user.id, ...form, username: requireUsername ? username : profile?.username || "", avatar_url: avatarUrl || profile?.avatar_url || null, ...(acceptedLegalPayload || {}) });
    } catch (e) {
      setError(isUsernameConflictError(e) ? t("usernameTaken") : (e.message || "Failed to save profile — please try again"));
    } finally { setSaving(false); }
  };

  const userInitials = form.name.split(" ").map(w => w[0]).join("").slice(0, 2).toUpperCase() || "?";
  const usernameError = requireUsername ? getUsernameError(normalizeUsernameInput(form.username)) : "";
  const saveDisabled = saving || !form.name.trim() || !!usernameError || (requireLegalAcceptance && !legalAccepted);
  return (
    <div style={{ minHeight: "100dvh", display: "flex", flexDirection: "column", background: C.bg, color: C.ink }}>
      <div style={{ padding: `calc(${SPACE[6]}px + env(safe-area-inset-top, 0px)) ${SPACE[4]}px ${SPACE[3]}px` }}>
        <KickrWordmark />
      </div>

      <div style={{ flex: 1, overflowY: "auto", padding: `0 ${SPACE[4]}px calc(${SPACE[5]}px + env(safe-area-inset-bottom, 0px))` }}>
        <section style={{ ...card, padding: SPACE[4], borderRadius: RADIUS.lg, boxShadow: SHADOW.sm, display: "flex", flexDirection: "column", gap: SPACE[4] }}>
          <div>
            <div style={{ ...TYPE.micro, color: C.inkMid, marginBottom: SPACE[2] }}>{t("onboardingProfile")}</div>
            <h1 style={{ ...T.h1, fontSize: 26, margin: 0 }}>{t("onboardingTitle")}</h1>
            <p style={{ ...T.body, color: C.inkMid, margin: `${SPACE[1] + 2}px 0 0` }}>
              {t("onboardingBody")}
            </p>
          </div>

          {/* Avatar */}
          <div style={{ display: "flex", alignItems: "center", gap: SPACE[3] }}>
          <input ref={fileInputRef} type="file" accept="image/*" style={{ display: "none" }} onChange={handleAvatarChange} />
          <button
            onClick={() => fileInputRef.current?.click()}
            style={{ position: "relative", width: 58, height: 58, borderRadius: "50%", border: "none", cursor: "pointer", padding: 0, background: "none", flexShrink: 0 }}
          >
            {avatarUrl
              ? <img src={avatarUrl} alt="avatar" style={{ width: 58, height: 58, borderRadius: "50%", objectFit: "cover" }} />
              : <div style={{ width: 58, height: 58, borderRadius: "50%", background: C.accentBg, color: C.accent, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 19, fontWeight: 900, boxShadow: `inset 0 0 0 1px ${C.accent}22` }}>{userInitials}</div>
            }
            <div style={{ position: "absolute", bottom: -2, right: -2, width: 24, height: 24, borderRadius: "50%", background: C.ink, display: "flex", alignItems: "center", justifyContent: "center", border: `2px solid ${C.surface}` }}>
              {avatarUploading
                ? <div style={{ width: 10, height: 10, borderRadius: "50%", border: `2px solid ${C.bg}`, borderTopColor: C.accent, animation: "spin 0.8s linear infinite" }} />
                : <Camera size={13} {...ICON} color={C.bg} />
              }
            </div>
          </button>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ ...T.h2, color: C.ink, marginBottom: 2 }}>{avatarUrl ? t("photoAdded") : t("profilePhoto")}</div>
            <button onClick={() => fileInputRef.current?.click()} style={{ border: "none", background: "transparent", color: C.accent, fontSize: 13, fontWeight: 850, padding: 0, cursor: "pointer" }}>
              {avatarUrl ? t("changePhoto") : t("addPhoto")}
            </button>
          </div>
          </div>

          <div>
            <Label>{t("yourName")}</Label>
            <input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="Erik Solberg" style={{ ...inputBase, borderRadius: RADIUS.md }} />
          </div>

          {requireUsername && (
            <div>
              <Label>{t("username")}</Label>
              <div style={{ display: "flex", alignItems: "center", border: `1px solid ${C.border}`, borderRadius: RADIUS.md, background: C.surface, overflow: "hidden" }}>
                <span style={{ paddingLeft: 13, color: C.inkFaint, fontSize: 15, fontWeight: 800 }}>@</span>
                <input
                  value={form.username}
                  onChange={e => setForm(f => ({ ...f, username: normalizeUsernameInput(e.target.value) }))}
                  placeholder={t("usernamePlaceholder")}
                  autoCapitalize="none"
                  autoCorrect="off"
                  spellCheck={false}
                  style={{ ...inputBase, border: "none", background: "transparent", borderRadius: 0, paddingLeft: 2 }}
                />
              </div>
              <div style={{ ...T.small, color: usernameError ? C.danger : C.inkFaint, marginTop: SPACE[2] }}>{usernameError || t("usernameHelp")}</div>
            </div>
          )}

          <div>
            <Label>{t("skillLevel")}</Label>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: SPACE[2] }}>
              {["Beginner", "Intermediate", "Advanced"].map(l => {
                const active = form.level === l;
                const lc = levelColor[l];
                return (
                  <button
                    key={l}
                    onClick={() => setForm(f => ({ ...f, level: l }))}
                    style={{
                      minHeight: 46,
                      padding: `${SPACE[2]}px ${SPACE[1]}px`,
                      borderRadius: RADIUS.md,
                      border: `1px solid ${active ? `${lc.dot}55` : C.border}`,
                      background: active ? lc.bg : C.bg,
                      color: active ? lc.text : C.ink,
                      cursor: "pointer",
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: 5,
                      fontSize: 12,
                      fontWeight: active ? 900 : 700,
                      lineHeight: 1.1,
                    }}
                  >
                    <span style={{ width: 7, height: 7, borderRadius: "50%", background: lc.dot }} />
                    {tLevel(l)}
                  </button>
                );
              })}
            </div>
          </div>

          <div>
            <Label>{t("position")}</Label>
            <div className="kickr-scroll-row" style={{ display: "flex", gap: SPACE[2], overflowX: "auto", paddingBottom: 2, scrollbarWidth: "none" }}>
              {["GK", "CB", "CM", "LW", "RW", "ST"].map(pos => {
                const active = form.position === pos;
                return (
                  <button
                    key={pos}
                    onClick={() => setForm(f => ({ ...f, position: active ? "" : pos }))}
                    style={{
                      minWidth: 54,
                      minHeight: 34,
                      padding: `0 ${SPACE[3]}px`,
                      borderRadius: RADIUS.pill,
                      border: `1px solid ${active ? `${C.accent}55` : C.border}`,
                      background: active ? C.accentBg : C.surface,
                      color: active ? C.accent : C.inkMid,
                      cursor: "pointer",
                      fontSize: 13,
                      fontWeight: active ? 900 : 750,
                      flexShrink: 0,
                    }}
                  >
                    {pos}
                  </button>
                );
              })}
            </div>
            <div style={{ ...T.small, color: C.inkFaint, marginTop: SPACE[2] }}>{t("optionalPosition")}</div>
          </div>

          {requireLegalAcceptance && (
            <div>
              <Label>{t("legalAgreement")}</Label>
              <LegalAgreementControl checked={legalAccepted} onChange={setLegalAccepted} />
              <div style={{ ...T.small, color: C.inkFaint, marginTop: SPACE[2] }}>{t("legalAgreementHelp")}</div>
            </div>
          )}

          {error && <div style={{ padding: `${SPACE[3]}px ${SPACE[4]}px`, background: dangerBg, borderRadius: RADIUS.md, fontSize: 13, color: C.danger, border: `1px solid ${dangerBorder}` }}>{error}</div>}
        </section>
      </div>

      <div style={{ padding: `${SPACE[3]}px ${SPACE[4]}px calc(${SPACE[4]}px + env(safe-area-inset-bottom, 0px))`, background: C.bg, borderTop: `1px solid ${C.border}` }}>
        <button
          onClick={handleSave}
          disabled={saveDisabled}
          style={{
            ...btnPrimary,
            width: "100%",
            padding: 15,
            borderRadius: RADIUS.md,
            fontSize: 15,
            fontWeight: 900,
            opacity: saveDisabled ? 0.42 : 1,
            cursor: saveDisabled ? "not-allowed" : "pointer",
            boxShadow: saveDisabled ? "none" : SHADOW.sm,
          }}
        >
          {saving ? t("saving") : t("letsPlay")}
        </button>
      </div>
    </div>
  );
}

// ─── Auth ─────────────────────────────────────────────────────────────────────
function AuthScreen({ onAuth }) {
  const { C, T, btnPrimary, btnSecondary, inputBase, dangerBg, dangerBorder, SPACE, RADIUS, SHADOW } = useTheme();
  const ui = { C, SPACE, RADIUS, SHADOW };
  const [mode, setMode] = useState("login"); // "login" | "signup" | "admin" | "forgot" | "forgotSent"
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [legalAccepted, setLegalAccepted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSubmit = async () => {
    setLoading(true); setError(null); setNotice(null);
    try {
      const email = form.email.trim();
	      if (mode === "signup") {
          if (!legalAccepted) throw new Error(t("legalAgreementRequired"));
          const legalPayload = getLegalAcceptancePayload();
	        const { data, error } = await supabase.auth.signUp({
            email,
            password: form.password,
            options: { data: { name: form.name.trim(), ...legalPayload } },
          });
	        if (error) throw error;
          if (data.user && data.session) {
            const { error: profileErr } = await supabase.from("profiles").upsert({
              id: data.user.id,
              name: form.name.trim(),
              ...legalPayload,
            });
            if (profileErr && isLegalSchemaMissingError(profileErr)) throw new Error(t("legalSchemaMissing"));
            if (profileErr) console.warn("[auth.signup.profileLegal] failed", profileErr);
          }
	        if (data.session && data.user) {
	          onAuth(data.user);
	        } else {
	          setMode("login");
	          setForm(f => ({ ...f, password: "" }));
	          setNotice("Check your email to confirm your account, then log in.");
	        }
	      } else {
	        const { data, error } = await supabase.auth.signInWithPassword({ email, password: form.password });
	        if (error) throw error;
	        if (mode === "admin") {
	          const { data: profile, error: profileError } = await supabase.from("profiles").select("role").eq("id", data.user.id).maybeSingle();
	          if (profileError) throw profileError;
	          if (profile?.role !== "admin") {
	            await supabase.auth.signOut();
	            throw new Error("This account does not have admin access.");
	          }
	        }
	        onAuth(data.user, { openAdminDashboard: mode === "admin" });
	      }
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  };

  const handleForgot = async () => {
    if (!form.email.trim()) { setError("Enter your email first"); return; }
    setLoading(true); setError(null);
    try {
      setNotice(null);
      const { error } = await supabase.auth.resetPasswordForEmail(form.email.trim(), { redirectTo: window.location.origin });
      if (error) throw error;
      setMode("forgotSent");
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  };

  const emailOk = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim());
	  const isValid = mode === "signup"
	    ? form.name.trim().length >= 2 && emailOk && form.password.length >= 6 && legalAccepted
	    : emailOk && form.password.length >= 6;

	  const headings = { login: t("welcomeBack"), signup: t("createAccount"), admin: "Admin dashboard", forgot: t("resetPassword"), forgotSent: t("emailSent") };

  return (
    <div style={{ height: "min(720px, 92vh)", display: "flex", flexDirection: "column", background: C.bg }}>
      <div style={{ padding: `calc(${SPACE[6]}px + env(safe-area-inset-top, 0px)) ${SPACE[4]}px ${SPACE[4]}px` }}>
        <KickrWordmark />
        <h1 style={{ ...T.h1, fontSize: 28, margin: `${SPACE[5]}px 0 0` }}>{headings[mode]}</h1>
	        <p style={{ ...T.body, marginTop: 6, marginBottom: 0, color: C.inkMid }}>
	          {mode === "forgot" ? t("forgotPasswordPrompt")
	            : mode === "forgotSent" ? t("checkInboxPrompt")
	            : mode === "admin" ? "Centralised moderation and operations for Kickr."
	            : t("tagline")}
	        </p>
      </div>

      <div style={{ flex: 1, overflowY: "auto", padding: `0 ${SPACE[4]}px ${SPACE[6]}px`, display: "flex", flexDirection: "column", gap: SPACE[4] }}>

        {/* ── Forgot sent ── */}
        {mode === "forgotSent" && (
          <>
            <div style={{ padding: SPACE[4], background: C.accentBg, borderRadius: RADIUS.lg, border: `1px solid ${C.accent}33`, textAlign: "center", boxShadow: SHADOW.sm }}>
              <div style={{ fontSize: 14, fontWeight: 600, color: C.ink, marginBottom: 4 }}>{t("resetLinkSentTo", { email: form.email })}</div>
              <div style={{ fontSize: 13, color: C.inkMid }}>{t("didntGetIt")}</div>
            </div>
            <button onClick={() => { setMode("login"); setNotice(null); }} style={{ ...btnPrimary, width: "100%", padding: 14, borderRadius: RADIUS.md, fontSize: 15 }}>{t("backToLogIn")}</button>
          </>
        )}

        {/* ── Forgot password form ── */}
        {mode === "forgot" && (
          <>
            <div>
              <Label>{t("email")}</Label>
              <input type="email" value={form.email} onChange={e => set("email", e.target.value)} placeholder="you@example.com" style={{ ...inputBase, borderRadius: RADIUS.md }} autoFocus onKeyDown={e => e.key === "Enter" && handleForgot()} />
            </div>
            {error && <div style={{ padding: "10px 14px", background: dangerBg, borderRadius: RADIUS.md, fontSize: 13, color: C.danger, border: `1px solid ${dangerBorder}` }}>{error}</div>}
            <button onClick={handleForgot} disabled={loading || !form.email.trim()} style={{ ...btnPrimary, width: "100%", padding: 14, borderRadius: RADIUS.md, fontSize: 15, opacity: loading || !form.email.trim() ? 0.4 : 1 }}>
              {loading ? t("sending") : t("sendResetLink")}
            </button>
            <button onClick={() => { setMode("login"); setError(null); setNotice(null); }} style={{ ...btnSecondary, width: "100%", padding: 14, borderRadius: RADIUS.md, fontSize: 15 }}>{t("backToLogIn")}</button>
          </>
        )}

        {/* ── Login / Signup ── */}
	        {(mode === "login" || mode === "signup" || mode === "admin") && (
          <>
            {/* Mode toggle */}
	            {mode !== "admin" && (
	              <div style={uiSegmentShell(ui)}>
	                {[["login", t("logIn")],["signup", t("signUp")]].map(([v, label]) => (
	                  <button key={v} onClick={() => { setMode(v); setError(null); setNotice(null); }} style={uiSegmentButton(ui, mode === v)}>{label}</button>
	                ))}
	              </div>
	            )}
	            {mode === "admin" && (
	              <div style={{ padding: SPACE[3], borderRadius: RADIUS.md, border: `1px solid ${C.accent}33`, background: C.accentBg, display: "flex", alignItems: "flex-start", gap: SPACE[3] }}>
	                <ShieldCheck size={18} {...ICON} color={C.accent} style={{ flexShrink: 0, marginTop: 1 }} />
	                <div style={{ minWidth: 0 }}>
	                  <div style={{ fontSize: 14, fontWeight: 850, color: C.ink }}>Admin access</div>
	                  <div style={{ ...T.small, color: C.inkMid, marginTop: 2 }}>Sign in with an admin account to open moderation, no-shows, reports, and operations in one place.</div>
	                </div>
	              </div>
	            )}

            {mode === "signup" && (
              <div>
                <Label>{t("name")}</Label>
                <input value={form.name} onChange={e => set("name", e.target.value)} placeholder="Erik Solberg" style={{ ...inputBase, borderRadius: RADIUS.md }} />
              </div>
            )}
            <div>
              <Label>{t("email")}</Label>
              <input type="email" value={form.email} onChange={e => set("email", e.target.value)} placeholder="you@example.com" style={{ ...inputBase, borderRadius: RADIUS.md }} />
            </div>
            <div>
              <Label>{t("password")}</Label>
              <input type="password" value={form.password} onChange={e => set("password", e.target.value)} placeholder={t("passwordPlaceholder")} style={{ ...inputBase, borderRadius: RADIUS.md }} onKeyDown={e => e.key === "Enter" && handleSubmit()} />
              {mode === "login" && (
	                <button onClick={() => { setMode("forgot"); setError(null); setNotice(null); }} style={{ background: "none", border: "none", cursor: "pointer", color: C.accent, fontWeight: 500, fontSize: 12, padding: "6px 0 0", display: "block" }}>
                  {t("forgotPassword")}
                </button>
              )}
            </div>

            {mode === "signup" && (
              <LegalAgreementControl checked={legalAccepted} onChange={setLegalAccepted} />
            )}

            {notice && <div style={{ padding: "10px 14px", background: C.accentBg, borderRadius: RADIUS.md, fontSize: 13, color: C.accent, border: `1px solid ${C.accent}33` }}>{notice}</div>}
            {error && <div style={{ padding: "10px 14px", background: dangerBg, borderRadius: RADIUS.md, fontSize: 13, color: C.danger, border: `1px solid ${dangerBorder}` }}>{error}</div>}

	            <button onClick={handleSubmit} disabled={loading || !isValid} style={{ ...btnPrimary, width: "100%", padding: 14, borderRadius: RADIUS.md, fontSize: 15, opacity: loading || !isValid ? 0.4 : 1, cursor: !isValid ? "not-allowed" : "pointer" }}>
	              {loading ? t("pleaseWait") : mode === "admin" ? "Open admin dashboard" : mode === "login" ? t("logIn") : t("createAccount")}
	            </button>
	            {mode === "admin" && (
	              <button onClick={() => { setMode("login"); setError(null); setNotice(null); }} style={{ ...btnSecondary, width: "100%", padding: 13, borderRadius: RADIUS.md, fontSize: 14 }}>
	                Back to player login
	              </button>
	            )}

	            {mode !== "admin" && <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
	              <div style={{ flex: 1, height: 1, background: C.border }} />
	              <span style={{ ...T.small }}>{t("or")}</span>
	              <div style={{ flex: 1, height: 1, background: C.border }} />
	            </div>}

	            {mode !== "admin" && <button onClick={async () => {
              if (mode === "signup" && !legalAccepted) {
                setError(t("legalAgreementRequired"));
                return;
              }
              setError(null);
              // On Capacitor (iOS/Android) use in-app browser with deep link redirect
              // so OAuth returns to app instead of opening Safari
              const isCapacitor = window.Capacitor?.isNativePlatform?.();
              const redirectTo = isCapacitor
                ? "com.dayanemagagnin.kickr://login-callback"
                : window.location.origin;
              if (isCapacitor) {
                const { Browser } = await import("@capacitor/browser");
                const { data } = await supabase.auth.signInWithOAuth({
                  provider: "google",
                  options: { redirectTo, skipBrowserRedirect: true },
                });
                if (data?.url) await Browser.open({ url: data.url });
              } else {
                await supabase.auth.signInWithOAuth({ provider: "google", options: { redirectTo } });
              }
	            }} style={{ ...btnSecondary, width: "100%", padding: 14, borderRadius: RADIUS.md, display: "flex", alignItems: "center", justifyContent: "center", gap: 10, fontSize: 14 }}>
	              <svg width="16" height="16" viewBox="0 0 18 18"><path fill="#4285F4" d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844c-.209 1.125-.843 2.078-1.796 2.717v2.258h2.908c1.702-1.567 2.684-3.875 2.684-6.615z"/><path fill="#34A853" d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9 18z"/><path fill="#FBBC05" d="M3.964 10.71A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.996 8.996 0 0 0 0 9c0 1.452.348 2.827.957 4.042l3.007-2.332z"/><path fill="#EA4335" d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.958L3.964 6.29C4.672 4.163 6.656 3.58 9 3.58z"/></svg>
	              {t("continueWithGoogle")}
	            </button>}

	            {mode !== "admin" && <p style={{ textAlign: "center", ...T.small, marginTop: 4 }}>
	              {mode === "login" ? t("noAccount") : t("alreadyRegistered")}
	              <button onClick={() => { setMode(mode === "login" ? "signup" : "login"); setError(null); setNotice(null); }} style={{ background: "none", border: "none", cursor: "pointer", color: C.accent, fontWeight: 600, fontSize: 12, padding: 0 }}>
	                {mode === "login" ? t("signUp") : t("logIn")}
	              </button>
	            </p>}

	            {mode !== "admin" && (
	              <button
	                onClick={() => { setMode("admin"); setError(null); setNotice(null); }}
	                style={{
	                  alignSelf: "center",
	                  background: "none",
	                  border: "none",
	                  color: C.inkFaint,
	                  cursor: "pointer",
	                  fontSize: 11,
	                  fontWeight: 650,
	                  padding: "2px 4px",
	                  marginTop: -6,
	                }}
	              >
	                Staff access
	              </button>
	            )}
          </>
        )}
      </div>
    </div>
  );
}

// ─── Map ──────────────────────────────────────────────────────────────────────
const VENUE_GAME_OVERLAP_EPSILON = 0.0005;
const VENUE_MAP_DUPLICATE_EPSILON = 0.0007;
const normaliseVenueMapKey = (value) => String(value || "")
  .normalize("NFKD")
  .replace(/[\u0300-\u036f]/g, "")
  .toLowerCase()
  .replace(/[^a-z0-9]+/g, " ")
  .trim();
const getVenueMapIdentityKey = (venue) => {
  const address = normaliseVenueMapKey(venue?.address);
  if (address) return `address:${address}`;
  const name = normaliseVenueMapKey(venue?.name);
  return name ? `name:${name}` : "";
};
const coordsAreClose = (aLat, aLng, bLat, bLng, epsilon) => {
  const latA = Number(aLat);
  const lngA = Number(aLng);
  const latB = Number(bLat);
  const lngB = Number(bLng);
  return Number.isFinite(latA) && Number.isFinite(lngA) && Number.isFinite(latB) && Number.isFinite(lngB)
    && Math.abs(latA - latB) <= epsilon
    && Math.abs(lngA - lngB) <= epsilon;
};
const buildVenueMapFeatures = (venues = [], games = []) => {
  const seenIdentities = new Set();
  const keptVenues = [];
  (venues || []).forEach(venue => {
    const lat = Number(venue?.lat);
    const lng = Number(venue?.lng);
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return;
    const hasGameAtVenue = (games || []).some(game => coordsAreClose(game?.lat, game?.lng, lat, lng, VENUE_GAME_OVERLAP_EPSILON));
    if (hasGameAtVenue) return;
    const identityKey = getVenueMapIdentityKey(venue);
    if (identityKey && seenIdentities.has(identityKey)) return;
    const sameMapSpot = keptVenues.some(existing => coordsAreClose(existing.lat, existing.lng, lat, lng, VENUE_MAP_DUPLICATE_EPSILON));
    if (sameMapSpot) return;
    if (identityKey) seenIdentities.add(identityKey);
    keptVenues.push({ ...venue, lat, lng });
  });
  return keptVenues.map(v => ({
    type: "Feature",
    geometry: { type: "Point", coordinates: [v.lng, v.lat] },
    properties: { gtype: "venue", vid: v.id, name: v.name, vtype: v.type, area: v.area },
  }));
};

function GameMap({ games, selectedGame, joined, userLocation, onVenueClick, selectedVenueId, onSelectCluster, venues, mapRef: externalMapRef }) {
  const { C, SHADOW } = useTheme();
  const mapContainer = useRef(null);
  const map = useRef(null);
  const userMarker = useRef(null);
  const [urgencyNow, setUrgencyNow] = useState(() => Date.now());
  const onVenueClickRef = useRef(onVenueClick);
  useEffect(() => { onVenueClickRef.current = onVenueClick; }, [onVenueClick]);
  const onSelectClusterRef = useRef(onSelectCluster);
  useEffect(() => { onSelectClusterRef.current = onSelectCluster; }, [onSelectCluster]);
  const gamesRef = useRef(games);
  useEffect(() => { gamesRef.current = games; }, [games]);
  const venuesRef = useRef(venues);
  useEffect(() => { venuesRef.current = venues; }, [venues]);
  useEffect(() => {
    const id = setInterval(() => setUrgencyNow(Date.now()), 60000);
    return () => clearInterval(id);
  }, []);
  const buildGameMapFeatures = useCallback((items = []) => items
    .filter(g => g.lat != null && g.lng != null)
    .map(g => {
      const isUrgent = isUrgentPlayerNeededGame(g, urgencyNow);
      return {
        type: "Feature",
        geometry: { type: "Point", coordinates: [+g.lng, +g.lat] },
        properties: {
          gtype: "game",
          gid: g.id,
          level: g.level || "All levels",
          isUrgent,
          isJoined: joined.includes(g.id),
          isFull: g.spotsFilled >= g.spotsTotal,
        },
      };
    }), [joined, urgencyNow]);
  const sortMapGames = useCallback((items) => [...items].sort((a, b) => {
    const aKey = `${a.date || "9999-12-31"}T${a.hour || "23:59"}`;
    const bKey = `${b.date || "9999-12-31"}T${b.hour || "23:59"}`;
    return aKey.localeCompare(bKey);
  }), []);
  const gamesAtSameMapSpot = useCallback((anchor) => {
    if (!anchor) return [];
    const anchorLat = Number(anchor.lat);
    const anchorLng = Number(anchor.lng);
    const anchorHasCoords = Number.isFinite(anchorLat) && Number.isFinite(anchorLng);
    const matches = gamesRef.current.filter(g => {
      if (!g) return false;
      if (String(g.id) === String(anchor.id)) return true;
      const lat = Number(g.lat);
      const lng = Number(g.lng);
      if (!anchorHasCoords || !Number.isFinite(lat) || !Number.isFinite(lng)) return false;
      // Treat games within roughly the same pitch footprint as one selectable
      // location. Exact coordinate overlap otherwise lets Mapbox expose only
      // the top rendered point, which hid the other games from the user.
      return Math.abs(lat - anchorLat) <= 0.0002 && Math.abs(lng - anchorLng) <= 0.0002;
    });
    return sortMapGames(matches);
  }, [sortMapGames]);
  const areGamesAtOneMapSpot = useCallback((items) => {
    const list = (items || []).filter(Boolean);
    if (list.length <= 1) return true;
    const anchorLat = Number(list[0].lat);
    const anchorLng = Number(list[0].lng);
    if (!Number.isFinite(anchorLat) || !Number.isFinite(anchorLng)) return false;
    return list.every(g => {
      const lat = Number(g.lat);
      const lng = Number(g.lng);
      return Number.isFinite(lat) && Number.isFinite(lng)
        && Math.abs(lat - anchorLat) <= 0.0002
        && Math.abs(lng - anchorLng) <= 0.0002;
    });
  }, []);
  const gamesFromMapFeatures = useCallback((features) => {
    const byId = new Map();
    (features || []).forEach(feature => {
      const gid = feature?.properties?.gid;
      if (gid == null) return;
      const game = gamesRef.current.find(g => String(g.id) === String(gid));
      if (game) byId.set(String(game.id), game);
    });
    return sortMapGames([...byId.values()]);
  }, [sortMapGames]);
  const showGamesFromMapFeatures = useCallback((features, options = {}) => {
    const { requireSameSpot = false } = options;
    const nextGames = gamesFromMapFeatures(features);
    if (requireSameSpot && !areGamesAtOneMapSpot(nextGames)) return 0;
    if (nextGames.length) onSelectClusterRef.current && onSelectClusterRef.current(nextGames);
    return nextGames.length;
  }, [areGamesAtOneMapSpot, gamesFromMapFeatures]);

  useEffect(() => {
    if (map.current) return;
    loadMapbox().then(mgl => {
      if (map.current || !mapContainer.current) return;
      mgl.accessToken = MAPBOX_TOKEN;
      map.current = new mgl.Map({
        container: mapContainer.current,
        style: C.mapStyle,
        center: [10.735, 59.922],
        zoom: 13,
      });
      map.current.addControl(new mgl.NavigationControl({ showCompass: false }), "top-right");

    // Venue and game dots use native Mapbox layers so they scale naturally.

    // Native venue layers — scale with zoom automatically like TooGoodToGo
    map.current.on("load", () => {
      map.current.addSource("venues", {
        type: "geojson",
        data: { type: "FeatureCollection", features: [] },
        cluster: true,
        clusterMaxZoom: 12,
        clusterRadius: 18,
      });
      map.current.addSource("games", {
        type: "geojson",
        data: { type: "FeatureCollection", features: [] },
        cluster: true,
        clusterMaxZoom: 16,
        clusterRadius: 36,
        clusterProperties: {
          urgentCount: ["+", ["case", ["==", ["get", "isUrgent"], true], 1, 0]],
        },
      });
      map.current.addLayer({
        id: "venue-clusters",
        type: "circle",
        source: "venues",
        filter: ["has", "point_count"],
        paint: {
          "circle-color": "#eef6ef",
          "circle-radius": ["step", ["get", "point_count"], 12, 5, 15, 15, 18],
          "circle-stroke-width": 1.5,
          "circle-stroke-color": "#fff",
        },
      });
      map.current.addLayer({
        id: "venue-cluster-count",
        type: "symbol",
        source: "venues",
        filter: ["has", "point_count"],
        layout: {
          "text-field": "{point_count_abbreviated}",
          "text-size": 12,
          "text-font": ["DIN Offc Pro Medium", "Arial Unicode MS Bold"],
        },
        paint: { "text-color": PALETTE.gray700 },
      });
      map.current.addLayer({
        id: "venue-points",
        type: "circle",
        source: "venues",
        filter: ["all", ["!", ["has", "point_count"]], ["==", ["get", "gtype"], "venue"]],
        paint: {
          "circle-color": ["case", ["==", ["get", "vtype"], "Cageball"], "#a78bfa", "#86efac"],
          "circle-radius": ["interpolate", ["linear"], ["zoom"], 10, 3, 14, 6, 16, 9],
          "circle-stroke-width": 1.5,
          "circle-stroke-color": "#fff",
          "circle-opacity": 0.82,
          "circle-pitch-alignment": "map",
        },
      });
      map.current.addLayer({
        id: "venue-labels",
        type: "symbol",
        source: "venues",
        filter: ["all", ["!", ["has", "point_count"]], ["==", ["get", "gtype"], "venue"]],
        layout: {
          "text-field": ["slice", ["get", "name"], 0, 14],
          "text-size": 9,
          "text-offset": [0, 1.4],
          "text-anchor": "top",
          "text-font": ["DIN Offc Pro Regular", "Arial Unicode MS Regular"],
        },
        paint: {
          "text-color": "#666",
          "text-halo-color": "#fff",
          "text-halo-width": 1,
        },
        minzoom: 14,
      });
      map.current.addLayer({
        id: "game-clusters",
        type: "circle",
        source: "games",
        filter: ["has", "point_count"],
        paint: {
          "circle-color": ["case", [">", ["get", "urgentCount"], 0], PALETTE.amber500, PALETTE.brand500],
          "circle-radius": ["step", ["get", "point_count"], 13, 3, 15, 8, 17],
          "circle-stroke-width": 3,
          "circle-stroke-color": PALETTE.white,
          "circle-opacity": 0.94,
        },
      });
      map.current.addLayer({
        id: "game-cluster-count",
        type: "symbol",
        source: "games",
        filter: ["has", "point_count"],
        layout: {
          "text-field": "{point_count_abbreviated}",
          "text-size": ["step", ["get", "point_count"], 11, 3, 12],
          "text-font": ["DIN Offc Pro Medium", "Arial Unicode MS Bold"],
          "text-allow-overlap": true,
          "text-ignore-placement": true,
        },
        paint: { "text-color": PALETTE.white },
      });
      map.current.addLayer({
        id: "game-halo",
        type: "circle",
        source: "games",
        filter: ["all", ["!", ["has", "point_count"]], ["==", ["get", "gtype"], "game"]],
        paint: {
          "circle-color": ["case",
            ["==", ["get", "isUrgent"], true], PALETTE.amber500,
            PALETTE.brand500,
          ],
          "circle-radius": ["interpolate", ["linear"], ["zoom"], 8, 8, 10, 10, 13, 13, 16, 16],
          "circle-opacity": ["case", ["==", ["get", "isUrgent"], true], 0.18, 0.12],
          "circle-pitch-alignment": "map",
        },
      });
      map.current.addLayer({
        id: "game-points",
        type: "circle",
        source: "games",
        filter: ["all", ["!", ["has", "point_count"]], ["==", ["get", "gtype"], "game"]],
        paint: {
          // Games are the primary action on the map, so they stay in the same
          // Kickr green as cards and CTAs instead of changing colour by level.
          "circle-color": ["case",
            ["==", ["get", "isUrgent"], true], PALETTE.amber600,
            ["==", ["get", "isFull"], true], PALETTE.gray500,
            PALETTE.brand500,
          ],
          "circle-radius": ["interpolate", ["linear"], ["zoom"], 8, 5.5, 10, 6.5, 13, 8, 15, 9.5, 18, 11],
          "circle-stroke-width": ["interpolate", ["linear"], ["zoom"], 8, 2.5, 14, 2],
          "circle-stroke-color": PALETTE.white,
          "circle-opacity": 1,
          "circle-pitch-alignment": "map",
        },
      });
      // Sync map ref to parent so search bar can flyTo
      if (externalMapRef) externalMapRef.current = map.current;
      // Eagerly load venue dots — doesn't wait for games to arrive from Supabase
      const venueFeatures = buildVenueMapFeatures(venuesRef.current, gamesRef.current);
      const gameFeatures = buildGameMapFeatures(gamesRef.current);
      map.current.getSource("venues").setData({ type: "FeatureCollection", features: venueFeatures });
      map.current.getSource("games").setData({ type: "FeatureCollection", features: gameFeatures });

      map.current.on("click", "venue-clusters", (e) => {
        map.current.easeTo({ center: e.lngLat, zoom: map.current.getZoom() + 2 });
      });
      map.current.on("click", "game-clusters", (e) => {
        const feature = e.features?.[0];
        const clusterId = feature?.properties?.cluster_id;
        const gameSource = map.current.getSource("games");
        const clusterCenter = feature?.geometry?.coordinates || [e.lngLat.lng, e.lngLat.lat];
        const zoomIntoCluster = () => {
          const fallbackZoom = Math.min(map.current.getZoom() + 2.5, 18);
          if (clusterId != null && gameSource?.getClusterExpansionZoom) {
            try {
              const maybeZoom = gameSource.getClusterExpansionZoom(clusterId, (err, zoom) => {
                map.current.easeTo({
                  center: clusterCenter,
                  zoom: err ? fallbackZoom : Math.min((zoom || fallbackZoom) + 0.4, 18),
                  duration: 350,
                });
              });
              if (maybeZoom?.then) {
                maybeZoom.then(zoom => {
                  map.current.easeTo({
                    center: clusterCenter,
                    zoom: Math.min((zoom || fallbackZoom) + 0.4, 18),
                    duration: 350,
                  });
                }).catch(() => {
                  map.current.easeTo({ center: clusterCenter, zoom: fallbackZoom, duration: 350 });
                });
              }
              return;
            } catch {
              // Fall back to a simple zoom below.
            }
          }
          map.current.easeTo({ center: clusterCenter, zoom: fallbackZoom, duration: 350 });
        };
        if (clusterId == null || !gameSource?.getClusterLeaves) {
          zoomIntoCluster();
          return;
        }
        let handled = false;
        const showLeaves = (leaves) => {
          if (handled) return;
          handled = true;
          const shown = showGamesFromMapFeatures(leaves, { requireSameSpot: true });
          if (!shown) zoomIntoCluster();
        };
        try {
          const maybePromise = gameSource?.getClusterLeaves?.(clusterId, 100, 0, (err, leaves) => {
            if (err) {
              zoomIntoCluster();
              return;
            }
            showLeaves(leaves);
          });
          if (maybePromise?.then) maybePromise.then(showLeaves).catch(() => {
            zoomIntoCluster();
          });
        } catch {
          zoomIntoCluster();
        }
      });
      map.current.on("click", "venue-points", (e) => {
        const vid = e.features[0].properties.vid;
        const venue = venuesRef.current.find(v => String(v.id) === String(vid));
        if (venue) onVenueClickRef.current && onVenueClickRef.current(venue);
      });
      map.current.on("click", "game-points", (e) => {
        const gid = e.features[0].properties.gid;
        const game = gamesRef.current.find(g => String(g.id) === String(gid));
        const sameSpotGames = gamesAtSameMapSpot(game);
        if (sameSpotGames.length) onSelectClusterRef.current && onSelectClusterRef.current(sameSpotGames);
      });
      map.current.on("click", "game-halo", (e) => {
        const gid = e.features[0].properties.gid;
        const game = gamesRef.current.find(g => String(g.id) === String(gid));
        const sameSpotGames = gamesAtSameMapSpot(game);
        if (sameSpotGames.length) onSelectClusterRef.current && onSelectClusterRef.current(sameSpotGames);
      });
      ["venue-clusters", "venue-points", "venue-labels", "game-clusters", "game-halo", "game-points"].forEach(layer => {
        map.current.on("mouseenter", layer, () => { map.current.getCanvas().style.cursor = "pointer"; });
        map.current.on("mouseleave", layer, () => { map.current.getCanvas().style.cursor = ""; });
      });
    });
    }); // end loadMapbox().then()
  }, [C.mapStyle, buildGameMapFeatures, externalMapRef, gamesAtSameMapSpot, showGamesFromMapFeatures]);

  useEffect(() => {
    if (!map.current || !userLocation || !mapboxgl) return;
    if (userMarker.current) userMarker.current.remove();
    const el = document.createElement("div");
    el.style.cssText = `width:12px;height:12px;border-radius:50%;background:${C.accent};border:2px solid ${C.bg};box-shadow:0 0 0 3px ${C.accentBg};`;
    userMarker.current = new mapboxgl.Marker({ element: el }).setLngLat([userLocation.lng, userLocation.lat]).addTo(map.current);
    map.current.flyTo({ center: [userLocation.lng, userLocation.lat], zoom: 13, duration: 1200 });
  }, [C.accent, C.accentBg, C.bg, userLocation]);

  // Keep venues and games in separate map sources. When they shared one
  // clustered source, zooming out made live games disappear into venue clusters.
  useEffect(() => {
    if (!map.current) return;
    const update = () => {
      const venueSource = map.current.getSource("venues");
      const gameSource = map.current.getSource("games");
      if (!venueSource || !gameSource) return;
      const venueFeatures = buildVenueMapFeatures(venues, games);
      const gameFeatures = buildGameMapFeatures(games);
      venueSource.setData({ type: "FeatureCollection", features: venueFeatures });
      gameSource.setData({ type: "FeatureCollection", features: gameFeatures });
    };
    if (map.current.isStyleLoaded()) update();
    else map.current.once("load", update);
  }, [buildGameMapFeatures, games, venues]);

  // Highlight selected venue
  useEffect(() => {
    if (!map.current || !map.current.isStyleLoaded() || !map.current.getLayer("venue-points")) return;
    map.current.setPaintProperty("venue-points", "circle-color", [
      "case",
      ["==", ["get", "vid"], selectedVenueId ?? -1], PALETTE.white,
      ["==", ["get", "vtype"], "Cageball"], PALETTE.violet500,
      PALETTE.brand500,
    ]);
    map.current.setPaintProperty("venue-points", "circle-stroke-color", [
      "case",
      ["==", ["get", "vid"], selectedVenueId ?? -1], PALETTE.gray900,
      PALETTE.white,
    ]);
    map.current.setPaintProperty("venue-points", "circle-opacity", [
      "case",
      ["==", ["get", "vid"], selectedVenueId ?? -1], 1,
      0.5,
    ]);
    map.current.setPaintProperty("venue-points", "circle-radius", [
      "interpolate", ["linear"], ["zoom"],
      10, ["case", ["==", ["get", "vid"], selectedVenueId ?? -1], 8, 4],
      14, ["case", ["==", ["get", "vid"], selectedVenueId ?? -1], 10, 6],
      16, ["case", ["==", ["get", "vid"], selectedVenueId ?? -1], 12, 8],
    ]);
  }, [selectedVenueId]);

  // Highlight selected game
  useEffect(() => {
    if (!map.current || !map.current.isStyleLoaded() || !map.current.getLayer("game-points")) return;
    map.current.setPaintProperty("game-points", "circle-radius", [
      "interpolate", ["linear"], ["zoom"],
      10, ["case", ["==", ["get", "gid"], selectedGame?.id ?? -1], 11, 8.5],
      13, ["case", ["==", ["get", "gid"], selectedGame?.id ?? -1], 14, 10],
      15, ["case", ["==", ["get", "gid"], selectedGame?.id ?? -1], 16, 12],
      18, ["case", ["==", ["get", "gid"], selectedGame?.id ?? -1], 18, 14],
    ]);
    map.current.setPaintProperty("game-points", "circle-stroke-width", [
      "case", ["==", ["get", "gid"], selectedGame?.id ?? -1], 3, 2,
    ]);
    map.current.setPaintProperty("game-points", "circle-stroke-color", [
      "case", ["==", ["get", "gid"], selectedGame?.id ?? -1], PALETTE.gray900, PALETTE.white,
    ]);
  }, [selectedGame]);

  return (
    <div style={{ width: "100%", height: "100%", position: "absolute", inset: 0 }}>
      <div ref={mapContainer} style={{ width: "100%", height: "100%", position: "absolute", inset: 0 }} />
      {userLocation && (
        <button
          onClick={() => map.current?.flyTo({ center: [userLocation.lng, userLocation.lat], zoom: 15, duration: 900 })}
          aria-label="Recenter map on me"
          style={{ position: "absolute", top: 84, right: 10, zIndex: 10, width: 36, height: 36, borderRadius: "50%", background: C.surface, border: "none", cursor: "pointer", boxShadow: SHADOW.md, display: "flex", alignItems: "center", justifyContent: "center", color: C.ink }}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="3"/><path d="M12 2v3M12 19v3M2 12h3M19 12h3"/>
            <path d="M12 2a10 10 0 1 0 0 20A10 10 0 0 0 12 2z" strokeOpacity="0.3"/>
          </svg>
        </button>
      )}
    </div>
  );
}

// ─── Game Card ────────────────────────────────────────────────────────────────
function GameCard({ game: g, joined, waitlisted, fav, onTap, onFav, isHost, onJoin, onWaitlist, isJoining }) {
  const { C, T, btnPrimary, btnSecondary, card, dangerBg, warnBg, SHADOW, RADIUS, SPACE } = useTheme();
  const full = g.spotsFilled >= g.spotsTotal;
  const filling = isFilling(g);
  const fillPct = (g.spotsFilled / g.spotsTotal) * 100;
  const distLabel = g.distance != null ? fmtDist(g.distance) : null;
  const countdown = fmtCountdown(g.date, g.hour);
  const waitlistCount = (g.waitlist || []).length;
  // One status pill, three states. Urgency (below minimum) beats capacity
  // info — a player who sees "9 spots left" doesn't know the game won't
  // actually run unless 9 more join.
  const spotsLeftCount = g.spotsTotal - g.spotsFilled;
  const moreNeeded = g.needsMorePlayers ? Math.max(1, (g.minPlayers || 0) - g.spotsFilled) : 0;
  const statusColor = full ? C.danger : moreNeeded > 0 ? C.warn : filling ? C.warn : C.accent;
  const statusBg    = full ? dangerBg  : moreNeeded > 0 ? warnBg  : filling ? warnBg  : C.accentBg;
  const spotText = full
    ? t("full")
    : moreNeeded > 0
      ? (moreNeeded === 1 ? t("oneMoreToStart") : t("moreToStart", { n: moreNeeded }))
      : spotsLeftCount === 1
        ? t("spotsLeft", { n: 1 })
        : t("spotsLeftPlural", { n: spotsLeftCount });

  return (
    <div onClick={onTap} style={{ ...card, cursor: "pointer", overflow: "hidden", position: "relative", transition: "box-shadow 0.15s, transform 0.15s" }}
      onMouseEnter={e => { if (window.matchMedia("(hover: hover)").matches) { e.currentTarget.style.boxShadow = SHADOW.md; e.currentTarget.style.transform = "translateY(-1px)"; } }}
      onMouseLeave={e => { e.currentTarget.style.boxShadow = SHADOW.sm; e.currentTarget.style.transform = ""; }}
    >
      <div style={{ padding: `${SPACE[4]}px ${SPACE[4]}px ${SPACE[3]}px` }}>
        {/* Top — chip row carries level/format/host/scope/status all in one
            line. The previous full-width "Filling fast" banner above the card
            felt like an admin notification; an inline status pill here reads
            as part of the card. */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: SPACE[2] }}>
          <div style={{ display: "flex", gap: SPACE[1] + 2, flexWrap: "wrap", alignItems: "center" }}>
            <LevelPill level={g.level} small />
            <span style={{ display: "inline-flex", alignItems: "center", padding: "3px 10px", borderRadius: 999, background: C.bg, fontSize: 12, fontWeight: 600, color: C.inkMid }}>{g.format}</span>
            {/* Informational chips read as neutral so the brand-green CTA at
                the bottom isn't competing with five other green chips. */}
            {isHost && <span style={{ display: "inline-flex", alignItems: "center", padding: "3px 10px", borderRadius: 999, background: C.bg, fontSize: 12, fontWeight: 600, color: C.ink }}>{t("hosting")}</span>}
            {g.isPrivate && <span title={t("private")} style={{ display: "inline-flex", alignItems: "center", color: C.inkMid }}><Lock size={12} {...ICON} /></span>}
            {g.visibility === "community" && (
              <span title={g.communityName || t("scopeCommunity")} style={{ display: "inline-flex", alignItems: "center", gap: 4, padding: "3px 10px", borderRadius: 999, background: C.bg, fontSize: 12, fontWeight: 600, color: C.inkMid, maxWidth: 120, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                <Users size={11} {...ICON} />
                {g.communityName || t("scopeCommunity")}
                {g.communityVerified && <VerifiedTick size={8} style={{ boxShadow: `0 0 0 1px ${C.bg}` }} />}
              </span>
            )}
            {/* Confirmation status now lives in the single title-row pill
                (see spotText below). Removed the duplicate chip here. */}
          </div>
          <button aria-label={fav ? t("removeFromSaved") : t("saveGame")} onClick={e => { e.stopPropagation(); onFav(); }} style={{ background: "none", border: "none", cursor: "pointer", padding: 10, margin: -6, color: fav ? C.ink : C.inkFaint, lineHeight: 1, minWidth: 44, minHeight: 44, display: "flex", alignItems: "center", justifyContent: "center" }}>
            <Bookmark size={18} fill={fav ? C.ink : "none"} color={fav ? C.ink : C.inkFaint} strokeWidth={1.8} />
          </button>
        </div>

        {/* Title — h2 weight reads as a card title, not a list-item label */}
        <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: SPACE[3], marginBottom: 8 }}>
          <div style={{ ...T.h2, color: C.ink, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", minWidth: 0 }}>{g.title}</div>
          <div style={{ flexShrink: 0, fontSize: 12, fontWeight: 700, color: statusColor, background: statusBg, padding: "4px 10px", borderRadius: RADIUS.pill }}>
            {spotText}
          </div>
        </div>

        {/* Meta — icon+label pairs, not dot-separated string. Each row is one
            unit so eyes can scan vertically (Airbnb listing card pattern). */}
        <div style={{ display: "flex", flexDirection: "column", gap: 4, marginBottom: 14 }}>
          {(() => {
            const isUrgent = countdown && (countdown === "Starting now" || /^Starts in \d+m$/.test(countdown));
            const timeStr = countdown || (g.time ? `${g.time}, ${g.hour}` : g.hour);
            const rows = [
              { Icon: Clock,   text: timeStr,    color: isUrgent ? C.danger : C.inkMid, weight: isUrgent ? 600 : 400 },
              { Icon: MapPin,  text: g.location, color: C.inkMid, weight: 400 },
            ];
            if (distLabel) rows.push({ Icon: Zap, text: distLabel, color: C.inkFaint, weight: 400 });
            return rows.map((r, i) => (
              <span key={i} style={{ display: "inline-flex", alignItems: "center", gap: 7, fontSize: 13, color: r.color, fontWeight: r.weight }}>
                <r.Icon size={13} {...ICON} />
                {r.text}
              </span>
            ));
          })()}
        </div>

        {/* Spots bar — bigger avatars (28 vs 24), thicker progress bar (6 vs 3) */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
          <div style={{ display: "flex" }}>
            {g.players.slice(0, 5).map((p, i) => (
              <div key={p.id} style={{ marginLeft: i === 0 ? 0 : -10, zIndex: 5 - i, position: "relative" }}>
                <Avatar initials={p.avatar} avatarUrl={p.avatarUrl} size={28} accent={p.isMe} />
              </div>
            ))}
            {g.players.length > 5 && <div style={{ marginLeft: -10, width: 28, height: 28, borderRadius: "50%", background: C.surface, border: `2px solid ${C.bg}`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, color: C.inkMid, fontWeight: 600, zIndex: 0 }}>+{g.players.length - 5}</div>}
          </div>
          <span style={{ fontSize: 13, fontWeight: 600, color: C.inkMid }}>{g.spotsFilled}/{g.spotsTotal}</span>
        </div>

        {/* Fill bar — 6px tall, fully rounded, smoother transition */}
        <div style={{ height: 6, background: C.bg, borderRadius: 999, overflow: "hidden" }}>
          <div style={{ height: "100%", width: `${fillPct}%`, background: full ? C.danger : fillPct >= 80 ? C.warn : C.accent, borderRadius: 999, transition: "width 0.4s ease, background 0.3s" }} />
        </div>

        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 12, gap: SPACE[3] }}>
          {joined
            ? <span style={{ fontSize: 12, fontWeight: 600, color: C.success, display: "flex", alignItems: "center", gap: 4 }}>
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><polyline points="20 6 9 17 4 12"/></svg>
                {t("joined")}
              </span>
            : waitlisted
              ? (onWaitlist
                  ? <button onClick={e => { e.stopPropagation(); onWaitlist(g.id); }} style={{ ...btnSecondary, padding: "7px 14px", borderRadius: RADIUS.pill, fontSize: 12, color: C.warn, borderColor: C.warn, minHeight: 32 }}>
                      {t("leaveWaitlist") || "Leave waitlist"}
                    </button>
                  : <span style={{ fontSize: 12, fontWeight: 600, color: C.warn }}>{t("onWaitlist")}</span>)
              : !isHost && !full && onJoin
                ? <button onClick={e => { e.stopPropagation(); if (!isJoining) onJoin(g.id); }} disabled={isJoining} style={{ ...btnPrimary, padding: "7px 16px", borderRadius: RADIUS.pill, fontSize: 13, opacity: isJoining ? 0.5 : 1, cursor: isJoining ? "not-allowed" : "pointer", minHeight: 34, boxShadow: SHADOW.sm }}>
                    {isJoining ? t("joining") : t("join")}
                  </button>
                : full && !isHost && onWaitlist
                  ? <button onClick={e => { e.stopPropagation(); onWaitlist(g.id); }} style={{ ...btnSecondary, padding: "7px 14px", borderRadius: RADIUS.pill, fontSize: 12, color: C.warn, borderColor: C.warn, minHeight: 32, display: "flex", alignItems: "center", gap: 4 }}>
                      <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
                      {t("joinWaitlist") || "Join waitlist"}{waitlistCount > 0 ? ` · ${waitlistCount}` : ""}
                    </button>
                  : <span />
          }
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            {g.ballHolder
              ? <span style={{ display: "inline-flex", alignItems: "center", gap: 4, fontSize: 12, color: C.inkMid }}>
                  <FootballIcon size={14} color={C.inkMid} />
                  {g.players.find(p => p.id === g.ballHolder)?.name?.split(" ")[0] || "?"}
                </span>
              : <span style={{ display: "inline-flex", alignItems: "center", gap: 4, fontSize: 12, color: C.inkMid }}>
                  <FootballIcon size={14} color={C.inkMid} />
                  {t("noBall")}
                </span>
            }
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={C.inkFaint} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6"/></svg>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Game Sheet ───────────────────────────────────────────────────────────────
function GameSheet({ game: g, joined, waitlisted, fav, onClose, onJoin, onWaitlist, onFav, currentUser, onCancel, showToast, onRefresh, onPatchGame, onClone, isJoining, isCloning, onInviteFriends, venues = [] }) {
  const { C, T, btnPrimary, btnSecondary, inputBase, card, dangerBg, dangerBorder, warnBg, warnBorder, successBg, successBorder, teams } = useTheme();
  const [tab, setTab] = useState("info");
  const [prevGameId, setPrevGameId] = useState(g.id);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancelReason, setCancelReason] = useState("");
  const [showEditModal, setShowEditModal] = useState(false);
  const [editForm, setEditForm] = useState({ title: g.title, description: g.description, date: g.date, hour: g.hour, duration: parseInt(g.duration) || 60, minPlayers: g.minPlayers || 4, minAge: g.minAge || null, ageRestriction: !!g.minAge, refereeId: g.refereeId || null, refereeName: g.refereeName || null });
  // Lazy-loaded friends list for editing the referee. null = not loaded yet.
  const [editRefFriends, setEditRefFriends] = useState(null);
  const [savingEdit, setSavingEdit] = useState(false);
  const [kickingPlayerId, setKickingPlayerId] = useState(null); // tracks in-flight kick operation
  const [showLeaveConfirm, setShowLeaveConfirm] = useState(false);
  const [showQRModal, setShowQRModal] = useState(false);
  const [qrDataUrl, setQrDataUrl] = useState(null);
  const [dispatchStep, setDispatchStep] = useState("select"); // "select" | "manual" | "random"
  const [teamA, setTeamA] = useState([]);
  const [teamB, setTeamB] = useState([]);
  const [savingTeams, setSavingTeams] = useState(false);
  const [viewingPlayer, setViewingPlayer] = useState(null); // { id, name, avatar, level, position }
  const [friendStatus, setFriendStatus] = useState(null); // null | "pending" | "accepted" | "sent"
  const [playerStats, setPlayerStats] = useState(null); // null | { gamesPlayed: number }
  const [playerStatsLoading, setPlayerStatsLoading] = useState(false);
  const [friendLoading, setFriendLoading] = useState(false);

  // Reset tab, modals, and edit form when a different game is opened (render-phase derived-state pattern)
  if (g.id !== prevGameId) {
    setPrevGameId(g.id);
    setTab("info");
    setShowLeaveConfirm(false);
    setShowEditModal(false);
    setShowCancelModal(false);
    setDispatchStep("select");
    setShowQRModal(false);
    setQrDataUrl(null);
    setEditForm({ title: g.title, description: g.description, date: g.date, hour: g.hour, duration: parseInt(g.duration) || 60, minPlayers: g.minPlayers || 4, minAge: g.minAge || null, ageRestriction: !!g.minAge, refereeId: g.refereeId || null, refereeName: g.refereeName || null });
    setEditRefFriends(null);
  }
  const [msgs, setMsgs] = useState([]);
  const [input, setInput] = useState("");
  const [loadingMsgs, setLoadingMsgs] = useState(false);
  const [weather, setWeather] = useState(null);
  const [, setTick] = useState(0);
  const endRef = useRef(null);
  const onPatchGameRef = useRef(onPatchGame);
  const mapGameChatMessage = useCallback((m) => {
    const name = m.user_name || currentUser?.user_metadata?.name || currentUser?.email?.split("@")[0] || "Player";
    return {
      id: m.id,
      user: name,
      avatar: (name || "??").slice(0, 2).toUpperCase(),
      text: m.text,
      time: m.created_at ? new Date(m.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : (m.time || "Just now"),
      mine: m.user_id === currentUser?.id,
      pending: m.pending === true,
    };
  }, [currentUser?.email, currentUser?.id, currentUser?.user_metadata?.name]);
  const full = g.spotsFilled >= g.spotsTotal;
  const past = isPastGame(g);
  const gameMentionCandidates = useMemo(() => {
	    const roster = [...(g.players || []), ...(g.waitlist || [])];
	    const byId = new Map();
	    roster.forEach(player => {
	      if (player?.id && player.id !== currentUser?.id && !byId.has(player.id)) byId.set(player.id, player);
	    });
	    return [...byId.values()];
	  }, [currentUser?.id, g.players, g.waitlist]);

  useEffect(() => { onPatchGameRef.current = onPatchGame; }, [onPatchGame]);

  // Derived flags — declared first so all useEffects below can depend on them
  // safely (pre-existing TDZ bug: a useEffect referenced `isHost` in its deps
  // array before this `const` was reached, which crashes the whole sheet on
  // mount with "Cannot access 'isHost' before initialization").
  const isHost = g.hostId === currentUser?.id;
  const isOutdoor = g.venueType === "Outdoor" || (!g.venueType && (g.location || "").toLowerCase().includes("park"));
  const handleClaimBall = async () => {
    if (!currentUser?.id) return;
    try {
      const { error } = await supabase.rpc("claim_game_ball", { gid: g.id });
      if (error) throw error;
      if (onPatchGame) onPatchGame({ ballHolder: currentUser.id });
      scheduleGameReminders(g, true);
      const volunteerName = currentUser?.user_metadata?.name || "Someone";
      if (g.hostId && g.hostId !== currentUser.id) {
        supabase.from("notifications").insert({ user_id: g.hostId, type: "ball_assigned", text: `${volunteerName} will bring the ball`, sub: g.title, game_id: g.id }).then(() => {}, () => {});
      }
    } catch (err) {
      console.warn("[ball.claim] failed", err);
      showToast?.("Could not claim the ball");
    }
  };
  const matchedVenue = useMemo(() => venues.find(v =>
    (g.venueId && v.id === g.venueId) ||
    (g.location && v.name === g.location) ||
    (g.lat != null && g.lng != null && Math.abs(+g.lat - +v.lat) < 0.0005 && Math.abs(+g.lng - +v.lng) < 0.0005)
  ), [venues, g.venueId, g.location, g.lat, g.lng]);
  const venueAddress = matchedVenue?.address || matchedVenue?.name || g.location || "";
  const matchedBackupVenue = useMemo(() => venues.find(v =>
    (g.backupVenueId && String(v.id) === String(g.backupVenueId)) ||
    (g.backupLocation && v.name === g.backupLocation) ||
    (g.backupLat != null && g.backupLng != null && Math.abs(+g.backupLat - +v.lat) < 0.0005 && Math.abs(+g.backupLng - +v.lng) < 0.0005)
  ), [venues, g.backupVenueId, g.backupLocation, g.backupLat, g.backupLng]);
  const backupAddress = matchedBackupVenue?.address || matchedBackupVenue?.name || g.backupLocation || "";
  const handleAddToCalendar = async () => {
    const event = buildGameCalendarEvent(g, backupAddress);
    if (!event) {
      if (showToast) showToast(t("couldNotOpenCalendar"));
      return;
    }

    if (window.Capacitor?.isNativePlatform?.()) {
      try {
        const { Browser } = await import("@capacitor/browser");
        await Browser.open({ url: event.googleUrl });
        if (showToast) showToast(t("calendarOpened"));
        return;
      } catch {
        window.location.assign(event.googleUrl);
        if (showToast) showToast(t("calendarOpened"));
        return;
      }
    }

    const opened = window.open(event.googleUrl, "_blank");
    if (opened) {
      opened.opener = null;
      if (showToast) showToast(t("calendarOpened"));
      return;
    }

    window.location.assign(event.googleUrl);
    if (showToast) showToast(t("calendarOpened"));
  };

  // Tick every minute so countdown stays live
  useEffect(() => {
    const id = setInterval(() => setTick(t => t + 1), 60000);
    return () => clearInterval(id);
  }, []);

  // Lazy-load friends list when host opens the edit modal — only when needed.
  useEffect(() => {
    if (!showEditModal || !isHost || !currentUser?.id || editRefFriends !== null) return;
    let mounted = true;
    (async () => {
      const { data: rows } = await supabase.from("friendships").select("requester_id, recipient_id, status")
        .or(`requester_id.eq.${currentUser.id},recipient_id.eq.${currentUser.id}`).eq("status", "accepted");
      const ids = (rows || []).map(r => r.requester_id === currentUser.id ? r.recipient_id : r.requester_id);
      if (ids.length === 0) { if (mounted) setEditRefFriends([]); return; }
      const { data: profs } = await supabase.from("profiles_public").select("user_id, name, avatar_url").in("user_id", ids);
      if (mounted) setEditRefFriends((profs || []).filter(Boolean));
    })();
    return () => { mounted = false; };
  }, [showEditModal, isHost, currentUser, editRefFriends]);

  // Fetch weather for outdoor games with a future date
  useEffect(() => {
    if (!isOutdoor || !g.lat || !g.lng || !g.date || !g.hour) return;
    const gameDate = new Date(`${g.date}T${g.hour}`);
    if (gameDate < new Date()) return; // past games
    const daysDiff = Math.ceil((gameDate - new Date()) / 86400000);
    if (daysDiff > 7) return; // Open-Meteo only goes 7 days
    let mounted = true;
    fetch(`https://api.open-meteo.com/v1/forecast?latitude=${g.lat}&longitude=${g.lng}&hourly=temperature_2m,precipitation_probability,weathercode&forecast_days=${daysDiff + 1}&timezone=Europe%2FOslo`)
      .then(r => r.json())
      .then(d => {
        if (!mounted) return;
        const hours = d.hourly?.time || [];
        const targetHour = `${g.date}T${g.hour.slice(0,5)}`;
        const idx = hours.findIndex(h => h === targetHour);
        if (idx === -1) return;
        const code = d.hourly.weathercode[idx];
        const temp = Math.round(d.hourly.temperature_2m[idx]);
        const rain = d.hourly.precipitation_probability[idx];
        const desc = code <= 1 ? "Clear" : code <= 3 ? "Partly cloudy" : code <= 49 ? "Foggy" : code <= 67 ? "Rain" : code <= 77 ? "Snow" : code <= 82 ? "Showers" : "Thunderstorm";
        const icon = code <= 1 ? "sun" : code <= 3 ? "cloud-sun" : "rain";
        setWeather({ temp, rain, desc, icon, bad: code >= 61 });
      })
      .catch(() => {});
    return () => { mounted = false; };
  }, [isOutdoor, g.lat, g.lng, g.date, g.hour]);

  useEffect(() => {
    if (tab !== "chat") return;
    let mounted = true;
    setLoadingMsgs(true);
    supabase.from("messages").select("*").eq("game_id", g.id).order("created_at", { ascending: true })
      .then(({ data }) => {
        if (!mounted) return;
        setMsgs((data || []).map(mapGameChatMessage));
        setLoadingMsgs(false);
      });
    const channel = supabase.channel(`game-${g.id}`)
      .on("postgres_changes", { event: "INSERT", schema: "public", table: "messages", filter: `game_id=eq.${g.id}` }, payload => {
        if (!mounted) return;
        const mapped = mapGameChatMessage(payload.new);
        setMsgs(prev => {
          if (prev.some(x => x.id === mapped.id)) return prev;
          const withoutPending = prev.filter(x => !(x.pending && x.mine === mapped.mine && x.text === mapped.text));
          return [...withoutPending, mapped];
        });
      })
      .on("postgres_changes", { event: "UPDATE", schema: "public", table: "games", filter: `id=eq.${g.id}` }, payload => {
        if (!mounted || !payload.new) return;
        const row = payload.new;
        // Patch only the fields that can change in real-time without a full reload
        onPatchGameRef.current?.({
          ballHolder: row.ball_holder ?? null,
          spotsTotal: row.spots_total,
          spotsFilled: row.spots_filled,
          status: row.status,
          title: row.title,
          description: row.description,
        });
      })
      .subscribe();
    // Polling fallback — refetch every 8s in case realtime is not enabled.
    // Merge by id instead of replacing so realtime-arrived messages aren't clobbered.
    const poll = setInterval(() => {
      if (!mounted) return;
      supabase.from("messages").select("*").eq("game_id", g.id).order("created_at", { ascending: true })
        .then(({ data }) => {
          if (!mounted || !data) return;
          setMsgs(prev => {
            const seen = new Map(prev.map(m => [m.id, m]));
            const mappedRows = data.map(mapGameChatMessage);
            mappedRows.forEach(m => seen.set(m.id, m));
            for (const [id, msg] of seen.entries()) {
              if (msg.pending && mappedRows.some(m => m.mine === msg.mine && m.text === msg.text)) seen.delete(id);
            }
            return Array.from(seen.values()).sort((a, b) => (a.id > b.id ? 1 : -1));
          });
        });
    }, 8000);
    return () => {
      mounted = false;
      clearInterval(poll);
      try { channel.unsubscribe?.(); } catch { /* noop — already torn down */ }
      supabase.removeChannel(channel);
    };
  }, [tab, g.id, mapGameChatMessage]);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [msgs]);

  const send = async () => {
    const text = input.trim().slice(0, 500);
    if (!text) return;
    setInput("");
    const optimisticId = `local-${Date.now()}-${Math.random().toString(36).slice(2)}`;
    const fallbackName = currentUser?.user_metadata?.name || currentUser?.email?.split("@")[0] || "Player";
    const optimisticMsg = mapGameChatMessage({
      id: optimisticId,
      user_id: currentUser?.id,
      user_name: fallbackName,
      text,
      time: "Just now",
      pending: true,
    });
    setMsgs(prev => [...prev, optimisticMsg]);
    // user_id and user_name are enforced by the DB trigger; we still send a fallback name for older schemas without the trigger
    const payload = { game_id: g.id, user_id: currentUser?.id, user_name: fallbackName, text };
    // Try once; if it fails, refresh the session (handles stale JWT → auth.uid() null → RLS reject) and retry.
    let { data: inserted, error } = await supabase.from("messages").insert(payload).select("*").maybeSingle();
    if (error) {
      const { error: refreshErr } = await supabase.auth.refreshSession();
      if (!refreshErr) {
        ({ data: inserted, error } = await supabase.from("messages").insert(payload).select("*").maybeSingle());
      }
    }
	    if (error) {
	      setMsgs(prev => prev.filter(m => m.id !== optimisticId));
	      const { data: sessionData } = await supabase.auth.getSession().catch(() => ({ data: null }));
	      console.error("[chat.send] failed", {
        code: error?.code, message: error?.message, details: error?.details, hint: error?.hint,
        gameId: g.id, userId: currentUser?.id, isHost, textLen: text.length,
        hasSession: !!sessionData?.session, sessionUid: sessionData?.session?.user?.id,
      });
      setInput(text); // restore the message so the user doesn't lose it
      // Surface the real reason in the toast so the bug is self-reporting instead of silent
	      const why = error?.message || error?.details || error?.code || "unknown";
	      if (showToast) showToast(`${t("failedToSendMessage")} (${why})`);
	    } else {
	      if (inserted) {
	        const saved = mapGameChatMessage(inserted);
	        setMsgs(prev => prev.map(m => m.id === optimisticId ? saved : m));
	      }
	      const mentionTargets = mentionedProfilesFromText(text, gameMentionCandidates, currentUser?.id);
	      if (mentionTargets.length) {
	        const senderName = currentUser?.user_metadata?.name || "Someone";
	        const preview = text.length > 80 ? `${text.slice(0, 77)}...` : text;
	        supabase.from("notifications").insert(mentionTargets.map(profile => ({
	          user_id: profile.id || profile.user_id,
	          type: "mention",
	          text: `${senderName} mentioned you in ${g.title}`,
	          sub: preview,
	          game_id: g.id,
	          read: false,
	        }))).then(({ error: mentionError }) => {
	          if (mentionError) console.warn("[chat.mention.notification] failed", mentionError);
	        }, mentionError => console.warn("[chat.mention.notification] failed", mentionError));
	      }
	    }
	  };

  const kickPlayer = async (playerId, playerName) => {
    if (!isHost || kickingPlayerId) return;
    setKickingPlayerId(playerId);
    try {
      // Verify ownership in DB: only kick if current user is the host of this game
      const { data: hostCheck } = await supabase.from("games").select("id").eq("id", g.id).eq("host_id", currentUser?.id).maybeSingle();
      if (!hostCheck) { if (showToast) showToast("Not authorized"); return; }
      const { error: delErr } = await supabase.from("game_players").delete().eq("game_id", g.id).eq("user_id", playerId);
      if (delErr) throw delErr;
      // Auto-promote the first waitlisted player so the roster stays full after a kick.
      // Best-effort: if the promote fails we still treat the kick as successful and rely on onRefresh to resync.
      const firstWaiting = (g.waitlist || [])[0];
      if (firstWaiting) {
        const { error: promoteErr } = await supabase.from("game_players").update({ status: "confirmed" }).eq("game_id", g.id).eq("user_id", firstWaiting.id);
        if (!promoteErr) {
          supabase.from("notifications").insert({ user_id: firstWaiting.id, type: "promoted", text: "You're off the waitlist", sub: g.title, game_id: g.id }).then(() => {}, () => {});
        } else {
          console.warn("[kickPlayer] auto-promote failed", promoteErr);
        }
      }
      if (showToast) showToast(t("playerRemovedFromGame", { name: playerName }));
      if (onRefresh) onRefresh();
    } catch (e) {
      console.error("[kickPlayer] failed", e, { gameId: g.id, playerId });
      reportError(e, { where: "kickPlayer", gameId: g.id, playerId });
      if (showToast) showToast(t("failedToRemovePlayer"));
    } finally { setKickingPlayerId(null); }
  };

  const promotePlayer = async (playerId, playerName) => {
    if (!isHost || kickingPlayerId) return;
    setKickingPlayerId(playerId);
    try {
      // Verify ownership in DB before promoting
      const { data: hostCheck } = await supabase.from("games").select("id").eq("id", g.id).eq("host_id", currentUser?.id).maybeSingle();
      if (!hostCheck) { if (showToast) showToast("Not authorized"); return; }
      await supabase.from("game_players").update({ status: "confirmed" }).eq("game_id", g.id).eq("user_id", playerId);
      // Notify the promoted player
      supabase.from("notifications").insert({ user_id: playerId, type: "promoted", text: "You're off the waitlist", sub: g.title, game_id: g.id }).then(() => {}, () => {});
      if (showToast) showToast(t("playerPromotedFromWaitlist", { name: playerName }));
      if (onRefresh) onRefresh();
    } catch {
      if (showToast) showToast(t("failedToPromotePlayer"));
    } finally { setKickingPlayerId(null); }
  };

  return (
    <div style={{ position: "absolute", inset: 0, zIndex: 50, background: C.bg, display: "flex", flexDirection: "column" }}>
      {/* Header — bigger title, icon-only chrome buttons, icon+label meta
          rows. The previous header packed back-arrow + tiny share/QR text
          buttons + 11px chips into a tight band; this version reads like an
          Airbnb listing detail page. */}
      <div style={{ paddingTop: "calc(12px + env(safe-area-inset-top, 44px))", paddingLeft: SPACE[4], paddingRight: SPACE[4], paddingBottom: 0, flexShrink: 0 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: SPACE[3] }}>
          <button aria-label="Close" onClick={onClose} style={{ width: 40, height: 40, borderRadius: "50%", background: C.surface, border: "none", cursor: "pointer", color: C.ink, display: "flex", alignItems: "center", justifyContent: "center", boxShadow: SHADOW.sm, transition: "background 0.12s" }}
            onMouseEnter={e => e.currentTarget.style.background = C.border}
            onMouseLeave={e => e.currentTarget.style.background = C.surface}
          >
            <ArrowLeft size={18} {...ICON} />
          </button>
          <div style={{ display: "flex", gap: SPACE[2], alignItems: "center" }}>
            <button aria-label={fav ? t("removeFromSaved") : t("saveGame")} onClick={onFav}
              style={{ width: 40, height: 40, borderRadius: "50%", background: C.surface, border: "none", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", boxShadow: SHADOW.sm, transition: "background 0.12s" }}
              onMouseEnter={e => e.currentTarget.style.background = C.border}
              onMouseLeave={e => e.currentTarget.style.background = C.surface}
            >
              <Bookmark size={18} fill={fav ? C.ink : "none"} color={fav ? C.ink : C.inkMid} strokeWidth={1.8} />
            </button>
            <button aria-label="Share" onClick={async () => {
              const url = `${window.location.origin}?game=${g.id}`;
              if (navigator.share) {
                try { await navigator.share({ title: g.title, text: `Join my football game: ${g.title}`, url }); }
                catch { try { await navigator.clipboard.writeText(url); if (showToast) showToast(t("linkCopied")); } catch { /* ignore */ } }
              } else {
                try { await navigator.clipboard.writeText(url); if (showToast) showToast(t("linkCopied")); }
                catch { if (showToast) showToast(t("couldNotCopyLink")); }
              }
            }} style={{ width: 40, height: 40, borderRadius: "50%", background: C.surface, border: "none", cursor: "pointer", color: C.ink, display: "flex", alignItems: "center", justifyContent: "center", boxShadow: SHADOW.sm, transition: "background 0.12s" }}
              onMouseEnter={e => e.currentTarget.style.background = C.border}
              onMouseLeave={e => e.currentTarget.style.background = C.surface}
            >
              <Share2 size={17} {...ICON} />
            </button>
          </div>
        </div>

        {/* Chip row — same vocabulary as GameCard so cards and detail feel cohesive */}
        <div style={{ display: "flex", alignItems: "center", gap: SPACE[1] + 2, marginBottom: SPACE[3], flexWrap: "wrap" }}>
          <LevelPill level={g.level} small />
          <span style={{ display: "inline-flex", padding: "3px 10px", borderRadius: 999, background: C.bg, fontSize: 12, fontWeight: 600, color: C.inkMid }}>{g.format}</span>
          {g.refereeId && (
            <span title={`${t("refereeLabel")}: ${g.refereeName || ""}`} aria-label={t("refereeBadge")} style={{ display: "inline-flex", alignItems: "center", gap: 4, padding: "3px 10px", borderRadius: 999, background: warnBg, color: C.warn, fontSize: 12, fontWeight: 600 }}>
              <ShieldCheck size={12} {...ICON} />
              {t("refereeBadge")}
            </span>
          )}
          {g.visibility === "community" && (
            <span title={g.communityName || t("scopeCommunity")} style={{ display: "inline-flex", alignItems: "center", gap: 4, padding: "3px 10px", borderRadius: 999, background: C.bg, fontSize: 12, fontWeight: 600, color: C.inkMid, maxWidth: 160, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              <Users size={11} {...ICON} />
              {g.communityName || t("scopeCommunity")}
              {g.communityVerified && <VerifiedTick size={8} style={{ boxShadow: `0 0 0 1px ${C.bg}` }} />}
            </span>
          )}
        </div>

        {/* Title + host row — title is display weight, host avatar reads as
            a small attribution rather than competing for the eye. */}
        <h2 style={{ ...TYPE.h1, color: C.ink, margin: `0 0 ${SPACE[3]}px`, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{g.title}</h2>

        {/* Meta — icon+label pairs in a row. Mirrors the GameCard pattern,
            same icons, larger size. Each pair fits tightly so the row stays
            on one line for normal cities. */}
        <div style={{ display: "flex", gap: SPACE[4], flexWrap: "wrap", paddingBottom: SPACE[3] }}>
          {(() => {
            const countdown = fmtCountdown(g.date, g.hour);
            const timeStr = countdown || (g.time ? `${g.time}${g.hour ? `, ${g.hour}` : ""}` : "");
            const isUrgent = countdown && (countdown === "Starting now" || /^Starts in \d+m$/.test(countdown));
            const rows = [];
            if (timeStr) rows.push({ Icon: Clock, text: timeStr, color: isUrgent ? C.danger : C.inkMid, weight: isUrgent ? 600 : 400 });
            if (g.location) rows.push({ Icon: MapPin, text: g.location, color: C.inkMid, weight: 400 });
            return rows.map((r, i) => (
              <span key={i} style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 13, color: r.color, fontWeight: r.weight }}>
                <r.Icon size={14} {...ICON} />
                {r.text}
              </span>
            ));
          })()}
          <span style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 13, color: C.inkFaint, marginLeft: "auto" }}>
            <Avatar initials={g.hostAvatar} size={20} />
            {g.host}
          </span>
        </div>

        {/* Tabs — simple underline with brand-green selected state. */}
        <div style={{ display: "flex", gap: SPACE[5], borderBottom: `1px solid ${C.border}` }}>
          {[["info", t("tabInfo")], ["players", t("tabPlayers")], ["chat", t("tabChat")]].map(([tabKey, tabLabel]) => (
            <button key={tabKey} onClick={() => setTab(tabKey)} style={{ background: "none", border: "none", borderBottom: `2px solid ${tab === tabKey ? C.accent : "transparent"}`, cursor: "pointer", padding: `${SPACE[2] + 2}px 0 ${SPACE[2] + 2}px`, marginBottom: -1, fontSize: 14, fontWeight: tab === tabKey ? 700 : 500, color: tab === tabKey ? C.ink : C.inkMid, transition: "color 0.15s, border-color 0.15s, font-weight 0.15s" }}>{tabLabel}</button>
          ))}
        </div>
      </div>

      {/* Body */}
      <div style={{ flex: 1, overflow: "hidden", display: "flex", flexDirection: "column" }}>
        {tab === "info" && (
          <div style={{ flex: 1, overflowY: "auto", padding: "16px 20px" }}>
            {/* Host dashboard summary */}
            {isHost && !past && (() => {
              const moreNeeded = Math.max(0, g.minPlayers - g.spotsFilled);
              const isFull = g.spotsFilled >= g.spotsTotal;
              const status = isFull ? t("full") : moreNeeded > 0 ? t("waitingForPlayers") : t("gameConfirmed");
              const statusColor = isFull ? C.warn : moreNeeded > 0 ? C.warn : C.success;
              return (
                <div style={{ padding: "12px 14px", background: C.accentBg, borderRadius: 10, marginBottom: 14, border: `1px solid ${C.accent}` }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <div>
                      <span style={{ fontSize: 22, fontWeight: 700, color: C.ink, letterSpacing: "-0.02em" }}>{g.spotsFilled}</span>
                      <span style={{ fontSize: 14, color: C.inkMid, marginLeft: 2 }}>/ {g.spotsTotal} players</span>
                    </div>
                    <span style={{ fontSize: 12, fontWeight: 700, color: statusColor, background: C.bg, padding: "4px 10px", borderRadius: 6 }}>{status}</span>
                  </div>
                  {moreNeeded > 0 && (
                    <div style={{ fontSize: 12, color: C.inkMid, marginTop: 6 }}>{t("confirmWhenEnoughPlayers", { n: g.minPlayers })}</div>
                  )}
                </div>
              );
            })()}
            {/* Confirmation state — non-host view */}
            {!past && !isHost && g.needsMorePlayers && (() => {
              const need = Math.max(1, (g.minPlayers || 0) - g.spotsFilled);
              return (
                <div style={{ padding: "10px 14px", background: warnBg, borderRadius: 8, marginBottom: 14, border: `1px solid ${warnBorder}` }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: C.warn }}>{need === 1 ? t("oneMorePlayerNeeded") : t("morePlayersNeeded", { n: need })}</div>
                  <div style={{ fontSize: 12, color: C.inkMid, marginTop: 2 }}>{t("autoConfirmsAt", { n: g.minPlayers })}</div>
                </div>
              );
            })()}
            {/* Weather widget for outdoor games */}
            {weather && (
              <div style={{ padding: "10px 14px", background: weather.bad ? dangerBg : successBg, borderRadius: 8, marginBottom: 14, border: `1px solid ${weather.bad ? dangerBorder : successBorder}`, display: "flex", alignItems: "center", gap: 12 }}>
                {(() => {
                  const WeatherIcon = weather.icon === "sun" ? SunMedium : weather.icon === "cloud-sun" ? CloudSun : CloudRain;
                  return <WeatherIcon size={24} {...ICON} color={weather.bad ? C.danger : C.success} />;
                })()}
                <div>
                  <div style={{ fontSize: 13, fontWeight: 600, color: weather.bad ? C.danger : C.success }}>{weather.desc} · {weather.temp}°C</div>
                  <div style={{ fontSize: 12, color: weather.bad ? C.danger : C.inkMid, marginTop: 2 }}>{weather.bad ? t("chanceOfRainBad", { rain: weather.rain }) : t("chanceOfRainGood", { rain: weather.rain })}</div>
                </div>
              </div>
            )}
            {/* Published teams */}
            {(g.teams?.A?.length || g.teams?.B?.length) && (
              <div style={{ marginBottom: 16 }}>
                <div style={{ ...T.label, marginBottom: 10 }}>{t("teams")}</div>
                <div style={{ display: "flex", gap: 10 }}>
                  {[[t("teamA"), g.teams.A, C.accent], [t("teamB"), g.teams.B, C.warn]].map(([label, playerIds, color]) => {
                    const players = (playerIds || []).map(id => g.players.find(p => p.id === id)).filter(Boolean);
                    return (
                      <div key={label} style={{ flex: 1, ...card, padding: "10px 12px" }}>
                        <div style={{ fontSize: 11, fontWeight: 700, color, letterSpacing: "0.06em", textTransform: "uppercase", marginBottom: 8 }}>{label}</div>
                        {players.map(p => (
                          <div key={p.id} style={{ display: "flex", alignItems: "center", gap: 6, padding: "3px 0" }}>
                            <Avatar initials={p.avatar} size={22} />
                            <span style={{ fontSize: 12, color: C.ink, flex: 1 }}>{p.name}</span>
                          </div>
                        ))}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
            {g.description ? <p style={{ ...T.body, margin: "0 0 16px" }}>{g.description}</p> : null}
            {/* Ball missing warning — show when game is within 24h and no one has claimed the ball */}
            {!past && !g.ballHolder && (joined || isHost) && g.date && g.hour && (() => {
              const gameTime = new Date(`${g.date}T${g.hour}`).getTime();
              const hoursAway = (gameTime - Date.now()) / 3600000;
              return hoursAway > 0 && hoursAway < 24;
            })() && (
              <div style={{ padding: "10px 14px", background: warnBg, borderRadius: 8, marginBottom: 14, border: `1px solid ${warnBorder}`, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <FootballIcon size={18} color={C.warn} />
                  <span style={{ fontSize: 13, fontWeight: 600, color: C.warn }}>{t("nobodyHasTheBall")}</span>
                </div>
                <button onClick={handleClaimBall} style={{ fontSize: 12, fontWeight: 600, color: C.warn, background: "none", border: `1px solid ${C.warn}`, borderRadius: 6, padding: "4px 10px", cursor: "pointer", flexShrink: 0 }}>{t("illBringIt")}</button>
              </div>
            )}
            {/* Tags */}
            {g.tags && g.tags.length > 0 && (
              <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 16 }}>
                {g.tags.map(tag => (
                  <span key={tag} style={{ padding: "4px 10px", background: C.surface, borderRadius: 20, fontSize: 12, color: C.inkMid, fontWeight: 500 }}>#{tag}</span>
                ))}
              </div>
            )}
            <div style={{ height: 3, background: C.surface, borderRadius: 2, overflow: "hidden", marginBottom: 6 }}>
              <div style={{ height: "100%", width: `${(g.spotsFilled/g.spotsTotal)*100}%`, background: full ? C.danger : C.accent, borderRadius: 2 }} />
            </div>
            {(g.waitlist || []).length > 0 && (
              <div style={{ ...T.small, color: C.warn, fontWeight: 500, marginBottom: 16 }}>{(g.waitlist || []).length === 1 ? t("playerOnWaitlist", { n: 1 }) : t("playersOnWaitlist", { n: (g.waitlist || []).length })}</div>
            )}
            <div style={{ display: "flex", flexDirection: "column", gap: 0 }}>
              {[[t("duration"), g.duration], [t("format"), g.format]].map(([k, v]) => (
                <div key={k} style={{ display: "flex", justifyContent: "space-between", padding: "10px 0", borderBottom: `1px solid ${C.border}` }}>
                  <span style={{ ...T.label }}>{k}</span>
                  <span style={{ ...T.h3 }}>{v}</span>
                </div>
              ))}
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px 0", borderBottom: `1px solid ${C.border}` }}>
                <span style={{ ...T.label }}>{t("venue")}</span>
                <a href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(venueAddress)}`} target="_blank" rel="noreferrer" style={{ fontSize: 13, fontWeight: 600, color: C.accent, textDecoration: "none", display: "flex", alignItems: "center", gap: 4, maxWidth: "62%", justifyContent: "flex-end" }}>
                  <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{venueAddress || t("getDirections")}</span>
                  <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/></svg>
                </a>
              </div>
              {backupAddress && (
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px 0", borderBottom: `1px solid ${C.border}` }}>
                  <span style={{ ...T.label }}>Backup pitch</span>
                  <a href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(backupAddress)}`} target="_blank" rel="noreferrer" style={{ fontSize: 13, fontWeight: 600, color: C.accent, textDecoration: "none", display: "flex", alignItems: "center", gap: 4, maxWidth: "62%", justifyContent: "flex-end" }}>
                    <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{backupAddress}</span>
                    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/></svg>
                  </a>
                </div>
              )}
              {g.refereeId && (
                <button
                  onClick={() => {
                    if (g.refereeId === currentUser?.id) return;
                    // Open the inline player viewer with what we know — friend
                    // status / stats are fetched by the same effects that handle
                    // taps on a player chip.
                    setViewingPlayer({
                      id: g.refereeId,
                      name: g.refereeName || "Referee",
                      avatar: (g.refereeName || "??").split(" ").map(w => w[0]).join("").slice(0,2).toUpperCase(),
                      avatarUrl: null,
                      level: "",
                      position: "",
                    });
                    setFriendStatus(null);
                  }}
                  style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px 0", borderBottom: `1px solid ${C.border}`, background: "transparent", border: "none", width: "100%", textAlign: "left", cursor: g.refereeId === currentUser?.id ? "default" : "pointer" }}
                >
                  <span style={{ ...T.label, display: "inline-flex", alignItems: "center", gap: 6 }}>
                    <Flag size={13} {...ICON} color={C.warn} />{t("refereeLabel")}
                  </span>
                  <span style={{ ...T.h3 }}>{g.refereeName || "—"}</span>
                </button>
              )}
            </div>
            {/* Add to calendar — for joined players or host */}
            {!past && (joined || isHost) && g.date && g.hour && (
              <div style={{ marginTop: 16, marginBottom: 4 }}>
                <button onClick={handleAddToCalendar} style={{ ...btnSecondary, width: "100%", padding: "10px 0", fontSize: 13, display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}>
                  <Calendar size={14} {...ICON} />
                  {t("addToCalendar")}
                </button>
              </div>
            )}

            {/* Host actions */}
            {isHost && !past && (
              <div style={{ marginTop: 20 }}>
                {!showCancelModal && !showEditModal && (
                  <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 8 }}>
                    {g.teams && (
                      <button onClick={async () => {
                        try {
                          await supabase.from("games").update({ teams: null }).eq("id", g.id);
                          if (showToast) showToast(t("teamsCleared"));
                          if (onRefresh) onRefresh();
                        } catch { if (showToast) showToast(t("failedToClearTeams")); }
                      }} style={{ ...btnSecondary, width: "100%", padding: "9px 16px", fontSize: 13, color: C.danger, borderColor: dangerBorder }}>
                        {t("clearTeams")}
                      </button>
                    )}
                    <div style={{ display: "flex", gap: 8 }}>
                      <button onClick={() => setShowEditModal(true)} style={{ ...btnSecondary, flex: 1, padding: "9px 16px", fontSize: 13 }}>{t("editGame")}</button>
                      <button disabled={isCloning} onClick={() => onClone && onClone(g)} style={{ ...btnSecondary, flex: 1, padding: "9px 16px", fontSize: 13, color: C.accent, borderColor: C.accent, opacity: isCloning ? 0.55 : 1 }}>
                        {isCloning ? t("publishing") : t("cloneForNextWeek")}
                      </button>
                    </div>
                    <button onClick={() => setShowCancelModal(true)} style={{ ...btnSecondary, width: "100%", color: C.danger, borderColor: dangerBorder, padding: "9px 16px", fontSize: 13 }}>
                      {t("cancelGame")}
                    </button>
                  </div>
                )}
                {showEditModal && (
                  <div style={{ ...card, padding: 16, marginBottom: 8 }}>
                    <div style={{ ...T.h3, marginBottom: 14 }}>{t("editGame")}</div>
                    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                      <div><Label>{t("titleLabel")}</Label><input value={editForm.title} onChange={e => setEditForm(f => ({...f, title: e.target.value.slice(0, 60)}))} maxLength={60} style={inputBase} /></div>
                      <div><Label>{t("descriptionLabel")}</Label><div style={{ position: "relative" }}><textarea value={editForm.description} onChange={e => setEditForm(f => ({...f, description: e.target.value.slice(0, 300)}))} rows={2} maxLength={300} style={{ ...inputBase, resize: "none" }} /><span style={{ position: "absolute", bottom: 6, right: 10, fontSize: 10, color: C.inkFaint, pointerEvents: "none" }}>{(editForm.description||"").length}/300</span></div></div>
                      <div><Label>{t("dateLabel")}</Label><CalendarPicker value={editForm.date} onChange={v => setEditForm(f => ({...f, date: v}))} min={format(new Date(), "yyyy-MM-dd")} /></div>
                      <div><Label>{t("timeLabel")}</Label><TimePicker value={editForm.hour} onChange={v => setEditForm(f => ({...f, hour: v}))} /></div>
                      <div><Label>{t("durationLabel", { minutes: editForm.duration })}</Label><input type="range" min={30} max={120} step={15} value={editForm.duration} onChange={e => setEditForm(f => ({...f, duration: parseInt(e.target.value)}))} style={{ width: "100%", accentColor: C.ink }} /></div>
                      {/* Referee — compact dropdown to keep edit modal short */}
                      <div>
                        <Label>{t("refereeOptional")}</Label>
                        <select
                          value={editForm.refereeId || ""}
                          onChange={e => {
                            const id = e.target.value || null;
                            const friend = (editRefFriends || []).find(f => f.user_id === id);
                            setEditForm(f => ({ ...f, refereeId: id, refereeName: friend ? (friend.name || "Player") : null }));
                          }}
                          style={{ ...inputBase, paddingRight: 30 }}
                        >
                          <option value="">— {t("refereeLabel")}: {t("noneLabel") || "None"} —</option>
                          {editRefFriends === null && editForm.refereeId && (
                            <option value={editForm.refereeId}>{editForm.refereeName || t("refereeLabel")}</option>
                          )}
                          {(editRefFriends || []).map(f => (
                            <option key={f.user_id} value={f.user_id}>{f.name || "Player"}</option>
                          ))}
                        </select>
                        {editRefFriends && editRefFriends.length === 0 && !editForm.refereeId && (
                          <div style={{ fontSize: 11, color: C.inkFaint, marginTop: 4 }}>{t("noFriendsForReferee")}</div>
                        )}
                      </div>
                    </div>
                    <div style={{ display: "flex", gap: 8, marginTop: 14 }}>
                      <button onClick={() => setShowEditModal(false)} style={{ ...btnSecondary, flex: 1, padding: "10px 0" }}>{t("cancel")}</button>
                      <button disabled={savingEdit || !editForm.title.trim()} onClick={async () => {
                        setSavingEdit(true);
                        try {
                          // Detect what changed so notification text is specific
                          const timeChanged = editForm.date !== g.date || editForm.hour !== g.hour;
                          const titleChanged = editForm.title.trim() !== g.title;
                          const refereeChanged = (editForm.refereeId || null) !== (g.refereeId || null);
                          const { error } = await supabase.from("games").update({ title: editForm.title.trim(), description: editForm.description, date: editForm.date, hour: editForm.hour, duration: `${editForm.duration} min`, min_players: editForm.minPlayers, min_age: null, referee_id: editForm.refereeId || null, referee_name: editForm.refereeId ? (editForm.refereeName || null) : null }).eq("id", g.id).eq("host_id", currentUser?.id);
                          if (error) throw error;
                          // If a *new* referee was just nominated, notify them.
                          // Skip if it's the same person already on the game, or the host.
                          if (refereeChanged && editForm.refereeId && editForm.refereeId !== currentUser?.id) {
                            supabase.from("notifications").insert({
                              user_id: editForm.refereeId,
                              type: "referee_assigned",
                              read: false,
                              game_id: g.id,
                              text: t("notifRefereeAssignedText", { title: editForm.title.trim() }),
                              sub: t("notifRefereeAssignedSub", { date: editForm.date, time: editForm.hour }),
                            }).then(() => {}, () => {});
                          }
                          // Notify all joined players (except host) — include host_id since this game is theirs
                          const playersToNotify = [...new Set((g.players || []).map(p => p.id).filter(id => id !== g.hostId).concat((g.waitlist || []).map(p => p.id)))];
                          if (playersToNotify.length > 0) {
                            const text = timeChanged
                              ? `Game time changed: ${editForm.title.trim()}`
                              : titleChanged ? `Game updated: ${editForm.title.trim()}` : `Game details updated`;
                            const { error: notifErr } = await supabase.from("notifications").insert(
                              playersToNotify.map(uid => ({ user_id: uid, type: "game_updated", text, sub: `${editForm.date} · ${editForm.hour}`, game_id: g.id }))
                            );
                            if (notifErr) console.warn("[notify] game_updated insert failed", notifErr);
                          }
                          setShowEditModal(false);
                          if (showToast) showToast(t("gameUpdated"));
                          if (onRefresh) onRefresh();
                        } catch {
                          if (showToast) showToast(t("failedToSave"));
                        } finally { setSavingEdit(false); }
                      }} style={{ ...btnPrimary, flex: 1, padding: "10px 0", opacity: savingEdit || !editForm.title.trim() ? 0.4 : 1 }}>{savingEdit ? t("saving") : t("saveChanges")}</button>
                    </div>
                  </div>
                )}
                {showCancelModal && (
                  <div style={{ ...card, padding: 16 }}>
                    <div style={{ ...T.h3, marginBottom: 8 }}>{t("whyCancelling")}</div>
                    <div style={{ display: "flex", flexDirection: "column", gap: 6, marginBottom: 12 }}>
                      {[[t("reasonVenue"), "Venue not available"], [t("reasonPlayers"), "Not enough players"], [t("reasonWeather"), "Weather"], [t("reasonPersonal"), "Personal reason"]].map(([label, r]) => (
                        <button key={r} onClick={() => setCancelReason(r)} style={{ padding: "10px 14px", borderRadius: 8, border: `1px solid ${cancelReason === r ? C.danger : C.border}`, background: cancelReason === r ? dangerBg : C.surface, color: cancelReason === r ? C.danger : C.ink, cursor: "pointer", fontSize: 13, textAlign: "left" }}>{label}</button>
                      ))}
                    </div>
                    <div style={{ display: "flex", gap: 8 }}>
                      <button onClick={() => setShowCancelModal(false)} style={{ ...btnSecondary, flex: 1, padding: "10px 0" }}>{t("back")}</button>
                      <button onClick={() => cancelReason && onCancel(g.id, cancelReason)} disabled={!cancelReason} style={{ ...btnPrimary, flex: 1, padding: "10px 0", background: C.danger, opacity: cancelReason ? 1 : 0.4 }}>{t("confirmCancel")}</button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
        {tab === "players" && (
          <div style={{ flex: 1, overflowY: "auto", padding: "12px 20px" }}>
            {/* Ball assignment banner */}
            {(() => {
              const ballPlayer = g.ballHolder ? g.players.find(p => p.id === g.ballHolder) : null;
              const iHaveIt = g.ballHolder === currentUser?.id;
              const canAct = !past && (joined || isHost);
              const setBall = async (userId) => {
                // Validate: userId must be null (clearing) or a confirmed player in the game
                const isValidPlayer = !userId || g.players?.some(p => p.id === userId);
                if (!isValidPlayer) return;
                if (userId === currentUser?.id) {
                  await handleClaimBall();
                  return;
                }
                if (onPatchGame) onPatchGame({ ballHolder: userId });
                // Host ownership enforced in query
                await supabase.from("games").update({ ball_holder: userId }).eq("id", g.id).eq("host_id", currentUser?.id);
                if (joined || isHost) scheduleGameReminders(g, userId === currentUser?.id);
                // Notify the assigned player (if it's not themselves assigning themselves)
                if (userId && userId !== currentUser?.id) {
                  const assignerName = currentUser?.user_metadata?.name || "The host";
                  supabase.from("notifications").insert({ user_id: userId, type: "ball_assigned", text: `You're bringing the ball to "${g.title}"`, sub: `${g.date} · ${g.hour} · assigned by ${assignerName}`, game_id: g.id }).then(() => {}, () => {});
                }
              };
              return (
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "10px 14px", borderRadius: 10, background: g.ballHolder ? C.accentBg : C.surface, border: `1px solid ${g.ballHolder ? C.accent + "44" : C.border}`, marginBottom: 14, gap: 10 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <FootballIcon size={22} color={g.ballHolder ? C.accent : C.inkMid} />
                    <div>
                      <div style={{ fontSize: 12, fontWeight: 600, color: C.inkMid, letterSpacing: "0.04em", textTransform: "uppercase" }}>{t("ballLabel")}</div>
                      <div style={{ fontSize: 13, fontWeight: 600, color: C.ink }}>
                        {ballPlayer ? (ballPlayer.isMe ? t("youreBreingIt") : `${ballPlayer.name}`) : t("nobodyYet")}
                      </div>
                    </div>
                  </div>
                  {canAct && (
                    iHaveIt
                      ? <button onClick={() => setBall(null)} style={{ fontSize: 12, fontWeight: 600, color: C.danger, background: "none", border: `1px solid ${C.border}`, borderRadius: 6, padding: "5px 10px", cursor: "pointer", flexShrink: 0 }}>{t("drop")}</button>
                      : !g.ballHolder
                        ? <button onClick={() => setBall(currentUser?.id)} style={{ fontSize: 12, fontWeight: 600, color: C.accent, background: "none", border: `1px solid ${C.accent}`, borderRadius: 6, padding: "5px 10px", cursor: "pointer", flexShrink: 0 }}>{t("illBringIt")}</button>
                        : isHost
                          ? <button onClick={() => setBall(null)} style={{ fontSize: 12, fontWeight: 600, color: C.inkFaint, background: "none", border: `1px solid ${C.border}`, borderRadius: 6, padding: "5px 10px", cursor: "pointer", flexShrink: 0 }}>{t("reassign")}</button>
                          : null
                  )}
                </div>
              );
            })()}

            {/* ── Inline Team Dispatch ── */}
            {isHost && !past && (() => {
              const hasTeams = !!(g.teams?.A?.length || g.teams?.B?.length);
              const assignedIds = new Set([...teamA, ...teamB].map(p => p.id));
              const unassigned = g.players.filter(p => !assignedIds.has(p.id));

              const randomize = () => {
                const levelScore = { "Advanced": 3, "Intermediate": 2, "Beginner": 1 };
                const teamScore = players => players.reduce((sum, p) => sum + (levelScore[p.level] || 2), 0);
                const targetA = Math.ceil(g.players.length / 2);
                const targetB = Math.floor(g.players.length / 2);
                const sorted = [...g.players]
                  .map((player, index) => ({ player, index, score: levelScore[player.level] || 2, tie: Math.random() }))
                  .sort((a, b) => b.score - a.score || a.tie - b.tie || a.index - b.index)
                  .map(item => item.player);
                const a = [], b = [];
                sorted.forEach(p => {
                  if (a.length >= targetA) { b.push(p); return; }
                  if (b.length >= targetB) { a.push(p); return; }
                  const score = levelScore[p.level] || 2;
                  const aAfter = Math.abs((teamScore(a) + score) - teamScore(b));
                  const bAfter = Math.abs(teamScore(a) - (teamScore(b) + score));
                  if (aAfter < bAfter) a.push(p);
                  else if (bAfter < aAfter) b.push(p);
                  else if (a.length <= b.length) a.push(p);
                  else b.push(p);
                });
                setTeamA(a); setTeamB(b);
                setDispatchStep("random");
              };

              const addToTeam = (player, team) => {
                setTeamA(prev => prev.filter(p => p.id !== player.id));
                setTeamB(prev => prev.filter(p => p.id !== player.id));
                if (team === "A") setTeamA(prev => [...prev, player]);
                else setTeamB(prev => [...prev, player]);
              };
              const removeFromTeam = (player) => {
                setTeamA(prev => prev.filter(p => p.id !== player.id));
                setTeamB(prev => prev.filter(p => p.id !== player.id));
              };

              const publishTeams = async () => {
                setSavingTeams(true);
                try {
                  await supabase.from("games").update({ teams: { A: teamA.map(p => p.id), B: teamB.map(p => p.id) } }).eq("id", g.id);
                  // Notify all players that teams are out
                  const allPlayerIds = [...teamA, ...teamB].map(p => p.id).filter(id => id !== g.hostId);
                  if (allPlayerIds.length > 0) {
                    supabase.from("notifications").insert(
                      allPlayerIds.map(uid => {
                        const inTeamA = teamA.some(p => p.id === uid);
                        return { user_id: uid, type: "teams_published", text: `Teams are out for "${g.title}"`, sub: `You're in Team ${inTeamA ? "A" : "B"}`, game_id: g.id };
                      })
                    ).then(() => {}, () => {});
                  }
                  if (showToast) showToast(t("teamsPublished"));
                  if (onRefresh) onRefresh();
                } catch { if (showToast) showToast(t("couldNotSaveTeams")); }
                setSavingTeams(false);
              };

              const clearTeams = async () => {
                try {
                  await supabase.from("games").update({ teams: null }).eq("id", g.id);
                  setTeamA([]); setTeamB([]); setDispatchStep("select");
                  if (showToast) showToast(t("teamsCleared"));
                  if (onRefresh) onRefresh();
                } catch { if (showToast) showToast(t("failedToClearTeams")); }
              };

              // If teams already published, show them prominently
              if (hasTeams && dispatchStep === "select") {
                const tA = g.players.filter(p => g.teams.A?.includes(p.id));
                const tB = g.players.filter(p => g.teams.B?.includes(p.id));
                return (
                  <div style={{ marginBottom: 16, borderRadius: 12, border: `1px solid ${C.border}`, overflow: "hidden" }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px 14px", background: C.surface, borderBottom: `1px solid ${C.border}` }}>
                      <span style={{ fontSize: 12, fontWeight: 700, color: C.inkMid, textTransform: "uppercase", letterSpacing: "0.06em" }}>Teams</span>
                      <div style={{ display: "flex", gap: 6 }}>
                        <button onClick={() => { setTeamA(tA); setTeamB(tB); setDispatchStep("random"); }} style={{ fontSize: 12, fontWeight: 600, color: C.inkMid, background: "none", border: `1px solid ${C.border}`, borderRadius: 6, padding: "3px 10px", cursor: "pointer" }}>Edit</button>
                        <button onClick={clearTeams} style={{ fontSize: 12, fontWeight: 600, color: C.danger, background: "none", border: `1px solid ${C.border}`, borderRadius: 6, padding: "3px 10px", cursor: "pointer" }}>Clear</button>
                      </div>
                    </div>
                    <div style={{ display: "flex" }}>
                      {[[t("teamA"), tA, teams.A.fg], [t("teamB"), tB, teams.B.fg]].map(([label, players, color]) => (
                        <div key={label} style={{ flex: 1, padding: "12px 14px", borderRight: label === t("teamA") ? `1px solid ${C.border}` : "none" }}>
                          <div style={{ fontSize: 11, fontWeight: 700, color, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 8 }}>{label} · {players.length}</div>
                          {players.map(p => (
                            <div key={p.id} style={{ display: "flex", alignItems: "center", gap: 6, padding: "5px 0" }}>
                              <Avatar initials={p.avatar} avatarUrl={p.avatarUrl} size={26} accent={p.isMe} />
                              <span style={{ fontSize: 13, color: C.ink, fontWeight: p.isMe ? 600 : 400, flex: 1 }}>{p.name}</span>
                              {p.id === g.ballHolder && <FootballIcon size={15} color={C.accent} title="Bringing the ball" />}
                            </div>
                          ))}
                        </div>
                      ))}
                    </div>
                  </div>
                );
              }

              // No teams yet — show dispatch controls inline
              if (dispatchStep === "select") {
                return (
                  <div style={{ marginBottom: 16, borderRadius: 12, border: `1px solid ${C.border}`, overflow: "hidden" }}>
                    <div style={{ padding: "10px 14px", background: C.surface, borderBottom: `1px solid ${C.border}` }}>
                      <span style={{ fontSize: 12, fontWeight: 700, color: C.inkMid, textTransform: "uppercase", letterSpacing: "0.06em" }}>Team Setup</span>
                    </div>
                    <div style={{ display: "flex", gap: 0 }}>
                      <button onClick={randomize} style={{ flex: 1, padding: "14px 12px", background: "transparent", border: "none", borderRight: `1px solid ${C.border}`, cursor: "pointer", textAlign: "center" }}>
                        <div style={{ width: 34, height: 34, borderRadius: "50%", background: C.accentBg, color: C.accent, display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 6px" }}>
                          <Zap size={18} {...ICON} />
                        </div>
                        <div style={{ fontSize: 13, fontWeight: 700, color: C.ink, marginBottom: 2 }}>{t("dispatchRandom")}</div>
                        <div style={{ fontSize: 11, color: C.inkFaint }}>Balanced by level</div>
                      </button>
                      <button onClick={() => { setTeamA([]); setTeamB([]); setDispatchStep("manual"); }} style={{ flex: 1, padding: "14px 12px", background: "transparent", border: "none", cursor: "pointer", textAlign: "center" }}>
                        <div style={{ width: 34, height: 34, borderRadius: "50%", background: C.accentBg, color: C.accent, display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 6px" }}>
                          <Users size={18} {...ICON} />
                        </div>
                        <div style={{ fontSize: 13, fontWeight: 700, color: C.ink, marginBottom: 2 }}>{t("dispatchManual")}</div>
                        <div style={{ fontSize: 11, color: C.inkFaint }}>Pick each player</div>
                      </button>
                    </div>
                  </div>
                );
              }

              // Random preview — inline two columns
              if (dispatchStep === "random") {
                return (
                  <div style={{ marginBottom: 16, borderRadius: 12, border: `1px solid ${C.border}`, overflow: "hidden" }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px 14px", background: C.surface, borderBottom: `1px solid ${C.border}` }}>
                      <span style={{ fontSize: 12, fontWeight: 700, color: C.inkMid, textTransform: "uppercase", letterSpacing: "0.06em" }}>Preview</span>
                      <button onClick={randomize} style={{ fontSize: 12, fontWeight: 600, color: C.accent, background: "none", border: "none", cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 5 }}>
                        <Zap size={13} {...ICON} />
                        Re-shuffle
                      </button>
                    </div>
                    <div style={{ display: "flex" }}>
                      {[[t("teamA"), teamA, teams.A.fg], [t("teamB"), teamB, teams.B.fg]].map(([label, players, color]) => (
                        <div key={label} style={{ flex: 1, padding: "12px 14px", borderRight: label === t("teamA") ? `1px solid ${C.border}` : "none" }}>
                          <div style={{ fontSize: 11, fontWeight: 700, color, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 8 }}>{label} · {players.length}</div>
                          {players.map(p => (
                            <div key={p.id} style={{ display: "flex", alignItems: "center", gap: 6, padding: "5px 0" }}>
                              <Avatar initials={p.avatar} avatarUrl={p.avatarUrl} size={26} accent={p.isMe} />
                              <div style={{ flex: 1 }}>
                                <div style={{ fontSize: 13, color: C.ink }}>{p.name}</div>
                                <LevelPill level={p.level} small />
                              </div>
                              {p.id === g.ballHolder && <FootballIcon size={15} color={C.accent} />}
                            </div>
                          ))}
                        </div>
                      ))}
                    </div>
                    <div style={{ display: "flex", gap: 8, padding: "12px 14px", borderTop: `1px solid ${C.border}` }}>
                      <button onClick={() => setDispatchStep("select")} style={{ flex: 1, padding: "10px 0", borderRadius: 8, border: `1px solid ${C.border}`, background: "transparent", color: C.inkMid, cursor: "pointer", fontSize: 13, fontWeight: 600 }}>Back</button>
                      <button disabled={savingTeams} onClick={publishTeams} style={{ flex: 2, padding: "10px 0", borderRadius: 8, border: "none", background: C.accent, color: C.accentText, cursor: savingTeams ? "not-allowed" : "pointer", fontSize: 13, fontWeight: 700, opacity: savingTeams ? 0.5 : 1 }}>
                        {savingTeams ? t("publishing") : t("confirmAndPublish")}
                      </button>
                    </div>
                  </div>
                );
              }

              // Manual assignment — inline
              if (dispatchStep === "manual") {
                return (
                  <div style={{ marginBottom: 16, borderRadius: 12, border: `1px solid ${C.border}`, overflow: "hidden" }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px 14px", background: C.surface, borderBottom: `1px solid ${C.border}` }}>
                      <span style={{ fontSize: 12, fontWeight: 700, color: C.inkMid, textTransform: "uppercase", letterSpacing: "0.06em" }}>Manual teams</span>
                      <button onClick={() => setDispatchStep("select")} style={{ fontSize: 12, color: C.inkFaint, background: "none", border: "none", cursor: "pointer" }}>← Back</button>
                    </div>
                    {/* Unassigned players */}
                    {unassigned.length > 0 && (
                      <div style={{ padding: "10px 14px", borderBottom: `1px solid ${C.border}` }}>
                        <div style={{ fontSize: 11, fontWeight: 600, color: C.inkMid, marginBottom: 6, textTransform: "uppercase", letterSpacing: "0.05em" }}>Tap to assign</div>
                        {unassigned.map(p => (
                          <div key={p.id} style={{ display: "flex", alignItems: "center", gap: 8, padding: "7px 0", borderBottom: `1px solid ${C.border}` }}>
                            <Avatar initials={p.avatar} avatarUrl={p.avatarUrl} size={28} />
                            <div style={{ flex: 1 }}>
                              <div style={{ fontSize: 13, fontWeight: 500, color: C.ink }}>{p.name}</div>
                              <LevelPill level={p.level} small />
                            </div>
                            <div style={{ display: "flex", gap: 6 }}>
                              <button onClick={() => addToTeam(p, "A")} style={{ padding: "5px 12px", borderRadius: 6, border: `2px solid ${C.accent}`, background: "transparent", color: C.accent, cursor: "pointer", fontSize: 13, fontWeight: 700 }}>A</button>
                              <button onClick={() => addToTeam(p, "B")} style={{ padding: "5px 12px", borderRadius: 6, border: `2px solid ${teams.B.fg}`, background: "transparent", color: teams.B.fg, cursor: "pointer", fontSize: 13, fontWeight: 700 }}>B</button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                    {/* Two team columns */}
                    <div style={{ display: "flex" }}>
                      {[[t("teamA"), teamA, teams.A.fg], [t("teamB"), teamB, teams.B.fg]].map(([label, players, color]) => (
                        <div key={label} style={{ flex: 1, padding: "10px 12px", borderRight: label === t("teamA") ? `1px solid ${C.border}` : "none" }}>
                          <div style={{ fontSize: 11, fontWeight: 700, color, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 6 }}>{label} · {players.length}</div>
                          {players.length === 0 && <div style={{ fontSize: 11, color: C.inkFaint, fontStyle: "italic" }}>Empty</div>}
                          {players.map(p => (
                            <div key={p.id} style={{ display: "flex", alignItems: "center", gap: 4, padding: "4px 0" }}>
                              <Avatar initials={p.avatar} size={22} />
                              <span style={{ fontSize: 12, color: C.ink, flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{p.name}</span>
                              <button onClick={() => removeFromTeam(p)} style={{ background: "none", border: "none", cursor: "pointer", color: C.danger, fontSize: 16, padding: "0 2px", lineHeight: 1, flexShrink: 0 }}>×</button>
                            </div>
                          ))}
                        </div>
                      ))}
                    </div>
                    <div style={{ display: "flex", gap: 8, padding: "12px 14px", borderTop: `1px solid ${C.border}` }}>
                      <button disabled={savingTeams || teamA.length === 0 || teamB.length === 0} onClick={publishTeams} style={{ flex: 1, padding: "10px 0", borderRadius: 8, border: "none", background: C.accent, color: C.accentText, cursor: (savingTeams || teamA.length === 0 || teamB.length === 0) ? "not-allowed" : "pointer", fontSize: 13, fontWeight: 700, opacity: (savingTeams || teamA.length === 0 || teamB.length === 0) ? 0.4 : 1 }}>
                        {savingTeams ? t("publishing") : t("confirmAndPublish")}
                      </button>
                    </div>
                  </div>
                );
              }
              return null;
            })()}

            {/* Player list — hidden while dispatching teams or when published teams are shown */}
            {dispatchStep === "select" && !(g.teams?.A?.length || g.teams?.B?.length) && g.players.map(p => (
              <div key={p.id} onClick={() => { if (!p.isMe) { setViewingPlayer(p); setFriendStatus(null); } }} style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 0", borderBottom: `1px solid ${C.border}`, cursor: p.isMe ? "default" : "pointer" }}>
                <Avatar initials={p.avatar} avatarUrl={p.avatarUrl} size={36} accent={p.isMe} />
                <div style={{ flex: 1 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <span style={{ ...T.h3 }}>{p.name}</span>
                    {p.isMe && <span style={{ fontSize: 10, fontWeight: 600, color: C.accent }}>{t("you")}</span>}
                    {p.id === g.ballHolder && <FootballIcon size={15} color={C.accent} />}
                    {/* Show team badge if teams exist */}
                    {g.teams && (
                      g.teams.A?.includes(p.id) ? <span style={{ fontSize: 10, fontWeight: 700, color: C.accent, background: C.accentBg, padding: "1px 6px", borderRadius: 4 }}>A</span>
                      : g.teams.B?.includes(p.id) ? <span style={{ fontSize: 10, fontWeight: 700, color: teams.B.fg, background: teams.B.bg, padding: "1px 6px", borderRadius: 4 }}>B</span>
                      : null
                    )}
                  </div>
                  <div style={{ display: "flex", gap: 8, marginTop: 2, flexWrap: "wrap", alignItems: "center" }}>
                    <LevelPill level={p.level} small />
                    {p.position && <span style={{ ...T.small, fontWeight: 500, color: C.inkMid }}>{p.position}</span>}
                  </div>
                </div>
                {isHost && !past && !p.isMe && p.id !== g.hostId && (
                  <button
                    onClick={e => { e.stopPropagation(); kickPlayer(p.id, p.name); }}
                    disabled={kickingPlayerId === p.id}
                    style={{ background: "none", border: `1px solid ${C.border}`, borderRadius: 6, padding: "4px 10px", fontSize: 11, fontWeight: 600, color: C.danger, cursor: kickingPlayerId === p.id ? "not-allowed" : "pointer", opacity: kickingPlayerId === p.id ? 0.5 : 1, flexShrink: 0 }}
                  >
                    {kickingPlayerId === p.id ? "…" : t("remove")}
                  </button>
                )}
              </div>
            ))}
            {dispatchStep === "select" && !(g.teams?.A?.length || g.teams?.B?.length) && Array(Math.max(0, g.spotsTotal - g.spotsFilled)).fill(0).map((_, i) => {
              const canInvite = !past && !!onInviteFriends && (joined || isHost);
              const RowTag = canInvite ? "button" : "div";
              return (
                <RowTag
                  key={i}
                  onClick={canInvite ? onInviteFriends : undefined}
                  aria-label={canInvite ? "Invite player" : undefined}
                  style={{
                    width: "100%", display: "flex", alignItems: "center", gap: 12,
                    padding: "10px 0", border: "none", borderBottom: `1px solid ${C.border}`,
                    background: "transparent", textAlign: "left",
                    cursor: canInvite ? "pointer" : "default", fontFamily: "inherit",
                  }}
                >
                  <div style={{ width: 36, height: 36, borderRadius: "50%", background: canInvite ? C.accentBg : C.surface, border: `1.5px dashed ${canInvite ? C.accent : C.border}`, display: "flex", alignItems: "center", justifyContent: "center", color: canInvite ? C.accent : C.border }}>
                    <Plus size={15} {...ICON} />
                  </div>
                  <span style={{ ...T.small, fontStyle: canInvite ? "normal" : "italic", color: canInvite ? C.accent : C.inkFaint, fontWeight: canInvite ? 600 : 400 }}>
                    {canInvite ? "Invite player" : t("openSpot")}
                  </span>
                </RowTag>
              );
            })}
            {dispatchStep === "select" && !(g.teams?.A?.length || g.teams?.B?.length) && (g.waitlist || []).length > 0 && (
              <div style={{ marginTop: 16 }}>
                <div style={{ ...T.label, marginBottom: 8, color: C.warn }}>{t("waitlistHeader", { n: (g.waitlist || []).length })}</div>
                {(g.waitlist || []).map(p => (
                  <div key={p.id} style={{ display: "flex", alignItems: "center", gap: 12, padding: "8px 0", borderBottom: `1px solid ${C.border}` }}>
                    <Avatar initials={(p.name || "??").slice(0, 2).toUpperCase()} size={34} />
                    <div style={{ flex: 1 }}>
                      <span style={{ ...T.h3 }}>{p.name}</span>
                      {p.isMe && <span style={{ fontSize: 10, fontWeight: 600, color: C.warn, marginLeft: 6 }}>{t("you")}</span>}
                    </div>
                    {isHost && !past && g.spotsFilled < g.spotsTotal
                      ? <button onClick={() => promotePlayer(p.id, p.name)} disabled={kickingPlayerId === p.id} style={{ padding: "4px 10px", borderRadius: 6, border: `1px solid ${C.accent}`, background: "transparent", color: C.accent, cursor: kickingPlayerId === p.id ? "not-allowed" : "pointer", fontSize: 12, fontWeight: 600, opacity: kickingPlayerId === p.id ? 0.5 : 1 }}>{kickingPlayerId === p.id ? "…" : t("promote")}</button>
                      : <span style={{ ...T.small, color: C.warn, fontWeight: 500 }}>{t("waiting")}</span>}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
        {tab === "chat" && (
          <div style={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden" }}>
            {(!joined && !isHost)
              ? <div style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: 32, gap: 12 }}>
                  <div style={{ ...T.h3, color: C.inkMid }}>{t("joinToAccessChat")}</div>
                  <p style={{ ...T.small, textAlign: "center", maxWidth: 220 }}>{t("chatOnlyForPlayers")}</p>
                  {!past && !full && <button onClick={() => onJoin(g.id)} style={{ ...btnPrimary, padding: "10px 24px", borderRadius: 8 }}>{t("joinGame")}</button>}
                </div>
              : <>
                  <div style={{ flex: 1, overflowY: "auto", padding: "12px 20px", display: "flex", flexDirection: "column", gap: 12 }}>
                    {loadingMsgs && <p style={{ ...T.small, textAlign: "center", marginTop: 40 }}>{t("loading")}</p>}
                    {!loadingMsgs && msgs.length === 0 && <p style={{ ...T.small, textAlign: "center", marginTop: 40 }}>{t("noMessagesYet")}</p>}
                    {msgs.map(m => (
                      <div key={m.id} style={{ display: "flex", flexDirection: m.mine ? "row-reverse" : "row", gap: 8, alignItems: "flex-end" }}>
                        {!m.mine && <Avatar initials={m.avatar} size={26} />}
                        <div style={{ maxWidth: "72%" }}>
                          {!m.mine && <div style={{ ...T.small, marginBottom: 3 }}>{m.user}</div>}
	                          <div style={{ background: m.mine ? C.accent : C.surface, color: m.mine ? C.accentText : C.ink, borderRadius: m.mine ? "12px 12px 3px 12px" : "12px 12px 12px 3px", padding: "9px 13px", fontSize: 14, lineHeight: 1.4, wordBreak: "break-word", whiteSpace: "pre-wrap" }}><MentionText text={m.text} own={m.mine} /></div>
                          <div style={{ ...T.small, marginTop: 3, textAlign: m.mine ? "right" : "left" }}>{m.time}</div>
                        </div>
                      </div>
                    ))}
                    <div ref={endRef} />
                  </div>
	                  <div style={{ padding: "6px 20px 0", borderTop: `1px solid ${C.border}`, display: "flex", gap: 6, overflowX: "auto", scrollbarWidth: "none" }}>
	                    {[t("chatMsg1"), t("chatMsg2"), t("chatMsg3"), t("chatMsg4")].map(msg => (
	                      <button key={msg} onMouseDown={e => { e.preventDefault(); setInput(msg); }} style={{ padding: "8px 14px", borderRadius: 999, border: `1px solid ${C.border}`, background: C.surface, color: C.inkMid, cursor: "pointer", fontSize: 13, fontWeight: 500, whiteSpace: "nowrap", flexShrink: 0, minHeight: 36 }}>{msg}</button>
	                    ))}
	                  </div>
	                  <MentionSuggestions draft={input} candidates={gameMentionCandidates} onPick={profile => setInput(prev => insertMentionHandle(prev, profile).slice(0, 500))} />
	                  <div style={{ padding: "8px 20px 16px", display: "flex", gap: 8 }}>
                    <input value={input} onChange={e => setInput(e.target.value.slice(0, 500))} onKeyDown={e => e.key === "Enter" && send()} placeholder={t("messageGroup")} maxLength={500} style={{ ...inputBase, borderRadius: 20, padding: "9px 16px" }} />
                    <button onClick={send} style={{ ...btnPrimary, padding: "0 16px", borderRadius: 20, fontSize: 13, opacity: input.trim() ? 1 : 0.3 }}>{t("send")}</button>
                  </div>
                </>
            }
          </div>
        )}
      </div>

      {/* QR Code Modal */}
      {showQRModal && (
        <div style={{ position: "absolute", inset: 0, zIndex: 100, background: "rgba(0,0,0,0.6)", display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }} onClick={() => setShowQRModal(false)}>
          <div onClick={e => e.stopPropagation()} style={{ background: C.bg, borderRadius: 16, padding: 24, display: "flex", flexDirection: "column", alignItems: "center", gap: 16, maxWidth: 320, width: "100%", animation: "fadeIn 0.2s ease" }}>
            <div style={{ ...T.h2, textAlign: "center" }}>{g.title}</div>
            <div style={{ ...T.small, color: C.inkMid, textAlign: "center" }}>{t("scanToJoin")}</div>
            {qrDataUrl && <img src={qrDataUrl} alt="QR Code" style={{ width: 200, height: 200, borderRadius: 8 }} />}
            <div style={{ fontSize: 11, color: C.inkFaint, textAlign: "center", maxWidth: 220 }}>Points to: {window.location.origin}?game={g.id}</div>
            <div style={{ display: "flex", gap: 8, width: "100%" }}>
              <button onClick={() => setShowQRModal(false)} style={{ ...btnSecondary, flex: 1, padding: "10px 0" }}>{t("close")}</button>
              <button onClick={async () => {
                if (qrDataUrl) {
                  const a = document.createElement("a");
                  a.href = qrDataUrl;
                  a.download = `kickr-${g.id}.png`;
                  a.click();
                }
              }} style={{ ...btnPrimary, flex: 1, padding: "10px 0" }}>{t("save")}</button>
            </div>
          </div>
        </div>
      )}

      {/* CTA */}
      {tab !== "chat" && (
        <div style={{ padding: "12px 20px 24px", borderTop: `1px solid ${C.border}` }}>
          {past
            ? <div style={{ padding: "13px 16px", background: C.surface, borderRadius: 10, textAlign: "center", border: `1px solid ${C.border}` }}>
                <span style={{ fontSize: 13, fontWeight: 600, color: C.inkMid }}>Completed game</span>
              </div>
            : isHost
            ? <div style={{ display: "flex", gap: 8 }}>
                {onInviteFriends && g.spotsFilled < g.spotsTotal && (
                  <button onClick={onInviteFriends} style={{ ...btnSecondary, flex: 1, padding: 13, borderRadius: 10 }}>Invite friends</button>
                )}
                <div style={{ flex: 1, padding: "13px 16px", background: C.accentBg, borderRadius: 10, textAlign: "center" }}>
                  <span style={{ fontSize: 13, fontWeight: 600, color: C.accent }}>You're hosting</span>
                </div>
              </div>
            : joined
              ? showLeaveConfirm
                ? <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                    <div style={{ padding: "10px 14px", background: dangerBg, borderRadius: 8, border: `1px solid ${dangerBorder}`, textAlign: "center" }}>
                      <div style={{ fontSize: 13, fontWeight: 600, color: C.danger, marginBottom: 2 }}>Leave this game?</div>
                      <div style={{ fontSize: 12, color: C.danger, opacity: 0.8 }}>Your spot will be given to the next person on the waitlist.</div>
                    </div>
                    <div style={{ display: "flex", gap: 8 }}>
                      <button onClick={() => setShowLeaveConfirm(false)} style={{ ...btnSecondary, flex: 1, padding: "11px 0" }}>Cancel</button>
                      <button onClick={() => { setShowLeaveConfirm(false); onJoin(g.id); }} disabled={isJoining} style={{ ...btnSecondary, flex: 1, padding: "11px 0", color: C.danger, borderColor: dangerBorder, opacity: isJoining ? 0.5 : 1 }}>{isJoining ? "Leaving…" : "Leave game"}</button>
                    </div>
                  </div>
                : <div style={{ display: "flex", gap: 8 }}>
                    {onInviteFriends && g.spotsFilled < g.spotsTotal && (
                      <button onClick={onInviteFriends} style={{ ...btnSecondary, flex: 1, padding: 13, borderRadius: 10 }}>Invite friends</button>
                    )}
                    <button onClick={() => setShowLeaveConfirm(true)} disabled={isJoining} style={{ ...btnSecondary, flex: 1, padding: 13, borderRadius: 10, color: C.danger, borderColor: dangerBorder, opacity: isJoining ? 0.5 : 1, cursor: isJoining ? "not-allowed" : "pointer" }}>{isJoining ? "Leaving…" : "Leave game"}</button>
                  </div>
              : full
                ? waitlisted
                  ? <button onClick={() => onWaitlist(g.id)} disabled={isJoining} style={{ ...btnSecondary, width: "100%", padding: 13, borderRadius: 10, color: C.warn, borderColor: warnBorder, opacity: isJoining ? 0.5 : 1 }}>{isJoining ? "…" : "Leave waitlist"}</button>
                  : <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                      <div style={{ padding: "8px 12px", background: dangerBg, borderRadius: 8, border: `1px solid ${dangerBorder}`, textAlign: "center" }}>
                        <span style={{ fontSize: 13, fontWeight: 600, color: C.danger }}>Game is full</span>
                        {(g.waitlist || []).length > 0 && <span style={{ fontSize: 12, color: C.danger, display: "block", marginTop: 2 }}>{(g.waitlist || []).length} on waitlist</span>}
                      </div>
                      <button onClick={() => onWaitlist(g.id)} disabled={isJoining} style={{ ...btnSecondary, width: "100%", padding: 11, borderRadius: 10, fontSize: 14, color: C.ink, opacity: isJoining ? 0.5 : 1 }}>{isJoining ? "Joining waitlist…" : "Join waitlist"}</button>
                    </div>
                : <button onClick={() => onJoin(g.id)} disabled={isJoining} style={{ ...btnPrimary, width: "100%", padding: 13, borderRadius: 10, fontSize: 15, opacity: isJoining ? 0.5 : 1, cursor: isJoining ? "not-allowed" : "pointer" }}>
                    {isJoining ? "Joining…" : "Join game"}
                  </button>
          }
        </div>
      )}

      {/* Player Profile Modal */}
      {viewingPlayer && (
        <div style={{ position: "absolute", inset: 0, zIndex: 110, background: "rgba(0,0,0,0.5)", display: "flex", alignItems: "flex-end" }} onClick={() => { setViewingPlayer(null); setFriendStatus(null); setPlayerStats(null); }}>
          <div onClick={e => e.stopPropagation()} style={{ background: C.bg, borderRadius: "20px 20px 0 0", width: "100%", padding: "20px 20px 32px", animation: "slideUp 0.25s ease" }}>
            <div style={{ display: "flex", justifyContent: "center", marginBottom: 16 }}>
              <div style={{ width: 36, height: 4, borderRadius: 2, background: C.border }} />
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 16, marginBottom: 20 }}>
              <Avatar initials={viewingPlayer.avatar} avatarUrl={viewingPlayer.avatarUrl} size={52} />
              <div>
                <div style={{ ...T.h2, marginBottom: 4 }}>{viewingPlayer.name}</div>
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
                  <LevelPill level={viewingPlayer.level} />
                  {viewingPlayer.position && <span style={{ fontSize: 12, color: C.inkMid, fontWeight: 500 }}>{viewingPlayer.position}</span>}
                  {viewingPlayer.id === g.hostId && <span style={{ fontSize: 11, fontWeight: 600, color: C.accent }}>Host</span>}
                </div>
              </div>
            </div>
            {playerStats && (
              <div style={{ padding: "12px 16px", background: C.surface, borderRadius: 10, marginBottom: 16, display: "flex", gap: 24 }}>
                <div style={{ textAlign: "center" }}>
                  <div style={{ fontSize: 20, fontWeight: 700, color: C.ink }}>{playerStats.gamesPlayed}</div>
                  <div style={{ fontSize: 11, color: C.inkFaint, marginTop: 2 }}>Games played</div>
                </div>
                <div style={{ textAlign: "center" }}>
                  <div style={{ fontSize: 20, fontWeight: 700, color: C.ink }}>{playerStats.gamesHosted}</div>
                  <div style={{ fontSize: 11, color: C.inkFaint, marginTop: 2 }}>Games hosted</div>
                </div>
              </div>
            )}
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {!playerStats && (
                <button onClick={async () => {
                  setPlayerStatsLoading(true);
                  try {
                    const [{ count: played }, { count: hosted }] = await Promise.all([
                      supabase.from("game_players").select("*", { count: "exact", head: true }).eq("user_id", viewingPlayer.id),
                      supabase.from("games").select("*", { count: "exact", head: true }).eq("host_id", viewingPlayer.id),
                    ]);
                    setPlayerStats({ gamesPlayed: played || 0, gamesHosted: hosted || 0 });
                  } catch { setPlayerStats({ gamesPlayed: "—", gamesHosted: "—" }); }
                  setPlayerStatsLoading(false);
                }} style={{ ...btnSecondary, padding: "10px 0", borderRadius: 10, opacity: playerStatsLoading ? 0.5 : 1 }}>
                  {playerStatsLoading ? "Loading…" : "View profile"}
                </button>
              )}
              {currentUser && viewingPlayer.id !== currentUser.id && (
                <button
                  disabled={friendLoading || friendStatus === "accepted" || friendStatus === "sent"}
                  onClick={async () => {
                    setFriendLoading(true);
                    const { data: existing } = await supabase.from("friendships").select("id, status").or(`and(requester_id.eq.${currentUser.id},recipient_id.eq.${viewingPlayer.id}),and(requester_id.eq.${viewingPlayer.id},recipient_id.eq.${currentUser.id})`).maybeSingle();
                    if (existing) {
                      setFriendStatus(existing.status === "accepted" ? "accepted" : "sent");
                    } else {
                      const { error } = await supabase.from("friendships").insert({ requester_id: currentUser.id, recipient_id: viewingPlayer.id, status: "pending" });
                      if (error) { showToast(t("couldNotSendRequest")); }
                      else {
                        setFriendStatus("sent");
                        showToast(t("friendRequestSent"));
                        const senderName = currentUser?.user_metadata?.name || currentUser?.email?.split("@")[0] || "Someone";
                        await supabase.from("notifications").insert({ user_id: viewingPlayer.id, type: "friend_request", text: `${senderName} sent you a friend request`, sub: currentUser.id, game_id: null, read: false });
                      }
                    }
                    setFriendLoading(false);
                  }}
                  style={{ ...btnPrimary, padding: "11px 0", borderRadius: 10, opacity: (friendLoading || friendStatus === "accepted" || friendStatus === "sent") ? 0.6 : 1 }}
                >
                  {friendLoading ? "…" : friendStatus === "accepted" ? "Already friends" : friendStatus === "sent" ? "Request sent" : "Add friend"}
                </button>
              )}
              <button onClick={() => { setViewingPlayer(null); setFriendStatus(null); setPlayerStats(null); }} style={{ ...btnSecondary, padding: "10px 0", borderRadius: 10 }}>Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Custom Location Picker ───────────────────────────────────────────────────
function CustomLocationPicker({ value, onChange }) {
  const { C, inputBase } = useTheme();
  const [query, setQuery] = useState(value || "");
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dropdownRect, setDropdownRect] = useState(null);
  const inputRef = useRef(null);
  const debounceRef = useRef(null);

  const search = (q) => {
    setQuery(q);
    clearTimeout(debounceRef.current);
    if (q.trim().length < 2) { setSuggestions([]); return; }
    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const res = await fetch(`https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(q)}.json?access_token=${MAPBOX_TOKEN}&country=no&proximity=10.7522,59.9139&limit=5&types=address,poi,neighborhood,locality`);
        const json = await res.json();
        const results = (json.features || []).map(f => ({ name: f.text, address: f.place_name, lat: f.center[1], lng: f.center[0] }));
        setSuggestions(results);
        if (results.length > 0 && inputRef.current) {
          const r = inputRef.current.getBoundingClientRect();
          setDropdownRect({ top: r.bottom + 4, left: r.left, width: r.width });
        }
      } catch { setSuggestions([]); }
      finally { setLoading(false); }
    }, 250);
  };

  const select = (s) => {
    setQuery(s.address);
    setSuggestions([]);
    onChange({ location: s.address, lat: s.lat, lng: s.lng });
  };

  return (
    <div style={{ position: "relative" }}>
      <input ref={inputRef} value={query} onChange={e => search(e.target.value)} placeholder="e.g. Tøyenparken, Oslo" style={{ ...inputBase, paddingRight: loading ? 36 : 14 }} autoComplete="off" />
      {loading && <div style={{ position: "absolute", right: 12, top: "50%", transform: "translateY(-50%)", width: 14, height: 14, border: `2px solid ${C.border}`, borderTopColor: C.accent, borderRadius: "50%", animation: "spin 0.6s linear infinite" }} />}
      {suggestions.length > 0 && dropdownRect && (
        <div style={{ position: "fixed", top: dropdownRect.top, left: dropdownRect.left, width: dropdownRect.width, background: C.surface, border: `1px solid ${C.border}`, borderRadius: 8, boxShadow: "0 8px 24px rgba(0,0,0,0.25)", zIndex: 9999, overflow: "hidden" }}>
          {suggestions.map((s, i) => (
            <button key={i} onMouseDown={e => { e.preventDefault(); select(s); }} style={{ width: "100%", padding: "10px 14px", background: "none", border: "none", borderBottom: i < suggestions.length - 1 ? `1px solid ${C.border}` : "none", cursor: "pointer", textAlign: "left" }}
              onMouseEnter={e => e.currentTarget.style.background = C.bg}
              onMouseLeave={e => e.currentTarget.style.background = "none"}
            >
              <div style={{ fontSize: 13, fontWeight: 500, color: C.ink }}>{s.name}</div>
              <div style={{ fontSize: 12, color: C.inkMid, marginTop: 2, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{s.address}</div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Calendar Picker ──────────────────────────────────────────────────────────
function CalendarPicker({ value, onChange, min }) {
  const { C } = useTheme();
  const today = new Date(); today.setHours(0,0,0,0);
  const minDate = min ? new Date(min + "T00:00:00") : today;
  const parseVal = v => v ? new Date(v + "T00:00:00") : null;
  const selected = parseVal(value);

  const [view, setView] = React.useState(() => {
    const d = selected || today;
    return { year: d.getFullYear(), month: d.getMonth() };
  });

  const DAYS = ["Su","Mo","Tu","We","Th","Fr","Sa"];
  const MONTHS = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
  const firstDay = new Date(view.year, view.month, 1).getDay();
  const daysInMonth = new Date(view.year, view.month + 1, 0).getDate();
  const prevMonth = () => setView(v => v.month === 0 ? { year: v.year-1, month: 11 } : { year: v.year, month: v.month-1 });
  const nextMonth = () => setView(v => v.month === 11 ? { year: v.year+1, month: 0 } : { year: v.year, month: v.month+1 });

  const selectDay = (day) => {
    const d = new Date(view.year, view.month, day);
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth()+1).padStart(2,"0");
    const dd = String(d.getDate()).padStart(2,"0");
    onChange(`${yyyy}-${mm}-${dd}`);
  };

  const SIZE = 30; // cell px

  return (
    <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 12, padding: "12px 14px", display: "inline-block", width: "100%", boxSizing: "border-box" }}>
      {/* Month nav */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
        <button aria-label="Previous month" onClick={prevMonth} style={{ background: "none", border: "none", borderRadius: 6, padding: 4, cursor: "pointer", color: C.inkMid, display: "flex", alignItems: "center" }}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><polyline points="15 18 9 12 15 6"/></svg>
        </button>
        <span style={{ fontSize: 13, fontWeight: 600, color: C.ink }}>{MONTHS[view.month]} {view.year}</span>
        <button aria-label="Next month" onClick={nextMonth} style={{ background: "none", border: "none", borderRadius: 6, padding: 4, cursor: "pointer", color: C.inkMid, display: "flex", alignItems: "center" }}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><polyline points="9 18 15 12 9 6"/></svg>
        </button>
      </div>
      {/* Day headers */}
      <div style={{ display: "grid", gridTemplateColumns: `repeat(7, ${SIZE}px)`, gap: 2, justifyContent: "space-between", marginBottom: 2 }}>
        {DAYS.map(d => (
          <div key={d} style={{ width: SIZE, textAlign: "center", fontSize: 10, fontWeight: 600, color: C.inkFaint, letterSpacing: "0.04em" }}>{d}</div>
        ))}
      </div>
      {/* Day grid */}
      <div style={{ display: "grid", gridTemplateColumns: `repeat(7, ${SIZE}px)`, gap: 2, justifyContent: "space-between" }}>
        {Array.from({ length: firstDay }).map((_, i) => <div key={`e${i}`} style={{ width: SIZE, height: SIZE }} />)}
        {Array.from({ length: daysInMonth }).map((_, i) => {
          const day = i + 1;
          const thisDate = new Date(view.year, view.month, day);
          const isDisabled = thisDate < minDate;
          const isToday = thisDate.getTime() === today.getTime();
          const isSelected = selected && thisDate.getTime() === selected.getTime();
          return (
            <button key={day} onClick={() => !isDisabled && selectDay(day)} disabled={isDisabled}
              style={{ width: SIZE, height: SIZE, borderRadius: 6, border: isToday && !isSelected ? `1px solid ${C.border}` : "1px solid transparent", background: isSelected ? C.accent : "transparent", color: isSelected ? C.accentText : isDisabled ? C.inkFaint : C.ink, fontSize: 12, fontWeight: isSelected || isToday ? 600 : 400, cursor: isDisabled ? "default" : "pointer", opacity: isDisabled ? 0.3 : 1, padding: 0 }}
            >{day}</button>
          );
        })}
      </div>
    </div>
  );
}

// ─── Range Calendar Picker (react-day-picker) ────────────────────────────────
function useKickrDayPickerStyles() {
  React.useEffect(() => {
    const id = "kickr-rdp-styles";
    if (document.getElementById(id)) return;
    const el = document.createElement("style");
    el.id = id;
    el.textContent = `
      .kickr-rdp .rdp-root {
        --rdp-accent-color: #22c55e;
        --rdp-accent-background-color: #f0fdf4;
        --rdp-range_middle-background-color: #dcfce7;
        --rdp-range_start-date-background-color: #22c55e;
        --rdp-range_end-date-background-color: #22c55e;
        --rdp-today-color: #22c55e;
        --rdp-day_button-border-radius: 8px;
        --rdp-day-height: auto;
        --rdp-day-width: auto;
        --rdp-day_button-height: 38px;
        --rdp-day_button-width: 100%;
        width: 100%;
        max-width: 100%;
        margin: 0;
        padding: 12px 8px;
        box-sizing: border-box;
        font-family: inherit;
      }
      .kickr-rdp .rdp-month { width: 100%; }
      .kickr-rdp .rdp-month_grid { width: 100%; table-layout: fixed; border-collapse: separate; border-spacing: 1px; }
      .kickr-rdp .rdp-day, .kickr-rdp .rdp-weekday { width: auto; text-align: center; padding: 1px; }
      .kickr-rdp .rdp-weekday { font-size: 11px; font-weight: 600; color: #9ca3af; text-align: center; padding: 4px 0; }
      .kickr-rdp .rdp-day_button { font-size: 13px; font-weight: 500; width: 100%; height: 38px; border-radius: 8px; cursor: pointer; }
      .kickr-rdp .rdp-selected .rdp-day_button { background: #22c55e !important; color: white !important; font-weight: 700; }
      .kickr-rdp .rdp-range_start .rdp-day_button,
      .kickr-rdp .rdp-range_end .rdp-day_button { background: #22c55e !important; color: white !important; font-weight: 700; }
      .kickr-rdp .rdp-range_middle .rdp-day_button { background: #dcfce7 !important; color: #15803d !important; border-radius: 0 !important; }
      .kickr-rdp .rdp-range_start .rdp-day_button { border-radius: 8px 0 0 8px !important; }
      .kickr-rdp .rdp-range_end .rdp-day_button { border-radius: 0 8px 8px 0 !important; }
      .kickr-rdp .rdp-range_start.rdp-range_end .rdp-day_button { border-radius: 8px !important; }
      .kickr-rdp .rdp-today:not(.rdp-range_start):not(.rdp-range_end) .rdp-day_button { color: #22c55e; font-weight: 700; border: 1.5px solid #22c55e; }
      .kickr-rdp .rdp-disabled .rdp-day_button { opacity: 0.2 !important; cursor: default; }
      .kickr-rdp .rdp-nav { display: flex; align-items: center; justify-content: space-between; padding: 0 4px 10px; }
      .kickr-rdp .rdp-month_caption { font-size: 14px; font-weight: 700; color: #111827; flex: 1; text-align: center; }
      .kickr-rdp .rdp-nav_button { background: none; border: 1px solid #e5e7eb; border-radius: 6px; padding: 6px; cursor: pointer; color: #6b7280; line-height: 0; }
      .kickr-rdp .rdp-nav_button:hover { background: #f9fafb; }
      .kickr-rdp .rdp-outside .rdp-day_button { opacity: 0.35; }
    `;
    document.head.appendChild(el);
  }, []);
}

function RangeCalendarPicker({ range, onRangeChange }) {
  const { C } = useTheme();
  const today = new Date(); today.setHours(0,0,0,0);
  useKickrDayPickerStyles();
  return (
    <div className="kickr-rdp" style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 12, width: "100%", boxSizing: "border-box", overflow: "hidden" }}>
      <DayPicker
        mode="range"
        selected={range}
        onSelect={onRangeChange}
        disabled={{ before: today }}
        defaultMonth={today}
        numberOfMonths={1}
      />
    </div>
  );
}

function SingleCalendarPicker({ selected, onSelect }) {
  const { C } = useTheme();
  const today = new Date(); today.setHours(0,0,0,0);
  useKickrDayPickerStyles();
  return (
    <div className="kickr-rdp" style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 12, width: "100%", boxSizing: "border-box", overflow: "hidden" }}>
      <DayPicker
        mode="single"
        selected={selected || undefined}
        onSelect={onSelect}
        disabled={{ before: today }}
        defaultMonth={selected || today}
        numberOfMonths={1}
      />
    </div>
  );
}

// ─── Time Picker ──────────────────────────────────────────────────────────────
function TimePicker({ value, onChange }) {
  const { C } = useTheme();
  return (
    <div style={{ display: "flex", alignItems: "center", border: `1px solid ${C.border}`, borderRadius: 8, background: C.surface, overflow: "hidden" }}>
      <input
        type="time"
        value={value || ""}
        onChange={e => onChange(e.target.value)}
        style={{ flex: 1, padding: "11px 14px", border: "none", background: "transparent", fontSize: 14, color: value ? C.ink : C.inkFaint, fontFamily: "inherit", outline: "none", cursor: "pointer", appearance: "none", WebkitAppearance: "none" }}
      />
      <div style={{ padding: "0 12px 0 0", color: C.inkMid, display: "flex", alignItems: "center", pointerEvents: "none" }}>
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
      </div>
    </div>
  );
}

// ─── Create Game ──────────────────────────────────────────────────────────────
// ─── Pull-to-Refresh ─────────────────────────────────────────────────────────
function PullToRefresh({ onRefresh, children, style }) {
  const { C } = useTheme();
  const containerRef = useRef(null);
  const startYRef = useRef(null);
  const pullYRef = useRef(0);
  const [pullProgress, setPullProgress] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  const refreshingRef = useRef(false);
  const THRESHOLD = 64;

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const onTouchStart = (e) => {
      if (el.scrollTop > 2 || refreshingRef.current) return;
      startYRef.current = e.touches[0].clientY;
    };
    const onTouchMove = (e) => {
      if (startYRef.current === null) return;
      const dy = e.touches[0].clientY - startYRef.current;
      if (dy <= 0 || el.scrollTop > 2) { startYRef.current = null; setPullProgress(0); return; }
      pullYRef.current = dy;
      setPullProgress(Math.min(dy / THRESHOLD, 1.2));
    };
    const onTouchEnd = async () => {
      if (startYRef.current === null) return;
      startYRef.current = null;
      const pulled = pullYRef.current;
      pullYRef.current = 0;
      if (pulled >= THRESHOLD) {
        refreshingRef.current = true;
        setRefreshing(true);
        setPullProgress(0);
        try { await onRefresh(); } finally { setRefreshing(false); refreshingRef.current = false; }
      } else {
        setPullProgress(0);
      }
    };
    el.addEventListener("touchstart", onTouchStart, { passive: true });
    el.addEventListener("touchmove", onTouchMove, { passive: true });
    el.addEventListener("touchend", onTouchEnd);
    return () => {
      el.removeEventListener("touchstart", onTouchStart);
      el.removeEventListener("touchmove", onTouchMove);
      el.removeEventListener("touchend", onTouchEnd);
    };
  }, [onRefresh]);

  const indicatorH = refreshing ? 44 : pullProgress > 0.05 ? Math.min(pullProgress * THRESHOLD * 0.65, 44) : 0;
  const isReady = pullProgress >= 1;
  return (
    <div ref={containerRef} style={{ ...style, overflowY: "auto" }}>
      <div style={{ height: indicatorH, overflow: "hidden", display: "flex", alignItems: "center", justifyContent: "center", transition: refreshing || pullProgress === 0 ? "height 0.25s ease" : "none" }}>
        <div style={{ width: 22, height: 22, borderRadius: "50%", border: `2.5px solid ${isReady || refreshing ? C.accent : C.border}`, borderTopColor: C.accent, animation: refreshing ? "spin 0.7s linear infinite" : "none", transform: refreshing ? "none" : `rotate(${pullProgress * 240}deg)`, opacity: indicatorH > 4 ? 1 : 0, transition: refreshing ? "none" : "border-color 0.15s" }} />
      </div>
      {children}
    </div>
  );
}

const CREATE_STEPS = ["Format", "Venue", "Time"];

function CreateScreen({ onBack, onCreate, onViewGame, prefill, userLocation, existingGames = [], venues = [], myCommunities = [] }) {
  const { C, T, btnPrimary, btnSecondary, inputBase, card, levelColor, warnBg, warnBorder, dangerBg, dangerBorder, successBg, successBorder, pitchType } = useTheme();
  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [repeatWeekly, setRepeatWeekly] = useState(false);
  const [repeatWeeks, setRepeatWeeks] = useState(4);
  const nextWeek = new Date(); nextWeek.setDate(nextWeek.getDate() + 7);
  const defaultDate = new Date(); defaultDate.setDate(defaultDate.getDate() + 1);
  const defaultDateStr = format(defaultDate, "yyyy-MM-dd");
  const cloneVenueId = prefill ? (prefill.venueId || venues.find(v => v.lat === prefill.lat && v.lng === prefill.lng)?.id || "custom") : null;
  const cloneBackupVenueId = prefill?.backupVenueId || (prefill?.backupLat && prefill?.backupLng ? venues.find(v => Math.abs(+v.lat - +prefill.backupLat) < 0.0005 && Math.abs(+v.lng - +prefill.backupLng) < 0.0005)?.id : null) || null;
  // Respect prefill.date when it points to today/future (e.g. accepted
  // challenge, availability-driven create). Past dates get bumped to nextWeek
  // so a stale clone doesn't try to schedule a game in the past.
  const todayStr = format(new Date(), "yyyy-MM-dd");
  const prefilledDate = prefill?.date && /^\d{4}-\d{2}-\d{2}$/.test(prefill.date) && prefill.date >= todayStr ? prefill.date : null;
  const [form, setForm] = useState(() => prefill ? {
    format: prefill.format || "5v5", level: prefill.level || "All levels", title: prefill.title || "",
    venueId: cloneVenueId, location: prefill.location, lat: prefill.lat, lng: prefill.lng,
    backupVenueId: cloneBackupVenueId, backupLocation: prefill.backupLocation || "", backupLat: prefill.backupLat || null, backupLng: prefill.backupLng || null,
    price: "Free", date: prefilledDate || format(nextWeek, "yyyy-MM-dd"), time: prefill.hour || "18:00",
    duration: parseInt(prefill.duration) || 60, description: prefill.description || "",
    minPlayers: Math.min(prefill.minPlayers || defaultMinPlayersForFormat(prefill.format || "5v5"), FORMAT_SPOTS[prefill.format || "5v5"] || 10), isPrivate: prefill.isPrivate || false, _autoTitle: "", customLocation: cloneVenueId === "custom",
    ageRestriction: false, minAge: null,
    // Phase 1 scope: derive from prefill.visibility if present, else from legacy isPrivate.
    visibility: prefill.visibility || (prefill.isPrivate ? "private" : "public"),
    communityId: prefill.communityId || null,
    // Optional referee — { id, name, avatar_url } or null
    refereeId: prefill.refereeId || null,
    refereeName: prefill.refereeName || null,
    refereeAvatarUrl: prefill.refereeAvatarUrl || null,
  } : { format: "5v5", level: "All levels", title: "", venueId: null, location: "", lat: null, lng: null, backupVenueId: null, backupLocation: "", backupLat: null, backupLng: null, price: "Free", date: defaultDateStr, time: "18:00", duration: "60", description: "", minPlayers: defaultMinPlayersForFormat("5v5"), isPrivate: false, _autoTitle: "", customLocation: false, ageRestriction: false, minAge: null, visibility: "public", communityId: null, refereeId: null, refereeName: null, refereeAvatarUrl: null });

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const customFormatActive = !STANDARD_FORMATS.includes(form.format);
  const customFormatSide = customFormatActive ? nearestCustomFormatSide(parseFormatSide(form.format)) : CUSTOM_FORMAT_SIDES[1];
  const chooseFormat = (format) => setForm(prev => ({ ...prev, format, minPlayers: defaultMinPlayersForFormat(format) }));
  const chooseCustomFormat = (side = customFormatSide) => {
    const safeSide = nearestCustomFormatSide(side);
    const format = `${safeSide}v${safeSide}`;
    setForm(prev => ({ ...prev, format, minPlayers: defaultMinPlayersForFormat(format) }));
  };
  const adjustCustomFormat = (delta) => {
    const currentSide = customFormatActive ? customFormatSide : CUSTOM_FORMAT_SIDES[1];
    const currentIndex = Math.max(0, CUSTOM_FORMAT_SIDES.indexOf(currentSide));
    const nextIndex = Math.max(0, Math.min(CUSTOM_FORMAT_SIDES.length - 1, currentIndex + delta));
    chooseCustomFormat(CUSTOM_FORMAT_SIDES[nextIndex]);
  };
  const [venueQuery, setVenueQuery] = useState("");
  const [venueOpen, setVenueOpen] = useState(false);
  const [venueCity, setVenueCity] = useState("Oslo"); // "Oslo" | "Bergen"
  const choiceSurface = active => ({
    border: `1px solid ${active ? C.accent : C.border}`,
    background: active ? C.accentBg : C.surface,
    color: active ? C.accent : C.ink,
    boxShadow: active ? `inset 0 0 0 1px ${C.accent}22` : "none",
  });

  const primaryVenue = useMemo(() => {
    if (!form.venueId) return null;
    if (form.venueId === "custom") {
      return form.location ? { id: "custom", name: form.location, lat: form.lat, lng: form.lng, type: "Outdoor", size: form.format, area: "Custom" } : null;
    }
    return venues.find(v => String(v.id) === String(form.venueId)) || null;
  }, [venues, form.venueId, form.location, form.lat, form.lng, form.format]);
  const backupVenue = useMemo(() => {
    if (form.backupVenueId) return venues.find(v => String(v.id) === String(form.backupVenueId)) || null;
    return form.backupLocation ? { id: "backup-custom", name: form.backupLocation, lat: form.backupLat, lng: form.backupLng, type: "Outdoor", size: form.format, area: "Backup" } : null;
  }, [venues, form.backupVenueId, form.backupLocation, form.backupLat, form.backupLng, form.format]);
  const pitchRisk = useMemo(() => getPitchRisk(primaryVenue, form.date, form.time, form.format), [primaryVenue, form.date, form.time, form.format]);
  const backupOptions = useMemo(() => {
    if (!primaryVenue) return [];
    const primaryId = form.venueId != null ? String(form.venueId) : null;
    const selectedBackupId = form.backupVenueId != null ? String(form.backupVenueId) : null;
    return venues
      .filter(v => String(v.id) !== primaryId && String(v.id) !== selectedBackupId)
      .filter(v => isVenueGoodForFormat(v, form.format))
      .map(v => {
        const distance = primaryVenue.lat && primaryVenue.lng && v.lat && v.lng ? getDistance(primaryVenue.lat, primaryVenue.lng, v.lat, v.lng) : 99;
        const sameArea = primaryVenue.area && v.area && primaryVenue.area === v.area;
        return { ...v, distance, sameArea, confidence: getPitchConfidence(v, form.date, form.time, form.format) };
      })
      .sort((a, b) => {
        if (a.sameArea !== b.sameArea) return a.sameArea ? -1 : 1;
        if (b.confidence !== a.confidence) return b.confidence - a.confidence;
        return a.distance - b.distance;
      })
      .slice(0, 3);
  }, [venues, primaryVenue, form.venueId, form.backupVenueId, form.format, form.date, form.time]);

	  const isDateTimeValid = (() => {
	    if (!form.date || !form.time) return true; // not set yet, allow
	    return isFutureGameDateTime(form.date, form.time);
	  })();

  const communityScopeValid = form.visibility !== "community" || !!form.communityId;
  const venueValid = form.venueId && form.title.trim() && (form.venueId !== "custom" || (form.location && form.lat));
  const canNext = [
    form.format && form.level && communityScopeValid,
    venueValid,
    isDateTimeValid && form.minPlayers > 0 && form.minPlayers <= (FORMAT_SPOTS[form.format] || parseFormatSide(form.format) * 2 || 10),
  ][step];
  const prefillNotice = (() => {
    if (!prefill) return null;
    if (prefill.source === "clone") return "Cloning last game - edit anything below";
    if (prefill.source === "group") return "Creating for group - edit anything below";
    if (prefill.source === "availability") return "Based on availability - edit anything below";
    if (prefill.source === "venue") return "Creating at this pitch - edit anything below";
    return null;
  })();

  const [conflictGames, setConflictGames] = useState([]);
  const [showConflictModal, setShowConflictModal] = useState(false);

	  const handleCreate = async () => {
	    if (!isDateTimeValid) return;
	    // Check for existing non-full games at the same venue on the same day
    if (form.lat && form.lng && form.date) {
      const conflicts = existingGames.filter(g => {
        if (!g.lat || !g.lng || !g.date) return false;
        if (g.spotsFilled >= g.spotsTotal) return false; // full games don't count
        if (g.date !== form.date) return false;
        const sameLat = Math.abs(+g.lat - form.lat) < 0.0005;
        const sameLng = Math.abs(+g.lng - form.lng) < 0.0005;
        return sameLat && sameLng;
      });
      if (conflicts.length > 0) {
        setConflictGames(conflicts);
        setShowConflictModal(true);
        return;
      }
    }
    setLoading(true);
    try { await onCreate(form, repeatWeekly ? repeatWeeks : 1); }
    finally { setLoading(false); }
  };

	  const doCreate = async () => {
	    if (!isDateTimeValid) {
	      setShowConflictModal(false);
	      return;
	    }
	    setShowConflictModal(false);
    setLoading(true);
    try { await onCreate(form, repeatWeekly ? repeatWeeks : 1); }
    finally { setLoading(false); }
  };

  const selectBackupPitch = (venue) => setForm(f => ({
    ...f,
    backupVenueId: venue.id,
    backupLocation: venue.name,
    backupLat: venue.lat,
    backupLng: venue.lng,
  }));
  const clearBackupPitch = () => setForm(f => ({ ...f, backupVenueId: null, backupLocation: "", backupLat: null, backupLng: null }));

  const PitchRiskNotice = ({ showBackupAction = false } = {}) => {
    if (!pitchRisk || pitchRisk.level === "low") return null;
    const high = pitchRisk.level === "high";
    const tone = high ? C.danger : C.warn;
    const bg = high ? dangerBg : warnBg;
    const border = high ? dangerBorder : warnBorder;
    return (
      <div style={{ padding: 12, background: bg, border: `1px solid ${border}`, borderRadius: 10, display: "flex", gap: 10, alignItems: "flex-start" }}>
        <AlertCircle size={17} {...ICON} color={tone} style={{ marginTop: 1, flexShrink: 0 }} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: tone }}>{pitchRisk.title}</div>
          <div style={{ fontSize: 12, color: C.inkMid, lineHeight: 1.35, marginTop: 3 }}>{pitchRisk.body}</div>
          {backupVenue && (
            <div style={{ marginTop: 8, fontSize: 12, color: C.success, fontWeight: 700 }}>Backup: {backupVenue.name}</div>
          )}
        </div>
        {showBackupAction && !backupVenue && (
          <button onClick={() => setStep(1)} style={{ ...btnSecondary, padding: "6px 10px", fontSize: 12, borderRadius: 8, flexShrink: 0 }}>Add backup</button>
        )}
      </div>
    );
  };

  const BackupPitchSelector = () => {
    if (!form.venueId) return null;
    return (
      <div style={{ padding: 14, background: C.surface, border: `1px solid ${C.border}`, borderRadius: 12 }}>
        <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "flex-start", marginBottom: 10 }}>
          <div>
            <div style={{ fontSize: 14, fontWeight: 800, color: C.ink }}>Backup pitch</div>
            <div style={{ fontSize: 12, color: C.inkMid, marginTop: 2, lineHeight: 1.35 }}>Optional, but recommended when the time or format is risky.</div>
          </div>
          {backupVenue && (
            <button onClick={clearBackupPitch} style={{ background: "transparent", border: "none", color: C.inkMid, fontSize: 12, fontWeight: 700, cursor: "pointer", padding: 2 }}>Clear</button>
          )}
        </div>
        {backupVenue ? (
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, padding: "10px 12px", background: C.bg, border: `1px solid ${successBorder}`, borderRadius: 10 }}>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: C.ink, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{backupVenue.name}</div>
              <div style={{ fontSize: 11, color: C.inkFaint, marginTop: 2 }}>{backupVenue.area || "Backup"}{backupVenue.type ? ` · ${backupVenue.type}` : ""}</div>
            </div>
            <span style={{ fontSize: 11, fontWeight: 800, color: C.success, background: successBg, border: `1px solid ${successBorder}`, borderRadius: 999, padding: "4px 8px", flexShrink: 0 }}>Selected</span>
          </div>
        ) : backupOptions.length > 0 ? (
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {backupOptions.map(v => {
              const label = confidenceLabel(v.confidence);
              return (
                <button key={v.id} onClick={() => selectBackupPitch(v)} style={{ width: "100%", padding: "10px 12px", background: C.bg, border: `1px solid ${C.border}`, borderRadius: 10, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, textAlign: "left" }}>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: C.ink, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{v.name}</div>
                    <div style={{ fontSize: 11, color: C.inkFaint, marginTop: 2 }}>{v.area || v.city}{v.distance < 50 ? ` · ${fmtDist(v.distance)}` : ""}</div>
                  </div>
                  <span style={{ fontSize: 10, fontWeight: 800, color: label.color, background: label.bg, borderRadius: 999, padding: "4px 8px", flexShrink: 0 }}>{label.label}</span>
                </button>
              );
            })}
          </div>
        ) : (
          <div style={{ fontSize: 12, color: C.inkMid, lineHeight: 1.35 }}>No strong backup found nearby for {form.format}. Consider choosing a more flexible format or another venue.</div>
        )}
      </div>
    );
  };

  const stepContent = [
    // Step 1 — Format
    <div key="format" style={{ display: "flex", flexDirection: "column", gap: 20 }}>
      <div>
        <Label>Format</Label>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
          {[...STANDARD_FORMATS, "custom"].map(f => {
            const isCustom = f === "custom";
            const active = isCustom ? customFormatActive : form.format === f;
            const customIndex = Math.max(0, CUSTOM_FORMAT_SIDES.indexOf(customFormatSide));
            if (isCustom) {
              return (
                <div key={f} style={{ padding: "10px 12px", borderRadius: 8, ...choiceSurface(active), minHeight: 54, display: "grid", gridTemplateColumns: "34px 1fr 34px", alignItems: "center", gap: 8, boxSizing: "border-box", transition: "all 0.15s" }}>
                  <button
                    type="button"
                    aria-label="Smaller custom format"
                    onClick={() => adjustCustomFormat(-1)}
                    style={{ width: 34, height: 34, borderRadius: "50%", border: `1px solid ${active ? C.accent : C.border}`, background: active ? C.bg : C.surface, color: customIndex === 0 && active ? C.inkFaint : active ? C.accent : C.inkMid, display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: 20, fontWeight: 800, lineHeight: 1, cursor: "pointer" }}
                  >
                    -
                  </button>
                  <button
                    type="button"
                    onClick={() => chooseCustomFormat(customFormatActive ? customFormatSide : 4)}
                    style={{ border: "none", background: "transparent", padding: 0, display: "flex", flexDirection: "column", alignItems: "center", minWidth: 0, lineHeight: 1.05, cursor: "pointer" }}
                  >
                    <span style={{ fontSize: 10, fontWeight: 800, color: active ? C.accent : C.inkMid, textTransform: "uppercase", letterSpacing: "0.05em" }}>Custom</span>
                    <span style={{ marginTop: 3, fontSize: 16, fontWeight: 850, color: active ? C.accent : C.ink }}>{customFormatActive ? form.format : "4v4"}</span>
                  </button>
                  <button
                    type="button"
                    aria-label="Larger custom format"
                    onClick={() => adjustCustomFormat(1)}
                    style={{ width: 34, height: 34, borderRadius: "50%", border: `1px solid ${active ? C.accent : C.border}`, background: active ? C.bg : C.surface, color: customIndex === CUSTOM_FORMAT_SIDES.length - 1 && active ? C.inkFaint : active ? C.accent : C.inkMid, display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: 20, fontWeight: 800, lineHeight: 1, cursor: "pointer" }}
                  >
                    +
                  </button>
                </div>
              );
            }
            return (
              <button key={f} type="button" onClick={() => chooseFormat(f)} style={{ padding: "14px", borderRadius: 8, ...choiceSurface(active), cursor: "pointer", fontSize: 15, fontWeight: active ? 800 : 650, transition: "all 0.15s", minHeight: 54 }}>
                {f}
              </button>
            );
          })}
        </div>
      </div>
      <div>
        <Label>Level</Label>
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          {["Beginner","Intermediate","Advanced","All levels"].map(l => {
            const active = form.level === l;
            const lc = levelColor[l];
            return (
              <button key={l} onClick={() => set("level", l)} style={{ padding: "12px 16px", borderRadius: 8, border: `1px solid ${active ? C.accent : C.border}`, background: active ? C.accentBg : C.surface, cursor: "pointer", display: "flex", alignItems: "center", gap: 12, textAlign: "left", transition: "all 0.15s" }}>
                <span style={{ width: 8, height: 8, borderRadius: "50%", background: lc.dot, flexShrink: 0 }} />
                <span style={{ fontSize: 14, fontWeight: active ? 800 : 500, color: active ? C.accent : C.ink }}>{l}</span>
              </button>
            );
          })}
        </div>
      </div>
      {/* Scope picker — public / community / private (Phase 1 of communities) */}
      <div>
        <Label>{t("gameScope")}</Label>
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          {[
            { key: "public",    label: t("scopePublic"),    desc: t("scopePublicDesc") },
            { key: "community", label: t("scopeCommunity"), desc: t("scopeCommunityDesc"), disabled: myCommunities.length === 0 },
            { key: "private",   label: t("scopePrivate"),   desc: t("scopePrivateDesc") },
          ].map(opt => {
            const active = form.visibility === opt.key;
            return (
              <button
                key={opt.key}
                onClick={() => {
                  if (opt.disabled) return;
                  // Reset community pick when leaving "community"
                  setForm(f => ({
                    ...f,
                    visibility: opt.key,
                    communityId: opt.key === "community"
                      ? (f.communityId || myCommunities[0]?.id || null)
                      : null,
                    isPrivate: opt.key === "private", // legacy mirror for back-compat
                  }));
                }}
                disabled={opt.disabled}
                style={{
                  padding: "12px 14px", borderRadius: 8,
                  border: `1px solid ${active ? C.accent : C.border}`,
                  background: active ? C.accentBg : C.surface,
                  color: active ? C.accent : C.ink,
                  cursor: opt.disabled ? "not-allowed" : "pointer",
                  opacity: opt.disabled ? 0.5 : 1,
                  textAlign: "left",
                }}
              >
                <div style={{ fontSize: 14, fontWeight: active ? 600 : 500 }}>{opt.label}</div>
                <div style={{ fontSize: 12, color: active ? C.accent : C.inkFaint, marginTop: 2 }}>
                  {opt.disabled && opt.key === "community" ? t("noCommunitiesYet") : opt.desc}
                </div>
              </button>
            );
          })}
        </div>
        {form.visibility === "community" && myCommunities.length > 0 && (
          <div style={{ marginTop: 10 }}>
            <Label>{t("pickCommunity")}</Label>
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              {myCommunities.map(c => {
                const active = String(form.communityId || "") === String(c.id);
                const initials = (c.name || "?").split(" ").map(w => w[0]).join("").slice(0, 2).toUpperCase();
                const memberText = c.member_count ? `${c.member_count} member${c.member_count === 1 ? "" : "s"}` : t("scopeCommunity");
                return (
                  <button
                    key={c.id}
                    type="button"
                    onClick={() => set("communityId", c.id)}
                    style={{ padding: "12px 14px", borderRadius: 8, border: `1px solid ${active ? C.accent : C.border}`, background: active ? C.accentBg : C.surface, color: active ? C.accent : C.ink, cursor: "pointer", display: "flex", alignItems: "center", gap: 12, textAlign: "left", transition: "all 0.15s" }}
                  >
                    {c.avatar_url ? (
                      <img src={c.avatar_url} alt="" style={{ width: 32, height: 32, borderRadius: "50%", objectFit: "cover", flexShrink: 0 }} />
                    ) : (
                      <span style={{ width: 32, height: 32, borderRadius: "50%", background: active ? C.surface : C.bg, color: active ? C.accent : C.inkMid, display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: 12, fontWeight: 850, flexShrink: 0 }}>
                        {initials}
                      </span>
                    )}
                    <span style={{ flex: 1, minWidth: 0 }}>
                      <span style={{ display: "flex", alignItems: "center", gap: 6, minWidth: 0 }}>
                        <span style={{ display: "block", fontSize: 14, fontWeight: active ? 800 : 600, color: active ? C.accent : C.ink, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{c.name}</span>
                        {c.verified && <VerifiedTick size={10} />}
                      </span>
                      <span style={{ display: "block", fontSize: 12, color: active ? C.accent : C.inkFaint, marginTop: 2 }}>{memberText}</span>
                    </span>
                    {active && <Check size={16} {...ICON} color={C.accent} style={{ flexShrink: 0 }} />}
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </div>
      <div>
        <Label>Description (optional)</Label>
        <div style={{ position: "relative" }}>
          <textarea value={form.description} onChange={e => set("description", e.target.value.slice(0, 300))} placeholder="Any details for players..." rows={3} maxLength={300} style={{ ...inputBase, resize: "none" }} />
          <span style={{ position: "absolute", bottom: 8, right: 12, fontSize: 10, color: form.description.length >= 270 ? C.warn : C.inkFaint }}>{form.description.length}/300</span>
        </div>
      </div>
    </div>,

    // Step 2 — Venue
    <div key="venue" style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <div>
        <Label>Game title</Label>
        <input value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value.slice(0, 60), _autoTitle: "" }))} placeholder="e.g. Wednesday 5v5" maxLength={60} style={inputBase} />
      </div>
      <div>
        <Label>Venue</Label>
        {(() => {
          const selectedVenue = form.venueId && form.venueId !== "custom" ? primaryVenue : null;

          // — Selected state: show a clean card —
          if (form.venueId) {
            const isCage = selectedVenue?.type === "Cageball";
            return (
              <>
                <div style={{ padding: "12px 16px", background: C.surface, borderRadius: 10, border: `1px solid ${C.border}`, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 14, fontWeight: 600, color: C.ink }}>{selectedVenue ? selectedVenue.name : "Custom location"}</div>
                    {selectedVenue && <div style={{ fontSize: 12, color: C.inkFaint, marginTop: 2 }}>{selectedVenue.area} · <span style={{ color: isCage ? pitchType.cage.fg : pitchType.field.fg }}>{isCage ? "Cageball" : "Pitch"}</span></div>}
                  </div>
                  <button onClick={() => { setForm(f => ({ ...f, venueId: null, location: "", lat: null, lng: null, backupVenueId: null, backupLocation: "", backupLat: null, backupLng: null, customLocation: false })); setVenueQuery(""); }}
                    style={{ fontSize: 12, color: C.inkMid, background: "transparent", border: `1px solid ${C.border}`, borderRadius: 6, padding: "4px 10px", cursor: "pointer", flexShrink: 0, marginLeft: 8 }}>Change</button>
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: 14, marginTop: 14 }}>
                  {selectedVenue && <VenueAvailabilityChart venue={selectedVenue} gameFormat={form.format} />}
                  <BackupPitchSelector />
                </div>
              </>
            );
          }

          // — Unselected state: nearest suggestion + search —
          return (
            <>
              {userLocation && (() => {
                const nearest = [...venues].filter(v => (v.city || "Oslo") === venueCity).sort((a, b) =>
                  getDistance(userLocation.lat, userLocation.lng, a.lat, a.lng) -
                  getDistance(userLocation.lat, userLocation.lng, b.lat, b.lng)
                )[0];
                const dist = getDistance(userLocation.lat, userLocation.lng, nearest.lat, nearest.lng);
                return (
                  <div style={{ padding: "10px 14px", background: successBg, borderRadius: 8, border: `1px solid ${successBorder}`, marginBottom: 8, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <div>
                      <div style={{ fontSize: 11, fontWeight: 600, color: C.success, marginBottom: 1 }}>Nearest to you</div>
                      <div style={{ fontSize: 13, color: C.ink }}>{nearest.name} · {fmtDist(dist)} away</div>
                    </div>
                    <button onClick={() => setForm(f => {
                      const dateStr = f.date || format(new Date(Date.now() + 86400000), "yyyy-MM-dd");
                      const [yr, mo, dy] = dateStr.split("-").map(Number);
                      const day = new Date(yr, mo - 1, dy).toLocaleDateString("en-US", { weekday: "long" });
                      const suggested = `${day} ${f.format} at ${nearest.name.split(" ").pop()}`;
                      const newTitle = !f.title || f.title === f._autoTitle ? suggested : f.title;
	                      return { ...f, venueId: nearest.id, location: nearest.name, lat: nearest.lat, lng: nearest.lng, backupVenueId: null, backupLocation: "", backupLat: null, backupLng: null, price: "Free", customLocation: false, _autoTitle: suggested, title: newTitle };
	                    })} style={{ ...btnPrimary, padding: "5px 12px", fontSize: 12, borderRadius: 6 }}>Use</button>
                  </div>
                );
              })()}
              {/* City toggle */}
              <div style={{ display: "flex", background: C.surface, borderRadius: 8, padding: 3, marginBottom: 10 }}>
                {["Oslo", "Bergen", "Trondheim"].map(city => (
                  <button key={city} onClick={() => setVenueCity(city)} style={{ flex: 1, padding: "8px 0", borderRadius: 6, border: "none", background: venueCity === city ? C.bg : "transparent", color: venueCity === city ? C.ink : C.inkMid, cursor: "pointer", fontSize: 13, fontWeight: venueCity === city ? 600 : 400, transition: "all 0.15s" }}>{city}</button>
                ))}
              </div>
              <div style={{ position: "relative" }}>
                <input
                  value={venueQuery}
                  onChange={e => { setVenueQuery(e.target.value); setVenueOpen(true); }}
                  onFocus={() => setVenueOpen(true)}
                  onBlur={() => setTimeout(() => setVenueOpen(false), 150)}
                  placeholder="Search venues…"
                  style={{ ...inputBase, paddingLeft: 38 }}
                />
                <span style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", fontSize: 15, pointerEvents: "none", opacity: 0.4 }}>⌕</span>
                {venueOpen && (() => {
                  const q = venueQuery.toLowerCase();
                  const filtered = venues.filter(v => {
                    if ((v.city || "Oslo") !== venueCity) return false;
                    if (q && !v.name.toLowerCase().includes(q) && !(v.area || "").toLowerCase().includes(q) && !v.type.toLowerCase().includes(q)) return false;
                    return true;
                  }).sort((a, b) => {
                    if (!userLocation) return 0;
                    return getDistance(userLocation.lat, userLocation.lng, a.lat, a.lng) - getDistance(userLocation.lat, userLocation.lng, b.lat, b.lng);
                  });
                  return (
                    <div style={{ position: "absolute", top: "calc(100% + 4px)", left: 0, right: 0, background: C.surface, border: `1px solid ${C.border}`, borderRadius: 10, zIndex: 50, maxHeight: 240, overflowY: "auto", boxShadow: "0 8px 24px rgba(0,0,0,0.35)" }}>
                      {filtered.length === 0 ? (
                        <div style={{ padding: "12px 16px", fontSize: 13, color: C.inkFaint }}>No venues match these filters</div>
	                      ) : filtered.map(v => {
	                        const fit = isVenueGoodForFormat(v, form.format);
	                        const cl = confidenceLabel(getPitchConfidence(v, form.date, form.time, form.format));
	                        return (
                          <button key={v.id} onMouseDown={() => {
                            setVenueOpen(false); setVenueQuery("");
                            setForm(f => {
                              const dateStr = f.date || format(new Date(Date.now() + 86400000), "yyyy-MM-dd");
                              const [yr, mo, dy] = dateStr.split("-").map(Number);
                              const day = new Date(yr, mo - 1, dy).toLocaleDateString("en-US", { weekday: "long" });
                              const venueParts = v.name.split(" ");
                              const shortVenue = venueParts.length > 1 && venueParts[0].toLowerCase() === "cageball" ? venueParts.slice(1).join(" ") : venueParts[venueParts.length - 1];
                              const suggested = `${day} ${f.format} at ${shortVenue}`;
                              const newTitle = !f.title || f.title === f._autoTitle ? suggested : f.title;
	                              return { ...f, venueId: v.id, location: v.name, lat: v.lat, lng: v.lng, backupVenueId: null, backupLocation: "", backupLat: null, backupLng: null, price: "Free", customLocation: false, _autoTitle: suggested, title: newTitle };
	                            });
                          }} style={{ width: "100%", padding: "10px 16px", background: "transparent", border: "none", borderBottom: `1px solid ${C.border}`, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "space-between", textAlign: "left" }}>
                            <div>
                              <div style={{ fontSize: 14, color: C.ink }}>{v.name}</div>
                              <div style={{ fontSize: 11, color: C.inkFaint, marginTop: 1 }}>{v.area} · {v.size}{userLocation ? ` · ${fmtDist(getDistance(userLocation.lat, userLocation.lng, v.lat, v.lng))}` : ""}</div>
                            </div>
	                            <span style={{ fontSize: 10, fontWeight: 800, color: fit ? cl.color : C.danger, background: fit ? cl.bg : dangerBg, padding: "3px 7px", borderRadius: 99, flexShrink: 0 }}>{fit ? cl.label : `Not for ${form.format}`}</span>
	                          </button>
                        );
                      })}
                    </div>
                  );
                })()}
              </div>
	              <button onMouseDown={() => { setVenueOpen(false); setVenueQuery(""); setForm(f => ({ ...f, venueId: "custom", customLocation: true, lat: 59.9139, lng: 10.7522, backupVenueId: null, backupLocation: "", backupLat: null, backupLng: null, price: "Free" })); }}
                style={{ marginTop: 8, width: "100%", padding: "11px 16px", background: "transparent", border: `1px dashed ${C.border}`, borderRadius: 10, cursor: "pointer", textAlign: "center" }}>
                <span style={{ fontSize: 13, color: C.inkMid }}>+ Custom location</span>
              </button>
            </>
          );
        })()}
      </div>
      {/* Custom location input */}
      {form.customLocation && (
        <>
          <div>
            <Label>Address</Label>
            <CustomLocationPicker value={form.location} onChange={({ location, lat, lng }) => setForm(f => ({ ...f, location, lat, lng }))} />
            {form.venueId === "custom" && !form.lat && form.location && (
              <p style={{ ...T.small, color: C.warn, marginTop: 6 }}>Select an address from the suggestions</p>
            )}
          </div>
        </>
      )}
    </div>,

    // Step 3 — Time
    <div key="time" style={{ display: "flex", flexDirection: "column", gap: 20 }}>
      {/* Date + time in a single card */}
	      <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 12, overflow: "hidden" }}>
	        <div style={{ padding: "12px 14px 0" }}>
	          <CalendarPicker value={form.date} onChange={v => set("date", v)} min={format(new Date(), "yyyy-MM-dd")} />
        </div>
        <div style={{ borderTop: `1px solid ${C.border}`, padding: "12px 14px", display: "flex", alignItems: "center", gap: 16 }}>
          <div style={{ flex: 1 }}>
            <Label>Kickoff time</Label>
            <TimePicker value={form.time} onChange={v => set("time", v)} />
	          </div>
	        </div>
	      </div>
	      <PitchRiskNotice showBackupAction />
	      {form.date && form.time && !isDateTimeValid && (
        <div style={{ padding: "10px 14px", background: dangerBg, borderRadius: 8, border: `1px solid ${dangerBorder}`, fontSize: 13, color: C.danger, fontWeight: 500 }}>
          This time has already passed — please pick a future time
        </div>
      )}
      <div>
        <Label>Duration — {form.duration} min</Label>
        <input type="range" min={30} max={120} step={15} value={form.duration} onChange={e => set("duration", e.target.value)} style={{ width: "100%", accentColor: C.ink }} />
        <div style={{ display: "flex", justifyContent: "space-between", ...T.small, marginTop: 4 }}>
          <span>30 min</span><span>120 min</span>
        </div>
      </div>
      <div>
        <Label>Minimum players to confirm game</Label>
        <p style={{ ...T.small, marginBottom: 8 }}>{t("minPlayersHint")}</p>
        <div style={{ display: "flex", gap: 8 }}>
          {minPlayerOptionsForFormat(form.format).map(n => (
            <button key={n} onClick={() => set("minPlayers", n)} style={{ flex: 1, padding: "12px 4px", borderRadius: 8, border: `1px solid ${form.minPlayers === n ? C.accent : C.border}`, background: form.minPlayers === n ? C.accentBg : C.surface, color: form.minPlayers === n ? C.accent : C.ink, cursor: "pointer", fontSize: 14, fontWeight: form.minPlayers === n ? 800 : 600, transition: "all 0.15s" }}>{n}</button>
          ))}
        </div>
      </div>
      {/* Repeat weekly — redesigned */}
      <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 12, overflow: "hidden" }}>
        <button onClick={() => setRepeatWeekly(v => !v)} style={{ width: "100%", padding: "14px 16px", background: "transparent", border: "none", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "space-between", textAlign: "left" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <div style={{ width: 34, height: 34, borderRadius: 8, background: repeatWeekly ? C.accentBg : C.bg, border: `1px solid ${repeatWeekly ? C.accent : C.border}`, display: "flex", alignItems: "center", justifyContent: "center", transition: "all 0.2s", flexShrink: 0 }}>
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke={repeatWeekly ? C.accent : C.inkMid} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M17 2l4 4-4 4"/><path d="M3 11V9a4 4 0 0 1 4-4h14"/><path d="M7 22l-4-4 4-4"/><path d="M21 13v2a4 4 0 0 1-4 4H3"/>
              </svg>
            </div>
            <div>
              <div style={{ fontSize: 14, fontWeight: 600, color: C.ink }}>Repeat weekly</div>
              <div style={{ fontSize: 12, color: C.inkFaint, marginTop: 1 }}>
                {repeatWeekly ? `${repeatWeeks} games · same time each week` : "Create the same game for multiple weeks"}
              </div>
            </div>
          </div>
          <div style={{ width: 44, height: 26, borderRadius: 13, background: repeatWeekly ? C.accent : C.border, position: "relative", transition: "background 0.2s", flexShrink: 0 }}>
            <span style={{ position: "absolute", top: 3, left: repeatWeekly ? 21 : 3, width: 20, height: 20, borderRadius: "50%", background: "#fff", transition: "left 0.2s", display: "block", boxShadow: "0 1px 3px rgba(0,0,0,0.25)" }} />
          </div>
        </button>
        {repeatWeekly && (
          <>
            <div style={{ height: 1, background: C.border }} />
            <div style={{ padding: "14px 16px" }}>
              {/* Week count pills */}
              <div style={{ display: "flex", gap: 6, marginBottom: 16 }}>
                {[2, 4, 8, 12].map(n => (
                  <button key={n} onClick={() => setRepeatWeeks(n)} style={{ flex: 1, padding: "9px 4px", borderRadius: 8, border: `1px solid ${repeatWeeks === n ? C.accent : C.border}`, background: repeatWeeks === n ? C.accentBg : "transparent", color: repeatWeeks === n ? C.accent : C.inkMid, cursor: "pointer", fontSize: 13, fontWeight: repeatWeeks === n ? 800 : 600, transition: "all 0.15s" }}>
                    {n}w
                  </button>
                ))}
              </div>
              {/* Date preview timeline */}
              {form.date && (
                <div style={{ display: "flex", flexDirection: "column" }}>
                  {Array.from({ length: Math.min(repeatWeeks, 12) }, (_, i) => {
                    const [y, mo, dy] = form.date.split("-").map(Number);
                    const dt = new Date(y, mo - 1, dy + i * 7);
                    const label = dt.toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short" });
                    const isFirst = i === 0;
                    const isLast = i === Math.min(repeatWeeks, 12) - 1;
                    return (
                      <div key={i} style={{ display: "flex", alignItems: "flex-start", gap: 10 }}>
                        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", width: 18, paddingTop: 3, flexShrink: 0 }}>
                          <div style={{ width: 8, height: 8, borderRadius: "50%", background: isFirst ? C.accent : C.border, flexShrink: 0 }} />
                          {!isLast && <div style={{ width: 1, height: 20, background: C.border, marginTop: 2 }} />}
                        </div>
                        <div style={{ paddingBottom: isLast ? 0 : 12 }}>
                          <span style={{ fontSize: 13, color: isFirst ? C.ink : C.inkMid, fontWeight: isFirst ? 600 : 400 }}>{label}</span>
                          {isFirst && <span style={{ marginLeft: 8, fontSize: 11, fontWeight: 600, color: C.accent, background: C.accentBg, padding: "1px 7px", borderRadius: 99 }}>First</span>}
                          {isLast && repeatWeeks > 12 && <span style={{ marginLeft: 6, fontSize: 11, color: C.inkFaint }}>…and more</span>}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </>
        )}
      </div>
	      {/* Summary */}
	      <div style={{ ...card, padding: 16 }}>
	        <div style={{ ...T.label, marginBottom: 10 }}>Review before publishing</div>
	        {[
	          ["Format", `${form.format} · ${form.level}`],
	          ["Venue", form.location || "—"],
	          ...(backupVenue ? [["Backup pitch", backupVenue.name]] : []),
	          ["Time", form.date && form.time ? `${form.date} at ${form.time}` : "—"],
	          ["Duration", `${form.duration} min`],
	          ["Min players to confirm", form.minPlayers],
	          ["Max players", FORMAT_SPOTS[form.format] || parseInt(form.format.split("v")[0]) * 2],
	        ].map(([k, v]) => (
	          <div key={k} style={{ display: "flex", justifyContent: "space-between", padding: "8px 0", borderBottom: `1px solid ${C.border}` }}>
	            <span style={{ ...T.small, color: C.inkMid }}>{k}</span>
	            <span style={{ ...T.h3, fontSize: 13, maxWidth: "58%", textAlign: "right", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{v}</span>
	          </div>
	        ))}
	      </div>
    </div>,
  ];

  return (
    <div style={{ height: "100vh", display: "flex", flexDirection: "column", background: C.bg }}>
      {/* Header */}
      <div style={{ paddingTop: "calc(16px + env(safe-area-inset-top, 44px))", paddingLeft: 20, paddingRight: 20, paddingBottom: 0, flexShrink: 0 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
          <button onClick={onBack} style={{ ...btnSecondary, padding: "6px 14px", fontSize: 13 }}>Cancel</button>
          <span style={{ ...T.small }}>Step {step + 1} of {CREATE_STEPS.length}</span>
        </div>
        {/* Progress */}
        <div style={{ display: "flex", gap: 6, marginBottom: 20 }}>
          {CREATE_STEPS.map((s, i) => (
            <div key={s} style={{ flex: 1 }}>
              <div style={{ height: 2, borderRadius: 1, background: i <= step ? C.ink : C.border, transition: "background 0.3s" }} />
              <div style={{ ...T.small, marginTop: 5, color: i <= step ? C.ink : C.inkFaint, fontWeight: i === step ? 600 : 400 }}>{s}</div>
            </div>
          ))}
        </div>
        {prefillNotice && step === 0 && <div style={{ fontSize: 11, fontWeight: 600, color: C.accent, background: C.accentBg, padding: "3px 10px", borderRadius: 20, display: "inline-flex", marginBottom: 8 }}>{prefillNotice}</div>}
        <h2 style={{ ...T.h2, margin: "0 0 4px" }}>{["What kind of game?", "Where and who?", "When is it?"][step]}</h2>
      </div>

      <div style={{ flex: 1, overflowY: "auto", padding: "16px 20px 0" }}>{stepContent[step]}</div>

      <div style={{ padding: "16px 20px 32px", display: "flex", gap: 10, borderTop: `1px solid ${C.border}` }}>
        {step > 0 && <button onClick={() => setStep(s => s - 1)} style={{ ...btnSecondary, flex: 1, padding: 13, borderRadius: 10 }}>{t("back")}</button>}
        <button onClick={() => step < CREATE_STEPS.length - 1 ? setStep(s => s + 1) : handleCreate()} disabled={!canNext || loading} style={{ ...btnPrimary, flex: 2, padding: 13, borderRadius: 10, fontSize: 15, opacity: canNext && !loading ? 1 : 0.35, cursor: canNext && !loading ? "pointer" : "not-allowed" }}>
          {loading ? t("creating") : step < CREATE_STEPS.length - 1 ? t("continue") : t("createGame")}
        </button>
      </div>

      {/* Duplicate venue conflict modal */}
      {showConflictModal && (
        <div onClick={() => setShowConflictModal(false)} style={{ position: "fixed", inset: 0, zIndex: 200, background: "rgba(0,0,0,0.55)", display: "flex", alignItems: "flex-end" }}>
          <div onClick={e => e.stopPropagation()} style={{ width: "100%", background: C.bg, borderRadius: "20px 20px 0 0", padding: "24px 20px 32px", boxSizing: "border-box" }}>
            <div style={{ width: 36, height: 4, borderRadius: 2, background: C.border, margin: "0 auto 20px" }} />
            <div style={{ fontSize: 18, fontWeight: 700, color: C.ink, marginBottom: 6 }}>Game already here</div>
            <p style={{ fontSize: 14, color: C.inkMid, marginBottom: 20, lineHeight: 1.5 }}>
              There {conflictGames.length === 1 ? "is" : "are"} already {conflictGames.length} game{conflictGames.length > 1 ? "s" : ""} at this venue on {form.date}. Want to join instead?
            </p>
            <div style={{ display: "flex", flexDirection: "column", gap: 10, marginBottom: 16 }}>
              {conflictGames.map(g => (
                <div key={g.id} style={{ padding: "12px 14px", borderRadius: 10, background: C.surface, border: `1px solid ${C.border}`, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                  <div>
                    <div style={{ fontSize: 14, fontWeight: 600, color: C.ink }}>{g.title}</div>
                    <div style={{ fontSize: 12, color: C.inkMid, marginTop: 2 }}>{g.hour} · {g.format} · {g.spotsFilled}/{g.spotsTotal} players</div>
                  </div>
                  <button onClick={() => { setShowConflictModal(false); onViewGame ? onViewGame(g) : onBack(); }} style={{ ...btnPrimary, padding: "7px 14px", fontSize: 13, borderRadius: 8, flexShrink: 0 }}>View</button>
                </div>
              ))}
            </div>
            <div style={{ display: "flex", gap: 10 }}>
              <button onClick={() => setShowConflictModal(false)} style={{ ...btnSecondary, flex: 1, padding: 13, borderRadius: 10 }}>Cancel</button>
              <button onClick={doCreate} style={{ ...btnPrimary, flex: 1, padding: 13, borderRadius: 10, background: C.surface, color: C.ink, border: `1px solid ${C.border}` }}>Create anyway</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Notif Panel ──────────────────────────────────────────────────────────────
function NotifPanel({ notifs, currentUserId, onClose, onTap, onReplyNotif, onMarkAllRead, onAcceptFriendNotif, onDeclineFriendNotif, onDeleteNotif, onClearAll, onAcceptGameInvite, onDeclineGameInvite }) {
  const { C, T, btnPrimary, btnSecondary, dangerBorder } = useTheme();
  return (
    <div style={{ position: "absolute", inset: 0, zIndex: 60, background: "rgba(0,0,0,0.3)" }} onClick={onClose}>
      <div onClick={e => e.stopPropagation()} style={{ position: "absolute", top: 0, left: 0, right: 0, bottom: 0, background: C.bg, display: "flex", flexDirection: "column", animation: "slideRight 0.25s ease" }}>
        <div style={{ paddingTop: "calc(20px + env(safe-area-inset-top, 44px))", paddingLeft: 20, paddingRight: 20, paddingBottom: 14, display: "flex", justifyContent: "space-between", alignItems: "center", borderBottom: `1px solid ${C.border}` }}>
          <h2 style={{ ...T.h2, margin: 0 }}>{t("notifications")}</h2>
          <div style={{ display: "flex", gap: 8 }}>
            {notifs.some(n => !n.read) && <button onClick={onMarkAllRead} style={{ background: "none", border: `1px solid ${C.border}`, borderRadius: 6, cursor: "pointer", color: C.accent, fontSize: 13, fontWeight: 600, padding: "4px 10px" }}>{t("markAllRead")}</button>}
            {notifs.length > 0 && <button onClick={onClearAll} style={{ background: "none", border: `1px solid ${C.border}`, borderRadius: 6, cursor: "pointer", color: C.danger, fontSize: 13, fontWeight: 600, padding: "4px 10px" }}>{t("clearAll")}</button>}
            <button onClick={onClose} style={{ ...btnSecondary, padding: "5px 12px", fontSize: 13 }}>Close</button>
          </div>
        </div>
        <div style={{ flex: 1, overflowY: "auto" }}>
          {notifs.length === 0
            ? <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", height: "100%", padding: 40, gap: 12 }}>
                <div style={{ width: 48, height: 48, borderRadius: "50%", background: C.surface, display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={C.inkFaint} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 0 1-3.46 0"/></svg>
                </div>
                <div style={{ ...T.h3, color: C.inkMid }}>No notifications yet</div>
                <p style={{ ...T.small, textAlign: "center", maxWidth: 200 }}>You'll be notified when someone joins your game or sends a friend request</p>
              </div>
            : notifs.map(n => {
                const isFriendReq = n.type === "friend_request" && !n.read && UUID_RE.test(String(n.sub || "")) && String(n.sub) !== String(currentUserId || "");
                const isGameInvite = n.type === "game_invite" && n.gameId;
	                const chatMeta = parseChatNotificationSub(n.sub);
	                const isChatReply = n.type === "chat_message" || n.type === "friend_message" || (n.type === "mention" && !!chatMeta?.kind);
                const hasActions = isFriendReq || isGameInvite || isChatReply;
                const subLabel = getNotifSubLabel(n);
                const NotifIcon = {
                  friend_request: User, friend_accepted: Handshake,
	                  friend_message: MessageCircle, chat_message: MessageCircle, mention: MessageCircle,
                  player_joined: CheckCircle2, player_left: Users,
                  game_cancelled: X, game_updated: Edit3,
                  game_invite: Send, promoted: Sparkles,
                  waitlist_joined: Clock, waitlist_left: ArrowLeft,
                  teams_published: Swords, ball_assigned: FootballIcon,
	                  availability_group: Calendar, game_review: Flag,
                }[n.type] || Bell;
                return (
                  <div key={n.id} onClick={() => isChatReply ? onReplyNotif(n) : !hasActions && onTap(n)} style={{ display: "flex", gap: 12, padding: "14px 20px", cursor: (isChatReply || !hasActions) ? "pointer" : "default", background: n.read ? C.bg : C.accentBg, borderBottom: `1px solid ${C.border}`, borderLeft: `3px solid ${n.read ? "transparent" : C.accent}`, alignItems: "flex-start" }}>
                    <div style={{ width: 24, height: 24, borderRadius: "50%", background: n.read ? C.surface : C.surface, color: n.read ? C.inkMid : C.accent, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, marginTop: 1 }}>
                      <NotifIcon size={14} {...ICON} />
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{ ...T.h3, fontSize: 13, marginBottom: 2 }}>{n.text}</div>
                      {isFriendReq
                        ? <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
                            <button onClick={e => { e.stopPropagation(); onDeclineFriendNotif(n.sub, n.id); }} style={{ ...btnSecondary, padding: "5px 10px", fontSize: 12, borderRadius: 6, color: C.danger, borderColor: dangerBorder }}>{t("decline")}</button>
                            <button onClick={e => { e.stopPropagation(); onAcceptFriendNotif(n.sub, n.id); }} style={{ ...btnPrimary, padding: "5px 10px", fontSize: 12, borderRadius: 6 }}>{t("accept")}</button>
                          </div>
                        : subLabel && <div style={{ ...T.small, color: C.inkMid }}>{subLabel}</div>
                      }
                      {isGameInvite && (
                        <div style={{ display: "flex", gap: 8, marginTop: 6 }}>
                          <button onClick={e => { e.stopPropagation(); onDeclineGameInvite(n.id); }} style={{ ...btnSecondary, padding: "5px 10px", fontSize: 12, borderRadius: 6, color: C.danger, borderColor: dangerBorder }}>Decline</button>
                          <button onClick={e => { e.stopPropagation(); onAcceptGameInvite(n.gameId, n.id); }} style={{ ...btnPrimary, padding: "5px 10px", fontSize: 12, borderRadius: 6 }}>View Game</button>
                        </div>
                      )}
                      {isChatReply && (
                        <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
                          <button onClick={e => { e.stopPropagation(); onReplyNotif(n); }} style={{ ...btnPrimary, padding: "6px 12px", fontSize: 12, borderRadius: 8, display: "inline-flex", alignItems: "center", gap: 6 }}>
                            <MessageCircle size={13} {...ICON} /> Reply
                          </button>
                        </div>
                      )}
                      <div style={{ ...T.small, marginTop: 4, color: C.inkFaint }}>{n.time}</div>
                    </div>
                    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 8, flexShrink: 0 }}>
                      <button onClick={e => { e.stopPropagation(); onDeleteNotif(n.id); }} style={{ background: "none", border: "none", cursor: "pointer", color: C.inkFaint, fontSize: 16, lineHeight: 1, padding: "2px 4px", borderRadius: 4 }} title="Delete">×</button>
                      {!n.read && !hasActions && <div style={{ width: 8, height: 8, borderRadius: "50%", background: C.accent }} />}
                    </div>
                  </div>
                );
              })
          }
        </div>
      </div>
    </div>
  );
}

// ─── My Games Tab ─────────────────────────────────────────────────────────────

function PastGameCard({ g, rating, onRate, onTap, currentUser, onRepeat, initialOpen = false, onReviewOpen, onReviewSubmitted }) {
  const { C, T, card, btnPrimary, btnSecondary, successBg, warnBg, warnBorder, RADIUS, SPACE } = useTheme();
  const [pitchFB, setPitchFB] = useState(() => {
    try { return JSON.parse(localStorage.getItem(`kickr_pfb_${g.id}`) || "null"); } catch { return null; }
  });
  const [reported, setReported] = useState(() => {
    try { return JSON.parse(localStorage.getItem(`kickr_reported_${g.id}`) || "[]"); } catch { return []; }
  });
  const [showReview, setShowReview] = useState(initialOpen);
	  const [reportSaving, setReportSaving] = useState(false);
	  const [reportSaved, setReportSaved] = useState(false);
	  const [reportSubmitted, setReportSubmitted] = useState(() => localStorage.getItem(`kickr_match_reported_${g.id}`) === "1");
	  const [motmId, setMotmId] = useState(() => localStorage.getItem(`kickr_motm_${g.id}`) || "");

  useEffect(() => {
    if (initialOpen) setShowReview(true);
  }, [initialOpen, g.id]);

  const submitFB = async (occupied, quality) => {
    const fb = { occupied, quality };
    setPitchFB(fb);
    localStorage.setItem(`kickr_pfb_${g.id}`, JSON.stringify(fb));
    if (!currentUser || !g.lat) return;
    supabase.from("pitch_feedback").upsert({
      venue_lat: parseFloat(g.lat), venue_lng: parseFloat(g.lng), venue_name: g.location,
      user_id: currentUser.id, game_id: g.id, date: g.date, hour: g.hour,
      was_occupied: occupied, quality_score: quality,
    }, { onConflict: "user_id,game_id" }).then(() => {}, () => {});
  };

  const toggleNoShow = (playerId) => {
    if (!playerId || !currentUser?.id || playerId === currentUser.id) return;
    // Only allow reporting for finished games (not active or cancelled)
    if (g.status === "cancelled") return;
    const wasReported = reported.includes(playerId);
    const next = wasReported ? reported.filter(x => x !== playerId) : [...reported, playerId];
    setReported(next);
    localStorage.setItem(`kickr_reported_${g.id}`, JSON.stringify(next));
  };

  const clearNoShows = () => {
    setReported([]);
    localStorage.setItem(`kickr_reported_${g.id}`, JSON.stringify([]));
  };

  const syncNoShowReports = async () => {
    if (!currentUser?.id) return;
    try {
      const { error: deleteError } = await supabase.from("no_show_reports")
        .delete()
        .eq("game_id", g.id)
        .eq("reporter_id", currentUser.id);
      if (deleteError) console.warn("[noShow] delete failed", deleteError);
      if (reported.length === 0) return;

      const rows = reported.map(playerId => ({
        game_id: g.id,
        reporter_id: currentUser.id,
        reported_user_id: playerId,
      }));
      const { error } = await supabase.from("no_show_reports").insert(rows);
      if (error && !String(error.message || "").toLowerCase().includes("duplicate")) {
        // Fallback to legacy direct increment (older schema without no_show_reports table)
        for (const playerId of reported) {
          const { data } = await supabase.from("profiles").select("no_shows").eq("id", playerId).maybeSingle();
          if (data) {
            const ns = (data.no_shows || 0) + 1;
            await supabase.from("profiles").update({ no_shows: ns }).eq("id", playerId);
          }
        }
      }
    } catch (err) {
      console.warn("[noShow] sync failed", err);
    }
  };

  const updatePitchFeedback = patch => {
    const next = {
      occupied: pitchFB?.occupied ?? null,
      quality: pitchFB?.quality || 0,
      ...patch,
    };
    if (next.quality && next.occupied == null) next.occupied = false;
    setPitchFB(next);
    localStorage.setItem(`kickr_pfb_${g.id}`, JSON.stringify(next));
    if (next.quality) submitFB(next.occupied, next.quality);
  };

  const submitMatchReport = async () => {
    if (reportSaving || !currentUser?.id) return;
    setReportSaving(true);
    try {
      if (pitchFB?.quality) await submitFB(pitchFB.occupied ?? false, pitchFB.quality);
      await syncNoShowReports();
      const payload = {
	        game_id: g.id,
	        user_id: currentUser.id,
	        game_rating: rating || null,
	        man_of_match_user_id: motmId || null,
	        pitch_was_occupied: pitchFB?.occupied ?? null,
        pitch_quality: pitchFB?.quality || null,
        no_show_user_ids: reported,
        submitted_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      };
	      let { error } = await supabase
	        .from("match_reports")
	        .upsert(payload, { onConflict: "game_id,user_id" });
	      if (error && /man_of_match_user_id|schema cache|column/i.test(`${error.message || ""} ${error.details || ""} ${error.hint || ""}`)) {
	        const legacyPayload = { ...payload };
	        delete legacyPayload.man_of_match_user_id;
	        ({ error } = await supabase
	          .from("match_reports")
	          .upsert(legacyPayload, { onConflict: "game_id,user_id" }));
	      }
	      if (error) console.warn("[match_reports.upsert] failed", error);
	      localStorage.setItem(`kickr_motm_${g.id}`, motmId || "");
	      localStorage.setItem(`kickr_match_reported_${g.id}`, "1");
	      supabase
	        .from("notifications")
	        .update({ read: true })
	        .eq("user_id", currentUser.id)
	        .eq("game_id", g.id)
	        .eq("type", "game_review")
	        .then(() => {}, () => {});
	      setReportSubmitted(true);
      setReportSaved(true);
      setShowReview(false);
      onReviewSubmitted?.(g.id);
    } catch (err) {
      console.warn("[match_reports.submit] failed", err);
    } finally {
      setReportSaving(false);
    }
  };

	  const motmPlayers = (g.players || []).filter(p => p.id);
	  const otherPlayers = (g.players || []).filter(p => p.id !== currentUser?.id);
	  const hasPitchFeedback = pitchFB?.occupied === true || pitchFB?.occupied === false || !!pitchFB?.quality;
	  const hasReportContent = rating > 0 || !!motmId || hasPitchFeedback || reported.length > 0;
  const reportComplete = reportSaved || reportSubmitted;
  const reviewState = reportComplete
    ? { label: t("reviewed"), tone: C.success, bg: successBg, Icon: CheckCircle2 }
    : hasReportContent
      ? { label: t("finishReport"), tone: C.warn, bg: warnBg, Icon: AlertCircle }
      : { label: t("wrapUp"), tone: C.accent, bg: C.accentBg, Icon: Flag };
  const openReview = (e) => {
    e.stopPropagation();
    setShowReview(true);
    onReviewOpen?.(g.id);
  };

  return (
    <>
      {/* Main game card */}
      <div style={{ ...card, overflow: "hidden", marginBottom: 0 }}>
        <div style={{ padding: "14px 16px", cursor: "pointer" }} onClick={onTap}>
          <div style={{ display: "flex", gap: 6, marginBottom: 8, alignItems: "center" }}>
            <LevelPill level={g.level} small />
            <span style={{ display: "inline-flex", padding: "2px 8px", borderRadius: 4, background: C.surface, fontSize: 11, fontWeight: 600, color: C.inkMid }}>{g.format}</span>
          </div>
          <div style={{ fontSize: 15, fontWeight: 700, color: C.ink, marginBottom: 3, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{g.title}</div>
          <div style={{ fontSize: 12, color: C.inkFaint, marginBottom: 10, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{g.location}</div>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: SPACE[3] }}>
            <span style={{ fontSize: 12, color: C.inkFaint, fontWeight: 700 }}>{g.time} · {g.hour}</span>
            <div style={{ display: "flex", alignItems: "center", gap: SPACE[2], minWidth: 0 }}>
              <button
                onClick={openReview}
                style={{ display: "inline-flex", alignItems: "center", gap: 6, minHeight: 32, padding: "6px 11px", borderRadius: RADIUS.pill, border: `1px solid ${reviewState.tone}44`, background: reviewState.bg, color: reviewState.tone, cursor: "pointer", fontSize: 12, fontWeight: 700, whiteSpace: "nowrap" }}
              >
                <reviewState.Icon size={13} {...ICON} />
                {reviewState.label}
              </button>
              {onRepeat && g.hostId === currentUser?.id && (
                <button onClick={e => { e.stopPropagation(); onRepeat(g); }} style={{ fontSize: 12, fontWeight: 700, color: C.accent, background: C.surface, border: `1px solid ${C.accent}`, borderRadius: RADIUS.pill, padding: "6px 12px", cursor: "pointer" }}>Repeat</button>
              )}
            </div>
          </div>
        </div>

      </div>

      {showReview && (
        <div
          onClick={() => setShowReview(false)}
          style={{ position: "fixed", inset: 0, zIndex: 260, background: "rgba(15, 23, 42, 0.36)", display: "flex", alignItems: "flex-end", justifyContent: "center" }}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-label={t("matchReportTitle")}
            onClick={e => e.stopPropagation()}
            style={{ width: "100%", maxWidth: 640, maxHeight: "90vh", background: C.bg, borderRadius: "24px 24px 0 0", boxShadow: "0 -18px 50px rgba(15,23,42,0.22)", overflow: "hidden", display: "flex", flexDirection: "column", animation: "slideUp 0.22s ease" }}
          >
            <div style={{ display: "flex", justifyContent: "center", padding: "10px 0 4px", flexShrink: 0 }}>
              <div style={{ width: 36, height: 4, borderRadius: 999, background: C.border }} />
            </div>

            <div style={{ padding: "10px 18px 14px", borderBottom: `1px solid ${C.border}`, flexShrink: 0 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: SPACE[3] }}>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: 12, fontWeight: 850, color: C.accent, marginBottom: 4 }}>{g.time} · {g.hour}</div>
                  <div style={{ fontSize: 22, lineHeight: 1.12, fontWeight: 950, color: C.ink }}>{t("matchReportTitle")}</div>
                  <div style={{ fontSize: 13, color: C.inkMid, marginTop: 4 }}>{t("matchReportHint")}</div>
                </div>
                <button
                  onClick={() => setShowReview(false)}
                  aria-label="Close"
                  style={{ width: 38, height: 38, borderRadius: "50%", border: `1px solid ${C.border}`, background: C.surface, color: C.inkMid, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", flexShrink: 0 }}
                >
                  <X size={18} {...ICON} />
                </button>
              </div>
            </div>

            <div style={{ overflowY: "auto", padding: "14px 18px calc(18px + env(safe-area-inset-bottom))", display: "flex", flexDirection: "column", gap: SPACE[3] }}>
              <div style={{ ...card, padding: SPACE[3], marginBottom: 0, boxShadow: "none" }}>
                <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 10, flexWrap: "wrap" }}>
                  <LevelPill level={g.level} small />
                  {g.format && <span style={{ display: "inline-flex", padding: "3px 9px", borderRadius: RADIUS.pill, background: C.surface, border: `1px solid ${C.border}`, fontSize: 11, fontWeight: 850, color: C.inkMid }}>{g.format}</span>}
                </div>
                <div style={{ fontSize: 17, fontWeight: 900, color: C.ink, lineHeight: 1.2, marginBottom: 4 }}>{g.title}</div>
                <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13, color: C.inkMid }}>
                  <MapPin size={14} {...ICON} />
                  <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{g.location}</span>
                </div>
              </div>

	              <section style={{ padding: SPACE[3], borderRadius: RADIUS.sm, border: `1px solid ${C.border}`, background: C.surface }}>
	                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: SPACE[2], marginBottom: SPACE[2] }}>
	                  <div style={{ fontSize: 15, fontWeight: 950, color: C.ink }}>{t("rateThisGame")}</div>
                  {rating > 0 && <span style={{ fontSize: 12, fontWeight: 950, color: C.warn, background: warnBg, borderRadius: RADIUS.pill, padding: "5px 9px" }}>{rating}/5</span>}
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(5, 1fr)", gap: 6 }}>
                  {[1,2,3,4,5].map(star => (
                    <button key={star} onClick={() => onRate(g.id, star)} aria-label={`${star} star rating`} style={{ minHeight: 44, borderRadius: RADIUS.sm, border: `1px solid ${rating >= star ? C.warn : C.border}`, background: rating >= star ? warnBg : C.bg, cursor: "pointer", fontSize: 25, color: rating >= star ? C.warn : C.inkFaint, lineHeight: 1 }}>★</button>
                  ))}
	                </div>
	              </section>

	              <section style={{ padding: SPACE[3], borderRadius: RADIUS.sm, border: `1px solid ${motmId ? `${C.accent}44` : C.border}`, background: motmId ? C.accentBg : C.surface }}>
	                <div style={{ display: "flex", gap: SPACE[2], alignItems: "flex-start", marginBottom: SPACE[3] }}>
	                  <div style={{ width: 34, height: 34, borderRadius: RADIUS.sm, background: C.bg, color: C.accent, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
	                    <Star size={17} {...ICON} />
	                  </div>
	                  <div style={{ minWidth: 0 }}>
	                    <div style={{ fontSize: 15, fontWeight: 950, color: C.ink }}>{t("manOfTheMatch")}</div>
	                    <div style={{ ...T.small, color: C.inkMid, marginTop: 2 }}>{t("manOfTheMatchHint")}</div>
	                  </div>
	                </div>
	                <div style={{ display: "flex", flexDirection: "column", gap: SPACE[2] }}>
	                  {motmPlayers.map(player => {
	                    const active = motmId === player.id;
	                    return (
	                      <button
	                        key={player.id}
	                        type="button"
	                        onClick={() => {
	                          const next = active ? "" : player.id;
	                          setMotmId(next);
	                          localStorage.setItem(`kickr_motm_${g.id}`, next);
	                        }}
	                        style={{ width: "100%", minHeight: 50, padding: "7px 12px", borderRadius: RADIUS.sm, border: `1px solid ${active ? C.accent : C.border}`, background: active ? C.bg : C.surface, color: C.ink, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "space-between", gap: SPACE[3], textAlign: "left" }}
	                      >
	                        <span style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 0 }}>
	                          <Avatar initials={player.avatar || communityInitials(player.name).slice(0, 2)} avatarUrl={player.avatarUrl} size={34} accent={active} />
	                          <span style={{ minWidth: 0 }}>
	                            <span style={{ display: "block", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", fontSize: 14, fontWeight: 900 }}>{player.name}</span>
	                            {player.username && <span style={{ display: "block", color: C.inkMid, fontSize: 12, fontWeight: 800 }}>@{player.username}</span>}
	                          </span>
	                        </span>
	                        <span style={{ width: 22, height: 22, borderRadius: "50%", border: `1px solid ${active ? C.accent : C.border}`, background: active ? C.accent : C.surface, color: active ? C.accentText : C.inkFaint, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
	                          {active ? <Check size={13} {...ICON} /> : null}
	                        </span>
	                      </button>
	                    );
	                  })}
	                </div>
	              </section>

	              <section style={{ padding: SPACE[3], borderRadius: RADIUS.sm, border: `1px solid ${reported.length > 0 ? warnBorder : C.border}`, background: reported.length > 0 ? warnBg : C.surface }}>
                <div style={{ display: "flex", gap: SPACE[2], alignItems: "flex-start", marginBottom: SPACE[3] }}>
                  <div style={{ width: 34, height: 34, borderRadius: RADIUS.sm, background: reported.length > 0 ? C.bg : C.accentBg, color: reported.length > 0 ? C.warn : C.accent, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                    <Users size={17} {...ICON} />
                  </div>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontSize: 15, fontWeight: 950, color: C.ink }}>{t("noShowQuestion")}</div>
                    <div style={{ ...T.small, color: C.inkMid, marginTop: 2 }}>{otherPlayers.length > 0 ? t("noShowCaution") : t("noPlayersToReport")}</div>
                  </div>
                </div>

                {otherPlayers.length > 0 && (
                  <div style={{ display: "flex", flexDirection: "column", gap: SPACE[2] }}>
                    <button
                      onClick={clearNoShows}
                      style={{ width: "100%", minHeight: 48, padding: "0 12px", borderRadius: RADIUS.sm, border: `1px solid ${reported.length === 0 ? C.accent : C.border}`, background: reported.length === 0 ? C.accentBg : C.bg, color: reported.length === 0 ? C.accent : C.ink, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "space-between", gap: SPACE[3], fontWeight: 900 }}
                    >
                      <span style={{ display: "flex", alignItems: "center", gap: 10 }}>
                        <CheckCircle2 size={18} {...ICON} />
                        {t("everyoneShowed")}
                      </span>
                      {reported.length === 0 && <span style={{ fontSize: 12, color: C.accent }}>{t("selected")}</span>}
                    </button>

                    {otherPlayers.map(player => {
                      const active = reported.includes(player.id);
                      return (
                        <button
                          key={player.id}
                          onClick={() => toggleNoShow(player.id)}
                          style={{ width: "100%", minHeight: 54, padding: "8px 12px", borderRadius: RADIUS.sm, border: `1px solid ${active ? C.warn : C.border}`, background: active ? warnBg : C.bg, color: C.ink, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "space-between", gap: SPACE[3], textAlign: "left" }}
                        >
                          <span style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 0 }}>
                            <span style={{ width: 34, height: 34, borderRadius: "50%", background: active ? C.warn : C.surface, color: active ? "#fff" : C.inkMid, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, fontWeight: 950, flexShrink: 0 }}>
                              {active ? "!" : communityInitials(player.name).slice(0, 1)}
                            </span>
                            <span style={{ minWidth: 0 }}>
                              <span style={{ display: "block", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", fontSize: 14, fontWeight: 900 }}>{player.name}</span>
                              <span style={{ display: "block", color: active ? C.warn : C.inkMid, fontSize: 12, fontWeight: 800 }}>{active ? t("reportedNoShow") : t("markNoShow")}</span>
                            </span>
                          </span>
                          <span style={{ width: 22, height: 22, borderRadius: "50%", border: `1px solid ${active ? C.warn : C.border}`, background: active ? C.warn : C.surface, color: active ? "#fff" : C.inkFaint, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                            {active ? <Check size={13} {...ICON} /> : null}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                )}
              </section>

              <section style={{ padding: SPACE[3], borderRadius: RADIUS.sm, border: `1px solid ${C.border}`, background: C.surface }}>
                <div style={{ display: "flex", alignItems: "center", gap: SPACE[2], marginBottom: SPACE[3] }}>
                  <div style={{ width: 34, height: 34, borderRadius: RADIUS.sm, background: C.accentBg, color: C.accent, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                    <MapPin size={17} {...ICON} />
                  </div>
                  <div>
                    <div style={{ fontSize: 15, fontWeight: 950, color: C.ink }}>{t("ratePitchOptional")}</div>
                    <div style={{ ...T.small, color: C.inkMid, marginTop: 2 }}>{t("ratePitchHelp")}</div>
                  </div>
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: SPACE[2], marginBottom: SPACE[3] }}>
                  {[[false, t("pitchFree")], [true, t("pitchTaken")]].map(([val, label]) => {
                    const active = pitchFB?.occupied === val;
                    return (
                      <button key={String(val)} onClick={() => updatePitchFeedback({ occupied: val })} style={{ minHeight: 42, borderRadius: RADIUS.sm, border: `1px solid ${active ? (val ? C.warn : C.accent) : C.border}`, background: active ? (val ? warnBg : C.accentBg) : C.bg, color: active ? (val ? C.warn : C.accent) : C.inkMid, fontSize: 13, fontWeight: 900, cursor: "pointer" }}>
                        {label}
                      </button>
                    );
                  })}
                </div>
	                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: SPACE[2], marginBottom: SPACE[2] }}>
	                  <span style={{ ...T.small, color: C.inkMid, fontWeight: 850 }}>{t("surface")}</span>
	                  {(pitchFB?.quality || 0) > 0 && <span style={{ fontSize: 12, fontWeight: 950, color: C.warn, background: warnBg, borderRadius: RADIUS.pill, padding: "5px 9px" }}>{pitchFB.quality}/5</span>}
	                </div>
	                <div style={{ display: "grid", gridTemplateColumns: "repeat(5, 1fr)", gap: 6 }}>
	                  {[1,2,3,4,5].map(star => (
	                    <button
	                      key={star}
	                      onClick={() => updatePitchFeedback({ quality: star })}
	                      aria-label={`${star} star pitch rating`}
	                      style={{ minHeight: 44, borderRadius: RADIUS.sm, border: `1px solid ${(pitchFB?.quality || 0) >= star ? C.warn : C.border}`, background: (pitchFB?.quality || 0) >= star ? warnBg : C.bg, cursor: "pointer", fontSize: 25, color: (pitchFB?.quality || 0) >= star ? C.warn : C.inkFaint, lineHeight: 1 }}
	                    >
	                      ★
	                    </button>
	                  ))}
	                </div>
	              </section>

              <div style={{ display: "grid", gridTemplateColumns: "0.85fr 1.35fr", gap: SPACE[2], position: "sticky", bottom: -18, paddingTop: SPACE[1], background: C.bg }}>
                <button onClick={() => setShowReview(false)} style={{ ...btnSecondary, padding: "13px 0", borderRadius: RADIUS.sm }}>{t("later")}</button>
                <button
                  onClick={submitMatchReport}
                  disabled={reportSaving}
                  style={{ ...btnPrimary, width: "100%", padding: "13px 0", borderRadius: RADIUS.sm, opacity: reportSaving ? 0.6 : 1, cursor: reportSaving ? "not-allowed" : "pointer" }}
                >
                  {reportSaving ? t("saving") : (hasReportContent ? t("submitReview") : t("everythingGood"))}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function RepeatGameSheet({ game, onClose, onRepeat, creating = false }) {
  const { C, T, btnPrimary, btnSecondary } = useTheme();
  const [selected, setSelected] = useState(() => (game.players || []).filter(p => !p.isMe).map(p => p.id));
  const [searchQ, setSearchQ] = useState("");
  const others = (game.players || []).filter(p => !p.isMe);
  const filtered = others.filter(p => !searchQ || p.name.toLowerCase().includes(searchQ.toLowerCase()));
  const toggle = id => setSelected(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);

  return (
    <div onClick={onClose} style={{ position: "fixed", inset: 0, zIndex: 200, background: "rgba(0,0,0,0.5)", display: "flex", alignItems: "flex-end" }}>
      <div onClick={e => e.stopPropagation()} style={{ width: "100%", background: C.bg, borderRadius: "20px 20px 0 0", maxHeight: "88vh", display: "flex", flexDirection: "column", animation: "slideUp 0.25s ease" }}>
        {/* Handle */}
        <div style={{ display: "flex", justifyContent: "center", padding: "10px 0 0", flexShrink: 0 }}>
          <div style={{ width: 32, height: 3, borderRadius: 2, background: C.border }} />
        </div>
        {/* Header */}
        <div style={{ padding: "12px 20px 14px", borderBottom: `1px solid ${C.border}`, flexShrink: 0 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
            <div>
              <div style={{ ...T.h2, marginBottom: 2 }}>Repeat game</div>
              <div style={{ fontSize: 13, color: C.inkMid }}>{game.title} · {game.location}</div>
            </div>
            <button onClick={onClose} aria-label="Close" style={{ width: 32, height: 32, borderRadius: "50%", background: C.surface, border: "none", cursor: "pointer", color: C.inkMid, flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center", transition: "background 0.12s" }}
              onMouseEnter={e => e.currentTarget.style.background = C.border}
              onMouseLeave={e => e.currentTarget.style.background = C.surface}
            ><X size={16} {...ICON} /></button>
          </div>
          {/* Game summary chips */}
          <div style={{ display: "flex", gap: 6, marginTop: 10, flexWrap: "wrap" }}>
            {[game.format, game.level].filter(Boolean).map(tag => (
              <span key={tag} style={{ fontSize: 11, fontWeight: 600, color: C.inkMid, background: C.surface, border: `1px solid ${C.border}`, borderRadius: 99, padding: "3px 10px" }}>{tag}</span>
            ))}
          </div>
        </div>
        {/* Player list */}
        <div style={{ flex: 1, overflowY: "auto", padding: "14px 20px" }}>
          {others.length === 0 ? (
            <div style={{ textAlign: "center", padding: "32px 0", color: C.inkFaint, fontSize: 13 }}>No other players from this game</div>
          ) : (
            <>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: C.inkMid, textTransform: "uppercase", letterSpacing: "0.05em" }}>Invite players from last time</div>
                <button onClick={() => setSelected(selected.length === others.length ? [] : others.map(p => p.id))} style={{ fontSize: 12, color: C.accent, background: "none", border: "none", cursor: "pointer", fontWeight: 600 }}>
                  {selected.length === others.length ? "Deselect all" : "Select all"}
                </button>
              </div>
              {others.length > 4 && (
                <div style={{ position: "relative", marginBottom: 10 }}>
                  <svg style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", pointerEvents: "none" }} width="12" height="12" viewBox="0 0 24 24" fill="none" stroke={C.inkFaint} strokeWidth="2" strokeLinecap="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
                  <input value={searchQ} onChange={e => setSearchQ(e.target.value)} placeholder="Search players…" style={{ width: "100%", padding: "8px 10px 8px 30px", borderRadius: 8, border: `1px solid ${C.border}`, background: C.surface, fontSize: 13, color: C.ink, boxSizing: "border-box" }} />
                </div>
              )}
              <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                {filtered.map(p => {
                  const on = selected.includes(p.id);
                  return (
                    <button key={p.id} onClick={() => toggle(p.id)} style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 12px", borderRadius: 10, border: `1px solid ${on ? C.accent : C.border}`, background: on ? C.accentBg : "transparent", cursor: "pointer", textAlign: "left", transition: "all 0.12s" }}>
                      <div style={{ width: 34, height: 34, borderRadius: "50%", background: on ? C.accent : C.surface, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, fontWeight: 700, color: on ? C.accentText : C.inkMid, flexShrink: 0, transition: "all 0.12s" }}>
                        {p.avatar || p.name?.slice(0,2).toUpperCase()}
                      </div>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: 14, fontWeight: on ? 600 : 400, color: C.ink }}>{p.name}</div>
                        {p.level && <div style={{ fontSize: 11, color: C.inkFaint, marginTop: 1 }}>{p.level}</div>}
                      </div>
                      <div style={{ width: 20, height: 20, borderRadius: "50%", border: `2px solid ${on ? C.accent : C.border}`, background: on ? C.accent : "transparent", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, transition: "all 0.12s" }}>
                        {on && <svg width="10" height="10" viewBox="0 0 12 12" fill="none"><polyline points="2,6 5,9 10,3" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>}
                      </div>
                    </button>
                  );
                })}
              </div>
            </>
          )}
        </div>
        {/* Footer */}
        <div style={{ padding: "12px 20px 32px", borderTop: `1px solid ${C.border}`, flexShrink: 0, display: "flex", gap: 10 }}>
          <button onClick={onClose} style={{ ...btnSecondary, flex: 1, padding: 13, borderRadius: 10 }}>Cancel</button>
          <button onClick={() => onRepeat(selected)} disabled={creating} style={{ ...btnPrimary, flex: 2, padding: 13, borderRadius: 10, fontSize: 15, opacity: creating ? 0.6 : 1, cursor: creating ? "not-allowed" : "pointer" }}>
            {creating ? "Creating..." : selected.length > 0 ? `Create game · invite ${selected.length}` : "Create game"}
          </button>
        </div>
      </div>
    </div>
  );
}

function MyGamesTab({ upcomingGames, pastGames, savedGames, renderList, onDiscover, onTap, currentUser, onRefresh, onRepeatGame, view: viewProp, setView: setViewProp, reviewGameIdToOpen = null, onReviewGameOpened }) {
  const { C, T, btnPrimary, inputBase, SPACE, RADIUS, SHADOW, TYPE } = useTheme();
  const ui = { C, SPACE, RADIUS, SHADOW };
  // Controlled when parent passes view+setView (used by "See all past games"
  // deep-link from ProfileScreen); otherwise fall back to local state.
  const [viewLocal, setViewLocal] = useState("joined");
  const view = viewProp ?? viewLocal;
  const setView = setViewProp ?? setViewLocal;
  const [mySearch, setMySearch] = useState("");
  const [repeatTarget, setRepeatTarget] = useState(null); // game to repeat
  const [repeatCreating, setRepeatCreating] = useState(false);
  const [expandedReportGameId, setExpandedReportGameId] = useState(null);
  const [ratings, setRatings] = useState(() => { try { return JSON.parse(localStorage.getItem("kickr_ratings") || "{}"); } catch { return {}; } });
  const rate = async (gameId, stars, currentUser) => {
    const next = { ...ratings, [gameId]: stars };
    setRatings(next);
    localStorage.setItem("kickr_ratings", JSON.stringify(next));
    if (currentUser) {
      try {
        await supabase.from("game_ratings").upsert(
          { game_id: gameId, rater_id: currentUser.id, rating: stars, created_at: new Date().toISOString() },
          { onConflict: "game_id,rater_id" }
        );
      } catch { /* localStorage already saved — DB sync is best-effort */ }
    }
  };
  const filterBySearch = (list) => {
    if (!mySearch.trim()) return list;
    const q = mySearch.toLowerCase();
    return list.filter(g => g.title.toLowerCase().includes(q) || (g.location || "").toLowerCase().includes(q));
  };

  // Most recent hosted past game for the "Repeat" banner
  const lastHostedGame = [...pastGames].reverse().find(g => g.hostId === currentUser?.id);
  const isReportComplete = (game) => {
    if (!game?.id) return true;
    if (localStorage.getItem(`kickr_match_reported_${game.id}`) === "1") return true;
    return !!(ratings[game.id] && localStorage.getItem(`kickr_pfb_${game.id}`));
  };
  const pendingReportGames = [...pastGames]
    .sort((a, b) => (b.endTime || gameStartMs(b)) - (a.endTime || gameStartMs(a)))
    .filter(game => !isReportComplete(game));
  const pendingReportCount = pendingReportGames.length;

	  useEffect(() => {
	    if (!reviewGameIdToOpen) return;
	    setView("past");
	    setExpandedReportGameId(reviewGameIdToOpen);
	    onReviewGameOpened?.();
	  }, [onReviewGameOpened, reviewGameIdToOpen, setView]);

  return (
    <div style={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden" }}>
      {repeatTarget && (
        <RepeatGameSheet
          game={repeatTarget}
          onClose={() => setRepeatTarget(null)}
          creating={repeatCreating}
          onRepeat={async (inviteIds) => {
            if (!onRepeatGame || repeatCreating) return;
            setRepeatCreating(true);
            try {
              await onRepeatGame(repeatTarget, inviteIds);
              setRepeatTarget(null);
            } finally {
              setRepeatCreating(false);
            }
          }}
        />
      )}
      {/* Toolbar — full-width, matches the Discover/Communities chrome. */}
      <div style={{ padding: `0 ${SPACE[4]}px ${SPACE[3]}px` }}>
        {/* Repeat last game quick action — subtle brand tint so it belongs to
            the same football theme as the rest of My Games. */}
        {lastHostedGame && (
          <button onClick={() => setRepeatTarget(lastHostedGame)} style={{ width: "100%", marginBottom: SPACE[3], padding: `${SPACE[3]}px ${SPACE[3] + 2}px`, borderRadius: RADIUS.md, border: `1px solid ${C.accent}22`, background: C.accentBg, boxShadow: SHADOW.sm, cursor: "pointer", display: "flex", alignItems: "center", gap: SPACE[3], textAlign: "left", transition: "transform 0.12s, box-shadow 0.12s" }}
            onMouseEnter={e => { e.currentTarget.style.transform = "translateY(-1px)"; e.currentTarget.style.boxShadow = SHADOW.md; }}
            onMouseLeave={e => { e.currentTarget.style.transform = ""; e.currentTarget.style.boxShadow = SHADOW.sm; }}
          >
            <div style={{ width: 36, height: 36, borderRadius: RADIUS.sm, background: C.surface, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, color: C.accent, border: `1px solid ${C.accent}22` }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                <path d="M17 2l4 4-4 4"/><path d="M3 11V9a4 4 0 0 1 4-4h14"/>
                <path d="M7 22l-4-4 4-4"/><path d="M21 13v2a4 4 0 0 1-4 4H3"/>
              </svg>
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 14, fontWeight: 600, color: C.ink }}>Repeat last game</div>
              <div style={{ fontSize: 12, color: C.inkMid, marginTop: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{lastHostedGame.title} · {lastHostedGame.location}</div>
            </div>
            <ChevronRight size={16} {...ICON} color={C.accent} />
          </button>
        )}

        {/* Pill search input — matches Communities/Discover */}
        <div style={{ position: "relative", marginBottom: SPACE[3] }}>
          <Search size={15} {...ICON} color={C.inkFaint} style={{ position: "absolute", left: SPACE[4], top: "50%", transform: "translateY(-50%)", pointerEvents: "none" }} />
          <input value={mySearch} onChange={e => setMySearch(e.target.value)} placeholder={t("searchMyGames")} style={{ ...inputBase, paddingLeft: SPACE[7] - 4, borderRadius: RADIUS.pill, background: C.surface }} />
        </div>

        {/* Segmented tabs — pending reviews live as a quiet badge on Past instead
            of another full-width card in the Upcoming view. */}
        <div style={{ ...uiSegmentShell(ui, { display: "grid", gridTemplateColumns: "repeat(3, 1fr)", width: "100%" }), padding: 4 }}>
          {[["joined", t("upcoming")], ["past", t("past")], ["saved", t("saved")]].map(([v, label]) => {
            const active = view === v;
            const showReviewBadge = v === "past" && pendingReportCount > 0;
            return (
              <button key={v} onClick={() => setView(v)} style={uiSegmentButton(ui, active, { minWidth: 0, padding: `${SPACE[2]}px 0`, activeWeight: 800, inactiveWeight: 650 })}>
                <span style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 6, minWidth: 0 }}>
                  <span>{label}</span>
                  {showReviewBadge && (
                    <span
                      aria-label={`${pendingReportCount} game${pendingReportCount === 1 ? "" : "s"} to review`}
                      style={{
                        minWidth: 18,
                        height: 18,
                        padding: "0 5px",
                        borderRadius: 999,
                        background: active ? C.accent : C.warn,
                        color: active ? C.accentText : "#fff",
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: 10,
                        fontWeight: 950,
                        lineHeight: 1,
                        boxShadow: active ? "none" : `0 0 0 2px ${C.surface}`,
                      }}
                    >
                      {pendingReportCount}
                    </span>
                  )}
                </span>
              </button>
            );
          })}
        </div>
      </div>
      <PullToRefresh style={{ flex: 1, padding: SPACE[4] }} onRefresh={onRefresh || (() => {})}>
        {view === "joined" && (() => {
          const list = filterBySearch(upcomingGames);
          return upcomingGames.length === 0
            ? <div style={{ textAlign: "center", padding: "60px 20px" }}>
                <p style={{ ...T.body, marginBottom: 16 }}>{t("noUpcomingGames")}</p>
                <button onClick={onDiscover} style={{ ...btnPrimary, padding: "10px 20px", borderRadius: 8 }}>{t("findAGame")}</button>
              </div>
            : <>
                {list.length === 0
                  ? <div style={{ textAlign: "center", padding: "40px 20px" }}>
                      <p style={{ ...T.body, marginBottom: 8 }}>{t("noGamesMatch", { q: mySearch })}</p>
                      <button onClick={() => setMySearch("")} style={{ ...T.small, background: "none", border: "none", cursor: "pointer", color: C.accent }}>{t("clearSearch")}</button>
                    </div>
                  : renderList(list)}
              </>;
        })()}
        {view === "past" && (() => {
          const list = filterBySearch(pastGames);
          return pastGames.length === 0
            ? <div style={{ textAlign: "center", padding: "60px 20px" }}>
                <p style={{ ...T.body, marginBottom: 16 }}>{t("noPastGames")}</p>
                <button onClick={onDiscover} style={{ ...btnPrimary, padding: "10px 20px", borderRadius: 8 }}>{t("findAGame")}</button>
              </div>
            : list.length === 0
              ? <div style={{ textAlign: "center", padding: "40px 20px" }}>
                  <p style={{ ...T.body, marginBottom: 8 }}>{t("noGamesMatch", { q: mySearch })}</p>
                  <button onClick={() => setMySearch("")} style={{ ...T.small, background: "none", border: "none", cursor: "pointer", color: C.accent }}>{t("clearSearch")}</button>
                </div>
		              : <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
		                  {list.map(g => <PastGameCard key={g.id} g={g} rating={ratings[g.id] || 0} onRate={(gid, stars) => rate(gid, stars, currentUser)} onTap={() => onTap && onTap(g)} currentUser={currentUser} onRepeat={onRepeatGame ? (game) => setRepeatTarget(game) : null} initialOpen={expandedReportGameId === g.id} onReviewOpen={setExpandedReportGameId} onReviewSubmitted={gid => setExpandedReportGameId(prev => prev === gid ? null : prev)} />)}
		                </div>;
        })()}
        {view === "saved" && (() => {
          const list = filterBySearch(savedGames);
          return savedGames.length === 0
            ? <div style={{ textAlign: "center", padding: "56px 20px", maxWidth: 300, margin: "0 auto" }}>
                <div style={{ width: 48, height: 48, borderRadius: "50%", background: C.accentBg, color: C.accent, margin: `0 auto ${SPACE[3]}px`, display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <Bookmark size={20} {...ICON} />
                </div>
                <p style={{ ...T.body, color: C.ink, fontWeight: 600, marginBottom: 4 }}>{t("noSavedGames")}</p>
                <p style={{ ...T.caption, margin: `0 0 ${SPACE[4]}px` }}>Save games from Discover and they will show up here.</p>
                <button onClick={onDiscover} style={{ ...btnPrimary, padding: "10px 20px", borderRadius: RADIUS.pill }}>{t("browseGames")}</button>
              </div>
            : list.length === 0
              ? <div style={{ textAlign: "center", padding: "40px 20px" }}>
                  <p style={{ ...T.body, marginBottom: 8 }}>{t("noGamesMatch", { q: mySearch })}</p>
                  <button onClick={() => setMySearch("")} style={{ ...T.small, background: "none", border: "none", cursor: "pointer", color: C.accent }}>{t("clearSearch")}</button>
                </div>
              : renderList(list);
        })()}
      </PullToRefresh>
    </div>
  );
}

// ─── Profile Screen ───────────────────────────────────────────────────────────
function ProfileScreen({ hostedCount, joinedCount, user, usernameEnabled = true, onProfileSave, onOpenSettings, onOpenAllPast, recentPastGames }) {
  const { C, T, btnSecondary, inputBase, dangerBg, dangerBorder, successBg, successBorder, SPACE, RADIUS, SHADOW, TYPE } = useTheme();
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const userName = user?.user_metadata?.name || user?.email?.split("@")[0] || "Player";
  const userInitials = userName.split(" ").map(w => w[0]).join("").slice(0, 2).toUpperCase();
  const [p, setP] = useState({ name: userName, username: "", age: "", level: "Intermediate", position: "", bio: "", avatar_url: null, games_played: 0, no_shows: 0 });
  const [d, setD] = useState(p);
  const [avatarUploading, setAvatarUploading] = useState(false);
  const avatarFileRef = useRef(null);
  const [hostRating, setHostRating] = useState(null); // { avg_rating, rating_count } or null

  useEffect(() => {
    if (!user) return;
    const fallbackName = user?.user_metadata?.name || user?.email?.split("@")[0] || "Player";
    supabase.from("profiles").select("*").eq("id", user.id).maybeSingle().then(({ data }) => {
      if (data) {
        const loaded = { name: data.name || fallbackName, username: data.username || "", age: data.age || "", level: data.level || "Intermediate", position: data.position || "", bio: data.bio || "", avatar_url: data.avatar_url || null, games_played: data.games_played || 0, no_shows: data.no_shows || 0 };
        setP(loaded); setD(loaded);
      }
    });
    // Best-effort: pull host rating aggregates if the view exists.
    supabase.from("host_rating_stats").select("avg_rating, rating_count").eq("user_id", user.id).maybeSingle()
      .then(({ data }) => { if (data) setHostRating(data); })
      .catch(() => { /* view may not exist yet — silently ignore */ });
  }, [user]);

  const [saveError, setSaveError] = useState(null);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const handleAgeChange = (value) => {
    const age = String(value || "").replace(/\D/g, "").slice(0, 2);
    setD(prev => ({ ...prev, age }));
  };
  const ageIsValid = (value = d.age) => {
    if (String(value || "").trim() === "") return true;
    const age = Number.parseInt(value, 10);
    return Number.isFinite(age) && age >= 10 && age <= 99;
  };

  const handleAvatarChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setAvatarUploading(true);
    setSaveError(null);
    try {
      const ext = (file.name.split(".").pop() || "jpg").toLowerCase();
      const path = `${user.id}.${ext}`;
      const { error: upErr } = await supabase.storage.from("Avatar").upload(path, file, { upsert: true, contentType: file.type });
      if (upErr) throw upErr;
      const { data } = supabase.storage.from("Avatar").getPublicUrl(path);
      const url = data.publicUrl;
      // Use UPDATE (not upsert) — profile always exists by this point, avoids INSERT RLS check
      const { error: dbErr } = await supabase.from("profiles").update({ avatar_url: url }).eq("id", user.id);
      if (dbErr) throw dbErr;
      setD(prev => ({ ...prev, avatar_url: url }));
      setP(prev => ({ ...prev, avatar_url: url }));
      if (onProfileSave) onProfileSave({ avatar_url: url });
    } catch (err) {
      setSaveError(err.message?.includes("Bucket not found") ? "Storage not set up — ask the admin to create an \"Avatar\" bucket in Supabase." : (err.message || "Upload failed — try again"));
    }
    finally { setAvatarUploading(false); }
  };

  const save = async () => {
    if (!d.name.trim()) return;
    const username = normalizeUsernameInput(d.username);
    const usernameError = usernameEnabled ? getUsernameError(username) : "";
    if (usernameError) {
      setSaveError(usernameError);
      return;
    }
    if (!ageIsValid()) {
      setSaveError("Enter an age between 10 and 99, or leave it blank.");
      return;
    }
    setSaving(true); setSaveError(null); setSaveSuccess(false);
    try {
      if (usernameEnabled) {
        const available = await checkUsernameAvailable(username, user.id);
        if (!available) {
          setSaveError(t("usernameTaken"));
          return;
        }
      }
      const parsedAge = d.age ? Number.parseInt(d.age, 10) : null;
      const updatePayload = { name: d.name.trim(), age: parsedAge, level: d.level, position: d.position, bio: d.bio, avatar_url: d.avatar_url };
      if (usernameEnabled) updatePayload.username = username;
      let { error } = await supabase.from("profiles").update(updatePayload).eq("id", user.id);
      if (error?.message?.includes("avatar_url") || error?.message?.includes("schema cache")) {
        const { avatar_url: _av, ...rest } = updatePayload;
        ({ error } = await supabase.from("profiles").update(rest).eq("id", user.id));
      }
      if (error) throw error;
      const savedProfile = { ...d, username: usernameEnabled ? username : d.username || "", age: parsedAge || "" };
      setP(savedProfile); setD(savedProfile); setEditing(false);
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 2500);
      if (onProfileSave) onProfileSave({ name: d.name.trim(), ...(usernameEnabled ? { username } : {}), age: parsedAge, level: d.level, position: d.position, avatar_url: d.avatar_url });
    } catch (e) {
      setSaveError(isUsernameConflictError(e) ? t("usernameTaken") : (e.message || "Failed to save — try again"));
    } finally { setSaving(false); }
  };

  const avatarSrc = p.avatar_url;

  // Reliability score is computed once for both the chip in the hero and the
  // "X% reliable · Y games" subtitle. Keeps the two displays in sync.
  const gp = p.games_played || (joinedCount + hostedCount) || 0;
  const ns = p.no_shows || 0;
  const relScore = gp > 0 ? Math.round(((gp - ns) / gp) * 100) : null;
  const relColor = relScore === null ? C.inkFaint : relScore >= 90 ? C.success : relScore >= 70 ? C.warn : C.danger;

  const statItems = [
    [t("joinedStat"), joinedCount, Calendar],
    [t("hostedGames"), hostedCount, ShieldCheck],
    [t("avgRatingReceived"), hostRating?.avg_rating != null ? hostRating.avg_rating.toFixed(1) : "—", Star],
    [t("memberSince"), user?.created_at ? new Date(user.created_at).toLocaleDateString("en-GB", { month: "short", year: "numeric" }) : "—", Clock],
  ];

  return (
    <div style={{ height: "100%", overflowY: "auto", background: C.bg }}>
      <div style={{ padding: `${SPACE[3]}px ${SPACE[4]}px ${SPACE[5]}px` }}>
        <div style={{
          position: "relative", overflow: "hidden",
          borderRadius: RADIUS.lg, background: C.surface,
          color: C.ink, padding: SPACE[4],
          border: `1px solid ${C.border}`,
          boxShadow: SHADOW.sm,
        }}>
          <div style={{ position: "relative", display: "flex", justifyContent: "flex-end", alignItems: "flex-start", gap: SPACE[3], marginBottom: SPACE[4] }}>
            {onOpenSettings && (
              <button onClick={onOpenSettings} aria-label={t("settings")} style={{ width: 36, height: 36, borderRadius: "50%", background: C.bg, border: `1px solid ${C.border}`, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", color: C.inkMid }}>
                <SettingsIcon size={19} {...ICON} />
              </button>
            )}
          </div>

          <div style={{ position: "relative", display: "flex", alignItems: "center", gap: SPACE[4] }}>
            <div style={{ position: "relative", flexShrink: 0 }}>
              <input ref={avatarFileRef} type="file" accept="image/*" style={{ display: "none" }} onChange={handleAvatarChange} />
              <button onClick={() => editing && avatarFileRef.current?.click()} style={{ width: 78, height: 78, borderRadius: "50%", border: `3px solid ${C.accentBg}`, padding: 0, cursor: editing ? "pointer" : "default", background: C.accent, position: "relative", overflow: "hidden" }}>
                <div style={{ position: "absolute", inset: 0, borderRadius: "50%", background: C.accent, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 30, fontWeight: 800, color: C.accentText }}>{userInitials}</div>
                {avatarSrc && <img src={avatarSrc} alt="avatar" onError={e => { e.currentTarget.style.display = "none"; }} style={{ position: "absolute", inset: 0, width: "100%", height: "100%", borderRadius: "50%", objectFit: "cover", background: C.accent }} />}
                {editing && (
                  <div style={{ position: "absolute", inset: 0, borderRadius: "50%", background: "rgba(0,0,0,0.42)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                    {avatarUploading
                      ? <div style={{ width: 18, height: 18, borderRadius: "50%", border: "2px solid rgba(255,255,255,0.4)", borderTopColor: "#fff", animation: "spin 0.8s linear infinite" }} />
                      : <Camera size={20} {...ICON} color="#fff" />
                    }
                  </div>
                )}
              </button>
            </div>

            <div style={{ flex: 1, minWidth: 0 }}>
              {editing
                ? <>
                    <input value={d.name} onChange={e => setD({...d, name: e.target.value})} style={{ ...inputBase, fontSize: 20, fontWeight: 700, padding: "8px 12px", borderRadius: RADIUS.sm }} />
                    {usernameEnabled && (
                      <div style={{ display: "flex", alignItems: "center", border: `1px solid ${C.border}`, borderRadius: RADIUS.sm, background: C.surface, overflow: "hidden", marginTop: 8 }}>
                        <span style={{ paddingLeft: 12, color: C.inkFaint, fontSize: 13, fontWeight: 850 }}>@</span>
                        <input
                          value={d.username || ""}
                          onChange={e => setD({ ...d, username: normalizeUsernameInput(e.target.value) })}
                          placeholder={t("usernamePlaceholder")}
                          autoCapitalize="none"
                          autoCorrect="off"
                          spellCheck={false}
                          style={{ ...inputBase, border: "none", background: "transparent", borderRadius: 0, padding: "8px 10px 8px 2px", fontSize: 13, fontWeight: 750 }}
                        />
                      </div>
                    )}
                  </>
                : <>
                    <h1 style={{ ...TYPE.h1, fontSize: 24, color: C.ink, margin: 0, lineHeight: 1.12, overflowWrap: "anywhere" }}>{p.name}</h1>
                    {p.username && <div style={{ fontSize: 13, fontWeight: 750, color: C.inkMid, marginTop: 5, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>@{p.username}</div>}
                  </>
              }
              {!editing && (
                <div style={{ display: "flex", gap: SPACE[2], flexWrap: "wrap", marginTop: SPACE[3] }}>
                  <span style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 12, fontWeight: 700, color: C.accent, background: C.accentBg, borderRadius: RADIUS.pill, padding: "5px 10px" }}>
                    <span style={{ width: 6, height: 6, borderRadius: "50%", background: C.accent }} />
                    {tLevel(p.level)}
                  </span>
                  {p.position && <span style={{ fontSize: 12, fontWeight: 700, color: C.inkMid, background: C.bg, borderRadius: RADIUS.pill, padding: "5px 10px" }}>{p.position}</span>}
                  {relScore !== null && (
                    <span style={{ display: "inline-flex", alignItems: "center", gap: 5, fontSize: 12, fontWeight: 700, color: relColor, background: `${relColor}18`, borderRadius: RADIUS.pill, padding: "5px 10px" }}>
                      <ShieldCheck size={13} {...ICON} />
                      {relScore}%
                    </span>
                  )}
                </div>
              )}
            </div>
          </div>

          <div style={{ position: "relative", display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: SPACE[2], marginTop: SPACE[5] }}>
            {statItems.map(([label, value, StatIcon]) => (
              <div key={label} style={{ minWidth: 0, padding: SPACE[3], borderRadius: RADIUS.sm, background: C.bg }}>
                {React.createElement(StatIcon, { size: 15, ...ICON, color: C.inkFaint })}
                <div style={{ fontSize: 20, fontWeight: 800, color: C.ink, lineHeight: 1.1, marginTop: SPACE[2], whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{value}</div>
                <div style={{ fontSize: 10, fontWeight: 700, color: C.inkFaint, marginTop: 3, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", textTransform: "uppercase" }}>{label}</div>
              </div>
            ))}
          </div>

          {p.bio && !editing && (
            <div style={{ position: "relative", fontSize: 14, color: C.inkMid, lineHeight: 1.45, marginTop: SPACE[4] }}>{p.bio}</div>
          )}

          <button onClick={() => editing ? save() : setEditing(true)} style={{ ...btnSecondary, position: "relative", marginTop: SPACE[4], padding: "9px 18px", fontSize: 13, fontWeight: 700, background: editing ? C.accent : C.surface, color: editing ? C.accentText : C.ink, borderColor: editing ? C.accent : C.border, borderRadius: RADIUS.pill }}>
            {saving ? t("saving") : editing ? t("saveProfile") : t("editProfile")}
          </button>
        </div>
      </div>

      {/* Stats: kept only in edit mode because the player card already carries
          the public numbers; duplicating them made the profile feel noisy. */}
      {editing && <div style={{ display: "flex", margin: "0 16px 20px", background: C.surface, border: `1px solid ${C.border}`, borderRadius: 16, padding: "4px 0" }}>
        {[
          [t("joinedStat"), joinedCount, true],
          [t("hostedGames"), hostedCount, true],
          [t("avgRatingReceived"), hostRating?.avg_rating != null ? hostRating.avg_rating.toFixed(1) : "—", true],
          [t("memberSince"), user?.created_at ? new Date(user.created_at).toLocaleDateString("en-GB", { month: "short", year: "numeric" }) : "—", false],
        ].map(([l, v, big], i, arr) => (
          <div key={l} style={{ flex: 1, padding: "12px 4px", textAlign: "center", borderRight: i < arr.length - 1 ? `1px solid ${C.border}` : "none" }}>
            <div style={{ fontSize: big ? 20 : 13, fontWeight: 700, color: C.ink, letterSpacing: "-0.02em", lineHeight: 1.2 }}>{v}</div>
            <div style={{ fontSize: 10, fontWeight: 600, color: C.inkFaint, textTransform: "uppercase", letterSpacing: "0.04em", marginTop: 4 }}>{l}</div>
          </div>
        ))}
      </div>}

      {/* Edit-mode form. Collapsed entirely when viewing — the profile isn't
          a settings screen. Fields only appear after tapping "Edit profile". */}
      {editing && (
        <div style={{ padding: "0 20px 20px", display: "flex", flexDirection: "column", gap: 16 }}>
          <div>
            <Label>{t("age")}</Label>
            <input type="text" inputMode="numeric" pattern="[0-9]*" value={d.age ?? ""} onChange={e => handleAgeChange(e.target.value)} style={inputBase} placeholder="25" maxLength={2} />
          </div>
          <div>
            <Label>{t("position")}</Label>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
              {["GK","CB","LB","RB","CDM","CM","CAM","LW","RW","ST"].map(pos => (
                <button key={pos} onClick={() => setD({...d, position: d.position === pos ? "" : pos})} style={{ padding: "7px 12px", borderRadius: 8, border: `1px solid ${d.position === pos ? C.accent : C.border}`, background: d.position === pos ? C.accent : C.surface, color: d.position === pos ? C.accentText : C.ink, cursor: "pointer", fontSize: 12, fontWeight: 600, transition: "all 0.15s" }}>{pos}</button>
              ))}
            </div>
          </div>
          <div>
            <Label>{t("level")}</Label>
            <div style={{ display: "flex", gap: 8 }}>
              {["Beginner","Intermediate","Advanced"].map(l => (
                <button key={l} onClick={() => setD({...d, level: l})} style={{ flex: 1, padding: "9px 4px", borderRadius: 8, border: `1px solid ${d.level===l ? C.accent : C.border}`, background: d.level===l ? C.accentBg : C.bg, cursor: "pointer", fontSize: 12, fontWeight: d.level===l ? 600 : 400, color: d.level===l ? C.accent : C.ink }}>{l}</button>
              ))}
            </div>
          </div>
          <div>
            <Label>{t("bio")}</Label>
            <div style={{ position: "relative" }}>
              <textarea value={d.bio} onChange={e => setD({...d, bio: e.target.value.slice(0, 200)})} rows={3} maxLength={200} style={{ ...inputBase, resize: "none" }} placeholder={t("noBioYet")} />
              <span style={{ position: "absolute", bottom: 8, right: 10, fontSize: 10, color: C.inkFaint, pointerEvents: "none" }}>{(d.bio||"").length}/200</span>
            </div>
          </div>
          {saveError && <div style={{ padding: "10px 14px", background: dangerBg, borderRadius: 8, fontSize: 13, color: C.danger, border: `1px solid ${dangerBorder}` }}>{saveError}</div>}
        </div>
      )}

      {saveSuccess && <div style={{ margin: "0 20px 16px", padding: "10px 14px", background: successBg, borderRadius: 8, fontSize: 13, color: C.success, border: `1px solid ${successBorder}`, textAlign: "center" }}>{t("profileSaved")}</div>}

      {/* Recent activity — one concise signal. Full history lives in My Games. */}
      {!editing && recentPastGames && recentPastGames.length > 0 && (
        <div style={{ padding: "0 20px 24px" }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
            <div style={{ ...T.label }}>Last match</div>
            {onOpenAllPast && (
              <button onClick={onOpenAllPast} style={{ background: "none", border: "none", cursor: "pointer", fontSize: 12, fontWeight: 600, color: C.accent, padding: 0, display: "inline-flex", alignItems: "center", gap: 4 }}>
                Past games
                <ChevronRight size={13} {...ICON} />
              </button>
            )}
          </div>
          {(() => {
            const game = recentPastGames[0];
            return (
              <button onClick={onOpenAllPast} style={{ width: "100%", padding: "12px 14px", display: "flex", alignItems: "center", gap: 12, background: C.surface, border: `1px solid ${C.border}`, borderRadius: 12, textAlign: "left", cursor: onOpenAllPast ? "pointer" : "default" }}>
                <div style={{ width: 40, height: 40, borderRadius: 10, background: C.accentBg, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                  <div style={{ fontSize: 9, fontWeight: 700, color: C.accent, textTransform: "uppercase", letterSpacing: "0.05em", lineHeight: 1 }}>{game.date ? new Date(game.date).toLocaleDateString("en-GB", { month: "short" }) : ""}</div>
                  <div style={{ fontSize: 15, fontWeight: 700, color: C.accent, lineHeight: 1.1 }}>{game.date ? new Date(game.date).getDate() : "-"}</div>
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: C.ink, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{game.title}</div>
                  <div style={{ fontSize: 11, color: C.inkFaint, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", marginTop: 2 }}>{game.location} · {game.format}</div>
                </div>
                <ChevronRight size={16} {...ICON} color={C.inkFaint} />
              </button>
            );
          })()}
        </div>
      )}

    </div>
  );
}

// ─── Settings Screen ──────────────────────────────────────────────────────────
// Full-screen overlay reached from the profile gear icon. Houses everything
// that's not part of the public-facing profile: account actions (sign out,
// delete), preferences (language, theme placeholder), notification toggles,
// and the legal/about links.
// Sub-components hoisted out so they keep stable identity across renders
// (otherwise eslint react-hooks/static-components fires and React remounts
// children unnecessarily).
function SettingsSection({ title, children }) {
  const { T, card } = useTheme();
  return (
    <div style={{ marginBottom: 24 }}>
      <div style={{ ...T.label, padding: "0 4px 8px" }}>{title}</div>
      <div style={{ ...card, overflow: "hidden" }}>{children}</div>
    </div>
  );
}
function SettingsRow({ label, value, onClick, danger, last }) {
  const { C } = useTheme();
  return (
    <button onClick={onClick} disabled={!onClick} style={{ width: "100%", display: "flex", alignItems: "center", justifyContent: "space-between", padding: "14px 16px", background: "transparent", border: "none", borderBottom: last ? "none" : `1px solid ${C.border}`, cursor: onClick ? "pointer" : "default", textAlign: "left", transition: "background 0.12s" }}
      onMouseEnter={e => { if (onClick) e.currentTarget.style.background = C.bg; }}
      onMouseLeave={e => { e.currentTarget.style.background = "transparent"; }}
    >
      <span style={{ fontSize: 15, color: danger ? C.danger : C.ink, fontWeight: danger ? 600 : 500 }}>{label}</span>
      <span style={{ fontSize: 14, color: C.inkFaint, display: "flex", alignItems: "center", gap: 6 }}>
        {value}
        {onClick && <ChevronRight size={16} {...ICON} color={C.inkFaint} />}
      </span>
    </button>
  );
}
function SettingsToggle({ label, on, onToggle, last }) {
  const { C } = useTheme();
  return (
    <div style={{ width: "100%", display: "flex", alignItems: "center", justifyContent: "space-between", padding: "13px 14px", borderBottom: last ? "none" : `1px solid ${C.border}` }}>
      <span style={{ fontSize: 14, color: C.ink }}>{label}</span>
      <button onClick={onToggle} aria-pressed={on} style={{ width: 40, height: 24, borderRadius: 12, background: on ? C.accent : C.border, border: "none", cursor: "pointer", padding: 0, position: "relative", transition: "background 0.15s" }}>
        <span style={{ position: "absolute", top: 2, left: on ? 18 : 2, width: 20, height: 20, borderRadius: "50%", background: "#fff", transition: "left 0.15s", boxShadow: "0 1px 3px rgba(0,0,0,0.15)" }} />
      </button>
    </div>
  );
}

function AdminModerationPanel({ currentUser, showToast }) {
  const { C, T, btnPrimary, btnSecondary, warnBg, dangerBg, SPACE, RADIUS } = useTheme();
  const [reports, setReports] = useState([]);
  const [noShowReports, setNoShowReports] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busyId, setBusyId] = useState(null);

  const loadReports = useCallback(async () => {
    if (!currentUser) return;
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from("community_reports")
        .select("id, community_id, reporter_id, reason, status, created_at")
        .in("status", ["open", "reviewing"])
        .order("created_at", { ascending: false })
        .limit(25);
      if (error) throw error;

      const rows = data || [];
      const communityIds = [...new Set(rows.map(row => row.community_id).filter(Boolean))];
      const reporterIds = [...new Set(rows.map(row => row.reporter_id).filter(Boolean))];
      let communityMap = {};
      let profileMap = {};

      if (communityIds.length) {
        const { data: communities } = await supabase
          .from("communities")
          .select("id, name, verified")
          .in("id", communityIds);
        (communities || []).forEach(group => { communityMap[group.id] = group; });
      }
      if (reporterIds.length) {
        const { data: profiles } = await supabase
          .from("profiles")
          .select("id, name")
          .in("id", reporterIds);
        (profiles || []).forEach(profile => { profileMap[profile.id] = profile; });
      }

      setReports(rows.map(row => ({
        ...row,
        communityName: communityMap[row.community_id]?.name || "Group",
        communityVerified: communityMap[row.community_id]?.verified === true,
        reporterName: profileMap[row.reporter_id]?.name || "Player",
      })));

      const { data: noShows, error: noShowError } = await supabase
        .from("no_show_reports")
        .select("id, game_id, reporter_id, reported_user_id, status, action, moderator_note, created_at")
        .in("status", ["open", "reviewing"])
        .order("created_at", { ascending: false })
        .limit(25);
      if (noShowError) {
        console.warn("[admin.no-shows.load] failed", noShowError);
        setNoShowReports([]);
      } else {
        const noShowRows = noShows || [];
        const gameIds = [...new Set(noShowRows.map(row => row.game_id).filter(Boolean))];
        const peopleIds = [...new Set(noShowRows.flatMap(row => [row.reporter_id, row.reported_user_id]).filter(Boolean))];
        let gameMap = {};
        let peopleMap = {};
        if (gameIds.length) {
          const { data: gamesForReports } = await supabase.from("games").select("id, title, date, hour").in("id", gameIds);
          (gamesForReports || []).forEach(game => { gameMap[game.id] = game; });
        }
        if (peopleIds.length) {
          const { data: people } = await supabase.from("profiles").select("id, name, no_shows").in("id", peopleIds);
          (people || []).forEach(profile => { peopleMap[profile.id] = profile; });
        }
        setNoShowReports(noShowRows.map(row => ({
          ...row,
          gameTitle: gameMap[row.game_id]?.title || "Game",
          gameTime: [gameMap[row.game_id]?.date, gameMap[row.game_id]?.hour].filter(Boolean).join(" · "),
          reporterName: peopleMap[row.reporter_id]?.name || "Player",
          reportedName: peopleMap[row.reported_user_id]?.name || "Player",
          reportedNoShows: peopleMap[row.reported_user_id]?.no_shows || 0,
        })));
      }
    } catch (e) {
      console.warn("[admin.group-reports.load] failed", e);
      showToast?.("Could not load group reports");
    } finally {
      setLoading(false);
    }
  }, [currentUser, showToast]);

  useEffect(() => { loadReports(); }, [loadReports]);

  const updateReport = async (report, status) => {
    if (!report?.id || !currentUser) return;
    setBusyId(report.id);
    try {
      const finalStatus = status === "resolved" || status === "dismissed";
      const { error } = await supabase
        .from("community_reports")
        .update({
          status,
          resolved_by: finalStatus ? currentUser.id : null,
          resolved_at: finalStatus ? new Date().toISOString() : null,
        })
        .eq("id", report.id);
      if (error) throw error;
      showToast?.(status === "reviewing" ? "Report marked for review" : "Report closed");
      await loadReports();
    } catch (e) {
      console.warn("[admin.group-reports.update] failed", e);
      showToast?.("Could not update report");
    } finally {
      setBusyId(null);
    }
  };

  const resolveNoShowReport = async (report, action) => {
    if (!report?.id || !currentUser) return;
    setBusyId(`noshow-${report.id}`);
    try {
      const status = action === "dismissed" ? "dismissed" : "resolved";
      const note = action === "suspend_24h"
        ? "Suspended for 24 hours after no-show report."
        : action === "ban"
          ? "Account removed after no-show report."
          : action === "warn"
            ? "Warning recorded after no-show report."
            : "No-show report dismissed.";
      const { error } = await supabase
        .from("no_show_reports")
        .update({
          status,
          action,
          moderator_id: currentUser.id,
          moderator_note: note,
          resolved_at: new Date().toISOString(),
        })
        .eq("id", report.id);
      if (error) throw error;

      if (action !== "dismissed") {
        const { data: profile } = await supabase.from("profiles").select("no_shows").eq("id", report.reported_user_id).maybeSingle();
        const patch = { no_shows: (profile?.no_shows || 0) + 1 };
        if (action === "suspend_24h") {
          patch.suspended_until = new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString();
        }
        if (action === "ban") {
          patch.banned = true;
          patch.banned_reason = "Repeated no-show reports";
        }
        await supabase.from("profiles").update(patch).eq("id", report.reported_user_id);
      }

      showToast?.(action === "dismissed" ? "No-show report dismissed" : "No-show action saved");
      await loadReports();
    } catch (e) {
      console.warn("[admin.no-shows.update] failed", e);
      showToast?.("Could not update no-show report");
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div style={{ borderTop: `1px solid ${C.border}` }}>
      <div style={{ padding: `${SPACE[4]}px ${SPACE[4]}px ${SPACE[3]}px`, borderBottom: `1px solid ${C.border}` }}>
        <div style={{ display: "flex", alignItems: "center", gap: SPACE[2], marginBottom: 4 }}>
          <Flag size={15} color={C.accent} {...ICON} />
          <div style={{ fontSize: 14, fontWeight: 900, color: C.ink }}>Group reports</div>
        </div>
        <div style={{ ...T.small, color: C.inkMid }}>Review reported groups and close moderation issues.</div>
      </div>
      {loading ? (
        <div style={{ padding: SPACE[4], color: C.inkMid, fontSize: 13 }}>Loading reports...</div>
      ) : reports.length === 0 ? (
        <div style={{ padding: SPACE[4], color: C.inkMid, fontSize: 13 }}>No open reports.</div>
      ) : reports.map(report => (
        <div key={report.id} style={{ padding: `${SPACE[3]}px ${SPACE[4]}px`, borderBottom: `1px solid ${C.border}` }}>
          <div style={{ display: "flex", alignItems: "flex-start", gap: SPACE[3] }}>
            <div style={{ width: 36, height: 36, borderRadius: "50%", background: report.status === "reviewing" ? warnBg : C.accentBg, color: report.status === "reviewing" ? C.warn : C.accent, display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 900, flexShrink: 0 }}>
              {communityInitials(report.communityName)}
            </div>
            <div style={{ minWidth: 0, flex: 1 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 6, minWidth: 0 }}>
                <span style={{ fontSize: 14, fontWeight: 850, color: C.ink, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{report.communityName}</span>
                {report.communityVerified && <VerifiedTick size={10} />}
              </div>
              <div style={{ ...T.small, color: C.inkMid }}>{report.reporterName} · {relativeTime(report.created_at)}</div>
            </div>
            <span style={{ fontSize: 10, fontWeight: 900, color: report.status === "reviewing" ? C.warn : C.accent, background: report.status === "reviewing" ? warnBg : C.accentBg, borderRadius: RADIUS.pill, padding: "5px 8px", textTransform: "uppercase" }}>{report.status}</span>
          </div>
          <div style={{ marginTop: SPACE[2], padding: SPACE[3], borderRadius: RADIUS.sm, background: C.bg, color: C.inkMid, fontSize: 13, lineHeight: 1.45, whiteSpace: "pre-wrap", wordBreak: "break-word" }}>{report.reason}</div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: SPACE[2], marginTop: SPACE[3] }}>
            <button onClick={() => updateReport(report, "reviewing")} disabled={busyId === report.id || report.status === "reviewing"} style={{ ...btnSecondary, padding: "9px 0", borderRadius: RADIUS.sm, fontSize: 12, opacity: busyId === report.id || report.status === "reviewing" ? 0.55 : 1 }}>Reviewing</button>
            <button onClick={() => updateReport(report, "resolved")} disabled={busyId === report.id} style={{ ...btnPrimary, padding: "9px 0", borderRadius: RADIUS.sm, fontSize: 12, opacity: busyId === report.id ? 0.55 : 1 }}>Resolve</button>
            <button onClick={() => updateReport(report, "dismissed")} disabled={busyId === report.id} style={{ ...btnSecondary, padding: "9px 0", borderRadius: RADIUS.sm, fontSize: 12, color: C.danger, background: dangerBg, opacity: busyId === report.id ? 0.55 : 1 }}>Dismiss</button>
          </div>
        </div>
      ))}
      <div style={{ padding: `${SPACE[4]}px ${SPACE[4]}px ${SPACE[3]}px`, borderTop: `1px solid ${C.border}`, borderBottom: `1px solid ${C.border}` }}>
        <div style={{ display: "flex", alignItems: "center", gap: SPACE[2], marginBottom: 4 }}>
          <AlertCircle size={15} color={C.warn} {...ICON} />
          <div style={{ fontSize: 14, fontWeight: 900, color: C.ink }}>No-show reports</div>
        </div>
        <div style={{ ...T.small, color: C.inkMid }}>Review players reported after completed games, then warn, suspend, remove, or dismiss.</div>
      </div>
      {loading ? (
        <div style={{ padding: SPACE[4], color: C.inkMid, fontSize: 13 }}>Loading no-show reports...</div>
      ) : noShowReports.length === 0 ? (
        <div style={{ padding: SPACE[4], color: C.inkMid, fontSize: 13 }}>No open no-show reports.</div>
      ) : noShowReports.map(report => (
        <div key={`noshow-${report.id}`} style={{ padding: `${SPACE[3]}px ${SPACE[4]}px`, borderBottom: `1px solid ${C.border}` }}>
          <div style={{ display: "flex", alignItems: "flex-start", gap: SPACE[3] }}>
            <div style={{ width: 36, height: 36, borderRadius: "50%", background: warnBg, color: C.warn, display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 900, flexShrink: 0 }}>
              !
            </div>
            <div style={{ minWidth: 0, flex: 1 }}>
              <div style={{ fontSize: 14, fontWeight: 850, color: C.ink, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{report.reportedName}</div>
              <div style={{ ...T.small, color: C.inkMid }}>Reported by {report.reporterName} · {relativeTime(report.created_at)}</div>
            </div>
            <span style={{ fontSize: 10, fontWeight: 900, color: C.warn, background: warnBg, borderRadius: RADIUS.pill, padding: "5px 8px", textTransform: "uppercase" }}>{report.status}</span>
          </div>
          <div style={{ marginTop: SPACE[2], padding: SPACE[3], borderRadius: RADIUS.sm, background: C.bg, color: C.inkMid, fontSize: 13, lineHeight: 1.45 }}>
            <b style={{ color: C.ink }}>{report.gameTitle}</b>
            {report.gameTime && <span> · {report.gameTime}</span>}
            <div style={{ marginTop: 4 }}>Previous no-shows: {report.reportedNoShows}</div>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: SPACE[2], marginTop: SPACE[3] }}>
            <button onClick={() => resolveNoShowReport(report, "warn")} disabled={busyId === `noshow-${report.id}`} style={{ ...btnSecondary, padding: "9px 0", borderRadius: RADIUS.sm, fontSize: 12, opacity: busyId === `noshow-${report.id}` ? 0.55 : 1 }}>Warn</button>
            <button onClick={() => resolveNoShowReport(report, "suspend_24h")} disabled={busyId === `noshow-${report.id}`} style={{ ...btnSecondary, padding: "9px 0", borderRadius: RADIUS.sm, fontSize: 12, color: C.warn, background: warnBg, opacity: busyId === `noshow-${report.id}` ? 0.55 : 1 }}>Suspend 24h</button>
            <button onClick={() => resolveNoShowReport(report, "ban")} disabled={busyId === `noshow-${report.id}`} style={{ ...btnSecondary, padding: "9px 0", borderRadius: RADIUS.sm, fontSize: 12, color: C.danger, background: dangerBg, opacity: busyId === `noshow-${report.id}` ? 0.55 : 1 }}>Remove</button>
            <button onClick={() => resolveNoShowReport(report, "dismissed")} disabled={busyId === `noshow-${report.id}`} style={{ ...btnSecondary, padding: "9px 0", borderRadius: RADIUS.sm, fontSize: 12, opacity: busyId === `noshow-${report.id}` ? 0.55 : 1 }}>Dismiss</button>
          </div>
        </div>
      ))}
    </div>
  );
}

function AdminPill({ label, tone = "neutral" }) {
  const { C, successBg, warnBg, dangerBg, RADIUS } = useTheme();
  const styles = {
    success: { color: C.success, background: successBg },
    warn: { color: C.warn, background: warnBg },
    danger: { color: C.danger, background: dangerBg },
    accent: { color: C.accent, background: C.accentBg },
    neutral: { color: C.inkMid, background: C.bg },
  };
  const s = styles[tone] || styles.neutral;
  return <span style={{ fontSize: 10, fontWeight: 900, color: s.color, background: s.background, borderRadius: RADIUS.pill, padding: "5px 8px", textTransform: "uppercase", whiteSpace: "nowrap" }}>{label}</span>;
}

function AdminMiniButton({ children, onClick, tone = "neutral", disabled }) {
  const { C, btnSecondary, successBg, warnBg, dangerBg, RADIUS } = useTheme();
  const color = tone === "danger" ? C.danger : tone === "warn" ? C.warn : tone === "success" ? C.success : tone === "accent" ? C.accent : C.ink;
  const background = tone === "danger" ? dangerBg : tone === "warn" ? warnBg : tone === "success" ? successBg : tone === "accent" ? C.accentBg : "transparent";
  return (
    <button onClick={onClick} disabled={disabled} style={{ ...btnSecondary, padding: "8px 10px", borderRadius: RADIUS.sm, fontSize: 12, color, background, opacity: disabled ? 0.5 : 1, cursor: disabled ? "not-allowed" : "pointer" }}>
      {children}
    </button>
  );
}

function AdminEmpty({ title, body }) {
  const { C, T, SPACE } = useTheme();
  return (
    <div style={{ padding: SPACE[5], textAlign: "center", color: C.inkMid }}>
      <div style={{ fontSize: 14, fontWeight: 900, color: C.ink }}>{title}</div>
      {body && <div style={{ ...T.small, marginTop: 4 }}>{body}</div>}
    </div>
  );
}

function adminDate(value) {
  if (!value) return "—";
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? String(value) : d.toLocaleString("en-GB", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });
}

function AdminOverviewPanel({ recentActions }) {
  const { C, T, card, SPACE, SHADOW, RADIUS } = useTheme();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let mounted = true;
    (async () => {
      setLoading(true);
      try {
        const today = format(new Date(), "yyyy-MM-dd");
        const [users, games, gameRows, groups, noShows, groupReports, matchReports] = await Promise.all([
          supabase.from("profiles").select("id", { count: "exact", head: true }),
          supabase.from("games").select("id", { count: "exact", head: true }).or(`date.gte.${today},date.is.null`).neq("status", "cancelled"),
          supabase
            .from("games")
            .select("id,status,date,hour,duration,visibility,is_private,format,spots_total,game_players(user_id,status)")
            .or(`date.gte.${today},date.is.null`)
            .limit(500),
          supabase.from("communities").select("id", { count: "exact", head: true }),
          supabase.from("no_show_reports").select("id", { count: "exact", head: true }).in("status", ["open", "reviewing"]),
          supabase.from("community_reports").select("id", { count: "exact", head: true }).in("status", ["open", "reviewing"]),
          supabase.from("match_reports").select("game_id", { count: "exact", head: true }),
        ]);
        if (gameRows.error) console.warn("[admin.overview.games] visibility count failed", gameRows.error);
        const playerVisibleGames = (gameRows.data || []).filter(g => !getDefaultDiscoverHiddenReason(g)).length;
        if (!mounted) return;
        setStats({
          users: users.count || 0,
          games: games.count || 0,
          playerVisibleGames,
          groups: groups.count || 0,
          openReports: (noShows.count || 0) + (groupReports.count || 0),
          matchReports: matchReports.count || 0,
        });
      } catch (e) {
        console.warn("[admin.overview] failed", e);
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, []);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: SPACE[4] }}>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: SPACE[3] }}>
        {[
          ["Users", stats?.users, "Profiles and account status"],
          ["Games", stats ? `${stats.playerVisibleGames}/${stats.games}` : null, "Public map / active rows"],
          ["Groups", stats?.groups, "Communities"],
          ["Open reports", stats?.openReports, "Needs moderation"],
        ].map(([title, value, body]) => (
          <div key={title} style={{ ...card, padding: SPACE[3], boxShadow: SHADOW.sm }}>
            <div style={{ fontSize: 12, fontWeight: 900, color: C.inkMid, textTransform: "uppercase" }}>{title}</div>
            <div style={{ fontSize: 24, fontWeight: 950, color: C.ink, marginTop: 6 }}>{loading ? "…" : value ?? "—"}</div>
            <div style={{ ...T.small, color: C.inkMid, marginTop: 3 }}>{body}</div>
          </div>
        ))}
      </div>

      <div style={{ ...card, padding: SPACE[4] }}>
        <div style={{ fontSize: 15, fontWeight: 950, color: C.ink, marginBottom: SPACE[3] }}>Admin work is centralised here</div>
        {[
          ["User actions", "Warn, suspend, ban/unban, clear no-shows, save notes."],
          ["Game moderation", "Search games, cancel problematic games, restore cancelled games."],
          ["Group moderation", "Verify groups, make groups private/public, inspect reported groups."],
          ["Venue catalog", "Add pitches, edit coordinates and details, remove stale venues."],
          ["Reports", "Review match wrap-ups, no-shows, pitch feedback and ratings."],
        ].map(([label, body]) => (
          <div key={label} style={{ display: "flex", gap: SPACE[3], padding: `${SPACE[2]}px 0`, borderBottom: `1px solid ${C.border}` }}>
            <div style={{ width: 8, height: 8, borderRadius: "50%", background: C.accent, marginTop: 7, flexShrink: 0 }} />
            <div>
              <div style={{ fontSize: 13, fontWeight: 850, color: C.ink }}>{label}</div>
              <div style={{ ...T.small, color: C.inkMid }}>{body}</div>
            </div>
          </div>
        ))}
      </div>

      <div style={{ ...card, overflow: "hidden" }}>
        <div style={{ padding: SPACE[4], borderBottom: `1px solid ${C.border}`, fontSize: 15, fontWeight: 950, color: C.ink }}>Latest admin actions</div>
        {recentActions.length ? recentActions.slice(0, 5).map(action => (
          <div key={action.id} style={{ padding: SPACE[3], borderBottom: `1px solid ${C.border}` }}>
            <div style={{ fontSize: 13, fontWeight: 850, color: C.ink }}>{action.label}</div>
            <div style={{ ...T.small, color: C.inkMid, marginTop: 2 }}>{action.detail} · {adminDate(action.at)}</div>
          </div>
        )) : <AdminEmpty title="No actions this session" body="Actions you take in this dashboard will appear here immediately." />}
      </div>

      <div style={{ ...card, padding: SPACE[4], borderRadius: RADIUS.md }}>
        <div style={{ fontSize: 15, fontWeight: 950, color: C.ink, marginBottom: SPACE[2] }}>Data sources</div>
        {[
          ["Account actions", "profiles.no_shows, banned, suspended_until, moderation_notes"],
          ["No-show queue", "no_show_reports"],
          ["Group reports", "community_reports"],
          ["Match wrap-ups", "match_reports"],
          ["Pitch reports", "pitch_feedback"],
          ["Game ratings", "game_ratings"],
        ].map(([label, value]) => (
          <div key={label} style={{ display: "flex", justifyContent: "space-between", gap: SPACE[3], padding: `${SPACE[2]}px 0`, borderBottom: `1px solid ${C.border}` }}>
            <span style={{ fontSize: 13, color: C.inkMid, fontWeight: 750 }}>{label}</span>
            <span style={{ fontSize: 12, color: C.ink, fontWeight: 850, textAlign: "right" }}>{value}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function AdminUsersPanel({ showToast, recordAction }) {
  const { C, T, inputBase, card, SPACE, RADIUS } = useTheme();
  const [query, setQuery] = useState("");
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [notes, setNotes] = useState({});

  const loadUsers = useCallback(async () => {
    setLoading(true);
    try {
      let req = supabase
        .from("profiles")
        .select("id,name,level,position,role,no_shows,banned,banned_reason,suspended_until,moderation_notes,certified_host,created_at")
        .order("created_at", { ascending: false })
        .limit(30);
      const q = query.trim();
      if (q) req = req.ilike("name", `%${q}%`);
      const { data, error } = await req;
      if (error) throw error;
      setUsers(data || []);
      setNotes(Object.fromEntries((data || []).map(p => [p.id, p.moderation_notes || ""])));
    } catch (e) {
      console.warn("[admin.users.load] failed", e);
      showToast?.("Could not load users");
    } finally {
      setLoading(false);
    }
  }, [query, showToast]);

  useEffect(() => { loadUsers(); }, [loadUsers]);

  const updateUser = async (profile, patch, label) => {
    setBusyId(profile.id);
    try {
      const { error } = await supabase.from("profiles").update(patch).eq("id", profile.id);
      if (error) throw error;
      setUsers(prev => prev.map(p => p.id === profile.id ? { ...p, ...patch } : p));
      recordAction(label, profile.name || profile.id);
      showToast?.(label);
    } catch (e) {
      console.warn("[admin.users.update] failed", e);
      showToast?.("Could not update user");
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: SPACE[3] }}>
      <div style={{ display: "flex", gap: SPACE[2] }}>
        <input value={query} onChange={e => setQuery(e.target.value)} onKeyDown={e => e.key === "Enter" && loadUsers()} placeholder="Search users by name" style={{ ...inputBase, flex: 1 }} />
        <AdminMiniButton onClick={loadUsers} tone="accent">Search</AdminMiniButton>
      </div>
      <div style={{ ...card, overflow: "hidden" }}>
        <div style={{ padding: SPACE[4], borderBottom: `1px solid ${C.border}` }}>
          <div style={{ fontSize: 15, fontWeight: 950, color: C.ink }}>Users</div>
          <div style={{ ...T.small, color: C.inkMid, marginTop: 2 }}>Moderate account status and save internal notes.</div>
        </div>
        {loading ? <AdminEmpty title="Loading users..." /> : users.length === 0 ? <AdminEmpty title="No users found" body="Try a different name." /> : users.map(profile => {
          const suspended = profile.suspended_until && new Date(profile.suspended_until).getTime() > Date.now();
          return (
            <div key={profile.id} style={{ padding: SPACE[4], borderBottom: `1px solid ${C.border}` }}>
              <div style={{ display: "flex", alignItems: "flex-start", gap: SPACE[3] }}>
                <div style={{ width: 40, height: 40, borderRadius: "50%", background: C.accentBg, color: C.accent, display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 950, flexShrink: 0 }}>
                  {communityInitials(profile.name || "Player")}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 15, fontWeight: 950, color: C.ink, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{profile.name || "Unnamed player"}</div>
                  <div style={{ ...T.small, color: C.inkMid }}>{profile.level || "No level"} · {profile.position || "No position"} · no-shows {profile.no_shows || 0}</div>
                </div>
                <div style={{ display: "flex", gap: 6, flexWrap: "wrap", justifyContent: "flex-end" }}>
                  {profile.role === "admin" && <AdminPill label="admin" tone="accent" />}
                  {profile.certified_host && <AdminPill label="certified" tone="success" />}
                  {profile.banned && <AdminPill label="banned" tone="danger" />}
                  {suspended && <AdminPill label="suspended" tone="warn" />}
                </div>
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: SPACE[2], marginTop: SPACE[3] }}>
                <AdminMiniButton disabled={busyId === profile.id} onClick={() => updateUser(profile, { suspended_until: new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString() }, "User suspended for 24h")} tone="warn">Suspend 24h</AdminMiniButton>
                <AdminMiniButton disabled={busyId === profile.id} onClick={() => updateUser(profile, { suspended_until: null }, "Suspension cleared")} tone="neutral">Clear suspension</AdminMiniButton>
                <AdminMiniButton disabled={busyId === profile.id} onClick={() => updateUser(profile, { banned: !profile.banned, banned_reason: profile.banned ? null : "Banned by admin" }, profile.banned ? "User unbanned" : "User banned")} tone={profile.banned ? "success" : "danger"}>{profile.banned ? "Unban" : "Ban"}</AdminMiniButton>
                <AdminMiniButton disabled={busyId === profile.id} onClick={() => updateUser(profile, { no_shows: 0 }, "No-shows cleared")} tone="neutral">Clear no-shows</AdminMiniButton>
                <AdminMiniButton disabled={busyId === profile.id} onClick={() => updateUser(profile, { certified_host: !profile.certified_host, certified_host_at: !profile.certified_host ? new Date().toISOString() : null }, profile.certified_host ? "Host certification removed" : "Host certified")} tone="accent">{profile.certified_host ? "Uncertify host" : "Certify host"}</AdminMiniButton>
                <AdminMiniButton disabled={busyId === profile.id || profile.role === "admin"} onClick={() => updateUser(profile, { role: "admin" }, "Admin role granted")} tone="accent">Make admin</AdminMiniButton>
              </div>
              <div style={{ marginTop: SPACE[3] }}>
                <textarea value={notes[profile.id] || ""} onChange={e => setNotes(n => ({ ...n, [profile.id]: e.target.value }))} placeholder="Internal moderation notes" rows={2} style={{ ...inputBase, resize: "vertical", borderRadius: RADIUS.sm, fontSize: 13 }} />
                <div style={{ display: "flex", justifyContent: "flex-end", marginTop: SPACE[2] }}>
                  <AdminMiniButton disabled={busyId === profile.id} onClick={() => updateUser(profile, { moderation_notes: notes[profile.id] || null }, "Moderation notes saved")} tone="accent">Save notes</AdminMiniButton>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function AdminGamesPanel({ showToast, recordAction }) {
  const { C, T, inputBase, card, SPACE } = useTheme();
  const [query, setQuery] = useState("");
  const [gamesList, setGamesList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busyId, setBusyId] = useState(null);

  const loadGamesForAdmin = useCallback(async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from("games")
        .select("id,title,format,level,location,date,hour,duration,status,host_id,host_name,visibility,is_private,community_id,created_at,cancelled_reason,lat,lng,spots_total,game_players(user_id,status)")
        .or(`date.gte.${format(new Date(), "yyyy-MM-dd")},date.is.null`)
        .order("date", { ascending: true })
        .order("hour", { ascending: true })
        .limit(60);
      if (error) throw error;
      setGamesList(data || []);
    } catch (e) {
      console.warn("[admin.games.load] failed", e);
      showToast?.("Could not load games");
    } finally {
      setLoading(false);
    }
  }, [showToast]);

  useEffect(() => { loadGamesForAdmin(); }, [loadGamesForAdmin]);

  const updateGame = async (game, patch, label) => {
    setBusyId(game.id);
    try {
      const { error } = await supabase.from("games").update(patch).eq("id", game.id);
      if (error) throw error;
      setGamesList(prev => prev.map(g => g.id === game.id ? { ...g, ...patch } : g));
      if (patch.status === "cancelled") {
        const { data: players } = await supabase.from("game_players").select("user_id").eq("game_id", game.id);
        const rows = (players || []).filter(p => p.user_id !== game.host_id).map(p => ({
          user_id: p.user_id,
          type: "game_cancelled",
          text: `${game.title || "A game"} was cancelled by admin`,
          sub: patch.cancelled_reason || "Admin moderation",
          game_id: game.id,
        }));
        if (rows.length) await supabase.from("notifications").insert(rows);
      }
      recordAction(label, game.title || game.id);
      showToast?.(label);
    } catch (e) {
      console.warn("[admin.games.update] failed", e);
      showToast?.("Could not update game");
    } finally {
      setBusyId(null);
    }
  };

  const q = query.trim().toLowerCase();
  const filtered = q ? gamesList.filter(g => [g.title, g.location, g.host_name, g.format, g.status].some(v => String(v || "").toLowerCase().includes(q))) : gamesList;
  const publicMapCount = gamesList.filter(g => !getDefaultDiscoverHiddenReason(g)).length;
  const hiddenActiveCount = gamesList.filter(g => g.status !== "cancelled" && getDefaultDiscoverHiddenReason(g)).length;
  const cancelledCount = gamesList.filter(g => g.status === "cancelled").length;
  const gamesSummary = gamesList.length
    ? `${publicMapCount} on the default public map · ${hiddenActiveCount} hidden active${cancelledCount ? ` · ${cancelledCount} cancelled/restorable` : ""}`
    : "Cancel or restore games without hunting through the player UI.";

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: SPACE[3] }}>
      <div style={{ display: "flex", gap: SPACE[2] }}>
        <input value={query} onChange={e => setQuery(e.target.value)} placeholder="Search games, hosts, places" style={{ ...inputBase, flex: 1 }} />
        <AdminMiniButton onClick={loadGamesForAdmin} tone="accent">Refresh</AdminMiniButton>
      </div>
      <div style={{ ...card, overflow: "hidden" }}>
        <div style={{ padding: SPACE[4], borderBottom: `1px solid ${C.border}` }}>
          <div style={{ fontSize: 15, fontWeight: 950, color: C.ink }}>Games</div>
          <div style={{ ...T.small, color: C.inkMid, marginTop: 2 }}>{gamesSummary}</div>
        </div>
        {loading ? <AdminEmpty title="Loading games..." /> : filtered.length === 0 ? <AdminEmpty title="No games found" /> : filtered.map(game => {
          const hiddenReason = getDefaultDiscoverHiddenReason(game);
          const visibility = getGameVisibility(game);
          const spotsFilled = getConfirmedPlayerCount(game);
          const spotsTotal = getGameSpotsTotal(game);
          const hasMapPin = Number.isFinite(Number(game.lat)) && Number.isFinite(Number(game.lng));
          return (
            <div key={game.id} style={{ padding: SPACE[4], borderBottom: `1px solid ${C.border}` }}>
              <div style={{ display: "flex", gap: SPACE[3], alignItems: "flex-start" }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 15, fontWeight: 950, color: C.ink, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{game.title || "Untitled game"}</div>
                  <div style={{ ...T.small, color: C.inkMid }}>{game.location || "No location"} · {game.date || "No date"} {game.hour || ""} · {game.host_name || "Unknown host"}</div>
                  <div style={{ ...T.small, color: C.inkFaint, marginTop: 3 }}>{game.format || "No format"} · {spotsTotal ? `${spotsFilled}/${spotsTotal} players` : `${spotsFilled} players`}</div>
                </div>
                <div style={{ display: "flex", gap: 6, flexWrap: "wrap", justifyContent: "flex-end" }}>
                  <AdminPill label={hiddenReason ? `hidden: ${hiddenReason}` : "public map"} tone={hiddenReason ? "warn" : "success"} />
                  <AdminPill label={visibility} tone={visibility === "public" ? "accent" : "neutral"} />
                  {!hasMapPin && <AdminPill label="no map pin" tone="warn" />}
                  <AdminPill label={game.status || "active"} tone={game.status === "cancelled" ? "danger" : "success"} />
                </div>
              </div>
              {hiddenReason && (
                <div style={{ ...T.small, color: C.inkMid, marginTop: SPACE[2], background: C.bg, border: `1px solid ${C.border}`, borderRadius: 10, padding: "8px 10px" }}>
                  Hidden from the default Discover map because this game is {hiddenReason.toLowerCase()}.
                </div>
              )}
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: SPACE[2], marginTop: SPACE[3] }}>
                <AdminMiniButton disabled={busyId === game.id || game.status === "cancelled"} onClick={() => updateGame(game, { status: "cancelled", cancelled_at: new Date().toISOString(), cancelled_reason: "Cancelled by admin" }, "Game cancelled")} tone="danger">Cancel game</AdminMiniButton>
                <AdminMiniButton disabled={busyId === game.id || game.status !== "cancelled"} onClick={() => updateGame(game, { status: "active", cancelled_at: null, cancelled_reason: null }, "Game restored")} tone="success">Restore</AdminMiniButton>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function AdminGroupsPanel({ showToast, recordAction }) {
  const { C, T, inputBase, card, SPACE } = useTheme();
  const [query, setQuery] = useState("");
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busyId, setBusyId] = useState(null);

  const loadGroups = useCallback(async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from("communities")
        .select("id,name,city,member_count,verified,is_private,join_policy,owner_id,created_at,description")
        .order("created_at", { ascending: false })
        .limit(60);
      if (error) throw error;
      setGroups(data || []);
    } catch (e) {
      console.warn("[admin.groups.load] failed", e);
      showToast?.("Could not load groups");
    } finally {
      setLoading(false);
    }
  }, [showToast]);

  useEffect(() => { loadGroups(); }, [loadGroups]);

  const updateGroup = async (group, patch, label) => {
    setBusyId(group.id);
    try {
      const { error } = await supabase.from("communities").update(patch).eq("id", group.id);
      if (error) throw error;
      setGroups(prev => prev.map(g => g.id === group.id ? { ...g, ...patch } : g));
      recordAction(label, group.name || group.id);
      showToast?.(label);
    } catch (e) {
      console.warn("[admin.groups.update] failed", e);
      showToast?.("Could not update group");
    } finally {
      setBusyId(null);
    }
  };

  const q = query.trim().toLowerCase();
  const filtered = q ? groups.filter(g => [g.name, g.city, g.join_policy].some(v => String(v || "").toLowerCase().includes(q))) : groups;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: SPACE[3] }}>
      <div style={{ display: "flex", gap: SPACE[2] }}>
        <input value={query} onChange={e => setQuery(e.target.value)} placeholder="Search groups" style={{ ...inputBase, flex: 1 }} />
        <AdminMiniButton onClick={loadGroups} tone="accent">Refresh</AdminMiniButton>
      </div>
      <div style={{ ...card, overflow: "hidden" }}>
        <div style={{ padding: SPACE[4], borderBottom: `1px solid ${C.border}` }}>
          <div style={{ fontSize: 15, fontWeight: 950, color: C.ink }}>Groups</div>
          <div style={{ ...T.small, color: C.inkMid, marginTop: 2 }}>Verify groups, adjust visibility, and review group state.</div>
        </div>
        {loading ? <AdminEmpty title="Loading groups..." /> : filtered.length === 0 ? <AdminEmpty title="No groups found" /> : filtered.map(group => (
          <div key={group.id} style={{ padding: SPACE[4], borderBottom: `1px solid ${C.border}` }}>
            <div style={{ display: "flex", alignItems: "flex-start", gap: SPACE[3] }}>
              <div style={{ width: 40, height: 40, borderRadius: 10, background: C.accentBg, color: C.accent, display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 950, flexShrink: 0 }}>{communityInitials(group.name || "G")}</div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 6, minWidth: 0 }}>
                  <span style={{ fontSize: 15, fontWeight: 950, color: C.ink, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{group.name || "Unnamed group"}</span>
                  {group.verified && <VerifiedTick size={10} />}
                </div>
                <div style={{ ...T.small, color: C.inkMid }}>{group.city || "No city"} · {group.member_count || 0} members · {group.join_policy || "open"}</div>
              </div>
              <div style={{ display: "flex", gap: 6, flexWrap: "wrap", justifyContent: "flex-end" }}>
                {group.verified && <AdminPill label="verified" tone="success" />}
                <AdminPill label={group.is_private ? "private" : "public"} tone={group.is_private ? "warn" : "accent"} />
              </div>
            </div>
            {group.description && <div style={{ marginTop: SPACE[2], ...T.small, color: C.inkMid, lineHeight: 1.4 }}>{group.description}</div>}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: SPACE[2], marginTop: SPACE[3] }}>
              <AdminMiniButton disabled={busyId === group.id} onClick={() => updateGroup(group, { verified: !group.verified }, group.verified ? "Group unverified" : "Group verified")} tone={group.verified ? "neutral" : "success"}>{group.verified ? "Unverify" : "Verify"}</AdminMiniButton>
              <AdminMiniButton disabled={busyId === group.id} onClick={() => updateGroup(group, { is_private: !group.is_private, join_policy: group.is_private ? "open" : "request" }, group.is_private ? "Group made public" : "Group made private")} tone="accent">{group.is_private ? "Make public" : "Make private"}</AdminMiniButton>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function AdminVenuesPanel({ showToast, recordAction, onVenuesChanged }) {
  const { C, T, inputBase, card, SPACE, RADIUS, warnBg, dangerBg } = useTheme();
  const [query, setQuery] = useState("");
  const [venuesList, setVenuesList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [setupError, setSetupError] = useState("");
  const [newForm, setNewForm] = useState(emptyAdminVenueForm);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState(emptyAdminVenueForm);
  const venueWriteSetupHint = "Venue write access is missing. Run admin/venues_admin_migration.sql in Supabase.";
  const withoutVenueDetails = (payload) => {
    const next = { ...payload };
    delete next.details;
    return next;
  };
  const isMissingDetailsColumnError = (e) => {
    const raw = `${e?.message || ""} ${e?.details || ""} ${e?.hint || ""} ${e?.code || ""}`.toLowerCase();
    return raw.includes("details")
      && (raw.includes("schema cache") || raw.includes("could not find") || raw.includes("column") || raw.includes("pgrst204"));
  };
  const isMissingVenueIdDefaultError = (e) => {
    const raw = `${e?.message || ""} ${e?.details || ""} ${e?.hint || ""} ${e?.code || ""}`.toLowerCase();
    return raw.includes('null value in column "id"')
      || (raw.includes("id") && raw.includes("violates not-null"))
      || (raw.includes("id") && raw.includes("not-null"))
      || (raw.includes("id") && raw.includes("23502"));
  };
  const insertVenuePayload = async (payload) => {
    const withGeneratedId = { ...payload, id: makeAdminVenueId() };
    const attempts = [payload];
    if (!payload.details) attempts.push(withoutVenueDetails(payload));
    attempts.push(withGeneratedId);
    if (!withGeneratedId.details) attempts.push(withoutVenueDetails(withGeneratedId));

    let lastResult = null;
    const seen = new Set();
    for (const nextPayload of attempts) {
      const key = JSON.stringify(nextPayload);
      if (seen.has(key)) continue;
      seen.add(key);
      const result = await supabase.from("venues").insert(nextPayload);
      if (!result.error) return result;
      lastResult = result;
      if (!isMissingDetailsColumnError(result.error) && !isMissingVenueIdDefaultError(result.error)) {
        return result;
      }
    }
    return lastResult;
  };
  const venueWriteErrorMessage = (e, fallback) => {
    const raw = `${e?.message || ""} ${e?.details || ""} ${e?.hint || ""} ${e?.code || ""}`.toLowerCase();
    const setupMissing = isMissingVenueIdDefaultError(e)
      || raw.includes("row-level security")
      || raw.includes("permission denied")
      || raw.includes("42501")
      || raw.includes("violates row-level security")
      || raw.includes("schema cache")
      || raw.includes("could not find")
      || raw.includes("pgrst204");
    if (setupMissing) {
      setSetupError(venueWriteSetupHint);
      return venueWriteSetupHint;
    }
    return fallback;
  };

  const loadVenuesForAdmin = useCallback(async () => {
    setLoading(true);
    setSetupError("");
    try {
      const { data, error } = await supabase.from("venues").select("*");
      if (error) throw error;
      setVenuesList(normaliseVenueList(data || []));
    } catch (e) {
      console.warn("[admin.venues.load] failed", e);
      setSetupError(e.message || "Could not load venues");
      setVenuesList([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadVenuesForAdmin(); }, [loadVenuesForAdmin]);

  const validateVenueForm = (form) => {
    const payload = venuePayloadFromForm(form);
    if (!payload.name) return { error: "Venue name is required" };
    if (!Number.isFinite(payload.lat) || payload.lat < -90 || payload.lat > 90) return { error: "Latitude must be between -90 and 90" };
    if (!Number.isFinite(payload.lng) || payload.lng < -180 || payload.lng > 180) return { error: "Longitude must be between -180 and 180" };
    if (!VENUE_TYPE_OPTIONS.includes(payload.type)) return { error: "Venue type is invalid" };
    if (payload.tier && !VENUE_TIER_OPTIONS.includes(payload.tier)) return { error: "Venue tier is invalid" };
    return { payload };
  };

  const refreshAfterVenueChange = async () => {
    await loadVenuesForAdmin();
    await onVenuesChanged?.();
  };

  const createVenue = async () => {
    const { payload, error } = validateVenueForm(newForm);
    if (error) {
      showToast?.(error);
      return;
    }
    setBusyId("new");
    try {
      const result = await insertVenuePayload(payload);
      if (result.error) throw result.error;
      setNewForm(emptyAdminVenueForm());
      recordAction?.("Venue added", payload.name);
      showToast?.("Venue added");
      await refreshAfterVenueChange();
    } catch (e) {
      console.warn("[admin.venues.create] failed", e);
      showToast?.(venueWriteErrorMessage(e, "Could not add venue"));
    } finally {
      setBusyId(null);
    }
  };

  const updateVenue = async (venue) => {
    const { payload, error } = validateVenueForm(editForm);
    if (error) {
      showToast?.(error);
      return;
    }
    setBusyId(venue.id);
    try {
      const runUpdate = (nextPayload) => supabase
        .from("venues")
        .update(nextPayload)
        .eq("id", venue.id)
        .select("id,tier")
        .maybeSingle();
      let result = await runUpdate(payload);
      if (result.error && isMissingDetailsColumnError(result.error) && !payload.details) {
        result = await runUpdate(withoutVenueDetails(payload));
      }
      if (result.error) throw result.error;
      if (!result.data?.id) throw new Error("Venue was not updated. Refresh and try again.");
      if ((result.data.tier || null) !== (payload.tier || null)) throw new Error("Venue tier did not update.");
      setEditingId(null);
      recordAction?.("Venue updated", payload.name);
      showToast?.("Venue updated");
      await refreshAfterVenueChange();
    } catch (e) {
      console.warn("[admin.venues.update] failed", e);
      showToast?.(venueWriteErrorMessage(e, "Could not update venue"));
    } finally {
      setBusyId(null);
    }
  };

  const deleteVenue = async (venue) => {
    if (!window.confirm(`Remove ${venue.name || "this venue"} from the venue catalog?`)) return;
    setBusyId(`delete-${venue.id}`);
    try {
      const { error } = await supabase.from("venues").delete().eq("id", venue.id);
      if (error) throw error;
      recordAction?.("Venue removed", venue.name || venue.id);
      showToast?.("Venue removed");
      if (editingId === venue.id) setEditingId(null);
      await refreshAfterVenueChange();
    } catch (e) {
      console.warn("[admin.venues.delete] failed", e);
      showToast?.(venueWriteErrorMessage(e, "Could not remove venue"));
    } finally {
      setBusyId(null);
    }
  };

  const importDefaultVenues = async () => {
    setBusyId("seed");
    try {
      const rows = VENUES_FALLBACK.map(venueSeedPayloadFromRow);
      let result = await supabase.from("venues").upsert(rows, { onConflict: "id" });
      if (result.error && isMissingDetailsColumnError(result.error) && rows.every(row => !row.details)) {
        result = await supabase.from("venues").upsert(rows.map(withoutVenueDetails), { onConflict: "id" });
      }
      if (result.error) throw result.error;
      recordAction?.("Venue catalog imported", `${rows.length} venues`);
      showToast?.("Venue catalog imported");
      await refreshAfterVenueChange();
    } catch (e) {
      console.warn("[admin.venues.seed] failed", e);
      showToast?.(venueWriteErrorMessage(e, "Could not import venue catalog"));
    } finally {
      setBusyId(null);
    }
  };

  const renderVenueForm = (form, setForm, saveLabel, onSave, saving, onCancel) => {
    const autoTier = effectiveVenueTier(form);
    const setFormValue = (key) => (e) => {
      const { value } = e.currentTarget;
      setForm(f => ({ ...f, [key]: value }));
    };
    return (
    <div style={{ display: "flex", flexDirection: "column", gap: SPACE[3] }}>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(130px, 1fr))", gap: SPACE[2] }}>
        <input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="Venue name" style={inputBase} />
        <input value={form.city} onChange={e => setForm(f => ({ ...f, city: e.target.value }))} placeholder="City" style={inputBase} />
        <select aria-label="Venue type" value={form.type} onChange={setFormValue("type")} onInput={setFormValue("type")} style={inputBase}>
          {VENUE_TYPE_OPTIONS.map(type => <option key={type} value={type}>{type}</option>)}
        </select>
      </div>
      <input value={form.address} onChange={e => setForm(f => ({ ...f, address: e.target.value }))} placeholder="Address" style={inputBase} />
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: SPACE[2] }}>
        <input value={form.lat} onChange={e => setForm(f => ({ ...f, lat: e.target.value }))} inputMode="decimal" placeholder="Latitude" style={inputBase} />
        <input value={form.lng} onChange={e => setForm(f => ({ ...f, lng: e.target.value }))} inputMode="decimal" placeholder="Longitude" style={inputBase} />
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(120px, 1fr))", gap: SPACE[2] }}>
        <input value={form.area} onChange={e => setForm(f => ({ ...f, area: e.target.value }))} placeholder="Area" style={inputBase} />
        <input value={form.size} onChange={e => setForm(f => ({ ...f, size: e.target.value }))} placeholder="Size" style={inputBase} />
        <input value={form.price} onChange={e => setForm(f => ({ ...f, price: e.target.value }))} placeholder="Price" style={inputBase} />
        <select aria-label="Venue tier" value={form.tier} onChange={setFormValue("tier")} onInput={setFormValue("tier")} style={inputBase}>
          <option value="">{autoTier ? `Auto tier (${autoTier})` : "Auto tier"}</option>
          <option value="A">Tier A</option>
          <option value="B">Tier B</option>
          <option value="C">Tier C</option>
        </select>
      </div>
      <input value={form.access} onChange={e => setForm(f => ({ ...f, access: e.target.value }))} placeholder="Access" style={inputBase} />
      <textarea value={form.details} onChange={e => setForm(f => ({ ...f, details: e.target.value.slice(0, 500) }))} rows={2} maxLength={500} placeholder="Details" style={{ ...inputBase, resize: "vertical", borderRadius: RADIUS.sm }} />
      <div style={{ display: "flex", gap: SPACE[2], justifyContent: "flex-end" }}>
        {onCancel && <AdminMiniButton onClick={onCancel} disabled={saving}>Cancel</AdminMiniButton>}
        <AdminMiniButton onClick={onSave} disabled={saving} tone="accent">{saving ? "Saving..." : saveLabel}</AdminMiniButton>
      </div>
    </div>
    );
  };

  const q = query.trim().toLowerCase();
  const filtered = q
    ? venuesList.filter(v => [v.name, v.address, v.area, v.city, v.type, v.size].some(value => String(value || "").toLowerCase().includes(q)))
    : venuesList;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: SPACE[3] }}>
      <div style={{ display: "flex", gap: SPACE[2] }}>
        <input value={query} onChange={e => setQuery(e.target.value)} placeholder="Search venues" style={{ ...inputBase, flex: 1 }} />
        <AdminMiniButton onClick={loadVenuesForAdmin} tone="accent">Refresh</AdminMiniButton>
      </div>

      {setupError && (
        <div style={{ ...card, padding: SPACE[4], background: warnBg }}>
          <div style={{ fontSize: 14, fontWeight: 950, color: C.ink }}>Venue admin setup needed</div>
          <div style={{ ...T.small, color: C.inkMid, marginTop: 4 }}>{setupError.includes("Run ") ? setupError : "Run `admin/venues_admin_migration.sql` in Supabase, then import the current catalog."}</div>
        </div>
      )}

      <div style={{ ...card, padding: SPACE[4] }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: SPACE[3], marginBottom: SPACE[3] }}>
          <div>
            <div style={{ fontSize: 15, fontWeight: 950, color: C.ink }}>Add venue</div>
            <div style={{ ...T.small, color: C.inkMid, marginTop: 2 }}>Coordinates are stored directly.</div>
          </div>
          <AdminMiniButton onClick={importDefaultVenues} disabled={busyId === "seed"} tone="success">Import catalog</AdminMiniButton>
        </div>
        {renderVenueForm(newForm, setNewForm, "Add venue", createVenue, busyId === "new")}
      </div>

      <div style={{ ...card, overflow: "hidden" }}>
        <div style={{ padding: SPACE[4], borderBottom: `1px solid ${C.border}` }}>
          <div style={{ fontSize: 15, fontWeight: 950, color: C.ink }}>Venues</div>
          <div style={{ ...T.small, color: C.inkMid, marginTop: 2 }}>{venuesList.length} database venues</div>
        </div>
        {loading ? <AdminEmpty title="Loading venues..." /> : filtered.length === 0 ? <AdminEmpty title="No venues found" body={setupError ? "Apply the SQL migration first." : "Add a venue or import the current catalog."} /> : filtered.map(venue => {
          const editing = editingId === venue.id;
          const tier = effectiveVenueTier(venue);
          const explicitTier = explicitVenueTier(venue);
          return (
            <div key={venue.id} style={{ padding: SPACE[4], borderBottom: `1px solid ${C.border}` }}>
              <div style={{ display: "flex", alignItems: "flex-start", gap: SPACE[3] }}>
                <div style={{ width: 40, height: 40, borderRadius: 10, background: venue.type === "Cageball" ? "#ede9fe" : C.accentBg, color: venue.type === "Cageball" ? PALETTE.violet600 : C.accent, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                  <MapPin size={18} {...ICON} />
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 15, fontWeight: 950, color: C.ink, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{venue.name}</div>
                  <div style={{ ...T.small, color: C.inkMid }}>{venue.address || "No address"} · {venue.lat.toFixed(5)}, {venue.lng.toFixed(5)}</div>
                </div>
                <div style={{ display: "flex", gap: 6, flexWrap: "wrap", justifyContent: "flex-end" }}>
                  <AdminPill label={venue.type || "venue"} tone={venue.type === "Cageball" ? "accent" : "success"} />
                  {tier && <AdminPill label={explicitTier ? `tier ${tier}` : `auto ${tier}`} tone={explicitTier ? "neutral" : "accent"} />}
                </div>
              </div>
              {venue.details && <div style={{ marginTop: SPACE[2], ...T.small, color: C.inkMid, lineHeight: 1.4 }}>{venue.details}</div>}
              {editing ? (
                <div style={{ marginTop: SPACE[3] }}>
                  {renderVenueForm(editForm, setEditForm, "Save venue", () => updateVenue(venue), busyId === venue.id, () => setEditingId(null))}
                </div>
              ) : (
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: SPACE[2], marginTop: SPACE[3] }}>
                  <AdminMiniButton disabled={!!busyId} onClick={() => { setEditingId(venue.id); setEditForm(venueFormFromRow(venue)); }} tone="accent">Edit details</AdminMiniButton>
                  <AdminMiniButton disabled={busyId === `delete-${venue.id}`} onClick={() => deleteVenue(venue)} tone="danger">Remove venue</AdminMiniButton>
                </div>
              )}
              {busyId === `delete-${venue.id}` && <div style={{ marginTop: SPACE[2], padding: SPACE[2], borderRadius: RADIUS.sm, background: dangerBg, color: C.danger, fontSize: 12, fontWeight: 800 }}>Removing...</div>}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function AdminReportsPanel({ showToast }) {
  const { C, T, card, SPACE } = useTheme();
  const [data, setData] = useState({ noShows: [], groupReports: [], matchReports: [], pitchFeedback: [], ratings: [] });
  const [loading, setLoading] = useState(false);

  const loadReportHistory = useCallback(async () => {
    setLoading(true);
    try {
      const [noShows, groupReports, matchReports, pitchFeedback, ratings] = await Promise.all([
        supabase.from("no_show_reports").select("*").order("created_at", { ascending: false }).limit(25),
        supabase.from("community_reports").select("*").order("created_at", { ascending: false }).limit(25),
        supabase.from("match_reports").select("*").order("submitted_at", { ascending: false }).limit(25),
        supabase.from("pitch_feedback").select("*").order("created_at", { ascending: false }).limit(25),
        supabase.from("game_ratings").select("*").order("created_at", { ascending: false }).limit(25),
      ]);
      const failed = [noShows, groupReports, matchReports, pitchFeedback, ratings].find(res => res.error);
      if (failed?.error) throw failed.error;
      setData({
        noShows: noShows.data || [],
        groupReports: groupReports.data || [],
        matchReports: matchReports.data || [],
        pitchFeedback: pitchFeedback.data || [],
        ratings: ratings.data || [],
      });
    } catch (e) {
      console.warn("[admin.reports.load] failed", e);
      showToast?.("Could not load report history");
    } finally {
      setLoading(false);
    }
  }, [showToast]);

  useEffect(() => { loadReportHistory(); }, [loadReportHistory]);

  const renderRows = (title, rows, render) => (
    <div style={{ ...card, overflow: "hidden" }}>
      <div style={{ padding: SPACE[4], borderBottom: `1px solid ${C.border}`, display: "flex", justifyContent: "space-between", gap: SPACE[3] }}>
        <div>
          <div style={{ fontSize: 15, fontWeight: 950, color: C.ink }}>{title}</div>
          <div style={{ ...T.small, color: C.inkMid }}>{rows.length} recent rows</div>
        </div>
      </div>
      {rows.length === 0 ? <AdminEmpty title="No rows yet" /> : rows.map(render)}
    </div>
  );

  if (loading) return <AdminEmpty title="Loading reports..." />;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: SPACE[4] }}>
      <div style={{ display: "flex", justifyContent: "flex-end" }}>
        <AdminMiniButton onClick={loadReportHistory} tone="accent">Refresh reports</AdminMiniButton>
      </div>
      {renderRows("No-show reports", data.noShows, row => (
        <div key={`ns-${row.id}`} style={{ padding: SPACE[3], borderBottom: `1px solid ${C.border}` }}>
          <div style={{ display: "flex", justifyContent: "space-between", gap: SPACE[2] }}>
            <span style={{ fontSize: 13, fontWeight: 850, color: C.ink }}>Reported player</span>
            <AdminPill label={row.status || "open"} tone={row.status === "resolved" ? "success" : row.status === "dismissed" ? "neutral" : "warn"} />
          </div>
          <div style={{ ...T.small, color: C.inkMid, marginTop: 4 }}>{row.reported_user_id} · action {row.action || "none"} · {adminDate(row.created_at)}</div>
        </div>
      ))}
      {renderRows("Group reports", data.groupReports, row => (
        <div key={`gr-${row.id}`} style={{ padding: SPACE[3], borderBottom: `1px solid ${C.border}` }}>
          <div style={{ display: "flex", justifyContent: "space-between", gap: SPACE[2] }}>
            <span style={{ fontSize: 13, fontWeight: 850, color: C.ink }}>Group report</span>
            <AdminPill label={row.status || "open"} tone={row.status === "resolved" ? "success" : row.status === "dismissed" ? "neutral" : "warn"} />
          </div>
          <div style={{ ...T.small, color: C.inkMid, marginTop: 4, whiteSpace: "pre-wrap" }}>{row.reason}</div>
        </div>
      ))}
      {renderRows("Match wrap-ups", data.matchReports, row => (
        <div key={`mr-${row.game_id}-${row.user_id}`} style={{ padding: SPACE[3], borderBottom: `1px solid ${C.border}` }}>
          <div style={{ fontSize: 13, fontWeight: 850, color: C.ink }}>Game rating {row.game_rating || "—"}/5 · pitch {row.pitch_quality || "—"}/5</div>
          <div style={{ ...T.small, color: C.inkMid, marginTop: 4 }}>No-shows: {(row.no_show_user_ids || []).length} · {adminDate(row.submitted_at)}</div>
        </div>
      ))}
      {renderRows("Pitch feedback", data.pitchFeedback, row => (
        <div key={`pf-${row.id}`} style={{ padding: SPACE[3], borderBottom: `1px solid ${C.border}` }}>
          <div style={{ fontSize: 13, fontWeight: 850, color: C.ink }}>{row.venue_name || "Venue"} · quality {row.quality_score || "—"}/5</div>
          <div style={{ ...T.small, color: C.inkMid, marginTop: 4 }}>{row.was_occupied ? "Taken" : "Free"} · {adminDate(row.created_at)}</div>
        </div>
      ))}
      {renderRows("Game ratings", data.ratings, row => (
        <div key={`rt-${row.id}`} style={{ padding: SPACE[3], borderBottom: `1px solid ${C.border}` }}>
          <div style={{ fontSize: 13, fontWeight: 850, color: C.ink }}>Rating {row.rating}/5</div>
          <div style={{ ...T.small, color: C.inkMid, marginTop: 4 }}>Game {row.game_id} · {adminDate(row.created_at)}</div>
        </div>
      ))}
    </div>
  );
}

function AdminActivityPanel({ recentActions }) {
  const { C, T, card, SPACE } = useTheme();
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);

  const loadHistory = useCallback(async () => {
    setLoading(true);
    try {
      const [noShows, groupReports] = await Promise.all([
        supabase.from("no_show_reports").select("id,status,action,moderator_note,resolved_at,created_at").in("status", ["resolved", "dismissed"]).order("resolved_at", { ascending: false }).limit(20),
        supabase.from("community_reports").select("id,status,resolved_at,created_at").in("status", ["resolved", "dismissed"]).order("resolved_at", { ascending: false }).limit(20),
      ]);
      const rows = [
        ...((noShows.data || []).map(row => ({ id: `ns-${row.id}`, label: `No-show ${row.status}`, detail: row.action || row.moderator_note || "Moderation action", at: row.resolved_at || row.created_at }))),
        ...((groupReports.data || []).map(row => ({ id: `gr-${row.id}`, label: `Group report ${row.status}`, detail: "Community report", at: row.resolved_at || row.created_at }))),
      ].sort((a, b) => new Date(b.at).getTime() - new Date(a.at).getTime()).slice(0, 25);
      setHistory(rows);
    } catch (e) {
      console.warn("[admin.activity.load] failed", e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadHistory(); }, [loadHistory]);

  const rows = [...recentActions, ...history].slice(0, 40);

  return (
    <div style={{ ...card, overflow: "hidden" }}>
      <div style={{ padding: SPACE[4], borderBottom: `1px solid ${C.border}`, display: "flex", justifyContent: "space-between", gap: SPACE[3] }}>
        <div>
          <div style={{ fontSize: 15, fontWeight: 950, color: C.ink }}>Activity</div>
          <div style={{ ...T.small, color: C.inkMid }}>Recent admin actions from this session and resolved reports.</div>
        </div>
        <AdminMiniButton onClick={loadHistory} tone="accent">Refresh</AdminMiniButton>
      </div>
      {loading ? <AdminEmpty title="Loading activity..." /> : rows.length === 0 ? <AdminEmpty title="No activity yet" /> : rows.map(action => (
        <div key={action.id} style={{ padding: SPACE[3], borderBottom: `1px solid ${C.border}` }}>
          <div style={{ fontSize: 13, fontWeight: 850, color: C.ink }}>{action.label}</div>
          <div style={{ ...T.small, color: C.inkMid, marginTop: 3 }}>{action.detail} · {adminDate(action.at)}</div>
        </div>
      ))}
    </div>
  );
}

function AdminDashboard({ user, userProfile, showToast, onClose, onSignOut, onVenuesChanged }) {
  const { C, T, btnPrimary, btnSecondary, card, SPACE, RADIUS, SHADOW } = useTheme();
  const isAppAdmin = userProfile?.role === "admin";
  const [adminTab, setAdminTab] = useState("overview");
  const [recentActions, setRecentActions] = useState([]);
  const recordAction = useCallback((label, detail) => {
    setRecentActions(prev => [{ id: `${Date.now()}-${Math.random()}`, label, detail, at: new Date().toISOString() }, ...prev].slice(0, 20));
  }, []);
  const tabs = [
    ["overview", "Overview"],
    ["users", "Users"],
    ["games", "Games"],
    ["groups", "Groups"],
    ["venues", "Venues"],
    ["reports", "Reports"],
    ["activity", "Activity"],
  ];

  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 260, background: C.bg, display: "flex", flexDirection: "column", fontFamily: "'Inter', system-ui, sans-serif" }}>
      <div style={{ display: "flex", alignItems: "center", gap: SPACE[3], padding: `calc(${SPACE[3]}px + env(safe-area-inset-top, 0px)) ${SPACE[4]}px ${SPACE[3]}px`, borderBottom: `1px solid ${C.border}`, flexShrink: 0 }}>
        <button onClick={onClose} aria-label="Back" style={{ width: 38, height: 38, borderRadius: "50%", background: C.surface, border: `1px solid ${C.border}`, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", color: C.ink, boxShadow: SHADOW.sm }}>
          <ArrowLeft size={18} {...ICON} />
        </button>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ ...T.h2, margin: 0 }}>Admin dashboard</div>
          <div style={{ ...T.small, color: C.inkMid, marginTop: 2 }}>{user?.email || "Admin account"}</div>
        </div>
        <button onClick={onSignOut} style={{ ...btnSecondary, padding: "8px 12px", borderRadius: RADIUS.pill, fontSize: 12 }}>
          Sign out
        </button>
      </div>

      <div style={{ flex: 1, overflowY: "auto", padding: `${SPACE[4]}px ${SPACE[4]}px calc(${SPACE[6]}px + env(safe-area-inset-bottom, 0px))` }}>
        {!isAppAdmin ? (
          <div style={{ ...card, padding: SPACE[5], textAlign: "center" }}>
            <div style={{ width: 54, height: 54, borderRadius: "50%", background: C.accentBg, color: C.accent, display: "flex", alignItems: "center", justifyContent: "center", margin: `0 auto ${SPACE[3]}px` }}>
              <ShieldCheck size={24} {...ICON} />
            </div>
            <div style={{ ...T.h2, marginBottom: SPACE[2] }}>Admin access required</div>
            <p style={{ ...T.body, color: C.inkMid, margin: `0 0 ${SPACE[4]}px` }}>This dashboard is only available for accounts with the admin role.</p>
            <button onClick={onClose} style={{ ...btnPrimary, padding: "12px 18px", borderRadius: RADIUS.md }}>Back to app</button>
          </div>
        ) : (
          <>
            <div style={{ display: "flex", gap: SPACE[2], overflowX: "auto", paddingBottom: SPACE[3], marginBottom: SPACE[3] }}>
              {tabs.map(([value, label]) => {
                const active = adminTab === value;
                return (
                  <button key={value} onClick={() => setAdminTab(value)} style={{ flex: "0 0 auto", border: `1px solid ${active ? C.accent : C.border}`, background: active ? C.accentBg : C.surface, color: active ? C.accent : C.inkMid, borderRadius: RADIUS.pill, padding: "9px 13px", fontSize: 13, fontWeight: 900, cursor: "pointer", boxShadow: active ? SHADOW.sm : "none" }}>
                    {label}
                  </button>
                );
              })}
            </div>

            {adminTab === "overview" && <AdminOverviewPanel recentActions={recentActions} />}
            {adminTab === "users" && <AdminUsersPanel showToast={showToast} recordAction={recordAction} />}
            {adminTab === "games" && <AdminGamesPanel showToast={showToast} recordAction={recordAction} />}
            {adminTab === "groups" && <AdminGroupsPanel showToast={showToast} recordAction={recordAction} />}
            {adminTab === "venues" && <AdminVenuesPanel showToast={showToast} recordAction={recordAction} onVenuesChanged={onVenuesChanged} />}
            {adminTab === "reports" && (
              <>
                <SettingsSection title="Live queues">
                  <AdminModerationPanel currentUser={user} showToast={showToast} />
                </SettingsSection>
                <AdminReportsPanel showToast={showToast} />
              </>
            )}
            {adminTab === "activity" && <AdminActivityPanel recentActions={recentActions} />}

            {adminTab === "overview" && (
              <SettingsSection title="Live moderation queues">
              <AdminModerationPanel currentUser={user} showToast={showToast} />
            </SettingsSection>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function SettingsScreen({ user, userProfile, onClose, onSignOut, onAccountDeleted, onOpenAdminDashboard }) {
  const { C, T, btnSecondary, dangerBg, dangerBorder } = useTheme();
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState("");
  const isAppAdmin = userProfile?.role === "admin";
  const legalStatus = hasAcceptedCurrentLegal(userProfile)
    ? `${t("legalAccepted")} ${formatLegalAcceptedDate(userProfile?.terms_accepted_at)}`
    : t("legalNotAccepted");

  // Notification preferences (localStorage; no backend dependency yet).
  const [notifPrefs, setNotifPrefs] = useState(() => {
    try { return JSON.parse(localStorage.getItem("kickr_notif_prefs") || "null") || { reminders: true, friends: true, chat: true }; }
    catch { return { reminders: true, friends: true, chat: true }; }
  });
  const toggleNotif = (k) => {
    const next = { ...notifPrefs, [k]: !notifPrefs[k] };
    setNotifPrefs(next);
    localStorage.setItem("kickr_notif_prefs", JSON.stringify(next));
    // If the user just turned reminders OFF, wipe what's already scheduled so the
    // toggle takes effect immediately instead of lingering on the OS queue.
    if (k === "reminders" && !next.reminders) cancelAllScheduledReminders();
  };

  const currentLang = lang;
  const setLanguage = (l) => {
    localStorage.setItem("kickr_lang", l);
    window.location.reload();
  };

  const handleDeleteAccount = async () => {
    setDeleting(true);
    setDeleteError("");
    try {
      // Best-effort binary cleanup. The authoritative account deletion happens
      // in the SECURITY DEFINER RPC below, where it can atomically cross RLS
      // boundaries and remove auth.users safely.
      try {
        for (const bucket of ["Avatar", "avatars"]) {
          const paths = [];
          const { data: rootFiles } = await supabase.storage.from(bucket).list("", { limit: 100, search: user.id });
          paths.push(...(rootFiles || []).filter(f => f.name === user.id || f.name?.startsWith(`${user.id}.`)).map(f => f.name));
          const { data: folderFiles } = await supabase.storage.from(bucket).list(user.id, { limit: 100 });
          paths.push(...(folderFiles || []).map(f => `${user.id}/${f.name}`));
          if (paths.length) await supabase.storage.from(bucket).remove([...new Set(paths)]);
        }
      } catch { /* bucket may not exist or file missing — ignore */ }

      const { error: rpcErr } = await supabase.rpc("delete_own_account");
      if (rpcErr) throw rpcErr;

      try { await supabase.auth.signOut({ scope: "local" }); } catch { /* user row is already gone */ }
      onAccountDeleted?.();
    } catch (e) {
      console.error("[kickr] delete account failed:", e);
      const message = /delete_own_account|PGRST202|Could not find the function|schema cache/i.test(`${e?.message || ""} ${e?.details || ""} ${e?.hint || ""}`)
        ? "Account deletion is not enabled on this Supabase project yet. Run admin/account_deletion_rpc.sql, then try again."
        : e?.message || "Could not delete account. Please try again.";
      setDeleteError(message);
      setDeleting(false);
    }
  };

  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 250, background: C.bg, display: "flex", flexDirection: "column" }}>
      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "calc(12px + env(safe-area-inset-top, 0px)) 16px 12px", borderBottom: `1px solid ${C.border}`, flexShrink: 0 }}>
        <button onClick={onClose} aria-label="Back" style={{ width: 36, height: 36, borderRadius: "50%", background: "transparent", border: "none", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", color: C.ink }}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6"/></svg>
        </button>
        <div style={{ ...T.h2 }}>{t("settings")}</div>
      </div>
      {/* Body */}
      <div style={{ flex: 1, overflowY: "auto", padding: "20px 16px calc(40px + env(safe-area-inset-bottom, 0px))" }}>

        <SettingsSection title={t("account")}>
          <SettingsRow label={user?.email || "—"} value="" />
          {/* Sign-out is reversible — a red button reads too much like "delete".
              iOS Settings treats sign-out as a neutral action; delete-account
              below stays destructive. */}
          <SettingsRow label={t("signOut")} onClick={onSignOut} last />
        </SettingsSection>

        <SettingsSection title={t("preferences")}>
          <SettingsRow label={t("language")} value={currentLang === "nb" ? "Norsk" : "English"} onClick={() => setLanguage(currentLang === "nb" ? "en" : "nb")} last />
        </SettingsSection>

        <SettingsSection title={t("notifications")}>
          <SettingsToggle label={t("notificationsGameReminders")} on={notifPrefs.reminders} onToggle={() => toggleNotif("reminders")} />
          <SettingsToggle label={t("notificationsFriendRequests")} on={notifPrefs.friends} onToggle={() => toggleNotif("friends")} />
          <SettingsToggle label={t("notificationsChat")} on={notifPrefs.chat} onToggle={() => toggleNotif("chat")} last />
        </SettingsSection>

        {isAppAdmin && (
          <SettingsSection title="Admin">
            <SettingsRow label="Admin dashboard" value="Open" onClick={onOpenAdminDashboard} last />
          </SettingsSection>
        )}

        <SettingsSection title={t("privacy")}>
          <SettingsRow label={t("profileVisibility")} value={t("profileVisibilityPublic")} last />
        </SettingsSection>

        <SettingsSection title={t("about")}>
          <SettingsRow label={t("appVersion")} value="1.0.0" />
          <SettingsRow label={t("legalAgreement")} value={legalStatus} />
          <SettingsRow label={t("termsOfService")} onClick={() => window.open("/terms/", "_blank")} />
          <SettingsRow label={t("privacyPolicy")} onClick={() => window.open("/privacy/", "_blank")} last />
        </SettingsSection>

        {/* Delete account */}
        {!confirmDelete
          ? <button onClick={() => { setDeleteError(""); setConfirmDelete(true); }} style={{ background: "none", border: "none", cursor: "pointer", color: C.inkFaint, fontSize: 12, padding: "8px 0", textDecoration: "underline", textAlign: "center", display: "block", margin: "8px auto" }}>
              {t("deleteAccount")}
            </button>
          : <div style={{ padding: 16, background: dangerBg, borderRadius: 10, border: `1px solid ${dangerBorder}`, display: "flex", flexDirection: "column", gap: 10 }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: C.danger }}>{t("deleteAccountTitle")}</div>
              <div style={{ fontSize: 12, color: C.inkMid, lineHeight: 1.5 }}>{t("deleteAccountBody")}</div>
              {deleteError && <div style={{ fontSize: 12, color: C.danger, lineHeight: 1.45 }}>{deleteError}</div>}
              <div style={{ display: "flex", gap: 8 }}>
                <button onClick={() => { setConfirmDelete(false); setDeleteError(""); }} style={{ ...btnSecondary, flex: 1, padding: "9px 0", fontSize: 13 }}>{t("cancel")}</button>
                <button onClick={handleDeleteAccount} disabled={deleting} style={{ flex: 1, padding: "9px 0", borderRadius: 8, border: "none", background: C.danger, color: "#fff", fontSize: 13, fontWeight: 600, cursor: deleting ? "not-allowed" : "pointer", opacity: deleting ? 0.6 : 1 }}>
                  {deleting ? t("deleting") : t("yesDelete")}
                </button>
              </div>
            </div>
        }
      </div>
    </div>
  );
}

// ─── Friends Screen ───────────────────────────────────────────────────────────
function FriendsScreen({ currentUser, onClose, showToast, invitableGames, reloadKey, onCreateGame, initialGameId, initialPlayerId, initialChatFriendId, embedded = false }) {
  const { C, T, btnPrimary, btnSecondary, inputBase, card, dangerBorder, SPACE, RADIUS, SHADOW } = useTheme();
  const [showMyQR, setShowMyQR] = useState(false);
  const [myQrDataUrl, setMyQrDataUrl] = useState(null);
  const [query, setQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [friends, setFriends] = useState([]);
  const [pendingIn, setPendingIn] = useState([]);
  const [pendingOut, setPendingOut] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showInviteFor, setShowInviteFor] = useState(null); // friendId
  const [inlinePlayer, setInlinePlayer] = useState(null); // { id, name, level, position, avatar_url } — player card shown on top
  const [inlineFriendStatus, setInlineFriendStatus] = useState(null);
  const [inlineFriendLoading, setInlineFriendLoading] = useState(false);
  const [chatFriend, setChatFriend] = useState(null);
  const [chatMessages, setChatMessages] = useState([]);
  const [chatDraft, setChatDraft] = useState("");
  const [chatLoading, setChatLoading] = useState(false);
  const [chatSending, setChatSending] = useState(false);
  const [, setChatUnavailable] = useState(false);
  const debounceRef = useRef(null);
  const chatScrollRef = useRef(null);
  const normalizeFriendProfile = useCallback((profile) => profile ? {
    ...profile,
    id: profile.id || profile.user_id,
  } : profile, []);
  const hideLegacyDirectChatNotice = useCallback((rows = []) => rows.filter(message => {
    const body = String(message?.body || "");
    return !/direct chat needs the friends sql migration/i.test(body) &&
      !/messages can be stored/i.test(body);
  }), []);
  const mergeDirectMessageRows = useCallback((incoming = [], { replace = false } = {}) => {
    const rows = hideLegacyDirectChatNotice(Array.isArray(incoming) ? incoming : [incoming]).filter(Boolean);
    setChatMessages(prev => {
      const now = Date.now();
      const pendingLocal = prev.filter(m => {
        if (!String(m.id).startsWith("local-")) return false;
        const localTime = new Date(m.created_at || 0).getTime();
        return Number.isFinite(localTime) && now - localTime < 2 * 60_000;
      });
      const next = replace ? pendingLocal : [...prev];
      rows.forEach(row => {
        const existingIndex = next.findIndex(m => m.id === row.id);
        if (existingIndex >= 0) {
          next[existingIndex] = { ...next[existingIndex], ...row };
          return;
        }
        const rowTime = new Date(row.created_at || 0).getTime();
        const optimisticIndex = next.findIndex(m => {
          if (!String(m.id).startsWith("local-")) return false;
          if (m.sender_id !== row.sender_id || m.recipient_id !== row.recipient_id || m.body !== row.body) return false;
          const localTime = new Date(m.created_at || 0).getTime();
          return Number.isFinite(rowTime) && Number.isFinite(localTime) && Math.abs(rowTime - localTime) < 2 * 60_000;
        });
        if (optimisticIndex >= 0) next[optimisticIndex] = row;
        else next.push(row);
      });
      return next.sort((a, b) => new Date(a.created_at || 0) - new Date(b.created_at || 0));
    });
  }, [hideLegacyDirectChatNotice]);

  // Auto-open player card when coming from search (initialPlayerId)
  useEffect(() => {
    if (!initialPlayerId) return;
	    supabasePublic.from("profiles_public").select("id,user_id,name,username,level,position,avatar_url").eq("user_id", initialPlayerId).maybeSingle()
      .then(({ data }) => { if (data) { setInlinePlayer(normalizeFriendProfile(data)); setInlineFriendStatus(null); } })
      .catch(() => {});
  }, [initialPlayerId, normalizeFriendProfile]);

  useEffect(() => {
    if (!initialChatFriendId || !currentUser) return;
	    supabasePublic.from("profiles_public").select("id,user_id,name,username,level,position,avatar_url").eq("user_id", initialChatFriendId).maybeSingle()
      .then(({ data }) => { if (data) setChatFriend(normalizeFriendProfile(data)); })
      .catch(() => {});
  }, [initialChatFriendId, currentUser, normalizeFriendProfile]);

  const load = useCallback(async () => {
    if (!currentUser) return;
    try {
      const { data, error } = await supabase
        .from("friendships")
        .select("*")
        .or(`requester_id.eq.${currentUser.id},recipient_id.eq.${currentUser.id}`);
      if (error) { setLoading(false); return; }
      const rows = data || [];

      // Collect all profile IDs we need to fetch
      const acceptedRows = rows.filter(f => f.status === "accepted");
      const pendingInRows = rows.filter(f => f.status === "pending" && f.recipient_id === currentUser.id);
      const pendingOutRows = rows.filter(f => f.status === "pending" && f.requester_id === currentUser.id);

      const friendIds = acceptedRows.map(f => f.requester_id === currentUser.id ? f.recipient_id : f.requester_id);
      const pendingInIds = pendingInRows.map(f => f.requester_id);
      const allIds = [...new Set([...friendIds, ...pendingInIds])];

      let profileMap = {};
      if (allIds.length > 0) {
        const { data: publicProfiles, error: publicError } = await supabasePublic
	          .from("profiles_public")
	          .select("id,user_id,name,username,level,position,avatar_url")
	          .in("user_id", allIds);
        if (publicError) {
          console.warn("[friends.profiles_public] failed", publicError);
        } else {
          (publicProfiles || []).forEach(profile => {
            const normalized = normalizeFriendProfile(profile);
            if (normalized?.id) profileMap[normalized.id] = normalized;
          });
        }
      }

      setFriends(friendIds.map(id => profileMap[id]).filter(Boolean));
      setPendingIn(pendingInRows.map(f => ({ ...f, requester: profileMap[f.requester_id] || { id: f.requester_id, name: "Unknown" } })));
      setPendingOut(pendingOutRows);
    } catch (e) {
      console.error("[friends.load] failed", e);
      reportError(e, { where: "friends.load" });
    }
    finally { setLoading(false); }
  }, [currentUser, normalizeFriendProfile]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (reloadKey) load(); }, [load, reloadKey]);

  // Realtime subscription for incoming friend requests
  useEffect(() => {
    if (!currentUser) return;
    let mounted = true;
    const channel = supabase.channel(`friendships-${currentUser.id}`)
      .on("postgres_changes", { event: "*", schema: "public", table: "friendships", filter: `recipient_id=eq.${currentUser.id}` }, () => {
        if (mounted) load();
      })
      .on("postgres_changes", { event: "*", schema: "public", table: "friendships", filter: `requester_id=eq.${currentUser.id}` }, () => {
        if (mounted) load();
      })
      .subscribe();
    return () => { mounted = false; supabase.removeChannel(channel); };
  }, [currentUser, load]);

  const search = (q) => {
    setQuery(q);
    clearTimeout(debounceRef.current);
    if (!q.trim() || q.trim().length < 2) { setSearchResults([]); return; }
    debounceRef.current = setTimeout(async () => {
      setSearching(true);
      let rows = [];
      const { data: publicProfiles, error: publicError } = await supabasePublic
	        .from("profiles_public")
	        .select("id,user_id,name,username,level,position,avatar_url")
	        .ilike("name", `%${q.trim()}%`)
        .limit(12);
      if (publicError) {
        console.warn("[friends.search.profiles_public] failed", publicError);
        rows = [];
      } else {
        rows = (publicProfiles || [])
          .map(normalizeFriendProfile)
          .filter(profile => profile?.id && profile.id !== currentUser.id)
          .slice(0, 8);
      }
      setSearchResults(rows);
      setSearching(false);
    }, 300);
  };

  const sendRequest = async (recipientId) => {
    if (!recipientId || recipientId === currentUser.id) return; // self-friend guard
    // Check for existing friendship or pending request in either direction
    const { data: existing } = await supabase.from("friendships")
      .select("id, status")
      .or(`and(requester_id.eq.${currentUser.id},recipient_id.eq.${recipientId}),and(requester_id.eq.${recipientId},recipient_id.eq.${currentUser.id})`)
      .maybeSingle();
    if (existing) {
      showToast(existing.status === "accepted" ? "You are already friends!" : "Friend request already sent");
      return;
    }
    const { error } = await supabase.from("friendships").insert({ requester_id: currentUser.id, recipient_id: recipientId, status: "pending" });
    if (error) { showToast(t("couldNotSendRequest")); return; }
    showToast(t("friendRequestSent"));
    setPendingOut(prev => [...prev, { requester_id: currentUser.id, recipient_id: recipientId, status: "pending" }]);
    // Notify the recipient
    const senderName = currentUser?.user_metadata?.name || currentUser?.email?.split("@")[0] || "Someone";
    const { error: notifErr } = await supabase.from("notifications").insert({ user_id: recipientId, type: "friend_request", text: `${senderName} sent you a friend request`, sub: currentUser.id, game_id: null, read: false });
    if (notifErr) console.warn("[notify] friend_request insert failed", notifErr);
  };

  const acceptRequest = async (f) => {
    // Ownership: only the recipient can accept — include recipient_id guard.
    // Status guard prevents double-fire if the request was already accepted.
    const { data: updated, error } = await supabase
      .from("friendships")
      .update({ status: "accepted" })
      .eq("id", f.id)
      .eq("recipient_id", currentUser.id)
      .eq("status", "pending")
      .select("id");
    if (error) { showToast(t("couldNotAcceptRequest")); return; }
    if (!updated || updated.length === 0) {
      // Already accepted or no longer pending — clean up local state silently
      setPendingIn(prev => prev.filter(x => x.id !== f.id));
      return;
    }
    setPendingIn(prev => prev.filter(x => x.id !== f.id));
    if (f.requester) setFriends(prev => [...prev, f.requester]);
    showToast(t("friendAdded"));
    // Notify the requester their request was accepted
    const myName = currentUser?.user_metadata?.name || currentUser?.email?.split("@")[0] || "Someone";
    const { error: notifErr } = await supabase.from("notifications").insert({ user_id: f.requester_id, type: "friend_accepted", text: `${myName} accepted your friend request`, sub: null, game_id: null });
    if (notifErr) console.warn("[notify] friend_accepted insert failed", notifErr);
  };

  const declineRequest = async (f) => {
    // Ownership: only a party to the friendship can delete it
    const { error } = await supabase.from("friendships").delete().eq("id", f.id).eq("recipient_id", currentUser.id);
    if (error) { showToast(t("couldNotDeclineRequest")); return; }
    setPendingIn(prev => prev.filter(x => x.id !== f.id));
  };

  const sendGameInvite = async (friendId, game) => {
    const senderName = currentUser?.user_metadata?.name || currentUser?.email?.split("@")[0] || "Someone";
    const payload = {
      user_id: friendId,
      type: "game_invite",
      text: `${senderName} invited you to join a game`,
      sub: game.title,
      game_id: game.id,
    };
    // Try once; if it fails, refresh the session (handles stale JWT → auth.uid() null → RLS reject) and retry.
    let { error } = await supabase.from("notifications").insert(payload);
    if (error) {
      const { error: refreshErr } = await supabase.auth.refreshSession();
      if (!refreshErr) {
        ({ error } = await supabase.from("notifications").insert(payload));
      }
    }
    if (error) {
      const { data: sessionData } = await supabase.auth.getSession().catch(() => ({ data: null }));
      console.error("[sendGameInvite] failed", {
        code: error?.code, message: error?.message, details: error?.details, hint: error?.hint,
        payload, gameId: game.id, friendId,
        hasSession: !!sessionData?.session, sessionUid: sessionData?.session?.user?.id,
      });
      const why = error?.message || error?.details || error?.code || "unknown";
      showToast(`${t("couldNotSendInvite")} (${why})`);
    } else {
      showToast(t("inviteSent", { title: game.title }));
    }
    setShowInviteFor(null);
  };

  const isFriend = (id) => friends.some(f => f?.id === id);
  const hasPendingOut = (id) => pendingOut.some(f => f.recipient_id === id);

  const loadFriendMessages = useCallback(async (friend) => {
    if (!currentUser || !friend?.id) return;
    setChatLoading(true);
    setChatUnavailable(false);
    try {
      const { data, error } = await supabase
        .from("direct_messages")
        .select("id,sender_id,recipient_id,body,created_at,read_at")
        .or(`and(sender_id.eq.${currentUser.id},recipient_id.eq.${friend.id}),and(sender_id.eq.${friend.id},recipient_id.eq.${currentUser.id})`)
        .order("created_at", { ascending: true })
        .limit(80);
      if (error) throw error;
      mergeDirectMessageRows(data || [], { replace: true });
      setChatUnavailable(false);
      await supabase
        .from("direct_messages")
        .update({ read_at: new Date().toISOString() })
        .eq("sender_id", friend.id)
        .eq("recipient_id", currentUser.id)
        .is("read_at", null);
    } catch (e) {
      console.warn("[friend.chat.load] failed", e);
      setChatMessages(prev => prev);
      setChatUnavailable(true);
    } finally {
      setChatLoading(false);
    }
  }, [currentUser, mergeDirectMessageRows]);

  useEffect(() => {
    if (!chatFriend) return;
    loadFriendMessages(chatFriend);
  }, [chatFriend, loadFriendMessages]);

  useEffect(() => {
    if (!currentUser || !chatFriend?.id) return undefined;
    const upsertMessage = (row) => {
      const belongsToThread =
        (row.sender_id === currentUser.id && row.recipient_id === chatFriend.id) ||
        (row.sender_id === chatFriend.id && row.recipient_id === currentUser.id);
      if (!belongsToThread) return;
      mergeDirectMessageRows(row);
    };
    const channel = supabase
      .channel(`direct-messages-${currentUser.id}-${chatFriend.id}`)
      .on("postgres_changes", { event: "INSERT", schema: "public", table: "direct_messages", filter: `recipient_id=eq.${currentUser.id}` }, payload => upsertMessage(payload.new))
      .on("postgres_changes", { event: "INSERT", schema: "public", table: "direct_messages", filter: `sender_id=eq.${currentUser.id}` }, payload => upsertMessage(payload.new))
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, [chatFriend, currentUser, mergeDirectMessageRows]);

  useEffect(() => {
    if (!currentUser || !chatFriend?.id) return undefined;
    let cancelled = false;
    const pollMessages = async () => {
      try {
        const { data, error } = await supabase
          .from("direct_messages")
          .select("id,sender_id,recipient_id,body,created_at,read_at")
          .or(`and(sender_id.eq.${currentUser.id},recipient_id.eq.${chatFriend.id}),and(sender_id.eq.${chatFriend.id},recipient_id.eq.${currentUser.id})`)
          .order("created_at", { ascending: true })
          .limit(80);
        if (error) throw error;
        if (cancelled) return;
        mergeDirectMessageRows(data || []);
        await supabase
          .from("direct_messages")
          .update({ read_at: new Date().toISOString() })
          .eq("sender_id", chatFriend.id)
          .eq("recipient_id", currentUser.id)
          .is("read_at", null);
      } catch (e) {
        console.warn("[friend.chat.poll] failed", e);
      }
    };
    const interval = window.setInterval(pollMessages, 4000);
    return () => {
      cancelled = true;
      window.clearInterval(interval);
    };
  }, [chatFriend, currentUser, mergeDirectMessageRows]);

  useEffect(() => {
    if (!chatScrollRef.current) return;
    chatScrollRef.current.scrollTop = chatScrollRef.current.scrollHeight;
  }, [chatMessages, chatFriend]);

  const sendFriendMessage = async () => {
    const body = chatDraft.trim();
    if (!body || !chatFriend?.id || !currentUser || chatSending) return;
    setChatSending(true);
    setChatUnavailable(false);
    const sentAt = new Date().toISOString();
    const localId = `local-${sentAt}-${Math.random().toString(36).slice(2)}`;
    const optimisticMessage = {
      id: localId,
      sender_id: currentUser.id,
      recipient_id: chatFriend.id,
      body,
      created_at: sentAt,
      read_at: null,
    };
    setChatDraft("");
    mergeDirectMessageRows(optimisticMessage);
    try {
      const { data, error } = await supabase
        .from("direct_messages")
        .insert({ sender_id: currentUser.id, recipient_id: chatFriend.id, body })
        .select("id,sender_id,recipient_id,body,created_at,read_at")
        .maybeSingle();
      if (error) throw error;
      setChatUnavailable(false);
      if (data) mergeDirectMessageRows(data);
      try {
        const { error: notificationError } = await supabase.from("notifications").insert({
          user_id: chatFriend.id,
          type: "friend_message",
          text: `${currentUser?.user_metadata?.name || currentUser?.email?.split("@")[0] || "Someone"} sent you a message`,
          sub: encodeChatNotificationSub({ kind: "friend_chat", senderId: currentUser.id, preview: body }),
          game_id: null,
          read: false,
        });
        if (notificationError) console.warn("[friend.chat.notification] failed", notificationError);
      } catch (notificationError) {
        console.warn("[friend.chat.notification] failed", notificationError);
      }
    } catch (e) {
      console.warn("[friend.chat.send] failed", e);
      setChatMessages(prev => prev.filter(message => message.id !== localId));
      setChatDraft(prev => prev || body);
      showToast?.(t("chatSendError"));
    } finally {
      setChatSending(false);
    }
  };

  return (
    <div style={{ position: embedded ? "relative" : "absolute", inset: embedded ? "auto" : 0, zIndex: embedded ? "auto" : 55, background: embedded ? "transparent" : C.bg, display: "flex", flexDirection: "column", animation: embedded ? "none" : "slideUp 0.25s ease" }}>
      {/* Header */}
      {!embedded && <div style={{ paddingTop: "calc(14px + env(safe-area-inset-top, 44px))", paddingLeft: 16, paddingRight: 16, paddingBottom: 14, borderBottom: `1px solid ${C.border}`, display: "flex", alignItems: "center", gap: 12, flexShrink: 0 }}>
        <button onClick={onClose} style={{ background: "none", border: "none", cursor: "pointer", color: C.inkMid, padding: 0, display: "flex", alignItems: "center", justifyContent: "center", minWidth: 44, minHeight: 44 }}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><polyline points="15 18 9 12 15 6"/></svg>
        </button>
        <div style={{ ...T.h2, flex: 1 }}>{t("friends")}</div>
        <button onClick={async () => {
          const url = `${window.location.origin}?addFriend=${currentUser.id}`;
          try {
            const dataUrl = await QRCode.toDataURL(url, { width: 256, margin: 2, color: { dark: C.isDark ? "#e8f0ff" : "#0f1623", light: C.isDark ? "#121e35" : "#ffffff" } });
            setMyQrDataUrl(dataUrl);
            setShowMyQR(true);
          } catch { showToast(t("couldNotGenerateQR")); }
        }} style={{ ...btnSecondary, padding: "6px 14px", fontSize: 13 }}>My QR</button>
      </div>}

      {showMyQR && (
        <div style={{ position: "absolute", inset: 0, zIndex: 80, background: "rgba(0,0,0,0.6)", display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }} onClick={() => setShowMyQR(false)}>
          <div onClick={e => e.stopPropagation()} style={{ background: C.bg, borderRadius: 16, padding: 24, display: "flex", flexDirection: "column", alignItems: "center", gap: 16, width: "min(300px, 100%)" }}>
            <div style={{ ...T.h3 }}>{t("addMeOnKickr")}</div>
            <div style={{ ...T.small, color: C.inkFaint, textAlign: "center" }}>{t("scanToSendRequest")}</div>
            {myQrDataUrl && <img src={myQrDataUrl} alt="Friend QR" style={{ width: 200, height: 200, borderRadius: 8 }} />}
            <button onClick={() => { navigator.clipboard?.writeText(`${window.location.origin}?addFriend=${currentUser.id}`).then(() => showToast(t("linkCopied"))); }} style={{ ...btnSecondary, width: "100%", padding: "10px 0" }}>{t("copyLink")}</button>
            <button onClick={() => setShowMyQR(false)} style={{ ...btnPrimary, width: "100%", padding: "10px 0" }}>{t("close")}</button>
          </div>
        </div>
      )}

      <div style={{ flex: embedded ? "0 0 auto" : 1, overflowY: embedded ? "visible" : "auto", padding: embedded ? 0 : "16px 20px", display: "flex", flexDirection: "column", gap: 20 }}>

        {/* "Inviting to" banner when opened from a game */}
        {(() => {
          const initialGame = initialGameId ? (invitableGames || []).find(g => g.id === initialGameId) : null;
          if (!initialGame) return null;
          return (
            <div style={{ background: C.accentBg, border: `1px solid ${C.accent}33`, borderRadius: 12, padding: "12px 16px", display: "flex", alignItems: "center", gap: 12 }}>
              <span style={{ width: 32, height: 32, borderRadius: "50%", background: C.surface, color: C.accent, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, boxShadow: `0 0 0 1px ${C.accent}33` }}>
                <Users size={17} {...ICON} />
              </span>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 11, fontWeight: 600, color: C.accent, textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 2 }}>Inviting friends to</div>
                <div style={{ fontSize: 14, fontWeight: 600, color: C.ink }}>{initialGame.title}</div>
                <div style={{ fontSize: 12, color: C.inkFaint, marginTop: 1 }}>{initialGame.spotsTotal - initialGame.spotsFilled} spot{initialGame.spotsTotal - initialGame.spotsFilled !== 1 ? "s" : ""} left · tap Invite on any friend below</div>
              </div>
            </div>
          );
        })()}

        {/* Search */}
        <div>
          <div style={{ ...T.label, marginBottom: 8 }}>{t("findPlayers")}</div>
          <div style={{ position: "relative" }}>
            <Search size={15} {...ICON} color={C.inkFaint} style={{ position: "absolute", left: SPACE[4], top: "50%", transform: "translateY(-50%)", pointerEvents: "none" }} />
            <input value={query} onChange={e => search(e.target.value)} placeholder={t("searchByName")} style={{ ...inputBase, paddingLeft: SPACE[7] - 4, paddingRight: searching ? 36 : 14, borderRadius: RADIUS.pill, background: C.surface }} />
            {searching && <div style={{ position: "absolute", right: 12, top: "50%", transform: "translateY(-50%)", width: 14, height: 14, border: `2px solid ${C.border}`, borderTopColor: C.accent, borderRadius: "50%", animation: "spin 0.6s linear infinite" }} />}
          </div>
          {searchResults.length > 0 && (
            <div style={{ marginTop: 8, ...card, overflow: "hidden" }}>
              {searchResults.map((p, i) => {
                const friend = isFriend(p.id);
                const pending = hasPendingOut(p.id);
                return (
                  <div key={p.id} style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 14px", borderBottom: i < searchResults.length - 1 ? `1px solid ${C.border}` : "none" }}>
                    <Avatar initials={(p.name || "??").slice(0, 2).toUpperCase()} size={36} />
                    <div style={{ flex: 1 }}>
                      <div style={{ ...T.h3 }}>{p.name}</div>
                      <LevelPill level={p.level || "Intermediate"} small />
                    </div>
                    {friend
                      ? <span style={{ fontSize: 12, fontWeight: 600, color: C.success }}>{t("friends_label")}</span>
                      : pending
                        ? <span style={{ fontSize: 12, color: C.inkFaint }}>{t("requested")}</span>
                        : <button onClick={() => sendRequest(p.id)} style={{ ...btnPrimary, padding: "5px 12px", fontSize: 12, borderRadius: 6 }}>{t("add")}</button>
                    }
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Incoming requests */}
        {pendingIn.length > 0 && (
          <div>
            <div style={{ ...T.label, marginBottom: 8, color: C.warn }}>{t("requests", { n: pendingIn.length })}</div>
            {pendingIn.map(f => (
              <div key={f.id} style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 0", borderBottom: `1px solid ${C.border}` }}>
                <Avatar initials={(f.requester?.name || "??").slice(0, 2).toUpperCase()} size={36} />
                <div style={{ flex: 1 }}>
                  <div style={{ ...T.h3 }}>{f.requester?.name}</div>
                  <LevelPill level={f.requester?.level || "Intermediate"} small />
                </div>
                <div style={{ display: "flex", gap: 6 }}>
                  <button onClick={() => declineRequest(f)} style={{ ...btnSecondary, padding: "5px 10px", fontSize: 12, borderRadius: 6, color: C.danger, borderColor: dangerBorder }}>{t("decline")}</button>
                  <button onClick={() => acceptRequest(f)} style={{ ...btnPrimary, padding: "5px 10px", fontSize: 12, borderRadius: 6 }}>{t("accept")}</button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Friends list */}
        <div>
          <div style={{ ...T.label, marginBottom: 8 }}>{t("yourFriends", { n: friends.length })}</div>
          {loading ? <div style={{ ...T.small, color: C.inkFaint }}>{t("loading")}</div>
            : friends.length === 0
              ? <div style={{ ...T.body, color: C.inkFaint, textAlign: "center", padding: "24px 0" }}>{t("noFriendsYet")}</div>
              : <div style={{ display: "flex", flexDirection: "column", gap: SPACE[3] }}>
                {friends.filter(Boolean).map(f => {
                const initialGame = initialGameId ? (invitableGames || []).find(g => g.id === initialGameId) : null;
                return (
                <div key={f.id}>
                  <div style={{ display: "flex", alignItems: "center", gap: SPACE[3], padding: SPACE[3], borderRadius: RADIUS.md, background: C.surface, boxShadow: SHADOW.sm }}>
                    <button
                      type="button"
                      onClick={() => { setInlinePlayer(f); setInlineFriendStatus("accepted"); }}
                      style={{ display: "flex", alignItems: "center", gap: SPACE[3], flex: 1, minWidth: 0, padding: 0, background: "transparent", border: "none", cursor: "pointer", textAlign: "left" }}
                      aria-label={`Open ${f.name}'s profile`}
                    >
                      <Avatar initials={(f.name || "??").slice(0, 2).toUpperCase()} avatarUrl={f.avatar_url} size={42} />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ ...T.h3, color: C.ink, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{f.name}</div>
                        <LevelPill level={f.level || "Intermediate"} small />
                      </div>
                    </button>
                    <div style={{ display: "flex", alignItems: "center", gap: SPACE[2], flexShrink: 0 }}>
                      <button
                        type="button"
                        aria-label={`Chat with ${f.name}`}
                        title="Chat"
                        onClick={() => setChatFriend(f)}
                        style={{
                          ...btnSecondary,
                          minHeight: 34,
                          padding: `0 ${SPACE[3]}px`,
                          borderRadius: RADIUS.pill,
                          display: "inline-flex",
                          alignItems: "center",
                          gap: SPACE[2],
                          color: C.inkMid,
                          fontSize: 13,
                          fontWeight: 800,
                          background: C.surface,
                        }}
                      >
                        <MessageCircle size={14} {...ICON} color={C.inkMid} />
                        <span>{t("chat")}</span>
                      </button>
                      <button
                        type="button"
                        aria-label={`Invite ${f.name}`}
                        title={t("invite")}
                        onClick={() => {
                          if (initialGame) { sendGameInvite(f.id, initialGame); }
                          else { setShowInviteFor(showInviteFor === f.id ? null : f.id); }
                        }}
                        style={{
                          ...btnSecondary,
                          minHeight: 34,
                          padding: `0 ${SPACE[3]}px`,
                          borderRadius: RADIUS.pill,
                          display: "inline-flex",
                          alignItems: "center",
                          gap: SPACE[2],
                          color: showInviteFor === f.id ? C.accent : C.inkMid,
                          fontSize: 13,
                          fontWeight: 850,
                          background: showInviteFor === f.id ? C.accentBg : C.surface,
                          borderColor: showInviteFor === f.id ? `${C.accent}33` : C.border,
                        }}
                      >
                        <Send size={14} {...ICON} color={showInviteFor === f.id ? C.accent : C.inkMid} />
                        <span>{t("invite")}</span>
                      </button>
                    </div>
                  </div>
                  {showInviteFor === f.id && (
                    <div style={{ paddingLeft: 48, paddingBottom: 8 }}>
                      {(() => {
                        const eligible = (invitableGames || []).filter(g => g.hostId !== f.id && !g.players?.some(p => p.id === f.id));
                        return eligible.length > 0
                          ? eligible.map(g => (
                              <button key={g.id} onClick={() => sendGameInvite(f.id, g)} style={{ display: "block", width: "100%", textAlign: "left", padding: "8px 12px", borderRadius: 8, border: `1px solid ${C.border}`, background: C.surface, cursor: "pointer", marginBottom: 4 }}>
                                <div style={{ fontSize: 13, fontWeight: 500, color: C.ink }}>{g.title}</div>
                                <div style={{ fontSize: 12, color: C.inkMid }}>{g.time} · {g.spotsTotal - g.spotsFilled} spots left</div>
                              </button>
                            ))
                          : <div style={{ padding: "10px 0", display: "flex", flexDirection: "column", gap: 8 }}>
                              <div style={{ ...T.small, color: C.inkFaint }}>{(invitableGames || []).length > 0 ? t("alreadyInGames", { firstName: f.name?.split(" ")[0] || "They" }) : t("noOpenGames")}</div>
                              {!(invitableGames || []).length && <button onClick={() => { onClose(); onCreateGame?.(); }} style={{ ...btnPrimary, padding: "7px 14px", fontSize: 12, borderRadius: 6, alignSelf: "flex-start" }}>{t("createAGame")}</button>}
                            </div>;
                      })()}
                    </div>
                  )}
                </div>
                );
              })}
              </div>
          }
        </div>
      </div>

      {/* Inline player card overlay — shown when tapping a player from search */}
      {chatFriend && (
        <div style={{ position: "fixed", inset: 0, zIndex: 190, background: "rgba(15,22,35,0.42)", display: "flex", alignItems: "flex-end" }} onClick={() => setChatFriend(null)}>
          <div onClick={e => e.stopPropagation()} style={{ width: "100%", maxHeight: "78vh", background: C.bg, borderRadius: "20px 20px 0 0", boxShadow: "0 -8px 30px rgba(0,0,0,0.22)", display: "flex", flexDirection: "column" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "14px 16px", borderBottom: `1px solid ${C.border}`, flexShrink: 0 }}>
              <Avatar initials={(chatFriend.name || "??").slice(0, 2).toUpperCase()} avatarUrl={chatFriend.avatar_url} size={38} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ ...T.h3, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{chatFriend.name}</div>
                <div style={{ ...T.small, color: C.inkFaint }}>{t("directChatSubtitle")}</div>
              </div>
              <button onClick={() => setChatFriend(null)} aria-label="Close chat" style={{ width: 34, height: 34, borderRadius: "50%", border: `1px solid ${C.border}`, background: C.surface, color: C.inkMid, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer" }}>
                <X size={16} {...ICON} />
              </button>
            </div>
            <div ref={chatScrollRef} style={{ flex: 1, minHeight: 220, overflowY: "auto", padding: 16, display: "flex", flexDirection: "column", gap: 8 }}>
              {chatLoading && <div style={{ ...T.small, color: C.inkFaint, textAlign: "center", padding: 16 }}>{t("loading")}</div>}
              {!chatLoading && chatMessages.length === 0 && (
                <div style={{ ...T.small, color: C.inkFaint, textAlign: "center", padding: 18 }}>
                  {t("directChatEmpty")}
                </div>
              )}
              {chatMessages.map(message => {
                const mine = message.sender_id === currentUser?.id;
                return (
                  <div key={message.id} style={{ alignSelf: mine ? "flex-end" : "flex-start", maxWidth: "78%" }}>
	                    <div style={{ padding: "9px 12px", borderRadius: mine ? "16px 16px 4px 16px" : "16px 16px 16px 4px", background: mine ? C.accent : C.surface, color: mine ? C.accentText : C.ink, border: mine ? "none" : `1px solid ${C.border}`, fontSize: 14, lineHeight: 1.35 }}>
	                      <MentionText text={message.body} own={mine} />
	                    </div>
                    <div style={{ marginTop: 3, textAlign: mine ? "right" : "left", color: C.inkFaint, fontSize: 10 }}>
                      {message.created_at ? format(new Date(message.created_at), "HH:mm") : ""}
                    </div>
                  </div>
                );
              })}
            </div>
	            <MentionSuggestions draft={chatDraft} candidates={chatFriend ? [chatFriend] : []} max={1} onPick={profile => setChatDraft(prev => insertMentionHandle(prev, profile).slice(0, 1000))} />
	            <div style={{ display: "flex", alignItems: "flex-end", gap: 8, padding: "10px 16px calc(10px + env(safe-area-inset-bottom, 0px))", borderTop: `1px solid ${C.border}`, background: C.bg, flexShrink: 0 }}>
              <textarea
                value={chatDraft}
                onChange={e => setChatDraft(e.target.value)}
                onKeyDown={e => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendFriendMessage(); } }}
                rows={1}
                maxLength={1000}
                placeholder={t("directChatPlaceholder")}
                style={{ flex: 1, minHeight: 38, maxHeight: 112, resize: "none", border: `1px solid ${C.border}`, borderRadius: 18, padding: "9px 12px", background: C.surface, color: C.ink, font: "inherit", outline: "none" }}
              />
              <button onClick={sendFriendMessage} disabled={chatSending || !chatDraft.trim()} aria-label="Send message" style={{ width: 38, height: 38, borderRadius: "50%", border: "none", background: C.accent, color: C.accentText, opacity: chatSending || !chatDraft.trim() ? 0.5 : 1, cursor: chatSending || !chatDraft.trim() ? "not-allowed" : "pointer", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                <Send size={16} {...ICON} color={C.accentText} />
              </button>
            </div>
          </div>
        </div>
      )}

      {inlinePlayer && (
        <div style={{ position: "absolute", inset: 0, zIndex: 110, background: "rgba(0,0,0,0.5)", display: "flex", alignItems: "flex-end" }} onClick={() => { setInlinePlayer(null); setInlineFriendStatus(null); }}>
          <div style={{ background: C.surface, borderRadius: "20px 20px 0 0", padding: 20, width: "100%", boxSizing: "border-box" }} onClick={e => e.stopPropagation()}>
            <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 16 }}>
              <Avatar initials={(inlinePlayer.name || "??").slice(0, 2).toUpperCase()} avatarUrl={inlinePlayer.avatar_url} size={52} />
              <div style={{ flex: 1 }}>
                <div style={{ ...T.h2, marginBottom: 4 }}>{inlinePlayer.name}</div>
                <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                  <LevelPill level={inlinePlayer.level} />
                  {inlinePlayer.position && <span style={{ fontSize: 12, color: C.inkMid, fontWeight: 500 }}>{inlinePlayer.position}</span>}
                </div>
              </div>
            </div>
            {currentUser && inlinePlayer.id !== currentUser.id && (
              <button
                disabled={inlineFriendLoading || inlineFriendStatus === "accepted" || inlineFriendStatus === "sent"}
                onClick={async () => {
                  setInlineFriendLoading(true);
                  const { data: existing } = await supabase.from("friendships").select("id, status").or(`and(requester_id.eq.${currentUser.id},recipient_id.eq.${inlinePlayer.id}),and(requester_id.eq.${inlinePlayer.id},recipient_id.eq.${currentUser.id})`).maybeSingle();
                  if (existing) {
                    setInlineFriendStatus(existing.status === "accepted" ? "accepted" : "sent");
                  } else {
                    const { error } = await supabase.from("friendships").insert({ requester_id: currentUser.id, recipient_id: inlinePlayer.id, status: "pending" });
                    if (error) { showToast(t("couldNotSendRequest")); }
                    else {
                      setInlineFriendStatus("sent");
                      showToast(t("friendRequestSent"));
                      const senderName = currentUser?.user_metadata?.name || currentUser?.email?.split("@")[0] || "Someone";
                      supabase.from("notifications").insert({ user_id: inlinePlayer.id, type: "friend_request", text: `${senderName} sent you a friend request`, sub: currentUser.id, game_id: null, read: false }).then(() => {}, () => {});
                    }
                  }
                  setInlineFriendLoading(false);
                }}
                style={{ ...btnPrimary, width: "100%", padding: "12px 0", borderRadius: 10, marginBottom: 8, opacity: (inlineFriendLoading || inlineFriendStatus === "accepted" || inlineFriendStatus === "sent") ? 0.6 : 1 }}
              >
                {inlineFriendStatus === "accepted" ? "✓ Already friends" : inlineFriendStatus === "sent" ? "✓ Request sent" : inlineFriendLoading ? "Sending…" : "Send friend request"}
              </button>
            )}
            <button onClick={() => { setInlinePlayer(null); setInlineFriendStatus(null); }} style={{ ...btnSecondary, width: "100%", padding: "10px 0", borderRadius: 10 }}>Close</button>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Communities Tab ──────────────────────────────────────────────────────────
// Modern Discover layout: hero, "Your communities" horizontal rail,
// featured spotlight, trending grid, all-list. Cards lean on cover_url +
// avatar_url; when missing we synthesize a deterministic gradient from the
// name so every card still feels distinct rather than flat green.

// Deterministic hash → hue. Same name always picks the same gradient,
// so a community without a cover keeps its visual identity across renders.
function communityHue(name) {
  let hash = 0;
  for (let i = 0; i < (name || "").length; i++) {
    hash = (hash * 31 + name.charCodeAt(i)) & 0xfffffff;
  }
  return hash % 360;
}
function communityGradient(name, dim = false) {
  const h = communityHue(name);
  const a = `hsl(${h}, 65%, ${dim ? 55 : 60}%)`;
  const b = `hsl(${(h + 40) % 360}, 70%, ${dim ? 40 : 45}%)`;
  return `linear-gradient(135deg, ${a} 0%, ${b} 100%)`;
}
function communityInitials(name) {
  return (name || "?").split(/\s+/).filter(Boolean).map(w => w[0]).slice(0, 2).join("").toUpperCase();
}

function CommunitiesTab({ user, myCommunities, discoverCommunities, loading, onOpenCommunity, onCreate, onRefresh, onOpenFriends, invitableGames = [], friendsReloadKey = 0, onCreateGame, showToast }) {
  const { C, T, TYPE, btnPrimary, inputBase, card, SPACE, RADIUS, SHADOW, warnBg } = useTheme();
  const ui = { C, SPACE, RADIUS, SHADOW };

  // Groups browsing stays intentionally light: search plus All/Mine.
  const [search, setSearch] = useState("");
  const [section, setSection] = useState("groups");
  const [showMine, setShowMine] = useState(false);
  const [friendsRefreshKey, setFriendsRefreshKey] = useState(0);
  const [communityJoinState, setCommunityJoinState] = useState({});
  const [joiningCommunityId, setJoiningCommunityId] = useState(null);

  // Pool everything we know about — discover excludes joined; merge for filters.
  const allKnown = useMemo(() => {
    const seen = new Set();
    const out = [];
    for (const c of [...myCommunities, ...discoverCommunities]) {
      if (!c || seen.has(c.id)) continue;
      seen.add(c.id);
      out.push(c);
    }
    return out;
  }, [myCommunities, discoverCommunities]);

  // The "All" list should include public groups the user has already joined.
  // `discoverCommunities` can exclude joined rows depending on RLS/query shape,
  // so merge it with memberships for the actual all-groups view.
  const filteredList = useMemo(() => {
    const base = showMine ? myCommunities : allKnown;
    let out = base.filter(c => !!c);
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      out = out.filter(c =>
        (c.name || "").toLowerCase().includes(q) ||
        (c.description || "").toLowerCase().includes(q) ||
        (c.tags || []).some(tag => tag.toLowerCase().includes(q))
      );
    }
    out.sort((a, b) => {
      const memberDelta = (b.member_count || 0) - (a.member_count || 0);
      if (memberDelta !== 0) return memberDelta;
      return new Date(b.created_at || 0) - new Date(a.created_at || 0);
    });
    return out;
  }, [showMine, myCommunities, allKnown, search]);

  const getCommunityMembershipState = (c) => {
    if (!c?.id) return null;
    if (communityJoinState[c.id]) return communityJoinState[c.id];
    if (c.owner_id === user?.id || myCommunities.some(m => m.id === c.id)) return "active";
    return null;
  };

  const handleJoinCommunity = async (community) => {
    if (!community?.id || joiningCommunityId) return;
    if (!user?.id) {
      showToast?.(t("signInToJoin"));
      return;
    }
    const policy = community.join_policy || (community.is_private ? "invite_only" : "open");
    if (policy === "invite_only") {
      showToast?.(community.is_private ? "This group is private. Ask an admin for an invite." : "This group is invite-only.");
      return;
    }
    setJoiningCommunityId(community.id);
    try {
      const status = policy === "request" ? "pending" : "active";
      const { data: existing, error: lookupError } = await supabase
        .from("community_members")
        .select("user_id, role, status")
        .eq("community_id", community.id)
        .eq("user_id", user.id)
        .maybeSingle();
      if (lookupError) throw lookupError;

      if (existing?.status === "active") {
        setCommunityJoinState(prev => ({ ...prev, [community.id]: "active" }));
        showToast?.(t("joined"));
        await onRefresh?.();
        return;
      }
      if (existing?.status === "pending") {
        setCommunityJoinState(prev => ({ ...prev, [community.id]: "pending" }));
        showToast?.(t("requestPending"));
        await onRefresh?.();
        return;
      }

      if (existing) {
        const { error } = await supabase
          .from("community_members")
          .update({ role: existing.role || "member", status })
          .eq("community_id", community.id)
          .eq("user_id", user.id);
        if (error) throw error;
      } else {
        let { error } = await supabase.from("community_members").insert({
          community_id: community.id,
          user_id: user.id,
          role: "member",
          status,
        });
        if (error) {
          const { error: refreshErr } = await supabase.auth.refreshSession();
          if (!refreshErr) {
            ({ error } = await supabase.from("community_members").insert({
              community_id: community.id,
              user_id: user.id,
              role: "member",
              status,
            }));
          }
        }
        if (error) throw error;
      }

      setCommunityJoinState(prev => ({ ...prev, [community.id]: status }));
      showToast?.(status === "pending" ? t("requestPending") : `${t("joinCommunity")} ✓`);

      if (status === "pending") {
        try {
          const { data: admins } = await supabase
            .from("community_members")
            .select("user_id")
            .eq("community_id", community.id)
            .in("role", ["owner", "admin"])
            .eq("status", "active");
          const senderName = user?.user_metadata?.name || user?.email?.split("@")[0] || "Someone";
          const rows = (admins || [])
            .filter(a => a.user_id !== user.id)
            .map(a => ({
              user_id: a.user_id,
              type: "community_join_request",
              text: `${senderName} requested to join ${community.name}`,
              sub: community.id,
              game_id: null,
              read: false,
            }));
          if (rows.length) {
            const { error: notifyError } = await supabase.from("notifications").insert(rows);
            if (notifyError) console.warn("[community.join.list.notify] failed", notifyError);
          }
        } catch (notifyErr) {
          console.warn("[community.join.list.notify] failed", notifyErr);
        }
      }
      await onRefresh?.();
    } catch (e) {
      console.warn("[community.join.list] failed", e);
      showToast?.(t("communityJoinError"));
    } finally {
      setJoiningCommunityId(null);
    }
  };

  // ── Trending category styling — three accent colours so the badges read
  //    instantly: blue/red/green as in the reference.
  const TRENDING_BADGES = {
    popular: { label: t("trendingPopular"), bg: "#2563eb", color: "#fff" },
    active:  { label: t("trendingActive"),  bg: "#dc2626", color: "#fff" },
    newest:  { label: t("trendingNewest"),  bg: "#16a34a", color: "#fff" },
  };

  // Compact horizontal-rail card for "Your communities".
  const RailCard = ({ c }) => (
    <button
      onClick={() => onOpenCommunity(c)}
      style={{
        flex: "0 0 156px", scrollSnapAlign: "start",
        display: "flex", flexDirection: "column",
        background: C.surface, border: `1px solid ${C.border}`, borderRadius: 14,
        overflow: "hidden", cursor: "pointer", textAlign: "left", padding: 0,
        transition: "transform 0.15s, box-shadow 0.15s",
      }}
      onMouseEnter={e => { e.currentTarget.style.transform = "translateY(-2px)"; e.currentTarget.style.boxShadow = "0 8px 20px rgba(0,0,0,0.06)"; }}
      onMouseLeave={e => { e.currentTarget.style.transform = ""; e.currentTarget.style.boxShadow = ""; }}
    >
      {/* Cover or generated gradient */}
      <div style={{
        height: 64, position: "relative",
        background: c.cover_url ? `url(${c.cover_url}) center/cover` : communityGradient(c.name),
      }}>
        {/* Avatar floats over the cover edge */}
        <div style={{
          position: "absolute", left: 12, bottom: -16,
          width: 36, height: 36, borderRadius: 10,
          background: c.avatar_url ? `url(${c.avatar_url}) center/cover` : C.surface,
          display: "flex", alignItems: "center", justifyContent: "center",
          color: C.ink, fontSize: 12, fontWeight: 700,
          boxShadow: `0 0 0 3px ${C.surface}`,
        }}>
          {!c.avatar_url && communityInitials(c.name)}
        </div>
      </div>
      <div style={{ padding: "22px 12px 12px" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 5, minWidth: 0, marginBottom: 2 }}>
          <span style={{ fontSize: 13, fontWeight: 700, color: C.ink, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", minWidth: 0, flex: 1 }}>
            {c.name}
          </span>
          {c.verified && <VerifiedTick size={9} />}
        </div>
        <div style={{ fontSize: 11, color: C.inkFaint }}>
          {(c.member_count || 0) === 1 ? t("memberSingular") : t("membersCount", { n: c.member_count || 0 })}
        </div>
      </div>
    </button>
  );

  // Featured spotlight — full-width tall card with cover gradient overlay.
  const FeaturedCard = ({ c }) => (
    <button
      onClick={() => onOpenCommunity(c)}
      style={{
        width: "100%", display: "block", textAlign: "left", border: "none",
        padding: 0, marginBottom: 16, borderRadius: 18, overflow: "hidden",
        cursor: "pointer", position: "relative",
        background: c.cover_url ? `url(${c.cover_url}) center/cover` : communityGradient(c.name, true),
        minHeight: 184,
        boxShadow: "0 4px 16px rgba(0,0,0,0.08)",
      }}
    >
      {/* Top-bottom gradient so text stays legible over any cover image */}
      <div style={{ position: "absolute", inset: 0, background: "linear-gradient(180deg, rgba(0,0,0,0.05) 0%, rgba(0,0,0,0.55) 100%)" }} />
      <div style={{ position: "relative", padding: "18px 18px 20px", color: "#fff" }}>
        <div style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "3px 10px", borderRadius: 999, background: "rgba(255,255,255,0.22)", backdropFilter: "blur(8px)", fontSize: 10, fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase" }}>
          <span style={{ width: 6, height: 6, borderRadius: "50%", background: "#fff", boxShadow: "0 0 0 4px rgba(255,255,255,0.25)" }} />
          {t("featuredCommunity")}
        </div>
      </div>
      <div style={{ position: "relative", padding: "60px 18px 18px", color: "#fff", display: "flex", alignItems: "flex-end", gap: 12 }}>
        <div style={{
          width: 52, height: 52, borderRadius: 12,
          background: c.avatar_url ? `url(${c.avatar_url}) center/cover` : "rgba(255,255,255,0.2)",
          backdropFilter: "blur(8px)",
          display: "flex", alignItems: "center", justifyContent: "center",
          color: "#fff", fontSize: 18, fontWeight: 700, flexShrink: 0,
          boxShadow: "0 0 0 2px rgba(255,255,255,0.3)",
        }}>
          {!c.avatar_url && communityInitials(c.name)}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 7, minWidth: 0, marginBottom: 3 }}>
            <span style={{ fontSize: 18, fontWeight: 800, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", textShadow: "0 1px 2px rgba(0,0,0,0.3)", minWidth: 0, flex: 1 }}>
              {c.name}
            </span>
            {c.verified && <VerifiedTick size={11} style={{ boxShadow: "0 0 0 2px rgba(255,255,255,0.88)" }} />}
          </div>
          <div style={{ fontSize: 12, opacity: 0.92, display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap" }}>
            <span>{(c.member_count || 0) === 1 ? t("memberSingular") : t("membersCount", { n: c.member_count || 0 })}</span>
            {c.city && <><span>·</span><span>{c.city}</span></>}
          </div>
        </div>
        <span style={{ fontSize: 11, fontWeight: 700, padding: "7px 12px", borderRadius: 999, background: "#fff", color: "#0f1623", flexShrink: 0 }}>
          {c.join_policy === "request" ? t("requestToJoin") : t("joinCommunity")}
        </span>
      </div>
    </button>
  );

  // Compact list card for Groups. Big generated cover cards looked decorative
  // when groups did not have real photos; this keeps the surface closer to a
  // sports app list: identity, location, members, tags, action.
  const CoverCard = ({ c, membershipState, joining }) => {
    const isOpen = c.join_policy !== "request" && !c.is_private;
    const joined = membershipState === "active";
    const pending = membershipState === "pending";
    const tagsToShow = (c.tags || []).slice(0, 3);
    const tagsMore = Math.max(0, (c.tags || []).length - tagsToShow.length);
    const meta = [
      c.city || t("noCity"),
      (c.member_count || 0) === 1 ? t("memberSingular") : t("membersCount", { n: c.member_count || 0 }),
      c.is_private ? t("communityPrivate") : t("communityPublic"),
    ];
    return (
      <div
        role="button"
        tabIndex={0}
        onClick={() => onOpenCommunity(c)}
        onKeyDown={e => {
          if (e.currentTarget !== e.target) return;
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            onOpenCommunity(c);
          }
        }}
        style={{
          width: "100%", display: "flex", alignItems: "flex-start", gap: SPACE[3],
          padding: SPACE[4], marginBottom: SPACE[3], borderRadius: RADIUS.md,
          border: `1px solid ${C.border}`, background: C.surface, boxShadow: SHADOW.sm,
          cursor: "pointer", textAlign: "left", transition: "transform 0.16s ease, box-shadow 0.16s ease",
        }}
        onMouseEnter={e => { e.currentTarget.style.transform = "translateY(-1px)"; e.currentTarget.style.boxShadow = SHADOW.md; }}
        onMouseLeave={e => { e.currentTarget.style.transform = ""; e.currentTarget.style.boxShadow = SHADOW.sm; }}
        aria-label={c.name}
      >
        <div style={{
          width: 54, height: 54, borderRadius: RADIUS.md,
          background: c.avatar_url
            ? `url(${c.avatar_url}) center/cover`
            : (c.cover_url ? `url(${c.cover_url}) center/cover` : communityGradient(c.name, true)),
          display: "flex", alignItems: "center", justifyContent: "center",
          color: "#fff", fontSize: 15, fontWeight: 900, flexShrink: 0,
          boxShadow: `inset 0 0 0 1px ${C.isDark ? "rgba(255,255,255,0.12)" : "rgba(15,22,35,0.06)"}`,
        }}>
          {!c.avatar_url && !c.cover_url && communityInitials(c.name)}
        </div>

        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: "flex", alignItems: "center", gap: SPACE[2], marginBottom: 3, minWidth: 0 }}>
            <div style={{ ...T.h2, color: C.ink, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", flex: 1 }}>{c.name}</div>
            {c.verified && <VerifiedTick size={11} />}
          </div>
          <div style={{ ...T.small, color: C.inkMid, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", marginBottom: SPACE[2] }}>
            {meta.filter(Boolean).join(" · ")}
          </div>
          {c.description && (
            <div style={{ ...T.caption, color: C.inkMid, lineHeight: 1.35, display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden", marginBottom: SPACE[2] }}>
              {c.description}
            </div>
          )}
          <div style={{ display: "flex", alignItems: "center", gap: SPACE[1] + 2, flexWrap: "wrap" }}>
            <span style={{ padding: `3px ${SPACE[2]}px`, borderRadius: RADIUS.pill, background: isOpen ? C.accentBg : C.bg, color: isOpen ? C.accent : C.inkMid, fontSize: 11, fontWeight: 800 }}>
              {isOpen ? t("communityOpen") : t("communityClosed")}
            </span>
            {tagsToShow.map(tag => (
              <span key={tag} style={{ padding: `3px ${SPACE[2]}px`, borderRadius: RADIUS.pill, background: C.bg, color: C.inkMid, fontSize: 11, fontWeight: 650 }}>{tag}</span>
            ))}
            {tagsMore > 0 && (
              <span style={{ padding: `3px ${SPACE[2]}px`, borderRadius: RADIUS.pill, background: C.bg, color: C.inkFaint, fontSize: 11, fontWeight: 650 }}>+{tagsMore}</span>
            )}
          </div>
        </div>

        <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: SPACE[2], flexShrink: 0 }}>
          {joined || pending ? (
            <span style={{ padding: `5px ${SPACE[2] + 2}px`, borderRadius: RADIUS.pill, background: joined ? C.accentBg : warnBg, color: joined ? C.accent : C.warn, fontSize: 11, fontWeight: 900, whiteSpace: "nowrap" }}>
              {joined ? t("joined") : t("requestPending")}
            </span>
          ) : (
            <button
              type="button"
              onClick={e => {
                e.stopPropagation();
                handleJoinCommunity(c);
              }}
              disabled={joining}
              style={{
                minHeight: 30,
                padding: `0 ${SPACE[3]}px`,
                borderRadius: RADIUS.pill,
                border: `1px solid ${isOpen ? `${C.accent}55` : C.border}`,
                background: isOpen ? C.accentBg : C.surface,
                color: isOpen ? C.accent : C.inkMid,
                fontSize: 12,
                fontWeight: 900,
                cursor: joining ? "wait" : "pointer",
                opacity: joining ? 0.65 : 1,
                whiteSpace: "nowrap",
              }}
            >
              {joining ? t("loading") : (isOpen ? t("joinCommunity") : t("requestToJoin"))}
            </button>
          )}
          <ChevronRight size={17} color={C.inkFaint} {...ICON} />
        </div>
      </div>
    );
  };

  // Skeleton placeholder while data loads.
  const renderSkeletonCard = (key) => (
    <div key={key} style={{ width: "100%", padding: 12, background: C.surface, border: `1px solid ${C.border}`, borderRadius: 14, display: "flex", gap: 12 }}>
      <div style={{ width: 56, height: 56, borderRadius: 12, background: C.bg, animation: "pulse 1.5s ease-in-out infinite" }} />
      <div style={{ flex: 1 }}>
        <div style={{ height: 12, width: "60%", borderRadius: 6, background: C.bg, marginBottom: 8, animation: "pulse 1.5s ease-in-out infinite" }} />
        <div style={{ height: 10, width: "40%", borderRadius: 6, background: C.bg, animation: "pulse 1.5s ease-in-out infinite" }} />
      </div>
    </div>
  );

  // Section header — shared style for "Trending Today" / "All Communities".
  const SectionHeader = ({ children, action }) => (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: SPACE[3], paddingLeft: SPACE[1] }}>
      <div style={{ ...TYPE.micro, color: C.inkMid }}>
        {children}
      </div>
      {action}
    </div>
  );

  const showInitialSkeletons = loading && myCommunities.length === 0 && discoverCommunities.length === 0;

  // Trending Today rail card — circular avatar over a coloured ring with the
  // category badge below. Clicking opens the community.
  const TrendingPick = ({ c, kind }) => {
    const badge = TRENDING_BADGES[kind];
    return (
      <button
        onClick={() => onOpenCommunity(c)}
        style={{
          flex: "0 0 96px", scrollSnapAlign: "start", display: "flex", flexDirection: "column",
          alignItems: "center", gap: 6, padding: 0, background: "transparent",
          border: "none", cursor: "pointer",
        }}
        aria-label={c.name}
      >
        <div style={{
          width: 72, height: 72, borderRadius: "50%", padding: 3,
          background: `conic-gradient(${badge.bg}, ${badge.bg} 75%, ${C.border} 75%, ${C.border} 100%)`,
          position: "relative",
        }}>
          <div style={{
            width: "100%", height: "100%", borderRadius: "50%",
            background: c.avatar_url
              ? `url(${c.avatar_url}) center/cover`
              : (c.cover_url ? `url(${c.cover_url}) center/cover` : communityGradient(c.name)),
            display: "flex", alignItems: "center", justifyContent: "center",
            color: "#fff", fontWeight: 800, fontSize: 18,
            border: `2px solid ${C.bg}`,
          }}>
            {!c.avatar_url && !c.cover_url && communityInitials(c.name)}
          </div>
          {c.verified && <VerifiedTick size={10} style={{ position: "absolute", right: 0, bottom: 0, boxShadow: `0 0 0 2px ${C.bg}` }} />}
        </div>
        <span style={{
          padding: "3px 10px", borderRadius: 999, background: badge.bg, color: badge.color,
          fontSize: 9, fontWeight: 700, letterSpacing: "0.04em", textTransform: "uppercase",
          whiteSpace: "nowrap",
        }}>
          {badge.label}
        </span>
        <span style={{ width: "100%", display: "flex", alignItems: "center", justifyContent: "center", gap: 4, minWidth: 0 }}>
          <span style={{
            fontSize: 11, fontWeight: 600, color: C.ink, textAlign: "center",
            overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", minWidth: 0,
          }}>
            {c.name}
          </span>
        </span>
      </button>
    );
  };

  const handleSectionRefresh = async () => {
    if (section === "friends") {
      setFriendsRefreshKey(key => key + 1);
      return;
    }
    return onRefresh?.();
  };

  return (
    <div style={{ flex: 1, overflowY: "auto", position: "relative" }}>
      <style>{`
        @keyframes pulse { 0%, 100% { opacity: 0.5; } 50% { opacity: 0.8; } }
        .kickr-scroll-row::-webkit-scrollbar { display: none; }
      `}</style>

      <PullToRefresh style={{ padding: `${SPACE[3]}px ${SPACE[4]}px ${SPACE[5]}px` }} onRefresh={handleSectionRefresh}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: SPACE[3], marginBottom: SPACE[4] }}>
          <div
            style={{
              ...uiSegmentShell(ui, { display: "grid", gridTemplateColumns: "repeat(2, 1fr)", flex: 1, border: true }),
              maxWidth: 360,
            }}
            role="tablist"
            aria-label={t("communitiesTab")}
          >
            {[["groups", t("communities")], ["friends", t("friends")]].map(([value, label]) => {
              const active = section === value;
              return (
                <button
                  key={value}
                  onClick={() => setSection(value)}
                  role="tab"
                  aria-selected={active}
                  style={uiSegmentButton(ui, active, {
                    flex: 1,
                    minWidth: 0,
                    minHeight: 38,
                    fontSize: 14,
                    activeWeight: 850,
                    inactiveWeight: 750,
                  })}
                >
                  {label}
                </button>
              );
            })}
          </div>

          {user && (
            <button
              onClick={() => section === "friends" ? onOpenFriends?.() : onCreate()}
              aria-label={section === "friends" ? "Add friend" : t("createCommunity")}
              style={{
                width: 42, height: 42, borderRadius: "50%",
                background: C.accent, border: "none", color: "#fff",
                display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer",
                boxShadow: SHADOW.sm, transition: "transform 0.15s, box-shadow 0.15s",
                flexShrink: 0,
              }}
              onMouseEnter={e => { e.currentTarget.style.transform = "scale(1.05)"; e.currentTarget.style.boxShadow = SHADOW.md; }}
              onMouseLeave={e => { e.currentTarget.style.transform = ""; e.currentTarget.style.boxShadow = SHADOW.sm; }}
            >
              <Plus size={20} {...ICON} strokeWidth={2.5} />
            </button>
          )}
        </div>

        {section === "friends" ? (
          user ? (
            <FriendsScreen
              currentUser={user}
              onClose={() => {}}
              showToast={showToast}
              invitableGames={invitableGames}
              reloadKey={`${friendsReloadKey}:${friendsRefreshKey}`}
              onCreateGame={onCreateGame}
              embedded
            />
          ) : (
            <div style={{ ...card, padding: SPACE[6], textAlign: "center" }}>
              <div style={{ width: 48, height: 48, borderRadius: "50%", background: C.accentBg, color: C.accent, margin: `0 auto ${SPACE[3]}px`, display: "flex", alignItems: "center", justifyContent: "center" }}>
                <Users size={22} {...ICON} />
              </div>
              <div style={{ ...TYPE.h2, color: C.ink, marginBottom: SPACE[1] + 2 }}>{t("friends")}</div>
              <div style={{ ...TYPE.body, color: C.inkMid, marginBottom: SPACE[4] }}>Sign in to add friends, invite them to games, and chat.</div>
              <button onClick={onOpenFriends} style={{ ...btnPrimary, padding: `${SPACE[3]}px ${SPACE[5]}px`, borderRadius: RADIUS.pill }}>Sign in</button>
            </div>
          )
        ) : (
          <>
        {/* Search — pill input with icon. Uses the token inputBase so the
            border colour and focus behaviour stay consistent with every
            other input in the app. */}
        <div style={{ position: "relative", marginBottom: SPACE[5] }}>
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
               style={{ position: "absolute", left: SPACE[4], top: "50%", transform: "translateY(-50%)", color: C.inkFaint, pointerEvents: "none" }}>
            <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
          </svg>
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder={t("searchCommunitiesPlaceholder")}
            style={{ ...inputBase, paddingLeft: SPACE[7] - 4, borderRadius: RADIUS.pill, background: C.surface }}
          />
        </div>

        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: SPACE[3], marginBottom: SPACE[4] }}>
          <div style={{ ...TYPE.micro, color: C.inkMid }}>{showMine ? t("mine") : t("discover")}</div>
          {user && (
            <div style={uiSegmentShell(ui)}>
              {[[false, t("all")], [true, t("mine")]].map(([value, label]) => {
                const active = showMine === value;
                return (
                  <button
                    key={String(value)}
                    onClick={() => setShowMine(value)}
                    aria-pressed={active}
                    style={uiSegmentButton(ui, active, { padding: `${SPACE[2] - 1}px ${SPACE[3]}px` })}
                  >
                    {label}
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* Loading skeletons on first load */}
        {showInitialSkeletons && (
          <div style={{ display: "flex", flexDirection: "column", gap: 10, marginBottom: 24 }}>
            {[0, 1, 2].map(renderSkeletonCard)}
          </div>
        )}

        {/* List */}
        {!showInitialSkeletons && filteredList.length > 0 && (
          <div>
            {filteredList.map(c => {
              const membershipState = getCommunityMembershipState(c);
              return <CoverCard key={c.id} c={c} membershipState={membershipState} joining={joiningCommunityId === c.id} />;
            })}
          </div>
        )}

        {/* Empty state — Lucide-style outlined icon (not emoji), tighter copy */}
        {!showInitialSkeletons && filteredList.length === 0 && (
          <div style={{
            margin: `${SPACE[5]}px 0`, padding: `${SPACE[6]}px ${SPACE[5]}px`,
            background: C.surface, borderRadius: RADIUS.lg, boxShadow: SHADOW.sm,
            textAlign: "center",
          }}>
            <div style={{ width: 48, height: 48, borderRadius: "50%", background: C.bg, color: C.inkFaint, margin: `0 auto ${SPACE[3]}px`, display: "flex", alignItems: "center", justifyContent: "center" }}>
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
            </div>
            <div style={{ ...TYPE.h2, color: C.ink, marginBottom: SPACE[1] + 2 }}>
              {search
                ? t("noCommunitiesMatch")
                : (showMine ? t("noJoinedCommunities") : t("noCommunitiesInCity"))}
            </div>
            {user && !showMine && !search && (
              <>
                <div style={{ ...TYPE.body, color: C.inkMid, marginBottom: SPACE[4], maxWidth: 280, margin: `0 auto ${SPACE[4]}px` }}>
                  {t("beFirstToCreate")}
                </div>
                <button onClick={onCreate} style={{ ...btnPrimary, padding: `${SPACE[3]}px ${SPACE[5]}px`, borderRadius: RADIUS.pill }}>
                  {t("createCommunity")}
                </button>
              </>
            )}
          </div>
        )}
          </>
        )}
      </PullToRefresh>
    </div>
  );
}

// ─── Create Community Modal ──────────────────────────────────────────────────
function CreateCommunityModal({ user, userProfile, onClose, onCreated, showToast }) {
  const { C, T, TYPE, btnPrimary, btnSecondary, inputBase, SPACE, RADIUS } = useTheme();
  const [form, setForm] = useState({
    name: "",
    description: "",
    is_private: false,
    join_policy: "open",
    city: userProfile?.city || "Oslo",
    tags: [],
  });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const toggleTag = (tag) => {
    setForm(f => {
      if (f.tags.includes(tag)) return { ...f, tags: f.tags.filter(x => x !== tag) };
      if (f.tags.length >= 5) {
        showToast?.("Pick up to 5 tags");
        return f;
      }
      return { ...f, tags: [...f.tags, tag] };
    });
  };

  const canCreate = !!user?.id && form.name.trim().length >= 3 && form.city.trim().length > 0 && !saving;

  const handleCreate = async () => {
    if (!user?.id) {
      showToast?.(t("signInToJoin"));
      return;
    }
    if (!canCreate) return;
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        description: form.description.trim() || null,
        is_private: form.is_private,
        // Private communities default to invite_only; public respect the picker.
        join_policy: form.is_private ? "invite_only" : form.join_policy,
        city: form.city.trim(),
        tags: form.tags,
        owner_id: user.id,
      };
      const createGroup = () => supabase.from("communities").insert(payload).select().single();
      let { data, error } = await createGroup();
      if (error) {
        const { error: refreshError } = await supabase.auth.refreshSession();
        if (!refreshError) ({ data, error } = await createGroup());
      }
      if (error) throw error;
      const { error: memberError } = await supabase.from("community_members").upsert(
        { community_id: data.id, user_id: user.id, role: "owner", status: "active" },
        { onConflict: "community_id,user_id" }
      );
      if (memberError) console.warn("[community.create] owner membership fallback failed", memberError);
      const createdGroup = {
        ...data,
        member_count: Math.max(1, data.member_count || 0),
        join_policy: data.join_policy || payload.join_policy,
        tags: data.tags || payload.tags,
      };
      showToast?.(t("createCommunity") + " ✓");
      await onCreated?.(createdGroup);
      onClose();
    } catch (e) {
      console.warn("[community.create] failed", e);
      showToast?.(t("communityCreateError"));
      setSaving(false);
    }
  };

  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 260, background: C.bg, display: "flex", flexDirection: "column" }}>
      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "calc(12px + env(safe-area-inset-top, 0px)) 16px 12px", borderBottom: `1px solid ${C.border}`, flexShrink: 0 }}>
        <button type="button" onClick={onClose} aria-label="Back" style={{ width: 36, height: 36, borderRadius: "50%", background: "transparent", border: "none", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", color: C.ink }}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
        </button>
        <div style={{ ...T.h2 }}>{t("createCommunity")}</div>
      </div>

      {/* Body */}
      <div style={{ flex: 1, overflowY: "auto", padding: "20px 16px" }}>
        <div style={{ marginBottom: 16 }}>
          <label style={{ ...T.small, fontWeight: 600, color: C.inkMid, display: "block", marginBottom: 6 }}>{t("communityName")}</label>
          <input value={form.name} onChange={e => set("name", e.target.value)} placeholder="Oslo Sunday Kickabouts" style={inputBase} maxLength={60} autoFocus />
        </div>

        <div style={{ marginBottom: 16 }}>
          <label style={{ ...T.small, fontWeight: 600, color: C.inkMid, display: "block", marginBottom: 6 }}>{t("communityDescription")}</label>
          <textarea value={form.description} onChange={e => set("description", e.target.value)} placeholder="A friendly group that plays every Sunday morning…" rows={3} maxLength={500} style={{ ...inputBase, resize: "vertical", fontFamily: "inherit" }} />
        </div>

        <div style={{ marginBottom: 16 }}>
          <label style={{ ...T.small, fontWeight: 600, color: C.inkMid, display: "block", marginBottom: 6 }}>{t("communityCity")}</label>
          <input value={form.city} onChange={e => set("city", e.target.value)} placeholder="Oslo" style={inputBase} />
        </div>

        {/* Privacy picker */}
        <div style={{ marginBottom: 16 }}>
          <label style={{ ...T.small, fontWeight: 600, color: C.inkMid, display: "block", marginBottom: 6 }}>{t("communityPrivacy")}</label>
          <div style={{ display: "flex", gap: 8 }}>
            {[
              { key: false, label: t("communityPublic"), desc: t("communityPublicDesc") },
              { key: true,  label: t("communityPrivate"), desc: t("communityPrivateDesc") },
            ].map(opt => (
              <button key={String(opt.key)} type="button" onClick={() => set("is_private", opt.key)} style={{ flex: 1, padding: "12px 10px", borderRadius: 10, border: `1.5px solid ${form.is_private === opt.key ? C.accent : C.border}`, background: form.is_private === opt.key ? C.accentBg : C.surface, cursor: "pointer", textAlign: "left" }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: form.is_private === opt.key ? C.accent : C.ink, marginBottom: 2 }}>{opt.label}</div>
                <div style={{ fontSize: 11, color: C.inkFaint, lineHeight: 1.4 }}>{opt.desc}</div>
              </button>
            ))}
          </div>
        </div>

        {/* Join policy — only for public */}
        {!form.is_private && (
          <div style={{ marginBottom: 16 }}>
            <label style={{ ...T.small, fontWeight: 600, color: C.inkMid, display: "block", marginBottom: 6 }}>{t("communityJoinPolicy")}</label>
            <div style={{ display: "flex", gap: 6 }}>
              {[["open", t("joinPolicyOpen")], ["request", t("joinPolicyRequest")]].map(([k, label]) => (
                <button key={k} type="button" onClick={() => set("join_policy", k)} style={{ flex: 1, padding: "10px 0", borderRadius: 8, border: `1px solid ${form.join_policy === k ? C.accent : C.border}`, background: form.join_policy === k ? C.accentBg : C.surface, color: form.join_policy === k ? C.accent : C.ink, fontSize: 12, fontWeight: 600, cursor: "pointer" }}>{label}</button>
              ))}
            </div>
          </div>
        )}

        {/* Tags */}
        <div style={{ marginBottom: 24 }}>
          <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: SPACE[2], marginBottom: SPACE[2] }}>
            <label style={{ ...TYPE.micro, color: C.inkMid }}>{t("communityTags")}</label>
            <span style={{ ...T.small, color: C.inkFaint }}>{form.tags.length}/5</span>
          </div>
          <div style={{ display: "flex", gap: SPACE[2], flexWrap: "wrap" }}>
            {GROUP_TAG_OPTIONS.map(tag => {
              const active = form.tags.includes(tag);
              return (
                <button
                  key={tag}
                  type="button"
                  onClick={() => toggleTag(tag)}
                  style={{
                    minHeight: 34,
                    padding: `0 ${SPACE[3]}px`,
                    borderRadius: RADIUS.pill,
                    border: `1px solid ${active ? `${C.accent}66` : C.border}`,
                    background: active ? C.accentBg : C.surface,
                    color: active ? C.accent : C.inkMid,
                    fontSize: 13,
                    fontWeight: active ? 850 : 650,
                    cursor: "pointer",
                  }}
                >
                  {tag}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Footer */}
      <div style={{ padding: "12px 16px calc(12px + env(safe-area-inset-bottom, 0px))", borderTop: `1px solid ${C.border}`, display: "flex", gap: 8, flexShrink: 0 }}>
        <button type="button" onClick={onClose} style={{ ...btnSecondary, flex: 1, padding: "12px 0", fontSize: 14 }}>{t("cancel")}</button>
        <button type="button" onClick={handleCreate} disabled={!canCreate} style={{ ...btnPrimary, flex: 2, padding: "12px 0", fontSize: 14, opacity: canCreate ? 1 : 0.5, cursor: canCreate ? "pointer" : "not-allowed" }}>
          {saving ? t("creatingCommunity") : t("createCommunity")}
        </button>
      </div>
    </div>
  );
}

// ─── Community Screen (detail view) ──────────────────────────────────────────
// ─── ChallengeCommunityModal ─────────────────────────────────────────────────
// Admin-only modal that lets the caller propose a match against another
// community. Loads the candidate list lazily — public communities (so we can
// challenge anyone visible) plus the caller's own joined private ones, minus
// the current community.
function ChallengeCommunityModal({ currentCommunity, onClose, onSubmit, busy }) {
  const { C, T, btnPrimary, inputBase } = useTheme();
  const [opponents, setOpponents] = useState([]);
  const [opponentId, setOpponentId] = useState("");
  const [date, setDate] = useState("");
  const [hour, setHour] = useState("");
  const [location, setLocation] = useState("");
  const [note, setNote] = useState("");

  useEffect(() => {
    let mounted = true;
    (async () => {
      // Public communities are visible to anyone via RLS; my own joined ones
      // are visible regardless of privacy. Union them to get opponents list.
      const [pubRes, mineRes] = await Promise.all([
        supabase.from("communities").select("id, name, avatar_url, verified").eq("is_private", false).neq("id", currentCommunity.id).order("name").limit(100),
        supabase.from("communities").select("id, name, avatar_url, verified").in("id", []), // placeholder — we already exclude self below from join
      ]);
      const seen = new Set();
      const list = [];
      for (const row of [...(pubRes.data || []), ...(mineRes.data || [])]) {
        if (row.id === currentCommunity.id || seen.has(row.id)) continue;
        seen.add(row.id);
        list.push(row);
      }
      if (mounted) setOpponents(list);
    })();
    return () => { mounted = false; };
  }, [currentCommunity.id]);

  const canSubmit = !!opponentId && !!date && !busy;

  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 300, background: "rgba(0,0,0,0.55)", display: "flex", alignItems: "flex-end", justifyContent: "center" }} onClick={onClose}>
      <div style={{ width: "100%", maxWidth: 520, background: C.bg, borderRadius: "16px 16px 0 0", padding: "16px 16px calc(16px + env(safe-area-inset-bottom, 0px))", display: "flex", flexDirection: "column", gap: 12, maxHeight: "92vh", overflowY: "auto" }} onClick={e => e.stopPropagation()}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <div style={{ ...T.h2 }}>{t("challengeAnotherCommunity")}</div>
          <button onClick={onClose} aria-label="Close" style={{ width: 32, height: 32, borderRadius: "50%", background: "transparent", border: "none", cursor: "pointer", color: C.inkMid, fontSize: 20, padding: 0 }}>×</button>
        </div>

        {opponents.length === 0 ? (
          <div style={{ padding: 20, textAlign: "center", fontSize: 13, color: C.inkFaint }}>{t("noOpponentsAvailable")}</div>
        ) : (
          <>
            <div>
              <div style={{ ...T.label, marginBottom: 6 }}>{t("pickOpponent")}</div>
              <div style={{ display: "flex", flexDirection: "column", gap: 4, maxHeight: 220, overflowY: "auto", border: `1px solid ${C.border}`, borderRadius: 10, background: C.surface, padding: 4 }}>
                {opponents.map(o => {
                  const init = (o.name || "?").split(" ").map(w => w[0]).join("").slice(0, 2).toUpperCase();
                  const selected = opponentId === o.id;
                  return (
                    <button key={o.id} type="button" onClick={() => setOpponentId(o.id)}
                      style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 10px", borderRadius: 8, border: "none", background: selected ? C.accentBg : "transparent", color: C.ink, cursor: "pointer", textAlign: "left" }}>
                      <div style={{ width: 28, height: 28, borderRadius: 8, background: o.avatar_url ? `url(${o.avatar_url}) center/cover` : C.accentBg, color: C.accent, fontSize: 11, fontWeight: 700, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                        {!o.avatar_url && init}
                      </div>
                      <div style={{ display: "flex", alignItems: "center", gap: 6, flex: 1, minWidth: 0 }}>
                        <span style={{ fontSize: 13, fontWeight: selected ? 600 : 500, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", minWidth: 0 }}>{o.name}</span>
                        {o.verified && <VerifiedTick size={9} />}
                      </div>
                      {selected && <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={C.accent} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>}
                    </button>
                  );
                })}
              </div>
            </div>

            <div style={{ display: "flex", gap: 8 }}>
              <div style={{ flex: 1 }}>
                <div style={{ ...T.label, marginBottom: 6 }}>{t("dateLabel")}</div>
                <input type="date" value={date} onChange={e => setDate(e.target.value)} style={{ ...inputBase, width: "100%" }} />
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ ...T.label, marginBottom: 6 }}>{t("timeLabel")}</div>
                <input type="time" value={hour} onChange={e => setHour(e.target.value)} style={{ ...inputBase, width: "100%" }} />
              </div>
            </div>

            <div>
              <div style={{ ...T.label, marginBottom: 6 }}>{t("locationLabel") || "Location"}</div>
              <input value={location} onChange={e => setLocation(e.target.value)} placeholder="—" style={{ ...inputBase, width: "100%" }} />
            </div>

            <div>
              <div style={{ ...T.label, marginBottom: 6 }}>{t("challengeNoteOptional")}</div>
              <textarea value={note} onChange={e => setNote(e.target.value)} rows={3} maxLength={500}
                style={{ ...inputBase, width: "100%", resize: "vertical", minHeight: 60, fontFamily: "inherit" }} />
            </div>

            <button onClick={() => onSubmit({ opponentId, date, hour, location, note })} disabled={!canSubmit}
              style={{ ...btnPrimary, padding: "11px 0", fontSize: 14, opacity: canSubmit ? 1 : 0.5, cursor: canSubmit ? "pointer" : "not-allowed" }}>
              {busy ? t("sendingChallenge") : t("proposeMatch")}
            </button>
          </>
        )}
      </div>
    </div>
  );
}

function CommunityScreen({ community, user, onClose, onRefresh, showToast, onDeleted, onCreateGame, onOpenGame, onJoinGame, joinedGameIds = [], waitlistedGameIds = [], joiningGameId = null, allKnownGames = [], initialView = "about" }) {
  const { C, T, btnPrimary, btnSecondary, card, RADIUS, SPACE, warnBg, dangerBorder } = useTheme();
  const [c, setC] = useState(community);
  const [members, setMembers] = useState([]);
  const [pendingMembers, setPendingMembers] = useState([]);
  const [myMembershipRow, setMyMembershipRow] = useState(null);
  const [games, setGames] = useState([]);
  const [view, setView] = useState(initialView || "about");
  const [busy, setBusy] = useState(false);
  const [confirmLeave, setConfirmLeave] = useState(false);
  const [uploadingKind, setUploadingKind] = useState(null); // "cover" | "avatar" | null
  const [showReport, setShowReport] = useState(false);
  const [reportReason, setReportReason] = useState("");
  const [reporting, setReporting] = useState(false);
  const coverInputRef = useRef(null);
  const avatarInputRef = useRef(null);
  const [posts, setPosts] = useState([]);
  const [postsLoading, setPostsLoading] = useState(false);
  const [draft, setDraft] = useState("");
  const [posting, setPosting] = useState(false);
  const [confirmDeletePost, setConfirmDeletePost] = useState(null); // post id
  const [shareableGames, setShareableGames] = useState([]); // user-hosted games not in any community
  const [sharing, setSharing] = useState(null); // game id currently being shared
  const [messages, setMessages] = useState([]);
  const [chatDraft, setChatDraft] = useState("");
  const [chatSending, setChatSending] = useState(false);
  const chatInputRef = useRef(null);
  const chatScrollRef = useRef(null);
  const [challenges, setChallenges] = useState([]); // both directions
  const [showChallengeModal, setShowChallengeModal] = useState(false);
  const [challengeBusy, setChallengeBusy] = useState(false);
  const communityUserId = user?.id;
  const currentUserName = user?.user_metadata?.name || user?.email?.split("@")[0] || "Player";
  const currentUserAvatarUrl = user?.user_metadata?.avatar_url || null;
  const currentUserLevel = user?.user_metadata?.level || null;

  useEffect(() => {
    setC(community);
    setView(initialView || "about");
  }, [community, initialView]);

  // Derive my membership from both the roster and my direct membership row.
  // The roster can be affected by profile-join/RLS shape, while the direct row
  // is the source of truth for whether the current user can act in the group.
  const seededMyMembership = user && c?.my_role
    ? { user_id: user.id, role: c.my_role, status: "active" }
    : null;
  const myMembership = user
    ? members.find(m => m.user_id === user.id && m.status === "active")
      || (myMembershipRow?.status === "active" ? myMembershipRow : null)
      || seededMyMembership
    : null;
	  const myPendingMembership = user
	    ? pendingMembers.find(m => m.user_id === user.id && m.status === "pending")
	      || (myMembershipRow?.status === "pending" ? myMembershipRow : null)
	    : null;
	  const isOwner = myMembership?.role === "owner";
	  const isAdmin = myMembership?.role === "owner" || myMembership?.role === "admin";

  useEffect(() => {
    setC(community);
    setMyMembershipRow(communityUserId && community?.my_role
      ? { user_id: communityUserId, role: community.my_role, status: "active" }
      : null);
  }, [community, communityUserId]);

  // Upload a cover banner or avatar logo. RLS on the community-images bucket
  // (see admin/community_images_storage.sql) restricts writes to owners/admins
  // of the community whose id is the first path segment.
  const uploadImage = async (kind, file) => {
    if (!file || !c?.id || !isAdmin) return;
    setUploadingKind(kind);
    try {
      const ext = (file.name.split(".").pop() || "jpg").toLowerCase();
      // Cache-bust by including a timestamp; same path also makes upsert clean.
      const path = `${c.id}/${kind}.${ext}`;
      const { error: upErr } = await supabase.storage
        .from("community-images")
        .upload(path, file, { upsert: true, contentType: file.type });
      if (upErr) throw upErr;
      const { data: pub } = supabase.storage.from("community-images").getPublicUrl(path);
      // Append a cache-buster so the browser refetches after re-upload.
      const url = `${pub.publicUrl}?v=${Date.now()}`;
      const column = kind === "cover" ? "cover_url" : "avatar_url";
      const { error: dbErr } = await supabase
        .from("communities")
        .update({ [column]: url })
        .eq("id", c.id);
      if (dbErr) throw dbErr;
      setC(prev => ({ ...prev, [column]: url }));
      onRefresh?.();
    } catch (e) {
      console.warn("[community.upload] failed", e);
      const msg = e.message?.includes("Bucket not found") ? t("imageBucketMissing") : t("imageUploadError");
      showToast?.(msg);
    } finally {
      setUploadingKind(null);
    }
  };

	  const fallbackMemberProfile = useCallback((row) => (
	    row?.user_id === user?.id
	      ? { name: currentUserName, username: normalizeUsernameInput(user?.user_metadata?.username || ""), avatar_url: currentUserAvatarUrl, level: currentUserLevel }
	      : { name: "Player", username: "", avatar_url: null, level: null }
	  ), [user?.id, user?.user_metadata?.username, currentUserName, currentUserAvatarUrl, currentUserLevel]);

	  const getMemberProfile = useCallback((row) => {
	    const profile = row?.profiles_public || row?.profiles || fallbackMemberProfile(row);
	    return {
	      ...profile,
	      name: String(profile?.name || "").trim() || fallbackMemberProfile(row).name,
	    };
	  }, [fallbackMemberProfile]);

	  const communityMentionCandidates = useMemo(() => (
	    (members || [])
	      .filter(member => member?.status === "active" && member.user_id !== user?.id)
	      .map(member => {
	        const profile = getMemberProfile(member);
	        return { id: member.user_id, user_id: member.user_id, ...profile };
	      })
	  ), [getMemberProfile, members, user?.id]);

  const hydrateCommunityMemberProfiles = useCallback(async (rows = []) => {
    const ids = [...new Set(rows.map(row => row?.user_id).filter(Boolean))];
    if (!ids.length) return rows;

    const byId = new Map();
    const publicProfiles = await supabasePublic
	      .from("profiles_public")
	      .select("user_id, name, username, avatar_url, level")
	      .in("user_id", ids);

    if (publicProfiles.error) {
      console.warn("[community.members.profiles_public] failed", publicProfiles.error);
    } else {
      (publicProfiles.data || []).forEach(profile => {
        if (profile?.user_id) byId.set(profile.user_id, profile);
      });
    }

    return rows.map(row => ({
      ...row,
      profiles_public: byId.get(row.user_id) || row.profiles_public || row.profiles || fallbackMemberProfile(row),
    }));
  }, [fallbackMemberProfile]);

  const loadMembers = useCallback(async () => {
    if (!c?.id) return;
    const { data, error } = await supabase
      .from("community_members")
      .select("user_id, role, status, joined_at")
      .eq("community_id", c.id)
      .in("status", ["active", "pending"])
      .order("joined_at", { ascending: true });
    if (error) {
      console.warn("[community.members] failed", error);
      setMembers([]);
      setPendingMembers([]);
      return;
    }
    const rows = await hydrateCommunityMemberProfiles(data || []);
    setMembers(rows.filter(row => row.status === "active"));
    setPendingMembers(rows.filter(row => row.status === "pending"));
  }, [c?.id, hydrateCommunityMemberProfiles]);

  const loadMyMembership = useCallback(async () => {
    if (!c?.id || !user?.id) {
      setMyMembershipRow(null);
      return;
    }
    const { data, error } = await supabase
      .from("community_members")
      .select("user_id, role, status, joined_at")
      .eq("community_id", c.id)
      .eq("user_id", user.id)
      .maybeSingle();
    if (!error) setMyMembershipRow(data || null);
  }, [c?.id, user?.id]);

  const handleReviewMember = async (userId, decision) => {
    if (!isAdmin || !userId || busy) return;
    setBusy(true);
    try {
      if (decision === "approved") {
        const { error } = await supabase
          .from("community_members")
          .update({ status: "active", role: "member" })
          .eq("community_id", c.id)
          .eq("user_id", userId);
        if (error) throw error;
      } else {
        const { error } = await supabase
          .from("community_members")
          .delete()
          .eq("community_id", c.id)
          .eq("user_id", userId);
        if (error) throw error;
      }
      await loadMembers();
      if (decision === "approved") setC(prev => ({ ...prev, member_count: (prev.member_count || 0) + 1 }));
      showToast?.(decision === "approved" ? "Member approved" : "Request rejected");
    } catch (e) {
      console.warn("[community.member.review] failed", e);
      showToast?.("Could not review request");
    } finally {
      setBusy(false);
    }
  };

  const loadCommunityGames = useCallback(async () => {
    if (!c?.id) return;
    const { data } = await supabase
      .from("games")
      .select("id, title, date, hour, location, format, level, duration, min_players, spots_total, status, host_id, community_id, visibility, game_players(user_id, status)")
      .eq("community_id", c.id)
      .neq("status", "cancelled")
      .order("date", { ascending: true });
    setGames(data || []);
  }, [c?.id]);

  const getCommunityAuthorProfile = useCallback((authorId) => {
	    if (authorId === user?.id) {
	      return { name: currentUserName, username: normalizeUsernameInput(user?.user_metadata?.username || ""), avatar_url: currentUserAvatarUrl };
	    }
	    const member = members.find(row => row.user_id === authorId);
	    const profile = member ? getMemberProfile(member) : null;
	    return { name: profile?.name || "Player", username: profile?.username || "", avatar_url: profile?.avatar_url || null };
	  }, [currentUserAvatarUrl, currentUserName, getMemberProfile, members, user?.id, user?.user_metadata?.username]);

  const hydrateCommunityAuthorProfiles = useCallback(async (rows = []) => {
    const list = Array.isArray(rows) ? rows : [rows].filter(Boolean);
    const ids = [...new Set(list.map(row => row?.author_id).filter(Boolean))];
    if (!ids.length) return list;

    const byId = new Map();
    ids.forEach(id => {
      const fallback = getCommunityAuthorProfile(id);
      if (fallback) byId.set(id, fallback);
    });

    const publicProfiles = await supabasePublic
	      .from("profiles_public")
	      .select("user_id, name, username, avatar_url")
	      .in("user_id", ids);
    if (publicProfiles.error) {
      console.warn("[community.authors.profiles_public] failed", publicProfiles.error);
    } else {
      (publicProfiles.data || []).forEach(profile => {
        if (profile?.user_id) byId.set(profile.user_id, profile);
      });
    }

    return list.map(row => ({
      ...row,
      profiles_public: row.profiles_public || row.profiles || byId.get(row.author_id) || getCommunityAuthorProfile(row.author_id),
    }));
  }, [getCommunityAuthorProfile]);

  const loadPosts = useCallback(async () => {
    if (!c?.id) return;
    setPostsLoading(true);
    try {
      const { data, error } = await supabase
        .from("community_posts")
        .select("id, body, pinned, created_at, updated_at, author_id")
        .eq("community_id", c.id)
        .order("pinned", { ascending: false })
        .order("created_at", { ascending: false })
        .limit(50);
      if (error) throw error;
      setPosts(await hydrateCommunityAuthorProfiles(data || []));
    } catch (e) {
      console.warn("[community.posts.load] failed", e);
      setPosts([]);
    } finally {
      setPostsLoading(false);
    }
  }, [c?.id, hydrateCommunityAuthorProfiles]);

  // Pull the caller's own hosted, future, non-cancelled games that haven't
  // yet been scoped to *any* community — these are the ones we offer to
  // "share" into the current community.
  const loadShareable = useCallback(async () => {
    if (!user?.id || !c?.id) { setShareableGames([]); return; }
    const today = new Date().toISOString().slice(0, 10);
    const { data } = await supabase
      .from("games")
      .select("id, title, date, hour, location, format, status, community_id")
      .eq("host_id", user.id)
      .gte("date", today)
      .neq("status", "cancelled")
      .is("community_id", null)
      .order("date", { ascending: true })
      .limit(20);
    setShareableGames(data || []);
  }, [user?.id, c?.id]);

  // Load challenges where this community is on either side. RLS already
  // restricts visibility to active members, so this is safe to fan out.
  const loadChallenges = useCallback(async () => {
    if (!c?.id) return;
    const { data } = await supabase
      .from("community_challenges")
      .select("id, status, proposed_date, proposed_hour, proposed_location, format, message, challenger_id, challenged_id, created_at, challenger:challenger_id(id, name, avatar_url, slug), challenged:challenged_id(id, name, avatar_url, slug)")
      .or(`challenger_id.eq.${c.id},challenged_id.eq.${c.id}`)
      .neq("status", "declined")
      .neq("status", "cancelled")
      .order("created_at", { ascending: false })
      .limit(20);
    setChallenges(data || []);
  }, [c?.id]);

  useEffect(() => { loadMembers(); loadMyMembership(); loadCommunityGames(); loadPosts(); loadShareable(); loadChallenges(); }, [loadMembers, loadMyMembership, loadCommunityGames, loadPosts, loadShareable, loadChallenges]);

  // Realtime: keep challenge state in sync — accept/decline lands instantly
  // on both sides of the rivalry.
  useEffect(() => {
    if (!c?.id) return;
    const channel = supabase
      .channel(`community-challenges:${c.id}`)
      .on("postgres_changes", { event: "*", schema: "public", table: "community_challenges" },
        () => { loadChallenges(); })
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, [c?.id, loadChallenges]);

  const handleSendChallenge = async ({ opponentId, date, hour, location, note }) => {
    if (!isAdmin || !opponentId || !date) return;
    setChallengeBusy(true);
    try {
      const { data: inserted, error } = await supabase.from("community_challenges").insert({
        challenger_id: c.id,
        challenged_id: opponentId,
        created_by: user.id,
        proposed_date: date,
        proposed_hour: hour || null,
        proposed_location: location || null,
        message: note || null,
      }).select("id").maybeSingle();
      if (error) throw error;
      // Notify admins of the challenged community so the rivalry surfaces.
      try {
        const [{ data: admins }, { data: opponent }] = await Promise.all([
          supabase.from("community_members").select("user_id").eq("community_id", opponentId).in("role", ["owner", "admin"]).eq("status", "active"),
          supabase.from("communities").select("name").eq("id", opponentId).maybeSingle(),
        ]);
        const rows = (admins || [])
          .filter(a => a.user_id !== user.id)
          .map(a => ({
            user_id: a.user_id,
            type: "community_challenge",
            text: `${c.name} challenged ${opponent?.name || "your community"} to a match`,
            sub: inserted?.id || null,
            game_id: null,
            read: false,
          }));
        if (rows.length) await supabase.from("notifications").insert(rows);
      } catch (notifyErr) {
        console.warn("[community.challenge.notify] failed", notifyErr);
      }
      setShowChallengeModal(false);
      showToast?.(t("challengeSent"));
      await loadChallenges();
    } catch (e) {
      console.warn("[community.challenge.send] failed", e);
      showToast?.(t("challengeError"));
    }
    setChallengeBusy(false);
  };

  const handleDecideChallenge = async (id, decision) => {
    if (!isAdmin) return;
    try {
      const { error } = await supabase
        .from("community_challenges")
        .update({ status: decision, decided_by: user.id, decided_at: new Date().toISOString() })
        .eq("id", id);
      if (error) throw error;
      // Notify the challenger's admins of the decision (skip 'cancelled', that
      // event is the challenger's own action).
      if (decision === "accepted" || decision === "declined") {
        try {
          const ch = challenges.find(x => x.id === id);
          if (ch) {
            const otherSideId = ch.challenger_id === c.id ? ch.challenged_id : ch.challenger_id;
            const { data: admins } = await supabase
              .from("community_members")
              .select("user_id")
              .eq("community_id", otherSideId)
              .in("role", ["owner", "admin"])
              .eq("status", "active");
            const verb = decision === "accepted" ? "accepted" : "declined";
            const rows = (admins || [])
              .filter(a => a.user_id !== user.id)
              .map(a => ({
                user_id: a.user_id,
                type: "community_challenge_decision",
                text: `${c.name} ${verb} the challenge`,
                sub: id,
                game_id: null,
                read: false,
              }));
            if (rows.length) await supabase.from("notifications").insert(rows);
          }
        } catch (notifyErr) {
          console.warn("[community.challenge.decide.notify] failed", notifyErr);
        }
      }
      await loadChallenges();
    } catch (e) {
      console.warn("[community.challenge.decide] failed", e);
      showToast?.(t("challengeUpdateError"));
    }
  };

  // ── Chat ────────────────────────────────────────────────────────────────
  const mergeCommunityMessageRows = useCallback((incoming = [], { replace = false } = {}) => {
    const rows = (Array.isArray(incoming) ? incoming : [incoming]).filter(Boolean);
    setMessages(prev => {
      const now = Date.now();
      const pendingLocal = prev.filter(m => {
        if (!String(m.id).startsWith("local-")) return false;
        const localTime = new Date(m.created_at || 0).getTime();
        return Number.isFinite(localTime) && now - localTime < 2 * 60_000;
      });
      const next = replace ? pendingLocal : [...prev];
      rows.forEach(row => {
        const normalized = {
          ...row,
          profiles_public: row.profiles_public || row.profiles || getCommunityAuthorProfile(row.author_id),
        };
        const existingIndex = next.findIndex(m => m.id === normalized.id);
        if (existingIndex >= 0) {
          next[existingIndex] = { ...next[existingIndex], ...normalized };
          return;
        }
        const rowTime = new Date(normalized.created_at || 0).getTime();
        const optimisticIndex = next.findIndex(m => {
          if (!String(m.id).startsWith("local-")) return false;
          if (m.author_id !== normalized.author_id || m.body !== normalized.body) return false;
          const localTime = new Date(m.created_at || 0).getTime();
          return Number.isFinite(rowTime) && Number.isFinite(localTime) && Math.abs(rowTime - localTime) < 2 * 60_000;
        });
        if (optimisticIndex >= 0) next[optimisticIndex] = normalized;
        else next.push(normalized);
      });
      return next.sort((a, b) => new Date(a.created_at || 0) - new Date(b.created_at || 0));
    });
  }, [getCommunityAuthorProfile]);

  const loadMessages = useCallback(async () => {
    if (!c?.id) return;
    const { data, error } = await supabase
      .from("community_messages")
      .select("id, body, created_at, author_id")
      .eq("community_id", c.id)
      .order("created_at", { ascending: false })
      .limit(100);
    if (error) {
      console.warn("[community.chat.load] failed", error);
      return;
    }
    // Reverse so oldest-first when rendered top-to-bottom.
    const rows = await hydrateCommunityAuthorProfiles((data || []).slice().reverse());
    mergeCommunityMessageRows(rows, { replace: true });
  }, [c?.id, hydrateCommunityAuthorProfiles, mergeCommunityMessageRows]);

  // Load chat once when the chat view is opened, and whenever community changes
  // while chat is the active tab. Keeps the upfront cost of the screen low.
  useEffect(() => {
    if (view !== "chat") return;
    loadMessages();
  }, [view, loadMessages]);

  // Realtime tail — append new messages without a full refetch.
  useEffect(() => {
    if (view !== "chat" || !c?.id) return;
    const channel = supabase
      .channel(`community-chat:${c.id}`)
      .on("postgres_changes", { event: "INSERT", schema: "public", table: "community_messages", filter: `community_id=eq.${c.id}` },
        async (payload) => {
          // Hydrate author profile for the inbound row.
          const row = payload.new;
	          const { data: prof } = await supabase
	            .from("profiles_public")
	            .select("name, username, avatar_url")
	            .eq("user_id", row.author_id)
            .maybeSingle();
          mergeCommunityMessageRows({ ...row, profiles_public: prof || getCommunityAuthorProfile(row.author_id) });
        })
      .on("postgres_changes", { event: "DELETE", schema: "public", table: "community_messages", filter: `community_id=eq.${c.id}` },
        (payload) => {
          setMessages(prev => prev.filter(m => m.id !== payload.old.id));
        })
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, [view, c?.id, getCommunityAuthorProfile, mergeCommunityMessageRows]);

  useEffect(() => {
    if (view !== "chat" || !c?.id) return undefined;
    let cancelled = false;
    const pollMessages = async () => {
      try {
        const { data, error } = await supabase
          .from("community_messages")
          .select("id, body, created_at, author_id")
          .eq("community_id", c.id)
          .order("created_at", { ascending: false })
          .limit(100);
        if (error) throw error;
        const rows = await hydrateCommunityAuthorProfiles((data || []).slice().reverse());
        if (!cancelled) mergeCommunityMessageRows(rows);
      } catch (e) {
        console.warn("[community.chat.poll] failed", e);
      }
    };
    const interval = window.setInterval(pollMessages, 4000);
    return () => {
      cancelled = true;
      window.clearInterval(interval);
    };
  }, [view, c?.id, hydrateCommunityAuthorProfiles, mergeCommunityMessageRows]);

  // Auto-scroll to bottom on new message (only if the user was already near
  // the bottom — don't yank them away while reading history).
  useEffect(() => {
    const el = chatScrollRef.current;
    if (!el) return;
    const nearBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 80;
    if (nearBottom) el.scrollTop = el.scrollHeight;
  }, [messages]);

  useEffect(() => {
    if (view !== "chat" || myMembership?.status !== "active") return;
    const id = window.setTimeout(() => chatInputRef.current?.focus(), 80);
    return () => window.clearTimeout(id);
  }, [view, myMembership?.status]);

  const handleSendMessage = async () => {
    const body = chatDraft.trim();
    if (!body || !user || !c?.id || chatSending) return;
    setChatSending(true);
    const sentAt = new Date().toISOString();
    const localId = `local-${sentAt}-${Math.random().toString(36).slice(2)}`;
    const optimisticMessage = {
      id: localId,
      community_id: c.id,
      author_id: user.id,
      body,
      created_at: sentAt,
      profiles_public: getCommunityAuthorProfile(user.id),
    };
    setChatDraft("");
    mergeCommunityMessageRows(optimisticMessage);
    try {
      const { data, error } = await supabase.from("community_messages").insert({
        community_id: c.id, author_id: user.id, body,
      }).select("id, community_id, body, created_at, author_id").maybeSingle();
      if (error) throw error;
      if (data) mergeCommunityMessageRows({ ...data, profiles_public: getCommunityAuthorProfile(user.id) });
	      try {
	        const senderName = user?.user_metadata?.name || user?.email?.split("@")[0] || "Someone";
	        const preview = body.length > 80 ? `${body.slice(0, 77)}...` : body;
	        const mentionTargets = mentionedProfilesFromText(body, communityMentionCandidates, user.id);
	        const mentionTargetIds = new Set(mentionTargets.map(profile => profile.id || profile.user_id).filter(Boolean));
	        const rows = (members || [])
	          .filter(member => member?.user_id && member.user_id !== user.id && member.status === "active")
		          .map(member => ({
		            user_id: member.user_id,
		            type: mentionTargetIds.has(member.user_id) ? "mention" : "chat_message",
		            text: mentionTargetIds.has(member.user_id) ? `${senderName} mentioned you in ${c.name}` : `${senderName} posted in ${c.name}`,
		            sub: encodeChatNotificationSub({ kind: "community_chat", communityId: c.id, communityName: c.name, preview }),
		            game_id: null,
		            read: false,
		          }));
        if (rows.length) {
          const { error: notificationError } = await supabase.from("notifications").insert(rows);
          if (notificationError) console.warn("[community.chat.notification] failed", notificationError);
        }
      } catch (notificationError) {
        console.warn("[community.chat.notification] failed", notificationError);
      }
      // Realtime will push the new row in; force-scroll for the sender too.
      requestAnimationFrame(() => {
        if (chatScrollRef.current) chatScrollRef.current.scrollTop = chatScrollRef.current.scrollHeight;
      });
    } catch (e) {
      console.warn("[community.chat.send] failed", e);
      setMessages(prev => prev.filter(message => message.id !== localId));
      setChatDraft(prev => prev || body);
      showToast?.(t("chatSendError"));
    } finally {
      setChatSending(false);
    }
  };

  const handleDeleteMessage = async (id) => {
    try {
      await supabase.from("community_messages").delete().eq("id", id);
      // Optimistic — realtime DELETE will also clean up if RLS allowed.
      setMessages(prev => prev.filter(m => m.id !== id));
    } catch (e) { console.warn("[community.chat.delete] failed", e); }
  };

  // Re-scope an existing game to this community. RLS allows the host to
  // update their own game; the visibility flips to "community" so it
  // appears scoped on game cards and in the Games tab.
  const handleShareGame = async (gameId) => {
    if (!gameId || !c?.id) return;
    setSharing(gameId);
    try {
      const { error } = await supabase
        .from("games")
        .update({ community_id: c.id, visibility: "community", is_private: false })
        .eq("id", gameId)
        .eq("host_id", user.id);
      if (error) throw error;
      await Promise.all([loadCommunityGames(), loadShareable()]);
      onRefresh?.();
    } catch (e) {
      console.warn("[community.shareGame] failed", e);
      showToast?.(t("postError"));
    }
    setSharing(null);
  };

  // Realtime: keep the feed live so members see new posts as they arrive.
  useEffect(() => {
    if (!c?.id) return;
    const channel = supabase
      .channel(`community-posts:${c.id}`)
      .on("postgres_changes", { event: "*", schema: "public", table: "community_posts", filter: `community_id=eq.${c.id}` },
        () => { loadPosts(); })
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, [c?.id, loadPosts]);

  const handlePost = async () => {
    const body = draft.trim();
    if (!body) { showToast?.(t("postEmptyError")); return; }
    if (!user || !c?.id || !isAdmin) return;
    setPosting(true);
    try {
      const { error } = await supabase.from("community_posts").insert({
        community_id: c.id,
        author_id: user.id,
        body,
      });
      if (error) throw error;
      setDraft("");
      await loadPosts();
    } catch (e) {
      console.warn("[community.post] failed", e);
      showToast?.(t("postError"));
    }
    setPosting(false);
  };

  const handleSetMemberRole = async (member, nextRole) => {
    if (!isOwner || !member?.user_id || member.role === "owner" || member.user_id === user?.id) return;
    setBusy(true);
    try {
      const { error } = await supabase
        .from("community_members")
        .update({ role: nextRole })
        .eq("community_id", c.id)
        .eq("user_id", member.user_id)
        .eq("status", "active");
      if (error) throw error;
      setMembers(prev => prev.map(row => row.user_id === member.user_id ? { ...row, role: nextRole } : row));
      showToast?.(nextRole === "admin" ? "Admin added" : "Admin removed");
      onRefresh?.();
    } catch (e) {
      console.warn("[community.member.role] failed", e);
      showToast?.(t("postError"));
    } finally {
      setBusy(false);
    }
  };

  const handleTogglePin = async (post) => {
    if (!isAdmin) return;
    try {
      await supabase.from("community_posts").update({ pinned: !post.pinned }).eq("id", post.id);
      await loadPosts();
    } catch (e) { console.warn("[community.pin] failed", e); }
  };

  const handleDeletePost = async (postId) => {
    try {
      await supabase.from("community_posts").delete().eq("id", postId);
      setConfirmDeletePost(null);
      await loadPosts();
    } catch (e) {
      console.warn("[community.deletePost] failed", e);
      showToast?.(t("postError"));
    }
  };

  const handleJoin = async () => {
    if (!user || busy) return;
    const policy = c.join_policy || (c.is_private ? "invite_only" : "open");
    if (policy === "invite_only") {
      showToast?.(c.is_private ? "This group is private. Ask an admin for an invite." : "This group is invite-only");
      return;
    }
    setBusy(true);
    try {
      const status = policy === "request" ? "pending" : "active";
      const { data: existing, error: lookupError } = await supabase
        .from("community_members")
        .select("user_id, role, status")
        .eq("community_id", c.id)
        .eq("user_id", user.id)
        .maybeSingle();
      if (lookupError) throw lookupError;

      if (existing?.status === "active") {
        setMyMembershipRow(existing);
        showToast?.(t("joined"));
        await loadMembers();
        setBusy(false);
        return;
      }
      if (existing?.status === "pending") {
        setMyMembershipRow(existing);
        showToast?.(t("requestPending"));
        await loadMembers();
        setBusy(false);
        return;
      }

      const wasActive = existing?.status === "active";
      if (existing) {
        const { error } = await supabase
          .from("community_members")
          .update({ role: existing.role || "member", status })
          .eq("community_id", c.id)
          .eq("user_id", user.id);
        if (error) throw error;
      } else {
        let { error } = await supabase.from("community_members").insert({
          community_id: c.id, user_id: user.id, role: "member", status,
        });
        if (error) {
          const { error: refreshErr } = await supabase.auth.refreshSession();
          if (!refreshErr) {
            ({ error } = await supabase.from("community_members").insert({
              community_id: c.id, user_id: user.id, role: "member", status,
            }));
          }
        }
        if (error) throw error;
      }
      setMyMembershipRow({ user_id: user.id, role: existing?.role || "member", status });
      showToast?.(status === "pending" ? t("requestPending") : t("joinCommunity") + " ✓");
      await loadMembers();
      // Bump member_count locally for snappy UI (trigger updates server-side)
      if (status === "active" && !wasActive) setC(prev => ({ ...prev, member_count: (prev.member_count || 0) + 1 }));
      // Notify the community's admins so requests don't sit unread.
      if (status === "pending" && existing?.status !== "pending") {
        try {
          const { data: admins } = await supabase
            .from("community_members")
            .select("user_id")
            .eq("community_id", c.id)
            .in("role", ["owner", "admin"])
            .eq("status", "active");
          const senderName = user?.user_metadata?.name || user?.email?.split("@")[0] || "Someone";
          const rows = (admins || [])
            .filter(a => a.user_id !== user.id)
            .map(a => ({
              user_id: a.user_id,
              type: "community_join_request",
              text: `${senderName} requested to join ${c.name}`,
              sub: c.id,
              game_id: null,
              read: false,
            }));
          if (rows.length) await supabase.from("notifications").insert(rows);
        } catch (notifyErr) {
          console.warn("[community.join] notify admins failed", notifyErr);
        }
      }
      onRefresh?.();
    } catch (e) {
      console.warn("[community.join] failed", e);
      showToast?.(t("communityJoinError"));
    }
    setBusy(false);
  };

  const handleLeave = async () => {
    if (!user || busy) return;
    setBusy(true);
    try {
      if (isOwner) {
        // Owner can't simply leave — they must transfer ownership or delete
        // the community. For Phase 1 we ship a simple auto-transfer: server
        // fn picks the successor, or deletes if none.
        const { data: successor } = await supabase.rpc("transfer_community_ownership", { cid: c.id });
        if (!successor) {
          // No successor — delete the community
          await supabase.from("communities").delete().eq("id", c.id);
          showToast?.(t("leaveCommunity") + " ✓");
          onDeleted?.(c.id);
          onClose();
          return;
        }
      }
      const { error } = await supabase.from("community_members").delete().eq("community_id", c.id).eq("user_id", user.id);
      if (error) throw error;
      setMyMembershipRow(null);
      showToast?.(t("leaveCommunity") + " ✓");
      setC(prev => ({ ...prev, member_count: Math.max(0, (prev.member_count || 0) - 1) }));
      onRefresh?.();
      onClose();
    } catch (e) {
      console.warn("[community.leave] failed", e);
      showToast?.(t("communityLeaveError"));
    }
    setBusy(false);
    setConfirmLeave(false);
  };

  const initials = (c.name || "?").split(" ").map(w => w[0]).join("").slice(0, 2).toUpperCase();

	  // Derive admin/owner roster from members (already loaded). Owner first.
	  const adminRoster = members.filter(m => m.role === "owner" || m.role === "admin");
	  const activeMemberCount = members.filter(m => !m.status || m.status === "active").length || c.member_count || 0;

	  // The status pill on the cover — Open / Closed / Private — mirrors the
	  // Discover card so the visual language carries through into the detail view.
	  const isOpen = c.join_policy !== "request" && !c.is_private;
	  const accessLabel = c.is_private ? "Private group" : c.join_policy === "request" ? "Request to join" : "Open group";
	  const accessDetail = c.is_private
	    ? "Only invited players can see and join."
	    : c.join_policy === "request"
	      ? "New players ask to join before entering."
	      : "Anyone can join and play with the group.";
	  const knownGamesById = useMemo(() => new Map((allKnownGames || []).map(g => [g.id, g])), [allKnownGames]);
  const communityGameRows = useMemo(() => (games || []).map(row => {
    const known = knownGamesById.get(row.id);
    const confirmedCount = (row.game_players || []).filter(p => !p.status || p.status === "confirmed").length;
    const spotsTotal = known?.spotsTotal ?? row.spots_total ?? FORMAT_SPOTS[row.format] ?? 10;
    const spotsFilled = known?.spotsFilled ?? confirmedCount;
    const minPlayers = known?.minPlayers ?? row.min_players ?? defaultMinPlayersForFormat(row.format || "5v5");
    const duration = known?.duration ?? row.duration ?? "60 min";
    const displayGame = known || {
      id: row.id,
      title: row.title,
      date: row.date || "",
      hour: row.hour || "",
      duration,
      format: row.format || "5v5",
      level: row.level || "All levels",
      location: row.location || "",
      spotsTotal,
      spotsFilled,
      minPlayers,
      needsMorePlayers: spotsFilled < minPlayers,
      players: [],
      waitlist: [],
      host: c.name,
      hostAvatar: initials,
      hostId: row.host_id,
      status: row.status || "active",
      visibility: row.visibility || "community",
      communityId: c.id,
      communityName: c.name,
      price: "Free",
      tags: [],
      description: "",
    };
    const endTime = known?.endTime ?? getGameEndTime({ date: row.date, hour: row.hour, duration });
    return {
      ...row,
      displayGame,
      endTime,
      past: isPastGame({ ...displayGame, date: row.date || displayGame.date, hour: row.hour || displayGame.hour, duration, endTime }),
      spotsTotal,
      spotsFilled,
      minPlayers,
      joined: joinedGameIds.includes(row.id),
      waitlisted: waitlistedGameIds.includes(row.id),
      full: spotsFilled >= spotsTotal,
    };
	  }).sort((a, b) => {
	    const aTime = new Date(`${a.date || "9999-12-31"}T${a.hour || "23:59"}`).getTime();
	    const bTime = new Date(`${b.date || "9999-12-31"}T${b.hour || "23:59"}`).getTime();
	    if (a.past && b.past) return bTime - aTime;
	    return aTime - bTime;
	  }), [games, knownGamesById, joinedGameIds, waitlistedGameIds, c.id, c.name, initials]);
	  const upcomingCommunityGames = communityGameRows.filter(g => !g.past);
	  const pastCommunityGames = communityGameRows.filter(g => g.past);
	  const nextCommunityGame = upcomingCommunityGames[0] || null;
  const formatCommunityGameDate = (game) => {
    if (!game.date) return "Date TBC";
    try {
      return format(new Date(`${game.date}T00:00:00`), "EEE d MMM");
    } catch {
      return game.date;
    }
  };
  const openCommunityGame = (game) => onOpenGame?.(game.displayGame);
  const renderCommunityGameCard = (game) => {
    const isJoiningThis = joiningGameId === game.id;
    const canDirectJoin = !!onJoinGame && !game.past && !game.joined && !game.waitlisted && !game.full;
    const actionLabel = game.past
      ? t("completed")
      : game.joined
        ? t("joined")
        : game.waitlisted
          ? (t("onWaitlist") || "Waitlisted")
          : game.full
            ? t("full")
            : isJoiningThis
              ? t("joining")
              : t("join");
    const actionTone = game.past || game.full || game.waitlisted
      ? { bg: C.bg, fg: C.inkMid, border: C.border }
      : game.joined
        ? { bg: C.accentBg, fg: C.accent, border: "transparent" }
        : { bg: C.accent, fg: C.accentText, border: C.accent };
    return (
      <div
        key={game.id}
        onClick={() => openCommunityGame(game)}
        style={{
          ...card,
          padding: SPACE[4],
          cursor: onOpenGame ? "pointer" : "default",
          opacity: game.past ? 0.72 : 1,
          borderColor: game.past ? C.border : game.joined ? `${C.accent}55` : C.border,
          background: game.past ? C.bg : C.surface,
          boxShadow: "none",
        }}
      >
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: SPACE[3], marginBottom: SPACE[2] }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 8 }}>
              <span style={{ padding: "3px 9px", borderRadius: RADIUS.pill, background: game.past ? C.surface : C.accentBg, color: game.past ? C.inkMid : C.accent, fontSize: 11, fontWeight: 800 }}>
                {game.past ? t("completed") : `${game.spotsFilled}/${game.spotsTotal}`}
              </span>
              <span style={{ padding: "3px 9px", borderRadius: RADIUS.pill, background: C.bg, color: C.inkMid, fontSize: 11, fontWeight: 750 }}>{game.format}</span>
              {!game.past && game.spotsFilled < game.minPlayers && (() => {
                const need = Math.max(1, game.minPlayers - game.spotsFilled);
                return (
                  <span style={{ padding: "3px 9px", borderRadius: RADIUS.pill, background: warnBg, color: C.warn, fontSize: 11, fontWeight: 800 }}>{need === 1 ? t("oneMoreToStart") : t("moreToStart", { n: need })}</span>
                );
              })()}
            </div>
            <div style={{ fontSize: 16, fontWeight: 850, color: C.ink, lineHeight: 1.15, marginBottom: 6, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {game.title}
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 3, color: C.inkMid, fontSize: 12 }}>
              <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                <Clock size={13} {...ICON} />
                {formatCommunityGameDate(game)} · {game.hour || t("timeTbc")}
              </span>
              <span style={{ display: "inline-flex", alignItems: "center", gap: 6, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                <MapPin size={13} {...ICON} />
                {game.location || t("venueTbc")}
              </span>
            </div>
          </div>
          <button
            type="button"
            onClick={e => {
              e.stopPropagation();
              if (canDirectJoin && !isJoiningThis) onJoinGame(game.id);
              else openCommunityGame(game);
            }}
            disabled={isJoiningThis}
            style={{
              minWidth: 76,
              minHeight: 38,
              padding: "0 13px",
              borderRadius: RADIUS.pill,
              border: `1px solid ${actionTone.border}`,
              background: actionTone.bg,
              color: actionTone.fg,
              fontSize: 12,
              fontWeight: 850,
              cursor: isJoiningThis ? "not-allowed" : "pointer",
              flexShrink: 0,
            }}
          >
            {actionLabel}
          </button>
        </div>
        {!game.past && (
          <div style={{ height: 5, background: C.bg, borderRadius: 999, overflow: "hidden" }}>
            <div style={{ width: `${Math.min(100, Math.round((game.spotsFilled / Math.max(1, game.spotsTotal)) * 100))}%`, height: "100%", background: game.full ? C.warn : C.accent, borderRadius: 999 }} />
          </div>
        )}
      </div>
    );
  };

  // Native share if available; fall back to clipboard. Slug-aware so links
  // are pretty when the slug is set.
  const handleShare = async () => {
    const url = `${window.location.origin}/communities/${c.slug || c.id}`;
    const payload = { title: c.name, text: c.description || c.name, url };
    try {
      if (navigator.share) {
        await navigator.share(payload);
        return;
      }
      await navigator.clipboard?.writeText(url);
      showToast?.(t("linkCopied"));
    } catch (e) {
      // User-cancelled share is fine — no toast needed.
      if (e?.name !== "AbortError") console.warn("[community.share] failed", e);
    }
  };

  // Reports are now first-class rows for the admin review queue. We keep the
  // old notification fallback so local/dev databases that have not run the
  // migration still record user intent instead of dropping the report.
  const handleSubmitReport = async () => {
    const reason = reportReason.trim();
    if (!reason || !user || reporting) return;
    setReporting(true);
    try {
      const { error: reportError } = await supabase.from("community_reports").insert({
        community_id: c.id,
        reporter_id: user.id,
        reason,
      });
      if (reportError) {
        console.warn("[community.report.queue] failed, falling back to notification", reportError);
        await supabase.from("notifications").insert({
          user_id: user.id,
          type: "community_report_submitted",
          text: `Report submitted for ${c.name}`,
          sub: reason.slice(0, 180),
          game_id: null,
          read: false,
        });
      }
      setShowReport(false);
      setReportReason("");
      showToast?.(t("reportSubmitted"));
    } catch (e) {
      console.warn("[community.report] failed", e);
      showToast?.(t("reportError"));
    }
    setReporting(false);
  };

  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 250, background: C.bg, display: "flex", flexDirection: "column" }}>
      <div style={{ padding: "calc(12px + env(safe-area-inset-top, 0px)) 16px 14px", borderBottom: `1px solid ${C.border}`, background: C.bg, flexShrink: 0 }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, marginBottom: 14 }}>
          <button onClick={onClose} aria-label="Back" style={{ width: 38, height: 38, borderRadius: "50%", background: C.surface, border: `1px solid ${C.border}`, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", color: C.ink, boxShadow: SHADOW.sm }}>
            <ArrowLeft size={18} {...ICON} />
          </button>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <span style={{ display: "inline-flex", alignItems: "center", gap: 5, height: 34, padding: "0 12px", borderRadius: RADIUS.pill, background: isOpen ? C.accentBg : C.surface, color: isOpen ? C.accent : C.inkMid, fontSize: 12, fontWeight: 850, border: `1px solid ${isOpen ? `${C.accent}33` : C.border}` }}>
              {isOpen ? <Check size={13} {...ICON} /> : <Lock size={13} {...ICON} />}
              {isOpen ? t("communityOpen") : t("communityClosed")}
            </span>
            <button onClick={handleShare} aria-label={t("shareCommunity")} style={{ width: 38, height: 38, borderRadius: "50%", background: C.surface, border: `1px solid ${C.border}`, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", color: C.inkMid }}>
              <Share2 size={17} {...ICON} />
            </button>
            {user && (
              <button onClick={() => setShowReport(true)} aria-label={t("reportCommunity")} style={{ width: 38, height: 38, borderRadius: "50%", background: C.surface, border: `1px solid ${C.border}`, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", color: C.inkMid }}>
                <Flag size={17} {...ICON} />
              </button>
            )}
            {isAdmin && (
              <>
                <input ref={coverInputRef} type="file" accept="image/*" style={{ display: "none" }}
                  onChange={e => { const f = e.target.files?.[0]; if (f) uploadImage("cover", f); e.target.value = ""; }} />
                <button
                  onClick={() => coverInputRef.current?.click()}
                  disabled={uploadingKind === "cover"}
                  aria-label={t("editCover")}
                  style={{ width: 38, height: 38, borderRadius: "50%", background: C.surface, border: `1px solid ${C.border}`, cursor: uploadingKind === "cover" ? "not-allowed" : "pointer", display: "flex", alignItems: "center", justifyContent: "center", color: C.inkMid, opacity: uploadingKind === "cover" ? 0.55 : 1 }}
                >
                  {uploadingKind === "cover"
                    ? <div style={{ width: 14, height: 14, border: `2px solid ${C.inkMid}`, borderTopColor: "transparent", borderRadius: "50%", animation: "spin 0.8s linear infinite" }} />
                    : <Camera size={17} {...ICON} />}
                </button>
              </>
            )}
          </div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <div style={{ position: "relative", flexShrink: 0 }}>
            <div style={{ width: 58, height: 58, borderRadius: 16, background: c.avatar_url ? `url(${c.avatar_url}) center/cover` : C.accentBg, border: `1px solid ${C.border}`, display: "flex", alignItems: "center", justifyContent: "center", color: C.accent, fontSize: 20, fontWeight: 900 }}>
              {!c.avatar_url && initials}
            </div>
            {isAdmin && (
              <>
                <input ref={avatarInputRef} type="file" accept="image/*" style={{ display: "none" }}
                  onChange={e => { const f = e.target.files?.[0]; if (f) uploadImage("avatar", f); e.target.value = ""; }} />
                <button
                  onClick={() => avatarInputRef.current?.click()}
                  disabled={uploadingKind === "avatar"}
                  aria-label={t("editLogo")}
                  style={{ position: "absolute", right: -4, bottom: -4, width: 24, height: 24, borderRadius: "50%", background: C.accent, color: C.accentText, border: `2px solid ${C.bg}`, cursor: uploadingKind === "avatar" ? "not-allowed" : "pointer", display: "flex", alignItems: "center", justifyContent: "center", padding: 0 }}
                >
                  {uploadingKind === "avatar"
                    ? <div style={{ width: 9, height: 9, border: `2px solid ${C.accentText}`, borderTopColor: "transparent", borderRadius: "50%", animation: "spin 0.8s linear infinite" }} />
                    : <Edit3 size={11} {...ICON} />}
                </button>
              </>
            )}
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 7, minWidth: 0 }}>
              <h2 style={{ margin: 0, color: C.ink, fontSize: 24, lineHeight: 1.1, fontWeight: 900, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{c.name}</h2>
              {c.verified && <VerifiedTick size={12} />}
            </div>
            <div style={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: "6px 12px", marginTop: 6, color: C.inkMid, fontSize: 13, fontWeight: 600 }}>
              {c.city && <span style={{ display: "inline-flex", alignItems: "center", gap: 5 }}><MapPin size={13} {...ICON} />{c.city}</span>}
              <span style={{ display: "inline-flex", alignItems: "center", gap: 5 }}><Users size={13} {...ICON} />{(c.member_count || 0) === 1 ? t("memberSingular") : t("membersCount", { n: c.member_count || 0 })}</span>
              <span style={{ display: "inline-flex", alignItems: "center", gap: 5 }}><Calendar size={13} {...ICON} />{games.length}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Sub-tabs — use the same quiet underline pattern as game detail tabs. */}
      <div
        style={{
          display: "flex",
          alignItems: "flex-end",
          gap: 22,
          margin: "8px 16px 0",
          borderBottom: `1px solid ${C.border}`,
          flexShrink: 0,
          overflowX: "auto",
          scrollbarWidth: "none",
        }}
      >
        {[
          ["about",   t("aboutTab")],
          ["games",   t("gamesTab")],
          ["feed",    t("feedTab")],
          ["chat",    t("chatTab")],
          ["members", t("membersTab")],
        ].map(([v, label]) => {
          const locked = !myMembership && (v === "chat" || v === "feed");
          return (
            <button
              key={v}
              onClick={() => { if (!locked) setView(v); }}
              disabled={locked}
              style={{
                position: "relative",
                minHeight: 42,
                padding: "0 0 10px",
                border: "none",
                borderBottom: `3px solid ${view === v ? C.accent : "transparent"}`,
                borderRadius: 0,
                background: "transparent",
                cursor: locked ? "not-allowed" : "pointer",
                fontSize: 14,
                fontWeight: view === v ? 850 : 750,
                color: view === v ? C.ink : C.inkMid,
                opacity: locked ? 0.5 : 1,
                transition: "color 0.15s, border-color 0.15s",
                flex: "0 0 auto",
                display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 4,
              }}
            >
              {label}
              {locked && (
                <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
              )}
            </button>
          );
        })}
      </div>

      {/* Body — chat takes a different layout so the composer is pinned and only
          the message list scrolls. Other views keep the simpler scroll-all box. */}
      <div style={{ flex: 1, overflow: view === "chat" ? "hidden" : "auto", display: view === "chat" ? "flex" : "block", flexDirection: "column", paddingTop: view === "chat" ? 0 : 16, paddingLeft: view === "chat" ? 0 : 16, paddingRight: view === "chat" ? 0 : 16, paddingBottom: view === "chat" ? 0 : ((user && !myMembership) ? 100 : 16) }}>
	        {view === "about" && (
	          <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
	            <div style={{ ...card, padding: 16, boxShadow: "none" }}>
	              <div style={{ display: "flex", alignItems: "flex-start", gap: 12 }}>
	                <div style={{ width: 36, height: 36, borderRadius: 10, background: C.accentBg, color: C.accent, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
	                  <Info size={18} {...ICON} />
	                </div>
	                <div style={{ flex: 1, minWidth: 0 }}>
	                  <div style={{ ...T.label, marginBottom: 6 }}>{t("aboutDescriptionTitle")}</div>
	                  <div style={{ fontSize: 14, color: c.description?.trim() ? C.ink : C.inkMid, lineHeight: 1.45 }}>
	                    {c.description?.trim() || "No description yet. Check the games, members, and feed to see how active this group is."}
	                  </div>
	                </div>
	              </div>
	              {(c.tags || []).length > 0 && (
	                <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 14 }}>
	                  {(c.tags || []).map(tag => (
	                    <span key={tag} style={{ fontSize: 12, padding: "5px 10px", background: C.bg, color: C.inkMid, borderRadius: RADIUS.pill, border: `1px solid ${C.border}`, fontWeight: 700 }}>
	                      {tag}
	                    </span>
	                  ))}
	                </div>
	              )}
	            </div>

	            <button
	              type="button"
	              onClick={() => nextCommunityGame ? openCommunityGame(nextCommunityGame) : isAdmin ? onCreateGame?.(c) : setView("games")}
	              style={{ ...card, padding: 0, boxShadow: "none", overflow: "hidden", textAlign: "left", cursor: "pointer", borderColor: nextCommunityGame ? `${C.accent}55` : C.border }}
	            >
	              <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "14px 16px" }}>
	                <div style={{ width: 38, height: 38, borderRadius: "50%", background: nextCommunityGame ? C.accentBg : C.bg, color: nextCommunityGame ? C.accent : C.inkMid, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
	                  <Calendar size={18} {...ICON} />
	                </div>
	                <div style={{ flex: 1, minWidth: 0 }}>
	                  <div style={{ fontSize: 11, color: C.inkFaint, fontWeight: 900, textTransform: "uppercase", letterSpacing: 0 }}>Next game</div>
	                  {nextCommunityGame ? (
	                    <>
	                      <div style={{ fontSize: 15, fontWeight: 900, color: C.ink, marginTop: 3, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{nextCommunityGame.title}</div>
	                      <div style={{ fontSize: 12, color: C.inkMid, marginTop: 4, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
	                        {formatCommunityGameDate(nextCommunityGame)} · {nextCommunityGame.hour || t("timeTbc")} · {nextCommunityGame.format}
	                      </div>
	                    </>
	                  ) : (
	                    <>
	                      <div style={{ fontSize: 15, fontWeight: 900, color: C.ink, marginTop: 3 }}>No game scheduled</div>
	                      <div style={{ fontSize: 12, color: C.inkMid, marginTop: 4 }}>{isAdmin ? "Create one for the group." : "Check back soon."}</div>
	                    </>
	                  )}
	                </div>
	                <ChevronRight size={18} {...ICON} color={C.inkFaint} />
	              </div>
	              {nextCommunityGame && (
	                <div style={{ height: 5, background: C.bg }}>
	                  <div style={{ width: `${Math.min(100, Math.round((nextCommunityGame.spotsFilled / Math.max(1, nextCommunityGame.spotsTotal)) * 100))}%`, height: "100%", background: C.accent }} />
	                </div>
	              )}
	            </button>

	            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
	              {[
	                { label: t("statMembersLabel"), value: activeMemberCount, icon: Users },
	                { label: "Upcoming", value: upcomingCommunityGames.length, icon: Calendar },
	                { label: "Played", value: pastCommunityGames.length, icon: CheckCircle2 },
	                { label: "Access", value: accessLabel.replace(" group", ""), icon: isOpen ? Check : Lock },
	              ].map(({ label, value, icon }) => {
	                const StatIcon = icon;
	                return (
	                  <div key={label} style={{ ...card, padding: 14, boxShadow: "none" }}>
	                    <div style={{ display: "flex", alignItems: "center", gap: 7, color: C.inkFaint, fontSize: 11, fontWeight: 900, textTransform: "uppercase" }}>
	                      <StatIcon size={13} {...ICON} />
	                      {label}
	                    </div>
	                    <div style={{ fontSize: typeof value === "number" ? 22 : 16, color: C.ink, fontWeight: 950, marginTop: 6, lineHeight: 1.1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{value}</div>
	                  </div>
	                );
	              })}
	            </div>

	            <div style={{ ...card, padding: 16, boxShadow: "none" }}>
	              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, marginBottom: 12 }}>
	                <div>
	                  <div style={{ ...T.label }}>Group leaders</div>
	                  <div style={{ fontSize: 12, color: C.inkMid, marginTop: 3 }}>{accessDetail}</div>
	                </div>
	                {adminRoster.length > 1 && <span style={{ fontSize: 11, fontWeight: 850, color: C.accent, background: C.accentBg, padding: "4px 8px", borderRadius: RADIUS.pill }}>{adminRoster.length} admins</span>}
	              </div>
	              {adminRoster.length > 0 ? (
	                <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
	                  {adminRoster.slice(0, 4).map(m => {
	                    const p = m.profiles_public || {};
	                    const name = p.name || "Player";
	                    const init = name.split(" ").map(w => w[0]).join("").slice(0, 2).toUpperCase();
	                    return (
	                      <div key={m.user_id} style={{ display: "flex", alignItems: "center", gap: 10 }}>
	                        <div style={{ width: 34, height: 34, borderRadius: "50%", background: p.avatar_url ? `url(${p.avatar_url}) center/cover` : C.accentBg, color: C.accent, fontSize: 12, fontWeight: 850, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
	                          {!p.avatar_url && init}
	                        </div>
	                        <div style={{ flex: 1, minWidth: 0 }}>
	                          <div style={{ fontSize: 14, color: C.ink, fontWeight: 850, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
	                            {name}{m.user_id === user?.id && <span style={{ fontSize: 12, color: C.inkMid, fontWeight: 600 }}> · {t("youSuffix")}</span>}
	                          </div>
	                        </div>
	                        <span style={{ fontSize: 10, padding: "3px 7px", background: m.role === "owner" ? C.accent : C.accentBg, color: m.role === "owner" ? C.accentText : C.accent, borderRadius: RADIUS.pill, fontWeight: 900 }}>
	                          {m.role === "owner" ? "Leader" : t("adminRole")}
	                        </span>
	                      </div>
	                    );
	                  })}
	                </div>
	              ) : (
	                <div style={{ fontSize: 13, color: C.inkFaint }}>No admins listed yet.</div>
	              )}
	            </div>

            {/* Member-only secondary actions */}
            {myMembership?.status === "active" && !confirmLeave && (
              <button onClick={() => setConfirmLeave(true)} style={{ ...btnSecondary, width: "100%", padding: "10px 0", fontSize: 13 }}>{t("leaveCommunity")}</button>
            )}
            {myMembership?.status === "active" && confirmLeave && (
              <div style={{ display: "flex", gap: 6 }}>
                <button onClick={() => setConfirmLeave(false)} style={{ ...btnSecondary, flex: 1, padding: "10px 0", fontSize: 13 }}>{t("cancel")}</button>
                <button onClick={handleLeave} disabled={busy} style={{ flex: 1, padding: "10px 0", borderRadius: 8, border: "none", background: C.danger, color: "#fff", fontSize: 13, fontWeight: 600, cursor: busy ? "not-allowed" : "pointer", opacity: busy ? 0.6 : 1 }}>{t("confirmLeaveCommunity")}</button>
              </div>
            )}
            {myMembership?.status === "pending" && (
              <div style={{ padding: "10px 12px", textAlign: "center", fontSize: 13, color: C.inkFaint, background: C.surface, border: `1px dashed ${C.border}`, borderRadius: 10 }}>
                {t("requestPending")}
              </div>
            )}
          </div>
        )}

        {view === "feed" && (
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            {/* Composer — visible only to admins and the group owner */}
            {isAdmin ? (
              <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 12, padding: 12 }}>
                <textarea
                  value={draft}
                  onChange={e => setDraft(e.target.value)}
                  placeholder={t("postPlaceholder")}
                  rows={3}
                  maxLength={2000}
                  style={{ width: "100%", minHeight: 64, resize: "vertical", border: "none", outline: "none", background: "transparent", color: C.ink, fontSize: 14, fontFamily: "inherit", lineHeight: 1.4, padding: 0 }}
                />
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 8 }}>
                  <div style={{ fontSize: 11, color: C.inkFaint }}>{draft.length}/2000</div>
                  <button onClick={handlePost} disabled={posting || !draft.trim()}
                    style={{ ...btnPrimary, padding: "7px 16px", fontSize: 13, opacity: (posting || !draft.trim()) ? 0.5 : 1, cursor: (posting || !draft.trim()) ? "not-allowed" : "pointer" }}>
                    {posting ? t("posting") : t("post")}
                  </button>
                </div>
              </div>
            ) : myMembership?.status === "active" ? (
              <div style={{ padding: "12px 14px", background: C.surface, border: `1px dashed ${C.border}`, borderRadius: 10, fontSize: 13, color: C.inkMid, display: "flex", alignItems: "center", gap: 10 }}>
                <ShieldCheck size={17} {...ICON} style={{ color: C.accent, flexShrink: 0 }} />
                <span>Only group admins can post in the feed.</span>
              </div>
            ) : (
              <div style={{ padding: "10px 12px", background: C.surface, border: `1px dashed ${C.border}`, borderRadius: 10, fontSize: 12, color: C.inkFaint, textAlign: "center" }}>
                {t("joinToPost")}
              </div>
            )}

            {/* Posts */}
            {postsLoading && posts.length === 0 ? (
              <div style={{ textAlign: "center", padding: 24, color: C.inkFaint, fontSize: 12 }}>…</div>
            ) : posts.length === 0 ? (
              <div style={{ textAlign: "center", padding: "48px 20px", color: C.inkFaint }}>
                <div style={{ width: 48, height: 48, borderRadius: "50%", background: C.bg, color: C.inkFaint, margin: "0 auto 12px", display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <MessageCircle size={22} {...ICON} />
                </div>
                <div style={{ fontSize: 15, fontWeight: 600, color: C.ink, marginBottom: 4 }}>{t("noPostsYet")}</div>
                <div style={{ fontSize: 13 }}>{t("beTheFirst")}</div>
              </div>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                {posts.map(p => {
                  const author = p.profiles_public || {};
                  const aName = author.name || "Player";
                  const aInit = aName.split(" ").map(w => w[0]).join("").slice(0, 2).toUpperCase();
                  const isMine = p.author_id === user?.id;
                  const canDelete = isMine || isAdmin;
                  const wasEdited = p.updated_at && p.created_at && (new Date(p.updated_at).getTime() - new Date(p.created_at).getTime() > 1500);
                  return (
                    <div key={p.id} style={{ background: C.surface, border: `1px solid ${p.pinned ? C.accent : C.border}`, borderRadius: 12, padding: 12, position: "relative" }}>
                      {p.pinned && (
                        <div style={{ display: "inline-flex", alignItems: "center", gap: 4, marginBottom: 8, fontSize: 10, fontWeight: 700, color: C.accent, textTransform: "uppercase", letterSpacing: "0.05em" }}>
                          <svg width="11" height="11" viewBox="0 0 24 24" fill="currentColor"><path d="M16 12V4h1V2H7v2h1v8l-2 2v2h5v6h2v-6h5v-2z"/></svg>
                          {t("pinned")}
                        </div>
                      )}
                      <div style={{ display: "flex", gap: 10, marginBottom: 8 }}>
                        <div style={{ width: 36, height: 36, borderRadius: "50%", background: author.avatar_url ? `url(${author.avatar_url}) center/cover` : C.accentBg, color: C.accent, fontSize: 12, fontWeight: 700, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                          {!author.avatar_url && aInit}
                        </div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: 13, fontWeight: 600, color: C.ink }}>{aName}</div>
                          <div style={{ fontSize: 11, color: C.inkFaint }}>
                            {relativeTime(p.created_at)}
                            {wasEdited && <span> · {t("edited")}</span>}
                          </div>
                        </div>
                        {(canDelete || isAdmin) && (
                          <div style={{ display: "flex", gap: 4 }}>
                            {isAdmin && (
                              <button onClick={() => handleTogglePin(p)} aria-label={p.pinned ? t("unpinPost") : t("pinPost")}
                                style={{ width: 28, height: 28, borderRadius: 6, border: `1px solid ${C.border}`, background: "transparent", color: p.pinned ? C.accent : C.inkFaint, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", padding: 0 }}>
                                <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor"><path d="M16 12V4h1V2H7v2h1v8l-2 2v2h5v6h2v-6h5v-2z"/></svg>
                              </button>
                            )}
                            {canDelete && (
                              confirmDeletePost === p.id
                                ? <button onClick={() => handleDeletePost(p.id)}
                                    style={{ height: 28, padding: "0 10px", borderRadius: 6, border: "none", background: C.danger, color: "#fff", fontSize: 11, fontWeight: 600, cursor: "pointer" }}>
                                    {t("confirmDeletePost")}
                                  </button>
                                : <button onClick={() => setConfirmDeletePost(p.id)} aria-label={t("deletePost")}
                                    style={{ width: 28, height: 28, borderRadius: 6, border: `1px solid ${C.border}`, background: "transparent", color: C.inkFaint, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", padding: 0 }}>
                                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/></svg>
                                  </button>
                            )}
                          </div>
                        )}
                      </div>
                      <div style={{ fontSize: 14, color: C.ink, lineHeight: 1.5, whiteSpace: "pre-wrap", wordBreak: "break-word" }}>{p.body}</div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {view === "games" && (
          <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
            {/* Create CTA — active members only */}
            {myMembership?.status === "active" && onCreateGame && (
              <button
                onClick={() => onCreateGame(c)}
                style={{ ...btnPrimary, width: "100%", padding: "12px 0", fontSize: 14, display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
                {t("createGameInCommunity")}
              </button>
            )}

            {/* Group challenges are intentionally hidden until the Phase 2
                negotiation/result-confirmation flow is ready end to end. */}
            {ENABLE_GROUP_CHALLENGES && isAdmin && (
              <button
                onClick={() => setShowChallengeModal(true)}
                style={{ width: "100%", padding: "11px 0", borderRadius: 8, border: `1px solid ${C.accent}`, background: C.accentBg, color: C.accent, fontSize: 13, fontWeight: 600, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/></svg>
                {t("challengeAnotherCommunity")}
              </button>
            )}

            {/* Challenges sections */}
            {ENABLE_GROUP_CHALLENGES && challenges.length > 0 && (() => {
              const incoming = challenges.filter(ch => ch.challenged_id === c.id && ch.status === "pending");
              const outgoing = challenges.filter(ch => ch.challenger_id === c.id && ch.status === "pending");
              const accepted = challenges.filter(ch => ch.status === "accepted");
              const renderRow = (ch, dir) => {
                const opponent = dir === "in" ? ch.challenger : ch.challenged;
                const oName = opponent?.name || "Community";
                const oInit = oName.split(" ").map(w => w[0]).join("").slice(0, 2).toUpperCase();
                return (
                  <div key={ch.id} style={{ padding: 12, background: C.surface, border: `1px solid ${C.border}`, borderRadius: 10, display: "flex", flexDirection: "column", gap: 8 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                      <div style={{ width: 36, height: 36, borderRadius: 10, background: opponent?.avatar_url ? `url(${opponent.avatar_url}) center/cover` : communityGradient(oName), color: "#fff", fontSize: 12, fontWeight: 700, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                        {!opponent?.avatar_url && oInit}
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 13, fontWeight: 600, color: C.ink }}>
                          {dir === "in" ? oName : `${t("vsLabel")} ${oName}`}
                        </div>
                        <div style={{ fontSize: 11, color: C.inkFaint }}>
                          {ch.proposed_date}{ch.proposed_hour ? ` · ${ch.proposed_hour}` : ""}
                          {ch.proposed_location ? ` · ${ch.proposed_location}` : ""}
                        </div>
                      </div>
                    </div>
                    {ch.message && (
                      <div style={{ fontSize: 13, color: C.inkMid, lineHeight: 1.4, paddingLeft: 46 }}>"{ch.message}"</div>
                    )}
                    {isAdmin && ch.status === "pending" && (
                      <div style={{ display: "flex", gap: 6, paddingLeft: 46 }}>
                        {dir === "in" ? (
                          <>
                            <button onClick={() => handleDecideChallenge(ch.id, "accepted")}
                              style={{ ...btnPrimary, flex: 1, padding: "7px 0", fontSize: 12, borderRadius: 8 }}>{t("challengeAccept")}</button>
                            <button onClick={() => handleDecideChallenge(ch.id, "declined")}
                              style={{ ...btnSecondary, flex: 1, padding: "7px 0", fontSize: 12, borderRadius: 8 }}>{t("challengeDecline")}</button>
                          </>
                        ) : (
                          <button onClick={() => handleDecideChallenge(ch.id, "cancelled")}
                            style={{ ...btnSecondary, flex: 1, padding: "7px 0", fontSize: 12, borderRadius: 8 }}>{t("challengeCancel")}</button>
                        )}
                      </div>
                    )}
                    {/* Once accepted, the challenger admin sees a CTA to spin up
                        the actual game — pre-filled with the proposed slot.
                        Skipped if a game has already been linked. */}
                    {isAdmin && ch.status === "accepted" && !ch.game_id && ch.challenger_id === c.id && onCreateGame && (
                      <div style={{ paddingLeft: 46 }}>
                        <button
                          onClick={() => onCreateGame(c, {
                            format: ch.format || undefined,
                            date: ch.proposed_date || undefined,
                            hour: ch.proposed_hour || undefined,
                            location: ch.proposed_location || undefined,
                            title: `${c.name} ${t("vsLabel")} ${oName}`,
                          })}
                          style={{ ...btnPrimary, width: "100%", padding: "8px 0", fontSize: 12, borderRadius: 8 }}
                        >
                          {t("createGameFromChallenge")}
                        </button>
                      </div>
                    )}
                  </div>
                );
              };
              return (
                <>
                  {incoming.length > 0 && (
                    <div>
                      <div style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 11, fontWeight: 700, color: C.inkMid, letterSpacing: "0.06em", textTransform: "uppercase", marginBottom: 6, paddingLeft: 4 }}>
                        <Swords size={12} {...ICON} /> {t("challengePendingIn")}
                      </div>
                      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                        {incoming.map(ch => renderRow(ch, "in"))}
                      </div>
                    </div>
                  )}
                  {outgoing.length > 0 && (
                    <div>
                      <div style={{ fontSize: 11, fontWeight: 700, color: C.inkMid, letterSpacing: "0.06em", textTransform: "uppercase", marginBottom: 6, paddingLeft: 4 }}>
                        {t("challengePendingOut")}
                      </div>
                      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                        {outgoing.map(ch => renderRow(ch, "out"))}
                      </div>
                    </div>
                  )}
                  {accepted.length > 0 && (
                    <div>
                      <div style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 11, fontWeight: 700, color: C.inkMid, letterSpacing: "0.06em", textTransform: "uppercase", marginBottom: 6, paddingLeft: 4 }}>
                        <Handshake size={12} {...ICON} /> {t("challengeAcceptedTitle")}
                      </div>
                      <div style={{ fontSize: 11, color: C.inkFaint, marginBottom: 6, paddingLeft: 4 }}>{t("challengeAcceptedHint")}</div>
                      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                        {accepted.map(ch => renderRow(ch, ch.challenger_id === c.id ? "out" : "in"))}
                      </div>
                    </div>
                  )}
                </>
              );
            })()}

            {/* Existing community games */}
            {communityGameRows.length === 0 ? (
              <div style={{ textAlign: "center", padding: "32px 20px", color: C.inkFaint, fontSize: 13 }}>
                {t("communityGamesEmpty")}
              </div>
            ) : (
              <>
                {upcomingCommunityGames.length > 0 && (
                  <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                    <div style={{ ...T.label, paddingLeft: 4 }}>Upcoming games</div>
                    {upcomingCommunityGames.map(renderCommunityGameCard)}
                  </div>
                )}
                {pastCommunityGames.length > 0 && (
                  <div style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: upcomingCommunityGames.length ? 8 : 0 }}>
                    <div style={{ ...T.label, paddingLeft: 4 }}>Past games</div>
                    {pastCommunityGames.slice(0, 3).map(renderCommunityGameCard)}
                  </div>
                )}
              </>
            )}

            {/* Shareable picker — own hosted, unscoped games */}
            {myMembership?.status === "active" && shareableGames.length > 0 && (
              <div style={{ marginTop: 4 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: C.inkMid, letterSpacing: "0.06em", textTransform: "uppercase", marginBottom: 6, paddingLeft: 4 }}>
                  {t("shareableGamesTitle")}
                </div>
                <div style={{ fontSize: 12, color: C.inkFaint, marginBottom: 8, paddingLeft: 4 }}>
                  {t("shareableGamesHint")}
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                  {shareableGames.map(g => (
                    <div key={g.id} style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 12px", background: C.surface, border: `1px dashed ${C.border}`, borderRadius: 10 }}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 13, fontWeight: 600, color: C.ink, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{g.title}</div>
                        <div style={{ fontSize: 11, color: C.inkFaint, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{g.date} · {g.hour} · {g.location}</div>
                      </div>
                      <button
                        onClick={() => handleShareGame(g.id)}
                        disabled={sharing === g.id}
                        style={{ padding: "6px 12px", borderRadius: 8, border: `1px solid ${C.accent}`, background: C.accentBg, color: C.accent, fontSize: 12, fontWeight: 600, cursor: sharing === g.id ? "not-allowed" : "pointer", opacity: sharing === g.id ? 0.6 : 1, flexShrink: 0 }}
                      >
                        {sharing === g.id ? t("sharingGame") : t("shareToCommunity")}
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {view === "chat" && (
          <>
            {/* Scroll area */}
            <div ref={chatScrollRef} style={{ flex: 1, overflowY: "auto", padding: "8px 16px 12px", display: "flex", flexDirection: "column", gap: 6 }}>
              {messages.length === 0 ? (
                <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", color: C.inkFaint, fontSize: 13 }}>
                  {t("chatEmpty")}
                </div>
              ) : (
                messages.map((m, i) => {
                  const author = m.profiles_public || {};
                  const aName = author.name || "Player";
                  const aInit = aName.split(" ").map(w => w[0]).join("").slice(0, 2).toUpperCase();
                  const isMine = m.author_id === user?.id;
                  const prev = messages[i - 1];
                  const grouped = prev && prev.author_id === m.author_id && (new Date(m.created_at).getTime() - new Date(prev.created_at).getTime() < 5 * 60_000);
                  const canDelete = isMine || isAdmin;
                  return (
                    <div key={m.id} style={{ display: "flex", flexDirection: isMine ? "row-reverse" : "row", gap: 8, alignItems: "flex-end", marginTop: grouped ? 0 : 6 }}>
                      <div style={{ width: 28, height: 28, borderRadius: "50%", background: author.avatar_url ? `url(${author.avatar_url}) center/cover` : C.accentBg, color: C.accent, fontSize: 11, fontWeight: 700, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, visibility: grouped ? "hidden" : "visible" }}>
                        {!author.avatar_url && aInit}
                      </div>
                      <div style={{ maxWidth: "78%", display: "flex", flexDirection: "column", alignItems: isMine ? "flex-end" : "flex-start" }}>
                        {!grouped && !isMine && (
                          <div style={{ fontSize: 11, color: C.inkFaint, marginBottom: 2, paddingLeft: 4 }}>{aName}</div>
                        )}
                        <div
                          onDoubleClick={canDelete ? () => handleDeleteMessage(m.id) : undefined}
                          title={canDelete ? "Double-tap to delete" : undefined}
                          style={{
                            padding: "8px 12px",
                            borderRadius: isMine ? "16px 16px 4px 16px" : "16px 16px 16px 4px",
                            background: isMine ? C.accent : C.surface,
                            color: isMine ? C.accentText : C.ink,
                            fontSize: 14,
                            lineHeight: 1.35,
                            wordBreak: "break-word",
                            whiteSpace: "pre-wrap",
                            border: isMine ? "none" : `1px solid ${C.border}`,
                          }}
	                        >
	                          <MentionText text={m.body} own={isMine} />
	                        </div>
                        <div style={{ fontSize: 10, color: C.inkFaint, marginTop: 2, paddingLeft: 4, paddingRight: 4 }}>{relativeTime(m.created_at)}</div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
            {/* Composer — pinned bottom */}
            {myMembership?.status === "active" ? (
	              <>
	              <MentionSuggestions draft={chatDraft} candidates={communityMentionCandidates} onPick={profile => setChatDraft(prev => insertMentionHandle(prev, profile).slice(0, 1000))} />
	              <div style={{ borderTop: `1px solid ${C.border}`, padding: "10px 16px calc(10px + env(safe-area-inset-bottom, 0px))", display: "flex", gap: 8, alignItems: "flex-end", background: C.bg }}>
		                <textarea
		                  ref={chatInputRef}
	                  value={chatDraft}
                  onChange={e => setChatDraft(e.target.value)}
                  onKeyDown={e => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); handleSendMessage(); } }}
                  placeholder={t("chatPlaceholder")}
                  rows={1}
                  maxLength={1000}
                  style={{ flex: 1, minHeight: 38, maxHeight: 120, resize: "none", padding: "9px 12px", borderRadius: 18, border: `1px solid ${C.border}`, background: C.surface, color: C.ink, fontSize: 14, fontFamily: "inherit", lineHeight: 1.3, outline: "none" }}
                />
                <button onClick={handleSendMessage} disabled={chatSending || !chatDraft.trim()}
                  aria-label="Send"
                  style={{ width: 38, height: 38, borderRadius: "50%", border: "none", background: C.accent, color: C.accentText, cursor: (chatSending || !chatDraft.trim()) ? "not-allowed" : "pointer", opacity: (chatSending || !chatDraft.trim()) ? 0.5 : 1, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
	                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/></svg>
	                </button>
	              </div>
	              </>
            ) : (
              <div style={{ borderTop: `1px solid ${C.border}`, padding: "12px 16px calc(12px + env(safe-area-inset-bottom, 0px))", textAlign: "center", fontSize: 12, color: C.inkFaint }}>
                {t("chatJoinToWrite")}
              </div>
            )}
          </>
        )}

        {view === "members" && (
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {isAdmin && pendingMembers.length > 0 && (
              <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 12, overflow: "hidden", marginBottom: 4 }}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 14px", borderBottom: `1px solid ${C.border}` }}>
                  <div style={{ fontSize: 13, fontWeight: 800, color: C.ink }}>Join requests</div>
                  <div style={{ fontSize: 11, fontWeight: 800, color: C.warn }}>{pendingMembers.length} pending</div>
                </div>
	                {pendingMembers.map(m => {
	                  const p = getMemberProfile(m);
	                  const name = p.name || "Player";
	                  const init = name.split(" ").map(w => w[0]).join("").slice(0, 2).toUpperCase();
	                  return (
                    <div key={`pending-${m.user_id}`} style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 12px", borderBottom: `1px solid ${C.border}` }}>
                      <div style={{ width: 34, height: 34, borderRadius: "50%", background: p.avatar_url ? `url(${p.avatar_url}) center/cover` : C.accentBg, color: C.accent, fontSize: 12, fontWeight: 800, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                        {!p.avatar_url && init}
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 13, fontWeight: 700, color: C.ink, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{name}</div>
                        <div style={{ fontSize: 11, color: C.inkFaint }}>{p.level || "Requested to join"}</div>
                      </div>
                      <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
                        <button onClick={() => handleReviewMember(m.user_id, "approved")} disabled={busy} style={{ border: "none", background: C.accentBg, color: C.accent, borderRadius: 999, padding: "7px 9px", fontSize: 11, fontWeight: 900, cursor: busy ? "not-allowed" : "pointer", opacity: busy ? 0.6 : 1 }}>
                          Approve
                        </button>
                        <button onClick={() => handleReviewMember(m.user_id, "rejected")} disabled={busy} style={{ border: `1px solid ${C.border}`, background: C.surface, color: C.inkMid, borderRadius: 999, padding: "7px 9px", fontSize: 11, fontWeight: 900, cursor: busy ? "not-allowed" : "pointer", opacity: busy ? 0.6 : 1 }}>
                          Reject
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
	            )}
	            {members.map(m => {
	              const p = getMemberProfile(m);
	              const name = p.name || "Player";
	              const init = name.split(" ").map(w => w[0]).join("").slice(0,2).toUpperCase();
	              const roleLabel = m.role === "owner" ? "Leader" : m.role === "admin" ? t("adminRole") : m.role === "moderator" ? t("modRole") : null;
	              const canManageRole = isOwner && m.role !== "owner" && m.user_id !== user?.id;
	              return (
	                <div key={m.user_id} style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 12px", background: C.surface, border: `1px solid ${C.border}`, borderRadius: 10 }}>
	                  <div style={{ width: 32, height: 32, borderRadius: "50%", background: p.avatar_url ? `url(${p.avatar_url}) center/cover` : C.accentBg, color: C.accent, fontSize: 12, fontWeight: 700, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                    {!p.avatar_url && init}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: C.ink, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {name}
                      {m.user_id === user?.id && <span style={{ fontSize: 11, color: C.inkFaint, fontWeight: 400 }}> · you</span>}
	                    </div>
	                    {p.level && <div style={{ fontSize: 11, color: C.inkFaint }}>{p.level}</div>}
	                  </div>
	                  <div style={{ display: "flex", alignItems: "center", gap: 6, flexShrink: 0 }}>
	                    {roleLabel && <span style={{ fontSize: 10, padding: "2px 6px", background: m.role === "owner" ? C.accent : C.accentBg, color: m.role === "owner" ? C.accentText : C.accent, borderRadius: 6, fontWeight: 700 }}>{roleLabel}</span>}
	                    {canManageRole && (
	                      <button
	                        onClick={() => handleSetMemberRole(m, m.role === "admin" ? "member" : "admin")}
	                        disabled={busy}
		                        style={{ border: `1px solid ${m.role === "admin" ? dangerBorder : C.accent}`, background: m.role === "admin" ? C.surface : C.accentBg, color: m.role === "admin" ? C.danger : C.accent, borderRadius: 999, padding: "6px 9px", fontSize: 10, fontWeight: 900, cursor: busy ? "not-allowed" : "pointer", opacity: busy ? 0.6 : 1, whiteSpace: "nowrap" }}
	                      >
	                        {m.role === "admin" ? "Remove admin" : "Make admin"}
	                      </button>
	                    )}
	                  </div>
	                </div>
	              );
	            })}
          </div>
        )}
      </div>

      {/* Sticky Join CTA — only when the user isn't a member yet. Always
          visible on top of any tab content so the primary action stays one
          tap away. */}
      {user && !myMembership && (
        <div style={{
          position: "absolute", left: 0, right: 0, bottom: 0,
          padding: "12px 16px calc(12px + env(safe-area-inset-bottom, 0px))",
          background: `linear-gradient(180deg, transparent, ${C.bg} 35%)`,
          pointerEvents: "none",
        }}>
          <button
            onClick={handleJoin}
            disabled={busy || !!myPendingMembership}
            style={{
              ...btnPrimary, width: "100%", padding: "14px 0", fontSize: 14,
              borderRadius: 999, fontWeight: 700, opacity: busy || myPendingMembership ? 0.6 : 1,
              boxShadow: myPendingMembership ? "none" : "0 6px 18px rgba(0,0,0,0.18)", pointerEvents: "auto",
              display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
            }}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
            {myPendingMembership ? "Request pending" : c.join_policy === "request" ? t("requestToJoin") : t("joinCommunity")}
          </button>
        </div>
      )}

      {/* Admin-only challenge modal */}
      {ENABLE_GROUP_CHALLENGES && showChallengeModal && isAdmin && (
        <ChallengeCommunityModal
          currentCommunity={c}
          onClose={() => setShowChallengeModal(false)}
          onSubmit={handleSendChallenge}
          busy={challengeBusy}
        />
      )}

      {/* Report Community modal */}
      {showReport && (
        <div style={{ position: "fixed", inset: 0, zIndex: 320, background: "rgba(0,0,0,0.55)", display: "flex", alignItems: "flex-end", justifyContent: "center" }} onClick={() => !reporting && setShowReport(false)}>
          <div style={{ width: "100%", maxWidth: 520, background: C.bg, borderRadius: "16px 16px 0 0", padding: "16px 16px calc(16px + env(safe-area-inset-bottom, 0px))", display: "flex", flexDirection: "column", gap: 12 }} onClick={e => e.stopPropagation()}>
            <div style={{ display: "flex", justifyContent: "center", paddingBottom: 4 }}>
              <div style={{ width: 36, height: 4, borderRadius: 2, background: C.border }} />
            </div>
            <div style={{ ...T.h3, color: C.ink }}>{t("reportCommunityTitle")}</div>
            <div style={{ fontSize: 13, color: C.inkMid, lineHeight: 1.5 }}>{t("reportCommunityHint")}</div>
            <textarea
              value={reportReason}
              onChange={e => setReportReason(e.target.value)}
              placeholder={t("reportReasonPlaceholder")}
              rows={4}
              maxLength={500}
              style={{ width: "100%", minHeight: 100, padding: 12, borderRadius: 10, border: `1px solid ${C.border}`, background: C.surface, color: C.ink, fontSize: 14, fontFamily: "inherit", lineHeight: 1.4, resize: "vertical", outline: "none" }}
              autoFocus
            />
            <div style={{ display: "flex", gap: 8, marginTop: 4 }}>
              <button onClick={() => { setShowReport(false); setReportReason(""); }} disabled={reporting} style={{ ...btnSecondary, flex: 1, padding: "12px 0", fontSize: 14 }}>{t("cancel")}</button>
              <button onClick={handleSubmitReport} disabled={!reportReason.trim() || reporting} style={{ flex: 2, padding: "12px 0", borderRadius: 8, border: "none", background: C.danger, color: "#fff", fontSize: 14, fontWeight: 700, cursor: (!reportReason.trim() || reporting) ? "not-allowed" : "pointer", opacity: (!reportReason.trim() || reporting) ? 0.5 : 1 }}>
                {reporting ? t("reporting") : t("submitReport")}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Quick relative-time formatter for community feed timestamps.
// Returns "just now", "{n}m ago", "{n}h ago", "{n}d ago", or a date.
function relativeTime(iso) {
  if (!iso) return "";
  const diff = Date.now() - new Date(iso).getTime();
  if (diff < 60_000) return t("justNow");
  const m = Math.floor(diff / 60_000);
  if (m < 60) return t("minutesAgo", { n: m });
  const h = Math.floor(m / 60);
  if (h < 24) return t("hoursAgo", { n: h });
  const d = Math.floor(h / 24);
  if (d < 7) return t("daysAgo", { n: d });
  return new Date(iso).toLocaleDateString();
}

// Removed duplicate haversine — use getDistance() instead

// ─── Availability Sheet ───────────────────────────────────────────────────────
const AVAILABILITY_FORMAT_OPTIONS = STANDARD_FORMATS;
const AVAILABILITY_LEVEL_OPTIONS = ["Beginner", "Intermediate", "Advanced"];
const AVAILABILITY_TIME_STEP_MINUTES = 30;
const AVAILABILITY_TIME_MINUTES_MIN = 0;
const AVAILABILITY_TIME_MINUTES_MAX = 23 * 60 + 30;
const AVAILABILITY_DEFAULT_FROM = "17:00";
const AVAILABILITY_DEFAULT_TO = "20:00";

const normalizeAvailabilityLevel = (level) => (!level || level === "Any" ? "All levels" : level);
const readAvailabilityLevelPreference = (level) => {
  const normalized = normalizeAvailabilityLevel(level);
  return normalized === "All levels" ? null : normalized;
};
const normalizeAvailabilityFormats = (formats) => (Array.isArray(formats) ? formats.filter(Boolean) : []);
const clampAvailabilityMinutes = (minutes) => Math.max(
  AVAILABILITY_TIME_MINUTES_MIN,
  Math.min(AVAILABILITY_TIME_MINUTES_MAX, Number.isFinite(minutes) ? minutes : AVAILABILITY_TIME_MINUTES_MIN),
);
const availabilityTimeToMinutes = (time, fallback = AVAILABILITY_TIME_MINUTES_MIN) => {
  const [hour, minute] = String(time || "").slice(0, 5).split(":").map(Number);
  if (!Number.isFinite(hour) || !Number.isFinite(minute)) return fallback;
  return clampAvailabilityMinutes((hour * 60) + minute);
};
const availabilityMinutesToTime = (minutes) => {
  const next = clampAvailabilityMinutes(Math.round(minutes / AVAILABILITY_TIME_STEP_MINUTES) * AVAILABILITY_TIME_STEP_MINUTES);
  const hour = Math.floor(next / 60);
  const minute = next % 60;
  return `${String(hour).padStart(2, "0")}:${String(minute).padStart(2, "0")}`;
};
const isAvailabilityAllDayTime = (from, to) => (
  availabilityTimeToMinutes(from, 0) <= 0
  && availabilityTimeToMinutes(to, AVAILABILITY_TIME_MINUTES_MAX) >= AVAILABILITY_TIME_MINUTES_MAX
);
const isAvailabilityAllDay = (row) => isAvailabilityAllDayTime(row?.time_from, row?.time_to);
const formatAvailabilityTimeRange = (row) => {
  if (!row) return "";
  if (isAvailabilityAllDay(row)) return t("allDay");
  return `${row.time_from?.slice(0, 5) || ""}-${row.time_to?.slice(0, 5) || ""}`;
};
const getAvailabilityGameStartTime = (row) => isAvailabilityAllDay(row)
  ? AVAILABILITY_DEFAULT_FROM
  : row?.time_from?.slice(0, 5);
const availabilityRowsCompatible = (mine, other) => {
  const myFormats = normalizeAvailabilityFormats(mine?.formats);
  const otherFormats = normalizeAvailabilityFormats(other?.formats);
  const formatMatch = myFormats.length === 0 || otherFormats.length === 0 || myFormats.some(f => otherFormats.includes(f));
  const myLevel = normalizeAvailabilityLevel(mine?.level);
  const otherLevel = normalizeAvailabilityLevel(other?.level);
  const levelMatch = myLevel === "All levels" || otherLevel === "All levels" || myLevel === otherLevel;
  return formatMatch && levelMatch;
};
const hydrateAvailabilityProfiles = async (rows = []) => {
  const ids = [...new Set(rows.map(r => r.user_id).filter(Boolean))];
  if (!ids.length) return rows;
  const { data, error } = await supabase
    .from("profiles_public")
    .select("user_id, name, level, avatar_url")
    .in("user_id", ids);
  if (error) {
    console.warn("[availability.profiles] failed", error);
    return rows;
  }
  const byId = new Map((data || []).map(p => [p.user_id, p]));
  return rows.map(r => ({ ...r, profiles_public: byId.get(r.user_id) || null }));
};

function AvailabilitySheet({ currentUser, userLocation, onClose, onSave, existingAvailabilities }) {
  const { C, T, btnPrimary, btnSecondary, SHADOW, RADIUS, SPACE } = useTheme();
  const first = existingAvailabilities?.[0];
  const last = existingAvailabilities?.length > 1 ? existingAvailabilities[existingAvailabilities.length - 1] : null;
  const [range, setRange] = useState(() => {
    if (first) return { from: new Date(first.date + "T00:00:00"), to: last ? new Date(last.date + "T00:00:00") : new Date(first.date + "T00:00:00") };
    const today = new Date(); today.setHours(0,0,0,0);
    return { from: today, to: today };
  });
  const [timeFrom, setTimeFrom] = useState(first?.time_from?.slice(0,5) || AVAILABILITY_DEFAULT_FROM);
  const [timeTo, setTimeTo] = useState(first?.time_to?.slice(0,5) || AVAILABILITY_DEFAULT_TO);
  const [allDay, setAllDay] = useState(() => first ? isAvailabilityAllDay(first) : false);
  const [formats, setFormats] = useState(normalizeAvailabilityFormats(first?.formats));
  const [level, setLevel] = useState(() => readAvailabilityLevelPreference(first?.level));
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  const toggleFormat = f => {
    setError("");
    setFormats(prev => prev.includes(f) ? prev.filter(x => x !== f) : [...prev, f]);
  };
  const toggleLevel = l => {
    setError("");
    setLevel(prev => prev === l ? null : l);
  };
  const fromMinutes = availabilityTimeToMinutes(timeFrom, availabilityTimeToMinutes(AVAILABILITY_DEFAULT_FROM));
  const toMinutes = availabilityTimeToMinutes(timeTo, availabilityTimeToMinutes(AVAILABILITY_DEFAULT_TO));
  const timeRangeValid = allDay || (!!timeFrom && !!timeTo && fromMinutes < toMinutes);
  const timeSummary = allDay ? t("allDay") : `${timeFrom}-${timeTo}`;
  const setAllDayValue = (next) => {
    setError("");
    setAllDay(next);
    if (next) {
      setTimeFrom("00:00");
      setTimeTo("23:59");
    } else if (isAvailabilityAllDayTime(timeFrom, timeTo)) {
      setTimeFrom(AVAILABILITY_DEFAULT_FROM);
      setTimeTo(AVAILABILITY_DEFAULT_TO);
    }
  };
  const setTimeRangeEdge = (edge, rawValue) => {
    setError("");
    setAllDay(false);
    if (edge === "from") {
      const next = Math.min(Number(rawValue), toMinutes - AVAILABILITY_TIME_STEP_MINUTES);
      setTimeFrom(availabilityMinutesToTime(next));
    } else {
      const next = Math.max(Number(rawValue), fromMinutes + AVAILABILITY_TIME_STEP_MINUTES);
      setTimeTo(availabilityMinutesToTime(next));
    }
  };
  const rangeSpan = AVAILABILITY_TIME_MINUTES_MAX - AVAILABILITY_TIME_MINUTES_MIN;
  const fromPct = ((fromMinutes - AVAILABILITY_TIME_MINUTES_MIN) / rangeSpan) * 100;
  const toPct = ((toMinutes - AVAILABILITY_TIME_MINUTES_MIN) / rangeSpan) * 100;
  const timeTrack = `linear-gradient(to right, ${C.border} 0%, ${C.border} ${fromPct}%, ${C.accent} ${fromPct}%, ${C.accent} ${toPct}%, ${C.border} ${toPct}%, ${C.border} 100%)`;
  const TimeRangeSlider = () => (
    <div>
      <style>{`
        .kickr-dual-range {
          position: absolute;
          inset: 0;
          width: 100%;
          margin: 0;
          opacity: 0;
          pointer-events: none;
        }
        .kickr-dual-range::-webkit-slider-thumb {
          pointer-events: auto;
          width: 28px;
          height: 28px;
          cursor: grab;
        }
        .kickr-dual-range::-moz-range-thumb {
          pointer-events: auto;
          width: 28px;
          height: 28px;
          cursor: grab;
        }
      `}</style>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: SPACE[3], marginBottom: SPACE[2] }}>
        <div>
          <div style={{ fontSize: 11, fontWeight: 900, color: C.inkFaint, textTransform: "uppercase" }}>{t("from")}</div>
          <div style={{ fontSize: 14, fontWeight: 900, color: C.ink }}>{availabilityMinutesToTime(fromMinutes)}</div>
        </div>
        <div style={{ textAlign: "right" }}>
          <div style={{ fontSize: 11, fontWeight: 900, color: C.inkFaint, textTransform: "uppercase" }}>{t("to")}</div>
          <div style={{ fontSize: 14, fontWeight: 900, color: C.ink }}>{availabilityMinutesToTime(toMinutes)}</div>
        </div>
      </div>
      <div style={{ position: "relative", height: 34, display: "flex", alignItems: "center" }}>
        <div style={{ position: "absolute", left: 0, right: 0, height: 8, borderRadius: RADIUS.pill, background: timeTrack, boxShadow: `inset 0 0 0 1px ${C.border}` }} />
        <div style={{ position: "absolute", left: `calc(${fromPct}% - 13px)`, width: 26, height: 26, borderRadius: "50%", background: C.accent, border: `3px solid ${C.surface}`, boxShadow: SHADOW.sm, pointerEvents: "none" }} />
        <div style={{ position: "absolute", left: `calc(${toPct}% - 13px)`, width: 26, height: 26, borderRadius: "50%", background: C.accent, border: `3px solid ${C.surface}`, boxShadow: SHADOW.sm, pointerEvents: "none" }} />
        <input
          className="kickr-dual-range"
          type="range"
          min={AVAILABILITY_TIME_MINUTES_MIN}
          max={Math.max(AVAILABILITY_TIME_MINUTES_MIN, toMinutes - AVAILABILITY_TIME_STEP_MINUTES)}
          step={AVAILABILITY_TIME_STEP_MINUTES}
          value={fromMinutes}
          onChange={e => setTimeRangeEdge("from", e.target.value)}
          aria-label={t("from")}
        />
        <input
          className="kickr-dual-range"
          type="range"
          min={Math.min(AVAILABILITY_TIME_MINUTES_MAX, fromMinutes + AVAILABILITY_TIME_STEP_MINUTES)}
          max={AVAILABILITY_TIME_MINUTES_MAX}
          step={AVAILABILITY_TIME_STEP_MINUTES}
          value={toMinutes}
          onChange={e => setTimeRangeEdge("to", e.target.value)}
          aria-label={t("to")}
        />
      </div>
    </div>
  );

  const dayCount = range?.from && range?.to
    ? eachDayOfInterval({ start: range.from, end: range.to }).length
    : range?.from ? 1 : 0;

  const handleSave = async () => {
    if (!currentUser || !range?.from) return;
    if (!timeRangeValid) {
      setError("Choose an end time after the start time.");
      return;
    }
    setSaving(true);
    setError("");
    try {
      const today = format(new Date(), "yyyy-MM-dd");
      const { error: deleteError } = await supabase.from("availability").delete().eq("user_id", currentUser.id).gte("date", today);
      if (deleteError) throw deleteError;
      const days = range.to
        ? eachDayOfInterval({ start: range.from, end: range.to })
        : [range.from];
      const savedTimeFrom = allDay ? "00:00" : timeFrom;
      const savedTimeTo = allDay ? "23:59" : timeTo;
      const rows = days.map(d => ({
        user_id: currentUser.id,
        date: format(d, "yyyy-MM-dd"),
        time_from: savedTimeFrom,
        time_to: savedTimeTo,
        lat: userLocation?.lat ?? null,
        lng: userLocation?.lng ?? null,
        formats,
        level: level || "All levels",
      }));
      const { error: insertError } = await supabase.from("availability").insert(rows);
      if (insertError) throw insertError;
      onSave(false);
    } catch (e) {
      console.warn("[availability.save] failed", e);
      setError(t("availabilitySaveError"));
    } finally {
      setSaving(false);
    }
  };

  const handleClear = async () => {
    if (!currentUser) return;
    setSaving(true);
    setError("");
    try {
      const today = format(new Date(), "yyyy-MM-dd");
      const { error: deleteError } = await supabase.from("availability").delete().eq("user_id", currentUser.id).gte("date", today);
      if (deleteError) throw deleteError;
      onSave(true);
    } catch (e) {
      console.warn("[availability.clear] failed", e);
      setError("Could not clear availability. Try again.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 300, display: "flex", flexDirection: "column", justifyContent: "flex-end" }}>
      <div onClick={onClose} style={{ position: "absolute", inset: 0, background: "rgba(0,0,0,0.5)" }} />
      <div style={{ position: "relative", background: C.bg, borderRadius: "20px 20px 0 0", maxHeight: "90vh", overflowY: "auto", paddingBottom: "calc(24px + env(safe-area-inset-bottom,0px))" }}>
        <div style={{ display: "flex", justifyContent: "center", padding: "12px 0 0" }}>
          <div style={{ width: 36, height: 4, borderRadius: 2, background: C.border }} />
        </div>
        <div style={{ padding: "16px 20px 0" }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
            <div style={{ ...T.h3 }}>{t("imAvailable")}</div>
            <button aria-label={t("close")} onClick={onClose} style={{ background: "none", border: "none", cursor: "pointer", color: C.inkFaint, padding: 4 }}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
            </button>
          </div>
          <div style={{ background: C.accentBg, border: `1px solid ${C.accent}33`, borderRadius: 10, padding: "12px 14px", marginBottom: 20, display: "flex", gap: 10 }}>
            <Zap size={16} {...ICON} color={C.accent} style={{ flexShrink: 0 }} />
            <div style={{ fontSize: 12, color: C.inkMid, lineHeight: 1.6 }}>
              {t("availabilityDesc", { n: 5 })}
            </div>
          </div>
          <div style={{ marginBottom: 16 }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
              <div style={{ ...T.label }}>Select days</div>
              {dayCount > 0 && <span style={{ fontSize: 12, color: C.accent, fontWeight: 600 }}>{dayCount} day{dayCount > 1 ? "s" : ""}</span>}
            </div>
            <RangeCalendarPicker range={range} onRangeChange={setRange} />
          </div>
          <div style={{ marginBottom: 16 }}>
            <div style={{ ...T.label, marginBottom: 8 }}>{t("availabilityTime")}</div>
            <div style={{ background: C.surface, borderRadius: RADIUS.md, boxShadow: SHADOW.sm, padding: SPACE[3] }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: SPACE[3], marginBottom: SPACE[3] }}>
                <div>
                  <div style={{ ...T.h3, color: C.ink }}>{timeSummary}</div>
                  <div style={{ ...T.small, color: C.inkFaint, marginTop: 2 }}>{t("availabilityTimeHint")}</div>
                </div>
                <button onClick={() => setAllDayValue(!allDay)} aria-pressed={allDay} style={{ border: "none", background: "transparent", padding: 0, cursor: "pointer", display: "flex", alignItems: "center", gap: 8, flexShrink: 0 }}>
                  <span style={{ fontSize: 12, fontWeight: 800, color: allDay ? C.accent : C.inkMid, whiteSpace: "nowrap" }}>{t("allDay")}</span>
                  <span style={{ width: 46, height: 26, borderRadius: RADIUS.pill, background: allDay ? C.accent : C.border, padding: 3, boxSizing: "border-box", display: "flex", alignItems: "center", justifyContent: allDay ? "flex-end" : "flex-start", transition: "background 0.15s" }}>
                    <span style={{ width: 20, height: 20, borderRadius: "50%", background: C.surface, boxShadow: SHADOW.sm }} />
                  </span>
                </button>
              </div>
              {!allDay && (
                <TimeRangeSlider />
              )}
              {allDay && <div style={{ marginTop: SPACE[2], fontSize: 11, fontWeight: 650, color: C.inkFaint }}>{t("allDayHint")}</div>}
            </div>
          </div>
          <div style={{ marginBottom: 16 }}>
            <div style={{ ...T.label, marginBottom: 8 }}>{t("formatOptional")}</div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
              {AVAILABILITY_FORMAT_OPTIONS.map(f => (
                <button key={f} onClick={() => toggleFormat(f)} style={{ padding: "6px 14px", borderRadius: 20, fontSize: 13, fontWeight: 500, border: `1.5px solid ${formats.includes(f) ? C.accent : C.border}`, background: formats.includes(f) ? C.accentBg : "transparent", color: formats.includes(f) ? C.accent : C.inkMid, cursor: "pointer" }}>{f}</button>
              ))}
            </div>
            <div style={{ marginTop: 6, fontSize: 11, color: C.inkFaint }}>{t("leaveBlankAnyFormat")}</div>
          </div>
          <div style={{ marginBottom: 24 }}>
            <div style={{ ...T.label, marginBottom: 8 }}>{t("levelOptional")}</div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
              {AVAILABILITY_LEVEL_OPTIONS.map(l => (
                <button key={l} onClick={() => toggleLevel(l)} style={{ padding: "6px 14px", borderRadius: 20, fontSize: 13, fontWeight: 500, border: `1.5px solid ${level === l ? C.accent : C.border}`, background: level === l ? C.accentBg : "transparent", color: level === l ? C.accent : C.inkMid, cursor: "pointer" }}>{tLevel(l)}</button>
              ))}
            </div>
            <div style={{ marginTop: 6, fontSize: 11, color: C.inkFaint }}>{t("leaveBlankAnyLevel")}</div>
          </div>
          {error && (
            <div style={{ marginBottom: 12, padding: "10px 12px", borderRadius: 10, border: `1px solid ${C.danger}44`, background: C.dangerBg || "#fef2f2", color: C.danger, fontSize: 12, fontWeight: 600, lineHeight: 1.35 }}>
              {error}
            </div>
          )}
          <div style={{ display: "flex", gap: 10 }}>
            {(existingAvailabilities?.length > 0) && (
              <button onClick={handleClear} disabled={saving} style={{ flex: 1, ...btnSecondary, padding: "13px 0", borderRadius: 12, border: `1px solid ${C.border}`, opacity: saving ? 0.55 : 1 }}>{t("clear")}</button>
            )}
            <button onClick={handleSave} disabled={saving || !range?.from || !timeRangeValid} style={{ flex: 2, ...btnPrimary, padding: "13px 0", borderRadius: 12, opacity: (saving || !range?.from || !timeRangeValid) ? 0.5 : 1 }}>
              {saving ? t("saving") : !range?.from ? "Select dates first" : !timeRangeValid ? "Check time" : t("setAvailability")}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Error Boundary ───────────────────────────────────────────────────────────
class ErrorBoundary extends React.Component {
  constructor(props) { super(props); this.state = { error: null }; }
  static getDerivedStateFromError(e) { return { error: e }; }
  render() {
    if (this.state.error) return (
      <div style={{ height: "100vh", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: 32, fontFamily: "system-ui", textAlign: "center", background: "#ffffff" }}>
        <div style={{ fontSize: 16, fontWeight: 700, color: "#000000", marginBottom: 12 }}>{t("somethingWrongErrorBoundary")}</div>
        <div style={{ fontSize: 13, color: "#333333", marginBottom: 8, maxWidth: 300, wordBreak: "break-word" }}>{this.state.error.message}</div>
        <div style={{ fontSize: 11, color: "#666666", marginBottom: 24, maxWidth: 300, wordBreak: "break-word" }}>{this.state.error.stack?.slice(0, 200)}</div>
        <button onClick={() => window.location.reload()} style={{ padding: "10px 20px", borderRadius: 8, background: "#22c55e", color: "#ffffff", border: "none", cursor: "pointer", fontSize: 14, fontWeight: 600 }}>{t("reloadApp")}</button>
      </div>
    );
    return this.props.children;
  }
}

// ─── App Root ─────────────────────────────────────────────────────────────────
export default function KickrApp() {
  return (
    <ErrorBoundary>
      <KickrAppInner />
    </ErrorBoundary>
  );
}

function KickrAppInner() {
  const theme = makeTheme(LIGHT_C);
  const { C, T, btnPrimary, btnSecondary, card, inputBase, dangerBg, levelColor, SPACE, RADIUS, SHADOW, TYPE } = theme;
  const ui = { C, SPACE, RADIUS, SHADOW };

  const [user, setUser] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [profileLoaded, setProfileLoaded] = useState(false);
  const [usernameSchemaReady, setUsernameSchemaReady] = useState(false);
  const [legalSchemaReady, setLegalSchemaReady] = useState(false);
  const [games, setGames] = useState([]);
  const [venues, setVenues] = useState(() => normaliseVenueList(VENUES_FALLBACK));
  const [selectedVenue, setSelectedVenue] = useState(null);
  const [pastGames, setPastGames] = useState([]);
  const [userProfile, setUserProfile] = useState(null);
  const [gamesLoading, setGamesLoading] = useState(true);
  const [tab, setTab] = useState("discover");
  // Sub-tab for the MyGames screen. Lifted up so ProfileScreen's
	  // "See all past games" can deep-link into the Past sub-tab.
	  const [myGamesView, setMyGamesView] = useState("joined");
	  const [reviewGameIdToOpen, setReviewGameIdToOpen] = useState(null);
  const [viewMode, setViewMode] = useState("map");
  const [selectedGame, setSelectedGame] = useState(null);
  const [clusterGames, setClusterGames] = useState(null); // games at same location
  const [joined, setJoined] = useState([]);
  const [waitlisted, setWaitlisted] = useState([]); // game IDs user is on waitlist for
  const [favs, setFavs] = useState(() => { try { return JSON.parse(localStorage.getItem("kickr_favs") || "[]"); } catch { return []; } });
  const [filter, setFilter] = useState("All");
  const [formatFilter, setFormatFilter] = useState("All");
  const [dateRange, setDateRange] = useState(() => [...FILTER_DATE_DEFAULT_RANGE]);
  const [sortBy, setSortBy] = useState("smart");
  const [search, setSearch] = useState("");
  const [searchPlayerResults, setSearchPlayerResults] = useState([]); // debounced profile search
  const searchDebounceRef = useRef(null);
  const [userLocation, setUserLocation] = useState(null);
  const [notifs, setNotifs] = useState([]);
  const [showNotifs, setShowNotifs] = useState(false);
  const prevGamesRef = useRef([]);
	  const seenSoonNotifsRef = useRef(new Set());
	  const autoCancelRunningRef = useRef(false);
	  const autoCancelLastRunRef = useRef(0);
	  const reviewNotifyRunningRef = useRef(false);
	  const reviewNotifyLastRunRef = useRef(0);
	  const mapRef = useRef(null); // lifted map ref for flyTo from search
  const [creating, setCreating] = useState(false);
  const [createPrefill, setCreatePrefill] = useState(null);
  const [radiusKm, setRadiusKm] = useState(null);
  const [showFullGames, setShowFullGames] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [toastQueue, setToastQueue] = useState([]); // queue of { id, msg } objects
  const [showAuthModal, setShowAuthModal] = useState(false);
  const toastTimerRef = useRef(null);
  const [joiningGameId, setJoiningGameId] = useState(null); // tracks in-flight join/leave for UI feedback
  const [cloningGameId, setCloningGameId] = useState(null);
  const joiningRef = useRef(false); // ref-level guard against stale-closure race conditions
  const [showFriendsScreen, setShowFriendsScreen] = useState(false);
  const [friendsVersion, setFriendsVersion] = useState(0);
  const [friendsInviteGameId, setFriendsInviteGameId] = useState(null);
  const [showSettings, setShowSettings] = useState(false);
  const [showAdminDashboard, setShowAdminDashboard] = useState(false);
  const [showAvailabilitySheet, setShowAvailabilitySheet] = useState(false);
  const [myAvailability, setMyAvailability] = useState(null);
  const [myAvailabilities, setMyAvailabilities] = useState([]); // all upcoming availability rows
  const [nearbyAvailable, setNearbyAvailable] = useState([]);
  const [nearbyAvailabilityPreview, setNearbyAvailabilityPreview] = useState([]);
  const availabilityLabel = useCallback((row) => {
    if (!row?.date) return "";
    const today = format(new Date(), "yyyy-MM-dd");
    const tomorrowDate = new Date(); tomorrowDate.setDate(tomorrowDate.getDate() + 1);
    const tomorrow = format(tomorrowDate, "yyyy-MM-dd");
    if (row.date === today) return t("freeToday");
    if (row.date === tomorrow) return "Tomorrow";
    return format(new Date(`${row.date}T00:00:00`), "EEE d MMM");
  }, []);
  const [addFriendPrompt, setAddFriendPrompt] = useState(null); // { userId, name, avatarUrl } | null
  const [searchPlayerCard, setSearchPlayerCard] = useState(null); // { id, name, level, position, avatar_url } — standalone player card from search
  const [searchPlayerCardFriendStatus, setSearchPlayerCardFriendStatus] = useState(null); // null | "sent" | "accepted"
  const [searchPlayerCardLoading, setSearchPlayerCardLoading] = useState(false);
  const [searchPlayerCardShowSharedGames, setSearchPlayerCardShowSharedGames] = useState(false);

  // Groups and friends tab state.
  const [myCommunities, setMyCommunities] = useState([]);
  const [discoverCommunities, setDiscoverCommunities] = useState([]);
  const [communitiesLoading, setCommunitiesLoading] = useState(false);
  const [openedCommunity, setOpenedCommunity] = useState(null);
  const [openedCommunityInitialView, setOpenedCommunityInitialView] = useState("about");
  const [showCreateCommunity, setShowCreateCommunity] = useState(false);
  const [friendsInitialChatUserId, setFriendsInitialChatUserId] = useState(null);

  const switchTab = (newTab) => {
    if (newTab === "my" && myGamesView === "saved" && savedGames.length === 0) {
      setMyGamesView(myUpcomingGames.length > 0 ? "joined" : myPastGames.length > 0 ? "past" : "saved");
    }
    setTab(newTab);
  };

  // Escape key to close overlays
  useEffect(() => {
    const handle = e => {
      if (e.key !== "Escape") return;
	    if (showAdminDashboard) { setShowAdminDashboard(false); return; }
	    if (showSettings) { setShowSettings(false); return; }
      if (showCreateCommunity) { setShowCreateCommunity(false); return; }
      if (openedCommunity) { setOpenedCommunity(null); setOpenedCommunityInitialView("about"); return; }
      if (showFriendsScreen) { setShowFriendsScreen(false); setFriendsInitialChatUserId(null); setFriendsInviteGameId(null); return; }
      if (showAvailabilitySheet) { setShowAvailabilitySheet(false); return; }
      if (creating) { setCreating(false); return; }
      if (showNotifs) { setShowNotifs(false); return; }
      if (addFriendPrompt) { setAddFriendPrompt(null); return; }
      if (clusterGames) { setClusterGames(null); return; }
      if (selectedGame) { setSelectedGame(null); return; }
      if (selectedVenue) { setSelectedVenue(null); return; }
      if (showFilters) { setShowFilters(false); return; }
    };
    document.addEventListener("keydown", handle);
    return () => document.removeEventListener("keydown", handle);
	  }, [showNotifs, selectedGame, selectedVenue, clusterGames, showFriendsScreen, showAvailabilitySheet, creating, addFriendPrompt, showFilters, showSettings, showAdminDashboard, showCreateCommunity, openedCommunity]);
  // Toast queue: enqueues messages; a separate effect drains them one at a time
  const toast_ = useCallback(msg => {
    setToastQueue(q => [...q, { id: Date.now() + Math.random(), msg }]);
  }, []);
  // Drain the queue: show first item, remove it after 3 s, then show next
  useEffect(() => {
    if (toastQueue.length === 0) return;
    clearTimeout(toastTimerRef.current);
    toastTimerRef.current = setTimeout(() => {
      setToastQueue(q => q.slice(1));
    }, 3000);
    return () => clearTimeout(toastTimerRef.current);
  }, [toastQueue]);
  // Centralized notification insert with error logging.
  // Pass a single object or an array; missing required fields are dropped.
  const notifyInsert = useCallback(async payload => {
    if (!payload) return;
    const arr = Array.isArray(payload) ? payload : [payload];
    const cleaned = arr.filter(n => n && n.user_id && n.type);
    if (cleaned.length === 0) return;
    try {
      const { error } = await supabase.from("notifications").insert(cleaned);
      if (error) console.warn("[notify] insert failed", { error, types: cleaned.map(n => n.type) });
    } catch (err) {
      console.warn("[notify] threw", err);
    }
  }, []);

  const removeCancelledGameFromLocalState = useCallback(gameId => {
    const matches = g => String(g?.id) === String(gameId);
    try {
      const cache = JSON.parse(localStorage.getItem("kickr_games_cache") || "[]");
      localStorage.setItem("kickr_games_cache", JSON.stringify(cache.filter(g => !matches(g))));
    } catch { /* ignore corrupt local cache */ }
    setGames(prev => prev.filter(g => !matches(g)));
    setPastGames(prev => prev.filter(g => !matches(g)));
    setClusterGames(prev => {
      if (!prev) return prev;
      const next = prev.filter(g => !matches(g));
      return next.length ? next : null;
    });
    setJoined(prev => prev.filter(id => String(id) !== String(gameId)));
    setWaitlisted(prev => prev.filter(id => String(id) !== String(gameId)));
    setFavs(prev => {
      const next = prev.filter(id => String(id) !== String(gameId));
      localStorage.setItem("kickr_favs", JSON.stringify(next));
      return next;
    });
    setSelectedGame(prev => prev && matches(prev) ? null : prev);
    cancelGameReminders(gameId);
  }, []);

  const notifyCancelledGameAudience = useCallback(async (game, reason, { includeHost = false, excludeUserId = null } = {}) => {
    if (!game?.id) return 0;
    const { data: players, error } = await supabase
      .from("game_players")
      .select("user_id")
      .eq("game_id", game.id);
    if (error) console.warn("[notify.cancelledGameAudience] player lookup failed", error);

    const audience = new Set((players || []).map(p => p.user_id).filter(Boolean));
    if (includeHost && game.hostId) audience.add(game.hostId);
    if (excludeUserId) audience.delete(excludeUserId);
    const rows = [...audience].map(userId => ({
      user_id: userId,
      type: "game_cancelled",
      text: `${game.title || "Your game"} was cancelled`,
      sub: reason ? `Reason: ${reason}` : "The game was cancelled.",
      game_id: game.id,
      read: false,
    }));
    await notifyInsert(rows);
    return rows.length;
  }, [notifyInsert]);

  const handleSelectGame = useCallback(g => setSelectedGame(prev => prev?.id === g.id ? null : g), []);

  // Geolocation — use Capacitor plugin so iOS shows the permission dialog
  useEffect(() => {
    (async () => {
      try {
        const { Geolocation } = await import("@capacitor/geolocation");
        const pos = await Geolocation.getCurrentPosition({ enableHighAccuracy: true, timeout: 10000 });
        setUserLocation({ lat: pos.coords.latitude, lng: pos.coords.longitude });
      } catch {
        // Fallback: try browser API (web), then default to Oslo center
        navigator.geolocation?.getCurrentPosition(
          pos => setUserLocation({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
          () => setUserLocation({ lat: 59.9139, lng: 10.7522 })
        );
      }
    })();
  }, []);

  // Load my active availability
  useEffect(() => {
    if (!user) { setMyAvailability(null); setMyAvailabilities([]); setNearbyAvailable([]); return; }
    const today = format(new Date(), "yyyy-MM-dd");
    supabase.from("availability").select("*").eq("user_id", user.id).gte("date", today).order("date", { ascending: true }).limit(30)
      .then(({ data, error }) => {
        if (error) throw error;
        const rows = data || [];
        setMyAvailabilities(rows);
        setMyAvailability(rows[0] || null);
      })
      .catch(e => {
        console.warn("[availability.load] failed", e);
        setMyAvailability(null);
        setMyAvailabilities([]);
        setNearbyAvailable([]);
      });
  }, [user]);

	  // Load nearby available players (radius-filtered)
	  useEffect(() => {
	    if (!user || !myAvailability) { setNearbyAvailable([]); return; }
	    const RADIUS_KM = 5;
    const center = userLocation || (myAvailability.lat ? { lat: myAvailability.lat, lng: myAvailability.lng } : null);
    supabase.from("availability")
      .select("*")
      .eq("date", myAvailability.date).neq("user_id", user.id)
      .lte("time_from", myAvailability.time_to).gte("time_to", myAvailability.time_from)
      .then(async ({ data, error }) => {
        if (error) throw error;
        const compatibleRows = (data || []).filter(p =>
          (!center || !p.lat || !p.lng || getDistance(center.lat, center.lng, p.lat, p.lng) <= RADIUS_KM) &&
          availabilityRowsCompatible(myAvailability, p)
        );
        setNearbyAvailable(await hydrateAvailabilityProfiles(compatibleRows));
	      })
	      .catch(() => setNearbyAvailable([]));
	  }, [user, myAvailability, userLocation]);

  // If I have not joined the availability pool yet, still show nearby momentum
  // so the map pill can invite me into an already-forming pickup window.
  useEffect(() => {
    if (!user || myAvailability || !userLocation) { setNearbyAvailabilityPreview([]); return; }
    let cancelled = false;
    const RADIUS_KM = 5;
    const today = format(new Date(), "yyyy-MM-dd");
    supabase.from("availability")
      .select("*")
      .gte("date", today)
      .neq("user_id", user.id)
      .order("date", { ascending: true })
      .order("time_from", { ascending: true })
      .limit(100)
      .then(async ({ data, error }) => {
        if (error) throw error;
        const byUser = new Map();
        (data || []).forEach(row => {
          if (!row.user_id || byUser.has(row.user_id)) return;
          if (!row.lat || !row.lng || getDistance(userLocation.lat, userLocation.lng, row.lat, row.lng) > RADIUS_KM) return;
          byUser.set(row.user_id, row);
        });
        const hydrated = await hydrateAvailabilityProfiles([...byUser.values()].slice(0, 12));
        if (!cancelled) setNearbyAvailabilityPreview(hydrated);
      })
      .catch(() => {
        if (!cancelled) setNearbyAvailabilityPreview([]);
      });
    return () => { cancelled = true; };
  }, [user, myAvailability, userLocation]);

  // Load communities — my memberships + city-scoped Discover.
  // Re-runs when user or their city changes.
  const loadCommunities = useCallback(async () => {
    setCommunitiesLoading(true);
    const city = userProfile?.city || "Oslo";
    try {
      // Discover: public communities in city, ordered by member count.
      // RLS on `communities` already restricts private rows to members.
      const discoverPromise = supabase
        .from("communities")
        .select("id, name, slug, description, avatar_url, cover_url, is_private, join_policy, city, tags, owner_id, member_count, created_at, verified")
        .eq("is_private", false)
        .eq("city", city)
        .order("member_count", { ascending: false })
        .limit(50);

      // Mine: rows where I have an active membership.
      const minePromise = user
        ? supabase
            .from("community_members")
            .select("role, status, joined_at, communities!inner(id, name, slug, description, avatar_url, cover_url, is_private, join_policy, city, tags, owner_id, member_count, created_at, verified)")
            .eq("user_id", user.id)
            .eq("status", "active")
            .order("joined_at", { ascending: false })
        : Promise.resolve({ data: [] });

      const [{ data: discoverRows }, { data: mineRows }] = await Promise.all([discoverPromise, minePromise]);
      const mine = (mineRows || []).map(r => ({ ...r.communities, my_role: r.role }));
      setMyCommunities(mine);
      setDiscoverCommunities(discoverRows || []);
    } catch (e) {
      console.warn("[communities.load] failed", e);
    } finally {
      setCommunitiesLoading(false);
    }
  }, [user, userProfile?.city]);

  useEffect(() => { loadCommunities(); }, [loadCommunities]);

  // Refresh availability + send group notifications when threshold met
  const handleRefreshAvailability = async () => {
    if (!user) return;
    try {
      const today = format(new Date(), "yyyy-MM-dd");
      const { data, error } = await supabase.from("availability").select("*").eq("user_id", user.id).gte("date", today).order("date", { ascending: true }).limit(30);
      if (error) throw error;
      const rows = data || [];
      const newAvail = rows[0] || null;
      setMyAvailabilities(rows);
      setMyAvailability(newAvail);
      if (!newAvail) { setNearbyAvailable([]); return; }
      const RADIUS_KM = 5;
      const center = userLocation || (newAvail.lat ? { lat: newAvail.lat, lng: newAvail.lng } : null);
      const { data: nearby, error: nearbyError } = await supabase.from("availability")
        .select("*")
        .eq("date", newAvail.date).neq("user_id", user.id)
        .lte("time_from", newAvail.time_to).gte("time_to", newAvail.time_from);
      if (nearbyError) throw nearbyError;
      const inRadius = (nearby || []).filter(p =>
        (!center || !p.lat || !p.lng || getDistance(center.lat, center.lng, p.lat, p.lng) <= RADIUS_KM) &&
        availabilityRowsCompatible(newAvail, p)
      );
      const hydratedNearby = await hydrateAvailabilityProfiles(inRadius);
      setNearbyAvailable(hydratedNearby);
      if (inRadius.length >= 4) {
        const hour = newAvail.time_from.slice(0, 5);
        // Sort ids so the hash is stable for the same cohort regardless of insert order.
        const sortedIds = inRadius.map(p => p.user_id).sort();
        const sub = JSON.stringify({ date: newAvail.date, hour, playerIds: sortedIds });
        // Dedupe: skip the blast if we already sent it for the same (date, hour, cohort) within the cooldown.
        // Without this, re-saving unchanged availability spams the same notification every time.
        const cooldownKey = `kickr_avail_blast_${user.id}_${newAvail.date}_${hour}`;
        const COOLDOWN_MS = 2 * 60 * 60 * 1000; // 2h
        let shouldSkip = false;
        try {
          const lastStr = localStorage.getItem(cooldownKey);
          if (lastStr) {
            const last = JSON.parse(lastStr);
            if (last?.sub === sub && typeof last.at === "number" && (Date.now() - last.at) < COOLDOWN_MS) {
              shouldSkip = true;
            }
          }
        } catch { /* corrupt JSON — fall through and send */ }
        if (!shouldSkip) {
          const total = inRadius.length + 1;
          const myName = userProfile?.name || "A player";
          const { error: selfNotifError } = await supabase.from("notifications").insert({
            user_id: user.id, type: "availability_group", read: false,
            text: `${total} players are free near you at ${hour} — create a game?`, sub,
          });
          if (selfNotifError) console.warn("[availability.blast.self] insert failed", selfNotifError);
          if (inRadius.length) {
            const { error: cohortNotifError } = await supabase.from("notifications").insert(
            inRadius.map(p => ({
              user_id: p.user_id, type: "availability_group", read: false,
              text: `${myName} + ${inRadius.length} others are free near you at ${hour} — create a game?`, sub,
            }))
            );
            if (cohortNotifError) console.warn("[availability.blast.cohort] insert failed", cohortNotifError);
          }
          try { localStorage.setItem(cooldownKey, JSON.stringify({ sub, at: Date.now() })); } catch { /* storage full or blocked — non-fatal */ }
        }
      }
    } catch (e) {
      console.warn("[availability.refresh] failed", e);
      setNearbyAvailable([]);
    }
  };

  useEffect(() => {
    [
      "kickr_filter",
      "kickr_formatFilter",
      "kickr_dateRange",
      "kickr_dateFilter",
      "kickr_sortBy_v2",
      "kickr_radiusKm",
      "kickr_showFullGames",
    ].forEach(key => localStorage.removeItem(key));
  }, []);

  // Load venues from Supabase. If the table is missing or empty, keep the
  // bundled catalog so the map/create flow still works in dev.
  const loadVenues = useCallback(async () => {
    try {
      const { data, error } = await supabase.from("venues").select("*");
      if (error) throw error;
      const nextVenues = normaliseVenueList(data || []);
      const usableVenues = nextVenues.length ? nextVenues : normaliseVenueList(VENUES_FALLBACK);
      setVenues(usableVenues);
      setSelectedVenue(prev => prev ? (usableVenues.find(v => String(v.id) === String(prev.id)) || null) : prev);
      return usableVenues;
    } catch (e) {
      console.warn("[venues.load] using fallback catalog", e);
      const fallbackVenues = normaliseVenueList(VENUES_FALLBACK);
      setVenues(fallbackVenues);
      return fallbackVenues;
    }
  }, []);

  useEffect(() => { loadVenues(); }, [loadVenues]);

  // Auth
  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user ?? null);
      setAuthLoading(false);
      if (window.location.hash.includes("access_token")) {
        window.history.replaceState({}, "", window.location.pathname);
      }
    });
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_e, session) => {
      setUser(session?.user ?? null);
      setAuthLoading(false);
      if (window.location.hash.includes("access_token")) {
        window.history.replaceState({}, "", window.location.pathname);
      }
    });

    // Handle deep link redirect from Google OAuth on iOS
    const handleAppUrlOpen = async (event) => {
      const url = event.url || event.detail?.url;
      if (!url || !url.includes("login-callback")) return;
      try {
        const { Browser } = await import("@capacitor/browser");
        await Browser.close().catch(() => {});

        // PKCE flow (Supabase v2 default): URL has ?code=...
        const urlObj = new URL(url);
        const code = urlObj.searchParams.get("code");
        if (code) {
          await supabase.auth.exchangeCodeForSession(code);
          return;
        }

        // Implicit flow fallback: URL has #access_token=...
        const hash = url.split("#")[1] || "";
        const params = new URLSearchParams(hash);
        const access_token = params.get("access_token");
        const refresh_token = params.get("refresh_token");
        if (access_token) {
          await supabase.auth.setSession({ access_token, refresh_token });
        }
      } catch (e) {
        console.error("[auth.deepLink] failed to restore session from callback URL", e);
        reportError(e, { where: "auth.deepLink" });
      }
    };

    // Listen for Capacitor App URL open events
    (async () => {
      try {
        const { App: CapApp } = await import("@capacitor/app");
        const { Browser } = await import("@capacitor/browser");
        CapApp.addListener("appUrlOpen", handleAppUrlOpen);
        // Fallback: when user manually closes the browser, check if session exists
        Browser.addListener("browserFinished", async () => {
          const { data: { session } } = await supabase.auth.getSession();
          if (session?.user) setUser(session.user);
        });
      } catch {
        // not in Capacitor environment
      }
    })();

    return () => subscription.unsubscribe();
  }, []);

  const handleSignOut = async ({ localOnly = false } = {}) => {
    try {
      await supabase.auth.signOut(localOnly ? { scope: "local" } : undefined);
    } catch (e) {
      if (!localOnly) console.warn("[auth.signOut] failed", e);
    }
    setUser(null); setGames([]); setPastGames([]); setJoined([]); setWaitlisted([]);
    setUserProfile(null); setNotifs([]); setTab("discover"); setSelectedGame(null);
    setCreating(false); setCreatePrefill(null); setShowNotifs(false);
    setMyAvailability(null); setMyAvailabilities([]); setNearbyAvailable([]);
	    setShowFriendsScreen(false); setShowAdminDashboard(false); setShowSettings(false); setAddFriendPrompt(null); setFriendsInviteGameId(null); setFriendsInitialChatUserId(null); setSearchPlayerCard(null);
    setMyCommunities([]); setDiscoverCommunities([]); setOpenedCommunity(null); setOpenedCommunityInitialView("about"); setShowCreateCommunity(false);
    // Clear ALL kickr_* localStorage keys so next user on same device starts clean.
    // Catches per-game keys (kickr_reported_*, kickr_pfb_*) too.
    try {
      const keys = Object.keys(localStorage);
      keys.forEach(k => { if (k.startsWith("kickr_")) localStorage.removeItem(k); });
    } catch { /* localStorage may be blocked (Safari private mode) */ }
    setFavs([]);
  };

  // Load user profile when user changes
  useEffect(() => {
    if (!user) { setProfileLoaded(false); setUsernameSchemaReady(false); setLegalSchemaReady(false); return; }
    let mounted = true;
    // Banned/suspended users are handled by <AccountStatusGate /> below — we
    // KEEP them signed in so the gate screen can show suspension end date,
    // reason, and (for permanent bans) the appeal contact. Auto-signing out
    // here is a UX dead-end: user sees a toast, gets booted, has no idea
    // when or why they can come back. The DB-side RLS blocks all writes via
    // is_active_user(), so leaving them signed in is safe.
    (async () => {
      let profile = null;
      const [{ data }, usernameProbe, legalProbe] = await Promise.all([
        supabase.from("profiles").select("*").eq("id", user.id).maybeSingle(),
        supabase.from("profiles").select("username").eq("id", user.id).maybeSingle(),
        supabase.from("profiles").select("terms_accepted_at,terms_version,privacy_version").eq("id", user.id).maybeSingle(),
      ]);
      profile = data || null;
      if (!mounted) return;
      if (profile) setUserProfile(profile);
      setUsernameSchemaReady(!usernameProbe.error);
      setLegalSchemaReady(!legalProbe.error);
      setProfileLoaded(true);
    })();
    return () => { mounted = false; };
  }, [user]);

  // Load games
	  const loadGames = useCallback(async (silent = false) => {
    if (!silent) setGamesLoading(true);
    try {
      // Server-side date floor: skip games whose date is more than 30 days
      // in the past. Cancelled games are filtered later via mapGame, but a
      // hard date floor stops the payload from growing forever.
      const dateFloor = (() => {
        const d = new Date(); d.setDate(d.getDate() - 30);
        return d.toISOString().slice(0, 10);
      })();

      const SELECT = "*, game_players(user_id, status), communities!community_id(id, name, slug, avatar_url, verified)";

      // Public + primary + hosted queries fire in parallel to halve perceived latency.
      // The public query intentionally uses an anon client: games marked public
      // should be visible to every visitor, even if an authenticated RLS policy
      // is stricter than the anonymous public read path.
      // The hosted-fallback exists because the games SELECT policy doesn't
      // always let a host see their own private/community game; until that
      // RLS gap is closed we need both.
      const [publicRes, primaryRes, hostedRes] = await Promise.all([
        supabasePublic
          .from("games")
          .select(SELECT)
          .or(`date.gte.${dateFloor},date.is.null`)
          .or("visibility.eq.public,visibility.is.null")
          .or("is_private.eq.false,is_private.is.null")
          .order("created_at", { ascending: false })
          .limit(500),
        supabase.from("games").select(SELECT).or(`date.gte.${dateFloor},date.is.null`).order("created_at", { ascending: false }).limit(500),
        user
          ? supabase.from("games").select(SELECT).eq("host_id", user.id).order("created_at", { ascending: false }).limit(200)
          : Promise.resolve({ data: [], error: null }),
      ]);
      const { data: publicData, error: publicError } = publicRes;
      if (publicError) console.error("[kickr] public games query error:", publicError);
      const { data: primaryData, error } = primaryRes;
      if (error) console.error("[kickr] primary games query error:", error);
      if (error && publicError) throw error;
      const { data: hostedData, error: hostedError } = hostedRes;
      if (hostedError) console.error("[kickr] hosted games query error:", hostedError);

      // Merge, deduplicate by id
      const seen = new Set();
      const merged = [...(publicData || []), ...(primaryData || []), ...(hostedData || [])].filter(g => {
        if (seen.has(g.id)) return false;
        seen.add(g.id);
        return true;
      });

      if (!merged.length && !publicData && !primaryData) return; // nothing to update — keep existing state

      const playerIds = [...new Set(merged.flatMap(g => (g.game_players || []).map(p => p.user_id).filter(Boolean)))];
      let profileById = {};
      if (playerIds.length > 0) {
	        const { data: playerProfiles, error: profilesError } = await supabasePublic
	          .from("profiles_public")
	          .select("user_id, name, username, level, position, avatar_url")
	          .in("user_id", playerIds);
        if (profilesError) console.error("[kickr] player profile query error:", profilesError);
        profileById = Object.fromEntries((playerProfiles || []).map(profile => [profile.user_id, profile]));
      }

      const data = merged.map(g => ({
        ...g,
        game_players: (g.game_players || []).map(player => ({
          ...player,
          profiles_public: profileById[player.user_id] || null,
        })),
      }));
      const now = new Date();

      const getGameEndTime = (date, hour, duration) => {
        const durationMins = parseInt(duration, 10) || 60;
        return date && hour
          ? new Date(`${date}T${hour}`).getTime() + durationMins * 60000
          : null;
      };

      const getGameDateLabel = date => {
        if (!date) return "TBD";
        const [y, m, d] = date.split("-").map(Number);
        const dt = new Date(y, m - 1, d);
        const today = new Date(); today.setHours(0,0,0,0);
        const tomorrow = new Date(today); tomorrow.setDate(today.getDate() + 1);
        if (dt.getTime() === today.getTime()) return t("todayLabel");
        if (dt.getTime() === tomorrow.getTime()) return t("tomorrowLabel");
        return dt.toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short" });
      };

      const normaliseCachedGame = g => ({
        ...g,
        time: getGameDateLabel(g.date),
        endTime: getGameEndTime(g.date, g.hour, g.duration),
        duration: g.duration || "60 min",
      });

      const mapGame = (g) => {
        const confirmedPlayers = (g.game_players || []).filter(p => !p.status || p.status === "confirmed");
        const spotsFilled = confirmedPlayers.length;
        const minPlayers = g.min_players || 2;
        const needsMorePlayers = spotsFilled < minPlayers;
        const endTime = getGameEndTime(g.date, g.hour, g.duration);
        return {
          venueType: (() => { const v = venues.find(v => v.lat === g.lat && v.lng === g.lng); return v?.type || (g.price === "Free" ? "Outdoor" : null); })(),
	          id: g.id, title: g.title, format: g.format, level: g.level, location: g.location,
	          backupVenueId: g.backup_venue_id || null,
	          backupLocation: g.backup_location || null,
	          backupLat: g.backup_lat || null,
	          backupLng: g.backup_lng || null,
          // Phase 1: visibility is the new source of truth; isPrivate stays as
          // a derived alias for older render paths still reading the bool.
          visibility: g.visibility || (g.is_private ? "private" : "public"),
          communityId: g.community_id || null,
          communityName: g.communities?.name || null,
          communityAvatarUrl: g.communities?.avatar_url || null,
          communityVerified: g.communities?.verified === true,
          isPrivate: g.visibility ? g.visibility === "private" : (g.is_private || false),
          lat: g.lat, lng: g.lng,
          time: getGameDateLabel(g.date),
          hour: g.hour || "", date: g.date || "", duration: g.duration || "60 min",
          price: g.price || "Free", description: g.description || "",
          spotsTotal: g.spots_total, spotsFilled, minPlayers, needsMorePlayers,
          host: g.host_name || "Unknown", hostAvatar: (g.host_name || "??").slice(0, 2).toUpperCase(),
          hostId: g.host_id, tags: g.tags || [], chat: [], status: g.status || "active",
          ballHolder: g.ball_holder || null,
          teams: g.teams || null,
          minAge: g.min_age || null,
          refereeId: g.referee_id || null,
          refereeName: g.referee_name || null,
          endTime,
          players: (g.game_players || []).filter(p => !p.status || p.status === "confirmed").map(p => {
            // Read from profiles_public (PII-stripped view) with legacy profiles fallback
            const prof = p.profiles_public || p.profiles || {};
            const name = prof.name || "Player";
            const initials = name.split(" ").map(w => w[0]).join("").slice(0,2).toUpperCase();
	            return { id: p.user_id, name, username: prof.username || "", avatar: initials, avatarUrl: prof.avatar_url || null, level: prof.level || "Intermediate", position: prof.position || "", isMe: p.user_id === user?.id };
	          }),
	          waitlist: (g.game_players || []).filter(p => p.status === "waitlisted").map(p => {
	            const prof = p.profiles_public || p.profiles || {};
	            const name = prof.name || "Player";
	            return { id: p.user_id, name, username: prof.username || "", avatarUrl: prof.avatar_url || null, isMe: p.user_id === user?.id };
	          }),
        };
      };

      const allMapped = (data || []).filter(g => g.status !== "cancelled").map(mapGame);

      // Active: game hasn't ended yet (end time in future, or no date set)
      const active = allMapped.filter(g => !g.endTime || g.endTime > now.getTime());
      // Past: game has ended (end time = start + duration is in the past)
      const past = allMapped.filter(g => g.endTime && g.endTime <= now.getTime());

      // Merge with localStorage-cached games (fallback for RLS gaps — only when DB has data)
      const cachedRaw = (() => { try { return JSON.parse(localStorage.getItem("kickr_games_cache") || "[]"); } catch { return []; } })();
      const dbIds = new Set(allMapped.map(g => g.id));
      // Remove cancelled games from cache
      const cancelledIds = new Set((data || []).filter(g => g.status === "cancelled").map(g => g.id));
      const cleanedCache = cachedRaw.filter(g => !cancelledIds.has(g.id) && g.status !== "cancelled").map(normaliseCachedGame);
      // If DB is empty (0 games returned), wipe the local cache too — it's stale
      if (allMapped.length === 0) localStorage.removeItem("kickr_games_cache");
      else if (cleanedCache.length !== cachedRaw.length) localStorage.setItem("kickr_games_cache", JSON.stringify(cleanedCache));
      // Only supplement from cache if DB returned some data (i.e. DB is working, not empty)
      // If DB is genuinely empty, don't show stale cached games
      const missingFromDB = allMapped.length > 0 ? cleanedCache.filter(g => !dbIds.has(g.id)) : [];
      const allActive = [...active, ...missingFromDB.filter(g => !g.endTime || g.endTime > now.getTime())];
      const allPast = [...past, ...missingFromDB.filter(g => g.endTime && g.endTime <= now.getTime())];

      setGames(allActive);
      setPastGames(allPast);

      if (user) {
        const { data: j } = await supabase.from("game_players").select("game_id, status").eq("user_id", user.id);
        setJoined((j || []).filter(x => !x.status || x.status === "confirmed").map(x => x.game_id));
        setWaitlisted((j || []).filter(x => x.status === "waitlisted").map(x => x.game_id));
      }
    } catch (e) {
    console.error("Failed to load games:", e);
    // On error, still try to load from cache so created games survive
    const cached = (() => { try { return JSON.parse(localStorage.getItem("kickr_games_cache") || "[]"); } catch { return []; } })();
    if (cached.length) {
      setGames(prev => prev.length ? prev : cached.filter(g => !g.endTime || g.endTime > Date.now()));
      setPastGames(prev => prev.length ? prev : cached.filter(g => g.endTime && g.endTime <= Date.now()));
    }
  }
	    finally { setGamesLoading(false); }
	  }, [user, venues]);

  const autoCancelHostFallback = useCallback(async game => {
    if (!user?.id || !game?.id || game.hostId !== user.id || !shouldAutoCancelGame(game)) return null;
    const { error } = await supabase
      .from("games")
      .update({
        status: "cancelled",
        cancelled_at: new Date().toISOString(),
        cancelled_reason: AUTO_CANCEL_REASON,
      })
      .eq("id", game.id)
      .eq("host_id", user.id)
      .neq("status", "cancelled");
    if (error) throw error;
    const notified = await notifyCancelledGameAudience(game, AUTO_CANCEL_REASON, { includeHost: true });
    removeCancelledGameFromLocalState(game.id);
    return { gameId: game.id, notified };
  }, [notifyCancelledGameAudience, removeCancelledGameFromLocalState, user?.id]);

	  const runAutoCancelSweep = useCallback(async ({ force = false } = {}) => {
    if (!user?.id) return;
    if (autoCancelRunningRef.current) return;
    const now = Date.now();
    if (!force && now - autoCancelLastRunRef.current < 55_000) return;
    autoCancelRunningRef.current = true;
    autoCancelLastRunRef.current = now;
    try {
      const { data, error } = await supabase.rpc("auto_cancel_underfilled_games");
      if (error) {
        const missingRpc = /auto_cancel_underfilled_games|PGRST202|Could not find the function|schema cache/i.test(`${error.message || ""} ${error.details || ""} ${error.hint || ""}`);
        if (!missingRpc) console.warn("[autoCancel] RPC failed", error);

        const hostTargets = user ? games.filter(g => shouldAutoCancelGame(g) && g.hostId === user.id) : [];
        if (!hostTargets.length) return;

        let cancelledCount = 0;
        let notifiedCount = 0;
        for (const game of hostTargets) {
          try {
            const result = await autoCancelHostFallback(game);
            if (result) {
              cancelledCount += 1;
              notifiedCount += result.notified || 0;
            }
          } catch (fallbackError) {
            console.warn("[autoCancel] host fallback failed", fallbackError);
          }
        }
        if (cancelledCount > 0) {
          toast_(notifiedCount > 0
            ? `Auto-cancelled ${cancelledCount} game${cancelledCount > 1 ? "s" : ""} — ${notifiedCount} player${notifiedCount > 1 ? "s" : ""} notified`
            : `Auto-cancelled ${cancelledCount} game${cancelledCount > 1 ? "s" : ""}`);
        }
        return;
      }

      const rows = Array.isArray(data) ? data : [];
      const cancelledIds = rows.map(row => row.game_id || row.cancelled_game_id).filter(Boolean);
      if (cancelledIds.length > 0) {
        cancelledIds.forEach(removeCancelledGameFromLocalState);
        loadGames(true);
      }
    } finally {
      autoCancelRunningRef.current = false;
    }
	  }, [autoCancelHostFallback, games, loadGames, removeCancelledGameFromLocalState, toast_, user]);

	  const runGameReviewNotificationSweep = useCallback(async ({ force = false } = {}) => {
	    if (!user?.id) return;
	    if (reviewNotifyRunningRef.current) return;
	    const now = Date.now();
	    if (!force && now - reviewNotifyLastRunRef.current < 55_000) return;
	    reviewNotifyRunningRef.current = true;
	    reviewNotifyLastRunRef.current = now;
	    try {
	      const { error } = await supabase.rpc("send_due_game_review_notifications");
	      if (error) {
	        const missingRpc = /send_due_game_review_notifications|PGRST202|Could not find the function|schema cache/i.test(`${error.message || ""} ${error.details || ""} ${error.hint || ""}`);
	        if (!missingRpc) console.warn("[gameReviewNotifications] RPC failed", error);
	      }
	    } finally {
	      reviewNotifyRunningRef.current = false;
	    }
	  }, [user?.id]);

	  useEffect(() => {
	    if (authLoading || gamesLoading || !user?.id || !profileLoaded) return;
	    runAutoCancelSweep({ force: true });
	    runGameReviewNotificationSweep({ force: true });
	    const sweepId = window.setInterval(() => {
	      runAutoCancelSweep();
	      runGameReviewNotificationSweep();
	    }, 60_000);
	    return () => window.clearInterval(sweepId);
	  }, [authLoading, gamesLoading, profileLoaded, runAutoCancelSweep, runGameReviewNotificationSweep, user?.id]);

  // Capture deep-link params on first render, before the URL gets cleaned up
  const deepLinkGameIdRef = useRef(new URLSearchParams(window.location.search).get("game"));
  const deepLinkAddFriendRef = useRef(new URLSearchParams(window.location.search).get("addFriend"));

  useEffect(() => {
    // Clean URL for logged-in users with deep-link params
    if (user && (deepLinkGameIdRef.current || deepLinkAddFriendRef.current)) {
      window.history.replaceState({}, "", window.location.pathname);
    }
    loadGames(); // load for everyone — games are public
  }, [user, loadGames]);

  // Handle ?addFriend= deep link — show send-request prompt once user is logged in
  useEffect(() => {
    const targetId = deepLinkAddFriendRef.current;
    if (!targetId || !user || user.id === targetId) return;
    deepLinkAddFriendRef.current = null; // consume once
    supabasePublic.from("profiles_public").select("user_id,name,avatar_url").eq("user_id", targetId).maybeSingle()
      .then(({ data }) => { if (data) setAddFriendPrompt({ userId: data.user_id, name: data.name, avatarUrl: data.avatar_url }); })
      .catch(() => {});
  }, [user]);

  // Open a shared game once the games list has loaded
  useEffect(() => {
    if (!games.length || !deepLinkGameIdRef.current || selectedGame) return;
    const found = games.find(g => g.id === deepLinkGameIdRef.current);
    if (found) {
      setSelectedGame(found);
      deepLinkGameIdRef.current = null; // clear so polling refreshes don't re-open
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [games]);
  // Removed 15s polling — realtime subscriptions handle live updates; polling causes unnecessary DB load

  // Sync selectedGame with fresh data whenever the games list updates
  useEffect(() => {
    if (!selectedGame) return;
    const updated = games.find(g => g.id === selectedGame.id);
    if (updated) setSelectedGame(updated);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [games]);

  // Realtime: reload games when spots change (debounced to avoid rapid fires)
  // No user guard — guests also need live player counts when browsing
  useEffect(() => {
    let debounceTimer = null;
    const debouncedReload = () => {
      clearTimeout(debounceTimer);
      debounceTimer = setTimeout(() => loadGames(true), 800);
    };
    const channel = supabase.channel("games-realtime")
      .on("postgres_changes", { event: "INSERT", schema: "public", table: "games" }, debouncedReload)
      .on("postgres_changes", { event: "UPDATE", schema: "public", table: "games" }, debouncedReload)
      .on("postgres_changes", { event: "DELETE", schema: "public", table: "games" }, debouncedReload)
      .on("postgres_changes", { event: "INSERT", schema: "public", table: "game_players" }, debouncedReload)
      .on("postgres_changes", { event: "UPDATE", schema: "public", table: "game_players" }, debouncedReload)
      .on("postgres_changes", { event: "DELETE", schema: "public", table: "game_players" }, debouncedReload)
      .subscribe();
    return () => { clearTimeout(debounceTimer); supabase.removeChannel(channel); };
  }, [loadGames]);

  // Load notifications from DB + realtime subscription
	  useEffect(() => {
	    if (!user) return;
	    let mounted = true;
	    const mapDbNotif = n => ({ id: n.id, text: n.text, sub: n.sub, time: formatNotifTime(n.created_at), gameId: n.game_id, read: n.read, type: n.type });
	    const mergeDbNotifications = (rows = []) => {
	      const clearedAt = parseInt(localStorage.getItem(`kickr_notifs_cleared_${user.id}`) || "0");
	      const dbMapped = rows
	        .filter(n => !isLaunchQaNotification(n))
	        .filter(n => !clearedAt || new Date(n.created_at).getTime() > clearedAt)
	        .map(mapDbNotif);
	      setNotifs(prev => {
	        const byId = new Map();
	        [...dbMapped, ...prev.filter(n => !isLaunchQaNotification(n))].forEach(n => byId.set(String(n.id), n));
	        return [...byId.values()].sort((a, b) => {
	          const aid = typeof a.id === "number" ? a.id : 0;
	          const bid = typeof b.id === "number" ? b.id : 0;
	          return bid - aid;
	        }).slice(0, 80);
	      });
	    };
	    const loadDbNotifications = async () => {
	      try {
	        const { data } = await supabase.from("notifications").select("*").eq("user_id", user.id).order("created_at", { ascending: false }).limit(50);
	        if (mounted && data) mergeDbNotifications(data);
	      } catch {
	        // Polling fallback is best-effort.
	      }
	    };
	    // Load existing notifications and keep a polling fallback for mobile/web
	    // sessions where the realtime socket is suspended or not yet subscribed.
	    loadDbNotifications();
	    const pollId = window.setInterval(loadDbNotifications, 5000);
	    // Realtime subscription for new notifications
	    const channel = supabase.channel(`notifs-${user.id}`)
	      .on("postgres_changes", { event: "INSERT", schema: "public", table: "notifications", filter: `user_id=eq.${user.id}` }, payload => {
	        if (!mounted) return;
	        const n = payload.new;
	        if (n.user_id !== user.id) return; // client-side guard in case RLS filter leaks
	        if (isLaunchQaNotification(n)) return;
	        const clearedAt2 = parseInt(localStorage.getItem(`kickr_notifs_cleared_${user.id}`) || "0");
	        if (clearedAt2 && new Date(n.created_at).getTime() <= clearedAt2) return;
	        const mapped = { id: n.id, text: n.text, sub: n.sub, time: "Just now", gameId: n.game_id, read: false, type: n.type };
	        setNotifs(prev => prev.some(x => x.id === n.id) ? prev : [mapped, ...prev]);
	      })
	      .on("postgres_changes", { event: "UPDATE", schema: "public", table: "notifications", filter: `user_id=eq.${user.id}` }, payload => {
	        if (!mounted) return;
	        const n = payload.new;
	        setNotifs(prev => prev.map(x => x.id === n.id ? { ...x, ...mapDbNotif(n) } : x));
	      })
	      .on("postgres_changes", { event: "DELETE", schema: "public", table: "notifications", filter: `user_id=eq.${user.id}` }, payload => {
	        if (!mounted) return;
	        const oldId = payload.old?.id;
	        if (oldId != null) setNotifs(prev => prev.filter(x => x.id !== oldId));
	      })
	      .subscribe();
	    return () => { mounted = false; window.clearInterval(pollId); supabase.removeChannel(channel); };
	  }, [user]);

  // Generate in-memory notifications when game state changes (almost-full + starts-soon only)
  useEffect(() => {
    if (!user || !games.length) return;
    const prev = prevGamesRef.current;
    if (!prev.length) { prevGamesRef.current = games; return; }
    // If the user just cleared all notifications, don't re-synthesize any for ~5s
    // (prevents the "come back after clear" flicker on the next games re-render)
    const clearedAt = parseInt(localStorage.getItem(`kickr_notifs_cleared_${user.id}`) || "0");
    if (clearedAt && Date.now() - clearedAt < 5000) { prevGamesRef.current = games; return; }

    const newNotifs = [];
    games.forEach(game => {
      const prevGame = prev.find(g => g.id === game.id);
      if (!prevGame) return;
      // Game you joined is filling up
      if (joined.includes(game.id) && game.hostId !== user.id) {
        const wasNotAlmostFull = prevGame.spotsFilled / prevGame.spotsTotal < 0.8;
        const isNowAlmostFull = game.spotsFilled / game.spotsTotal >= 0.8 && game.spotsFilled < game.spotsTotal;
        if (wasNotAlmostFull && isNowAlmostFull) {
          newNotifs.push({ id: `full-${game.id}-${Date.now()}`, text: "Game is almost full!", sub: game.title, time: "Just now", gameId: game.id, read: false });
        }
      }
    });

    // "Starts soon" notifications for joined games (within 2 hours)
    games.forEach(game => {
      if (!joined.includes(game.id) || game.hostId === user.id) return;
      if (!game.date || !game.hour) return;
      const start = new Date(`${game.date}T${game.hour}`);
      const minsAway = (start - new Date()) / 60000;
      if (minsAway > 0 && minsAway <= 120) {
        const key = `soon-${game.id}-${game.date}-${game.hour}`;
        if (!seenSoonNotifsRef.current.has(key)) {
          // Prevent unbounded growth — clear when set gets large
          if (seenSoonNotifsRef.current.size > 200) seenSoonNotifsRef.current.clear();
          seenSoonNotifsRef.current.add(key);
          newNotifs.push({ id: key, text: `Game starting soon`, sub: `${game.title} · ${Math.round(minsAway)}min`, time: "Upcoming", gameId: game.id, read: false });
        }
      }
    });

    if (newNotifs.length > 0) setNotifs(prev => [...newNotifs, ...prev]);
    prevGamesRef.current = games;
  }, [games, user, joined]);

  const handleJoin = async gameId => {
    if (!user) { setShowAuthModal(true); return; }
    if (joiningRef.current) return;
    joiningRef.current = true;
    setJoiningGameId(gameId);

    const leaving = joined.includes(gameId);
    const gameForAction = [...games, ...pastGames].find(g => g.id === gameId);
    if (gameForAction && isPastGame(gameForAction)) {
      toast_("This game has already finished");
      joiningRef.current = false; setJoiningGameId(null);
      return;
    }
    const userName = userProfile?.name || user?.user_metadata?.name || "Player";
    const userInitials = userName.split(" ").map(w => w[0]).join("").slice(0, 2).toUpperCase();
    const mePlayer = { id: user.id, name: userName, avatar: userInitials, level: userProfile?.level || "Intermediate", position: userProfile?.position || "", isMe: true };
    const updateGame = (leaving_) => g => {
      if (g.id !== gameId) return g;
      return leaving_
        ? { ...g, spotsFilled: g.spotsFilled - 1, players: g.players.filter(p => p.id !== user.id) }
        : { ...g, spotsFilled: g.spotsFilled + 1, players: [...g.players.filter(p => p.id !== user.id), mePlayer] };
    };
    const senderName = userProfile?.name || user?.user_metadata?.name || "Player";
    try {
      if (leaving) {
        const game = games.find(g => g.id === gameId);
        const { error: delErr } = await supabase.from("game_players").delete().eq("game_id", gameId).eq("user_id", user.id);
        if (delErr) throw delErr;
        setJoined(prev => prev.filter(id => id !== gameId));
        cancelGameReminders(gameId);
        // Notify host
        if (game?.hostId && game.hostId !== user.id) {
          notifyInsert({ user_id: game.hostId, type: "player_left", text: `${senderName} left your game`, sub: game.title, game_id: gameId });
        }
        // Try to promote the first waitlisted player BEFORE mutating local state, so the UI can reflect the promote in a single update.
        const firstWaiting = (game?.waitlist || [])[0];
        let promoted = false;
        if (firstWaiting) {
          const { error: promoteErr } = await supabase.from("game_players").update({ status: "confirmed" }).eq("game_id", gameId).eq("user_id", firstWaiting.id);
          if (!promoteErr) {
            promoted = true;
            notifyInsert({ user_id: firstWaiting.id, type: "promoted", text: "You're off the waitlist", sub: game.title, game_id: gameId });
          } else {
            console.warn("[handleJoin.leave] auto-promote failed", promoteErr);
          }
        }
        // Single local-state update reflecting both the leave and (if applicable) the promote.
        // If promoted: spotsFilled stays constant (leaver -1 + promoted +1), promoted player moves waitlist → players.
        // Otherwise: spotsFilled -1 and leaver removed from players.
        const updateLeave = g_ => {
          if (g_.id !== gameId) return g_;
          const nextPlayers = g_.players.filter(p => p.id !== user.id);
          if (promoted && firstWaiting) {
            return {
              ...g_,
              players: [...nextPlayers, firstWaiting],
              waitlist: (g_.waitlist || []).filter(p => p.id !== firstWaiting.id),
            };
          }
          return { ...g_, spotsFilled: g_.spotsFilled - 1, players: nextPlayers };
        };
        setGames(prev => prev.map(updateLeave));
        setSelectedGame(prev => prev?.id === gameId ? updateLeave(prev) : prev);
        toast_(promoted && firstWaiting ? t("leftGameWaitlistPromoted", { name: firstWaiting.name }) : t("leftGame"));
      } else {
        const game = games.find(g => g.id === gameId);
        // Re-check capacity from DB to prevent race-condition over-fill
        const { count: confirmedCount } = await supabase
          .from("game_players")
          .select("*", { count: "exact", head: true })
          .eq("game_id", gameId)
          .eq("status", "confirmed");
        if (game && confirmedCount != null && confirmedCount >= (game.spotsTotal || 0)) {
          toast_(t("gameFull") || "Game is full");
          joiningRef.current = false; setJoiningGameId(null);
          return;
        }
        const { error: insertErr } = await supabase
          .from("game_players")
          .insert({ game_id: gameId, user_id: user.id, status: "confirmed" });
        if (insertErr) throw insertErr;
        setJoined(prev => prev.includes(gameId) ? prev : [...prev, gameId]);
        setGames(prev => prev.map(updateGame(false)));
        setSelectedGame(prev => prev?.id === gameId ? updateGame(false)(prev) : prev);
        // Notify all current players (host + others) except the joiner
        const playersToNotify = (game?.players || []).map(p => p.id).filter(id => id !== user.id);
        if (game?.hostId && !playersToNotify.includes(game.hostId)) playersToNotify.push(game.hostId);
        if (playersToNotify.length > 0) {
          notifyInsert(
            playersToNotify.filter(id => id !== user.id).map(id => ({ user_id: id, type: "player_joined", text: `${senderName} joined your game`, sub: game.title, game_id: gameId }))
          );
        }
        scheduleGameReminders(game);
        toast_(t("youJoined"));
      }
    } catch (e) {
      console.error("[handleJoin] failed", e, { gameId, leaving, userId: user?.id });
      reportError(e, { where: "handleJoin", gameId, leaving });
      toast_(t("somethingWentWrong"));
    }
    finally { joiningRef.current = false; setJoiningGameId(null); }
  };

  const handleWaitlist = async gameId => {
    if (!user || joiningRef.current) return; // auth guard + double-click guard
    joiningRef.current = true;
    setJoiningGameId(gameId);
    const gameForAction = [...games, ...pastGames].find(g => g.id === gameId);
    if (gameForAction && isPastGame(gameForAction)) {
      toast_("This game has already finished");
      joiningRef.current = false; setJoiningGameId(null);
      return;
    }
    const leavingWaitlist = waitlisted.includes(gameId);
    try {
      if (leavingWaitlist) {
        await supabase.from("game_players").delete().eq("game_id", gameId).eq("user_id", user.id);
        setWaitlisted(prev => prev.filter(id => id !== gameId));
        const updateGame = g => g.id === gameId ? { ...g, waitlist: (g.waitlist || []).filter(p => p.id !== user.id) } : g;
        setGames(prev => prev.map(updateGame));
        setSelectedGame(prev => prev?.id === gameId ? updateGame(prev) : prev);
        toast_(t("removedFromWaitlist"));
        // Notify host that player left the waitlist
        const game = games.find(g => g.id === gameId);
        if (game?.hostId && game.hostId !== user.id) {
          const senderName = userProfile?.name || user?.user_metadata?.name || "Someone";
          notifyInsert({ user_id: game.hostId, type: "waitlist_left", text: `${senderName} left the waitlist`, sub: game.title, game_id: gameId });
        }
      } else {
        const userName = userProfile?.name || user?.user_metadata?.name || "Player";
        await supabase.from("game_players").insert({ game_id: gameId, user_id: user.id, status: "waitlisted" });
        setWaitlisted(prev => [...prev, gameId]);
        const meWait = { id: user.id, name: userName, isMe: true };
        const updateGame = g => g.id === gameId ? { ...g, waitlist: [...(g.waitlist || []).filter(p => p.id !== user.id), meWait] } : g;
        setGames(prev => prev.map(updateGame));
        setSelectedGame(prev => prev?.id === gameId ? updateGame(prev) : prev);
        toast_(t("addedToWaitlist"));
        // Notify host that someone joined the waitlist
        const game = games.find(g => g.id === gameId);
        if (game?.hostId && game.hostId !== user.id) {
          notifyInsert({ user_id: game.hostId, type: "waitlist_joined", text: `${userName} joined your waitlist`, sub: game.title, game_id: gameId });
        }
      }
    } catch (e) {
      console.error("[handleWaitlist] failed", e, { gameId, leavingWaitlist, userId: user?.id });
      reportError(e, { where: "handleWaitlist", gameId, leavingWaitlist });
      toast_(t("somethingWentWrong"));
    }
    finally { joiningRef.current = false; setJoiningGameId(null); }
  };

  const handleFav = gameId => setFavs(prev => {
    const next = prev.includes(gameId) ? prev.filter(id => id !== gameId) : [...prev, gameId];
    localStorage.setItem("kickr_favs", JSON.stringify(next));
    return next;
  });

  const handleCancel = async (gameId, reason) => {
    try {
      const cancelledGame = [...games, ...pastGames].find(g => g.id === gameId);
      if (cancelledGame && isPastGame(cancelledGame)) {
        toast_("Past games cannot be cancelled");
        return;
      }

	      // Host ownership enforced in query — non-hosts get 0 rows affected (RLS will also block)
	      const { error: cancelErr } = await supabase
	        .from("games")
	        .update({ status: "cancelled", cancelled_at: new Date().toISOString(), cancelled_reason: reason })
	        .eq("id", gameId)
	        .eq("host_id", user.id);
	      if (cancelErr) throw cancelErr;

	      // Write cancellation notifications to DB for each joined/waitlisted player,
	      // excluding the host who initiated the manual cancel.
	      const playerCount = await notifyCancelledGameAudience(cancelledGame || { id: gameId }, reason || "The host cancelled this game.", { excludeUserId: user.id });
	      removeCancelledGameFromLocalState(gameId);

	      toast_(playerCount > 0 ? t("gameCancelledNotified", { n: playerCount, s: playerCount > 1 ? "s" : "" }) : t("gameCancelled"));
    } catch (e) {
      console.error("[cancelGame] failed", e, { gameId, reason });
      reportError(e, { where: "cancelGame", gameId });
      toast_(t("errorCancellingGame"));
    }
  };


  const handleCreate = async (form, repeatCount = 1) => {
    if (!user) { toast_(t("mustBeSignedIn")); return; }
    const userName = userProfile?.name || user?.user_metadata?.name || "Player";
    const spotsTotal = FORMAT_SPOTS[form.format] || parseInt(form.format.split("v")[0]) * 2;
    const minPlayers = Math.min(form.minPlayers || defaultMinPlayersForFormat(form.format), spotsTotal);

	    // Validate: block creating a game in the past
	    if (!isFutureGameDateTime(form.date, form.time)) {
	      toast_("Game time is in the past — pick a future time");
	      return;
	    }
    if (form.visibility === "community" && !form.communityId) {
      toast_("Pick a group before creating a group game");
      return;
    }

    // Build one game row per week in the series
    const gameRows = Array.from({ length: repeatCount }, (_, i) => {
      let gameDate = form.date;
      if (i > 0 && form.date) {
        // Use local date math to avoid UTC midnight timezone drift
        const [y, mo, dy] = form.date.split("-").map(Number);
        const dt = new Date(y, mo - 1, dy + i * 7);
        gameDate = `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, "0")}-${String(dt.getDate()).padStart(2, "0")}`;
      }
	      return {
	        title: form.title.trim(), format: form.format, level: form.level, location: form.location,
	        lat: form.lat, lng: form.lng, date: gameDate, hour: form.time,
	        backup_venue_id: form.backupVenueId ? String(form.backupVenueId) : null,
	        backup_location: form.backupLocation || null,
	        backup_lat: form.backupLat || null,
	        backup_lng: form.backupLng || null,
	        duration: `${form.duration} min`, price: "Free", description: form.description.trim(),
        spots_total: spotsTotal, host_id: user.id, host_name: userName,
        tags: [], status: "active", min_players: minPlayers,
        // Phase 1: write both visibility (new) and is_private (legacy mirror)
        // until older clients are off the field.
        visibility: form.visibility || (form.isPrivate ? "private" : "public"),
        is_private: form.visibility ? form.visibility === "private" : (form.isPrivate || false),
        community_id: form.visibility === "community" ? (form.communityId || null) : null,
        min_age: null,
        // Optional referee — null when host didn't nominate one
        referee_id: form.refereeId || null,
        referee_name: form.refereeId ? (form.refereeName || null) : null,
      };
    });

	    try {
	      // Batch insert all game rows at once (much faster than sequential for repeat series)
	      let { data: allCreated, error: insertError } = await supabase.from("games").insert(gameRows).select();
	      const missingBackupColumns = insertError && /backup_(venue_id|location|lat|lng)/i.test(`${insertError.message || ""} ${insertError.details || ""} ${insertError.hint || ""}`);
		      if (missingBackupColumns) {
		        const rowsWithoutBackup = gameRows.map(row => {
		          const cleanRow = { ...row };
		          delete cleanRow.backup_venue_id;
		          delete cleanRow.backup_location;
		          delete cleanRow.backup_lat;
		          delete cleanRow.backup_lng;
		          return cleanRow;
		        });
	        const retry = await supabase.from("games").insert(rowsWithoutBackup).select();
	        allCreated = retry.data;
	        insertError = retry.error;
		        if (!insertError) toast_(t("backupPitchSyncWarning"));
	      }
	      if (insertError) throw insertError;
      // Insert host into game_players for each created game
      for (const created of allCreated) {
        const { error: gpErr } = await supabase.from("game_players").upsert({ game_id: created.id, user_id: user.id, status: "confirmed" }, { onConflict: "game_id,user_id" });
        if (gpErr) console.error("[kickr] game_players insert failed:", gpErr);
      }

      // Notify the nominated referee, if any. Friend-scoped notifications RLS
      // already permits this (caller has friendship edge with referee).
      if (form.refereeId && form.refereeId !== user.id) {
        notifyInsert(allCreated.map(created => ({
          user_id: form.refereeId,
          type: "referee_assigned",
          read: false,
          game_id: created.id,
          text: t("notifRefereeAssignedText", { title: form.title.trim() }),
          sub: t("notifRefereeAssignedSub", { date: created.date || form.date, time: form.time }),
        })));
      }

      // Auto-add available players if triggered from availability group.
      // Respects spots_total: extras go to waitlist instead of overfilling.
      if (createPrefill?.autoInvite?.length && allCreated.length > 0) {
        const gameId = allCreated[0].id;
        const gameTime = form.time || "00:00";
        // spotsTotal was already derived from form.format above — reuse it so 3v3/7v7/11v11
        // honour their real capacity instead of falling back to 10.
        const { data: stillAvail } = await supabase.from("availability")
          .select("user_id").in("user_id", createPrefill.autoInvite)
          .eq("date", form.date).lte("time_from", gameTime).gte("time_to", gameTime);
        const validIds = (stillAvail || []).map(a => a.user_id).filter(id => id !== user.id);
        if (validIds.length > 0) {
          // Host already takes 1 slot
          const remainingConfirmed = Math.max(0, spotsTotal - 1);
          const confirmedIds = validIds.slice(0, remainingConfirmed);
          const waitlistIds = validIds.slice(remainingConfirmed);
          const rows = [
            ...confirmedIds.map(uid => ({ game_id: gameId, user_id: uid, status: "confirmed" })),
            ...waitlistIds.map(uid => ({ game_id: gameId, user_id: uid, status: "waitlisted" })),
          ];
          const { error: addErr } = await supabase.from("game_players").upsert(rows, { onConflict: "game_id,user_id" });
          if (addErr) console.warn("[autoInvite] upsert failed", addErr);
          notifyInsert([
            ...confirmedIds.map(uid => ({
              user_id: uid, type: "game_invite", read: false, game_id: gameId,
              text: `You've been added to "${form.title.trim()}"`,
              sub: `Based on your availability · ${form.date} ${gameTime}`,
            })),
            ...waitlistIds.map(uid => ({
              user_id: uid, type: "game_invite", read: false, game_id: gameId,
              text: `Waitlisted for "${form.title.trim()}"`,
              sub: `Game was full · ${form.date} ${gameTime}`,
            })),
          ]);
        }
      }

      // Build optimistic game objects for all created games
      const durationMins = parseInt(form.duration) || 60;
      const venueType = (() => { const v = venues.find(v => v.lat === form.lat && v.lng === form.lng); return v?.type || "Outdoor"; })();
      const makeTimeLabel = (dateStr) => {
        if (!dateStr) return "TBD";
        const [y, m, d] = dateStr.split("-").map(Number);
        const dt = new Date(y, m - 1, d);
        const today = new Date(); today.setHours(0,0,0,0);
        const tomorrow = new Date(today); tomorrow.setDate(today.getDate() + 1);
        if (dt.getTime() === today.getTime()) return t("todayLabel");
        if (dt.getTime() === tomorrow.getTime()) return t("tomorrowLabel");
        return dt.toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short" });
      };
      const optimisticGames = allCreated.map(created => {
        const gameDate = created.date || form.date;
        const endTime = gameDate && form.time
          ? new Date(`${gameDate}T${form.time}`).getTime() + durationMins * 60000
          : null;
        return {
	          id: created.id, title: form.title, format: form.format, level: form.level,
	          location: form.location, lat: form.lat, lng: form.lng, date: gameDate, hour: form.time,
	          backupVenueId: form.backupVenueId ? String(form.backupVenueId) : null,
	          backupLocation: form.backupLocation || null,
	          backupLat: form.backupLat || null,
	          backupLng: form.backupLng || null,
          visibility: created.visibility || form.visibility || (form.isPrivate ? "private" : "public"),
          communityId: created.community_id || (form.visibility === "community" ? (form.communityId || null) : null),
          isPrivate: created.visibility ? created.visibility === "private" : (created.is_private ?? !!form.isPrivate),
          duration: `${form.duration} min`, price: "Free", description: form.description || "",
          spotsTotal, spotsFilled: 1, minPlayers, needsMorePlayers: 1 < minPlayers,
          host: userName, hostAvatar: userName.slice(0, 2).toUpperCase(), hostId: user.id,
          tags: [], status: "active", endTime, time: makeTimeLabel(gameDate), venueType,
          players: [{ id: user.id, name: userName, avatar: userName.slice(0, 2).toUpperCase(), level: userProfile?.level || "Intermediate", position: userProfile?.position || "", isMe: true }],
          waitlist: [], chat: [],
        };
      });

      // Persist to localStorage so games survive reload even if RLS blocks SELECT
      const cache = (() => { try { return JSON.parse(localStorage.getItem("kickr_games_cache") || "[]"); } catch { return []; } })();
      const newCache = [...optimisticGames, ...cache.filter(g => !optimisticGames.find(og => og.id === g.id))].slice(0, 50);
      localStorage.setItem("kickr_games_cache", JSON.stringify(newCache));

      // Add to local state immediately — no waiting for DB
      setGames(prev => [...optimisticGames, ...prev]);
      setJoined(prev => [...prev, ...allCreated.map(c => c.id)]);

      setCreating(false);
      setCreatePrefill(null);
      setTab("my");
      setSelectedGame(optimisticGames[0] || null);
      // Schedule a reminder for the first game in the series (host is a player too)
      if (allCreated[0]) scheduleGameReminders({ ...allCreated[0], location: form.location });
      toast_(repeatCount > 1 ? t("gamesCreatedSeries", { n: repeatCount }) : t("gameCreated"));
      // Don't call loadGames here — it would overwrite the optimistic state
      // Realtime subscription handles syncing
    } catch (e) { toast_("Error: " + (e.message || "could not create game")); console.error(e); }
  };

	  const handleCloneNextWeek = async (sourceGame, inviteIds = []) => {
    if (!user) { toast_(t("mustBeSignedIn")); return; }
    if (!sourceGame) return;
    if (cloningGameId === sourceGame.id) return;
    setCloningGameId(sourceGame.id);
    const userName = userProfile?.name || user?.user_metadata?.name || "Player";
	    const nextDate = getNearestFutureSameWeekdayDate(sourceGame.date, sourceGame.hour || "18:00");
    const formatName = sourceGame.format || "5v5";
    const spotsTotal = sourceGame.spotsTotal || FORMAT_SPOTS[formatName] || parseFormatSide(formatName) * 2 || 10;
    const minPlayers = Math.min(sourceGame.minPlayers || defaultMinPlayersForFormat(formatName), spotsTotal);
    const row = {
      title: (sourceGame.title || "").trim() || `${formatName} at ${sourceGame.location || "pitch"}`,
      format: formatName,
      level: sourceGame.level || "All levels",
      location: sourceGame.location || "",
      lat: sourceGame.lat ?? null,
      lng: sourceGame.lng ?? null,
      date: nextDate,
      hour: sourceGame.hour || "18:00",
      backup_venue_id: sourceGame.backupVenueId ? String(sourceGame.backupVenueId) : null,
      backup_location: sourceGame.backupLocation || null,
      backup_lat: sourceGame.backupLat || null,
      backup_lng: sourceGame.backupLng || null,
      duration: sourceGame.duration || "60 min",
      price: sourceGame.price || "Free",
      description: sourceGame.description || "",
      spots_total: spotsTotal,
      host_id: user.id,
      host_name: userName,
      tags: sourceGame.tags || [],
      status: "active",
      min_players: minPlayers,
      visibility: sourceGame.visibility || (sourceGame.isPrivate ? "private" : "public"),
      is_private: sourceGame.visibility ? sourceGame.visibility === "private" : !!sourceGame.isPrivate,
      community_id: sourceGame.visibility === "community" ? (sourceGame.communityId || null) : null,
      min_age: sourceGame.minAge || null,
      referee_id: sourceGame.refereeId || null,
      referee_name: sourceGame.refereeId ? (sourceGame.refereeName || null) : null,
    };

    try {
      let { data: created, error: insertError } = await supabase.from("games").insert(row).select().single();
      const missingBackupColumns = insertError && /backup_(venue_id|location|lat|lng)/i.test(`${insertError.message || ""} ${insertError.details || ""} ${insertError.hint || ""}`);
      if (missingBackupColumns) {
        const cleanRow = { ...row };
        delete cleanRow.backup_venue_id;
        delete cleanRow.backup_location;
        delete cleanRow.backup_lat;
        delete cleanRow.backup_lng;
        const retry = await supabase.from("games").insert(cleanRow).select().single();
        created = retry.data;
        insertError = retry.error;
        if (!insertError) toast_(t("backupPitchSyncWarning"));
      }
      if (insertError) throw insertError;

	      const { error: gpErr } = await supabase.from("game_players").upsert(
	        { game_id: created.id, user_id: user.id, status: "confirmed" },
	        { onConflict: "game_id,user_id" }
	      );
	      if (gpErr) console.error("[kickr] clone game_players insert failed:", gpErr);
	      const uniqueInviteIds = [...new Set((inviteIds || []).filter(id => id && id !== user.id))];
	      if (uniqueInviteIds.length > 0) {
	        notifyInsert(uniqueInviteIds.map(uid => ({
	          user_id: uid,
	          type: "game_invite",
	          read: false,
	          game_id: created.id,
	          text: `${userName} invited you to a repeat game`,
	          sub: `${row.title} · ${row.date} ${row.hour}`,
	        })));
	      }

      const durationMins = parseInt(row.duration, 10) || 60;
      const endTime = row.date && row.hour ? new Date(`${row.date}T${row.hour}`).getTime() + durationMins * 60000 : null;
      const makeTimeLabel = (dateStr) => {
        const [y, m, d] = dateStr.split("-").map(Number);
        const dt = new Date(y, m - 1, d);
        const today = new Date(); today.setHours(0, 0, 0, 0);
        const tomorrow = new Date(today); tomorrow.setDate(today.getDate() + 1);
        if (dt.getTime() === today.getTime()) return t("todayLabel");
        if (dt.getTime() === tomorrow.getTime()) return t("tomorrowLabel");
        return dt.toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short" });
      };
      const clonedGame = {
        id: created.id,
        title: row.title,
        format: row.format,
        level: row.level,
        location: row.location,
        lat: row.lat,
        lng: row.lng,
        date: row.date,
        hour: row.hour,
        backupVenueId: row.backup_venue_id,
        backupLocation: row.backup_location,
        backupLat: row.backup_lat,
        backupLng: row.backup_lng,
        duration: row.duration,
        price: row.price,
        description: row.description,
        spotsTotal,
        spotsFilled: 1,
        minPlayers,
        needsMorePlayers: 1 < minPlayers,
        host: userName,
        hostAvatar: userName.slice(0, 2).toUpperCase(),
        hostId: user.id,
        tags: row.tags,
        status: "active",
        visibility: row.visibility,
        communityId: row.community_id,
        isPrivate: row.is_private,
        minAge: row.min_age,
        refereeId: row.referee_id,
        refereeName: row.referee_name,
        endTime,
        time: makeTimeLabel(row.date),
        venueType: sourceGame.venueType,
        players: [{ id: user.id, name: userName, avatar: userName.slice(0, 2).toUpperCase(), level: userProfile?.level || "Intermediate", position: userProfile?.position || "", isMe: true }],
        waitlist: [],
        chat: [],
      };

      const cache = (() => { try { return JSON.parse(localStorage.getItem("kickr_games_cache") || "[]"); } catch { return []; } })();
      localStorage.setItem("kickr_games_cache", JSON.stringify([clonedGame, ...cache.filter(g => g.id !== clonedGame.id)].slice(0, 50)));
      setGames(prev => [clonedGame, ...prev]);
      setJoined(prev => prev.includes(clonedGame.id) ? prev : [clonedGame.id, ...prev]);
      setSelectedGame(clonedGame);
      setTab("my");
      scheduleGameReminders({ ...created, location: row.location });
	      toast_(uniqueInviteIds.length > 0 ? `Game created · invited ${uniqueInviteIds.length}` : t("gameClonedForNextWeek"));
    } catch (e) {
      console.error("[cloneNextWeek] failed", e);
      toast_("Error: " + (e.message || "could not clone game"));
    } finally {
      setCloningGameId(null);
    }
  };

  const markNotifRead = (n) => {
    if (!n || n.read) return;
    setNotifs(prev => prev.map(x => x.id === n.id ? { ...x, read: true } : x));
    supabase.from("notifications").update({ read: true }).eq("id", n.id).eq("user_id", user.id).then(() => {}, () => {});
  };

  const resolveCommunityForChatNotif = async (n) => {
    const meta = parseChatNotificationSub(n?.sub);
    const communityId = meta?.communityId || null;
    const communityName = meta?.communityName || getLegacyCommunityNameFromChatNotifText(n?.text);
    const known = [...myCommunities, ...discoverCommunities].find(group => {
      if (!group) return false;
      if (communityId) return group.id === communityId;
      return communityName && String(group.name || "").toLowerCase() === communityName.toLowerCase();
    });
    if (known) return known;
    if (!communityId && !communityName) return null;
    let query = supabase
      .from("communities")
      .select("id, name, slug, description, avatar_url, cover_url, is_private, join_policy, city, tags, owner_id, member_count, created_at, verified")
      .limit(1);
    query = communityId ? query.eq("id", communityId) : query.ilike("name", communityName);
    const { data, error } = await query.maybeSingle();
    if (error) throw error;
    return data || null;
  };

  const resolveFriendIdForChatNotif = async (n) => {
    const meta = parseChatNotificationSub(n?.sub);
    if (meta?.senderId) return meta.senderId;
    const friendName = getLegacyFriendNameFromChatNotifText(n?.text);
    if (!friendName) return null;
    const { data, error } = await supabasePublic
      .from("profiles_public")
      .select("user_id")
      .ilike("name", friendName)
      .limit(1)
      .maybeSingle();
    if (error) throw error;
    return data?.user_id || null;
  };

	  const handleReplyNotif = async (n) => {
	    if (!user || !n) return;
	    markNotifRead(n);
	    try {
	      const meta = parseChatNotificationSub(n?.sub);
	      const replyKind = n.type === "chat_message" ? "community_chat" : n.type === "friend_message" ? "friend_chat" : meta?.kind;
	      if (replyKind === "community_chat") {
	        const community = await resolveCommunityForChatNotif(n);
	        if (!community) { toast_("Could not open this group chat"); return; }
	        setOpenedCommunityInitialView("chat");
	        setOpenedCommunity(community);
	        setShowNotifs(false);
	        setTab("communities");
	        return;
	      }
	      if (replyKind === "friend_chat") {
	        const friendId = await resolveFriendIdForChatNotif(n);
	        if (!friendId) { toast_("Could not open this friend chat"); return; }
	        setFriendsInviteGameId(null);
        setFriendsInitialChatUserId(friendId);
        setShowFriendsScreen(true);
        setShowNotifs(false);
      }
    } catch (err) {
      console.warn("[notification.reply] failed", err);
      toast_("Could not open chat");
    }
  };

	  const handleNotifTap = async n => {
	    if (n.type === "friend_request" || n.type === "friend_accepted") return; // handled inline
	    const chatMeta = parseChatNotificationSub(n?.sub);
	    if (n.type === "chat_message" || n.type === "friend_message" || (n.type === "mention" && !!chatMeta?.kind)) {
	      handleReplyNotif(n);
	      return;
	    }
	    markNotifRead(n);
	    if (n.type === "game_review") {
	      setShowNotifs(false);
	      setTab("my");
	      setMyGamesView("past");
	      if (n.gameId) setReviewGameIdToOpen(n.gameId);
	      return;
	    }
    if (n.type === "availability_group") {
      setShowNotifs(false);
      try {
        const d = JSON.parse(n.sub);
        setCreatePrefill({ source: "availability", date: d.date, hour: d.hour, autoInvite: d.playerIds });
        setCreating(true);
      } catch { setTab("discover"); setViewMode("list"); }
      return;
    }
    if (COMMUNITY_NOTIFICATION_TYPES.has(n.type) && UUID_RE.test(String(n.sub || ""))) {
      setShowNotifs(false);
      try {
        const community = await resolveCommunityForChatNotif({ sub: JSON.stringify({ [CHAT_NOTIFICATION_META_KEY]: true, communityId: n.sub }) });
        if (community) {
          setOpenedCommunityInitialView(n.type.includes("join") ? "members" : "about");
          setOpenedCommunity(community);
          setTab("communities");
        } else {
          toast_("Could not open this group");
        }
      } catch (err) {
        console.warn("[notification.community] failed", err);
        toast_("Could not open this group");
      }
      return;
    }
    const g = games.find(x => x.id === n.gameId);
    if (g) { setSelectedGame(g); setShowNotifs(false); setTab("discover"); }
  };
  const handleMarkAllRead = async () => {
    if (!user) return;
    const prevNotifs = notifs;
    setNotifs(prev => prev.map(n => ({ ...n, read: true })));
    try {
      const { error } = await supabase.from("notifications").update({ read: true }).eq("user_id", user.id).eq("read", false);
      if (error) {
        console.warn("[markAllRead] failed", error);
        setNotifs(prevNotifs); // revert optimistic update
        toast_(t("somethingWentWrong"));
      }
    } catch (err) {
      console.warn("[markAllRead] threw", err);
      setNotifs(prevNotifs);
    }
  };

  // Helper: only delete notification from DB if it's a real DB row (not a synthetic in-memory id)
  const deleteDbNotif = (notifId) => {
    const id = String(notifId || "");
    const isDbId = /^\d+$/.test(id) || UUID_RE.test(id);
    if (isDbId) {
      // Always include user_id ownership check — prevents deleting another user's notification by id
      supabase.from("notifications").delete().eq("id", notifId).eq("user_id", user.id).then(() => {}, () => {});
    }
  };

  const handleAcceptFriendNotif = async (requesterId, notifId) => {
    if (!requesterId || String(requesterId) === String(user?.id || "")) {
      setNotifs(prev => prev.filter(n => n.id !== notifId));
      deleteDbNotif(notifId);
      toast_("Request no longer available");
      return;
    }
    const { data } = await supabase.from("friendships").select("id, status").eq("requester_id", requesterId).eq("recipient_id", user.id).maybeSingle();
    if (!data) { toast_("Request no longer available"); return; }
    if (data.status === "accepted") { toast_("Already friends"); setNotifs(prev => prev.filter(n => n.id !== notifId)); deleteDbNotif(notifId); return; }
    // Ownership: include recipient_id guard so only the intended recipient can accept
    const { data: updated, error } = await supabase.from("friendships").update({ status: "accepted" }).eq("id", data.id).eq("recipient_id", user.id).eq("status", "pending").select("id");
    if (error) { toast_("Could not accept request"); return; }
    if (!updated || updated.length === 0) { toast_("Already accepted"); setNotifs(prev => prev.filter(n => n.id !== notifId)); deleteDbNotif(notifId); return; }
    setNotifs(prev => prev.filter(n => n.id !== notifId));
    deleteDbNotif(notifId);
    const myName = userProfile?.name || user?.user_metadata?.name || "Someone";
    notifyInsert({ user_id: requesterId, type: "friend_accepted", text: `${myName} accepted your friend request`, sub: "You are now friends!", game_id: null, read: false });
    setFriendsVersion(v => v + 1);
    toast_("Friend added!");
  };

  const handleDeclineFriendNotif = async (requesterId, notifId) => {
    if (!requesterId || String(requesterId) === String(user?.id || "")) {
      setNotifs(prev => prev.filter(n => n.id !== notifId));
      deleteDbNotif(notifId);
      return;
    }
    try {
      await supabase.from("friendships").delete().eq("requester_id", requesterId).eq("recipient_id", user.id);
    } catch (err) {
      console.warn("[friend.decline] failed", err);
    }
    setNotifs(prev => prev.filter(n => n.id !== notifId));
    deleteDbNotif(notifId);
  };

  const handleAcceptGameInvite = (gameId, notifId) => {
    setNotifs(prev => prev.filter(n => n.id !== notifId));
    deleteDbNotif(notifId);
    const g = games.find(x => x.id === gameId);
    if (g) { setSelectedGame(g); setShowNotifs(false); setTab("discover"); }
  };

  const handleDeclineGameInvite = (notifId) => {
    setNotifs(prev => prev.filter(n => n.id !== notifId));
    deleteDbNotif(notifId);
  };

  const handleDeleteNotif = (notifId) => {
    setNotifs(prev => prev.filter(n => n.id !== notifId));
    deleteDbNotif(notifId);
  };

  const handleClearAllNotifs = async () => {
    setNotifs([]);
    if (!user) return;
    // Bump the cleared timestamp so any DB notification with created_at <= now is hidden on next load
    localStorage.setItem(`kickr_notifs_cleared_${user.id}`, String(Date.now()));
    // Also wipe in-memory generator caches so client-synthesized notifs don't instantly re-appear
    seenSoonNotifsRef.current = new Set();
    prevGamesRef.current = games; // treat current state as the new baseline
    // Mark the re-engagement nudge as seen for the next 7 days so it can't re-fire immediately
    localStorage.setItem(`kickr_reengaged_${user.id}`, String(Date.now()));
    // DB delete with session-refresh retry (same pattern as invite/chat send)
    let { error } = await supabase.from("notifications").delete().eq("user_id", user.id);
    if (error) {
      const { error: refreshErr } = await supabase.auth.refreshSession();
      if (!refreshErr) {
        ({ error } = await supabase.from("notifications").delete().eq("user_id", user.id));
      }
    }
    if (error) console.error("[clearAllNotifs] DB delete failed", error);
  };

  // Re-engagement notification: nudge inactive players
  useEffect(() => {
    if (!user || !games.length) return;
    const key = `kickr_reengaged_${user.id}`;
    const lastSent = localStorage.getItem(key);
    if (lastSent && Date.now() - parseInt(lastSent) < 7 * 24 * 60 * 60 * 1000) return; // max once/week
    // If the user just cleared all notifications, don't push the re-engagement nudge either
    const clearedAt = parseInt(localStorage.getItem(`kickr_notifs_cleared_${user.id}`) || "0");
    if (clearedAt && Date.now() - clearedAt < 60000) return;
    // Check if user has joined any game in the last 14 days
    const cutoff = Date.now() - 14 * 24 * 60 * 60 * 1000;
    const hasRecentGame = games.some(g => {
      if (!joined.includes(g.id) && g.hostId !== user.id) return false;
      const t = g.date ? new Date(`${g.date}T${g.hour || "00:00"}`).getTime() : 0;
      return t > cutoff;
    });
    if (!hasRecentGame) {
      localStorage.setItem(key, String(Date.now()));
      setNotifs(prev => [{
        id: `reengagement-${Date.now()}`,
        type: "reengagement",
        text: "Time to get back on the pitch!",
        sub: "You haven't played in a while — find a game nearby.",
        gameId: null,
        time: "Today",
        read: false,
      }, ...prev]);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, games.length]);

  // Unread badge respects Settings → Notifications toggles so muted categories
  // don't light up the bell after the panel hides them.
  const unread = (() => {
    const prefs = getNotifPrefs();
    const FRIEND_TYPES = new Set(["friend_request", "friend_accepted"]);
	    const CHAT_TYPES = new Set(["chat_message", "friend_message", "mention"]);
    return notifs.filter(n => {
      if (isLaunchQaNotification(n)) return false;
      if (n.read) return false;
      if (!prefs.friends && FRIEND_TYPES.has(n.type)) return false;
      if (!prefs.chat && CHAT_TYPES.has(n.type)) return false;
      return true;
    }).length;
  })();

  const dateRangeActive = !isDefaultFilterDateRange(dateRange);
  const activeFilterCount = [filter !== "All", formatFilter !== "All", dateRangeActive, radiusKm !== null, sortBy !== "smart", showFullGames].filter(Boolean).length;
  const hasActiveDiscoverFilters = !!search || activeFilterCount > 0;
  const resetDiscoverFilters = () => {
    setSearch("");
    setFilter("All");
    setFormatFilter("All");
    setDateRange([...FILTER_DATE_DEFAULT_RANGE]);
    setRadiusKm(null);
    setSortBy("smart");
    setShowFullGames(false);
  };

  const filtered = React.useMemo(() => games
    .map(g => ({ ...g, distance: userLocation ? getDistance(userLocation.lat, userLocation.lng, g.lat, g.lng) : null }))
    .filter(g => {
      const matchLevel = filter === "All" || g.level === filter;
      const matchFormat = formatFilter === "All" || g.format === formatFilter;
      const q = search.toLowerCase();
      const matchSearch = !q
        || g.title.toLowerCase().includes(q)
        || g.location.toLowerCase().includes(q)
        || g.level.toLowerCase().includes(q)
        || (g.host || "").toLowerCase().includes(q)
        || (g.tags || []).some(t => t.toLowerCase().includes(q))
        || (g.players || []).some(p => `${p.name || ""} ${p.level || ""} ${p.position || ""}`.toLowerCase().includes(q));
      const matchRadius = radiusKm === null || !userLocation || g.distance == null || isNaN(g.distance) || g.distance <= radiusKm;
      const dayOffset = getDateOffset(g.date);
      const matchDate = !dateRangeActive || (dayOffset != null && dayOffset >= dateRange[0] && dayOffset <= dateRange[1]);
      const isFull = g.spotsFilled >= g.spotsTotal;
      const matchCapacity = showFullGames || !isFull || g.hostId === user?.id || joined.includes(g.id) || waitlisted.includes(g.id);
      // Hide private games from people who didn't create/join them
      if (g.isPrivate && g.hostId !== user?.id && !joined.includes(g.id) && !waitlisted.includes(g.id)) return false;
      // Hide community-scoped games from non-members. RLS already gates the
      // SELECT, but a user can still be holding a stale game from when they
      // were a member, or from a public→community flip mid-session — defence
      // in depth so the Discover feed never leaks community context.
      if (g.visibility === "community" && g.hostId !== user?.id && !joined.includes(g.id) && !waitlisted.includes(g.id)) {
        if (!g.communityId) return false;
        if (!myCommunities.some(c => c.id === g.communityId)) return false;
      }
      return matchLevel && matchFormat && matchSearch && matchDate && matchRadius && matchCapacity;
    })
    .sort((a, b) => {
      if (sortBy === "smart") {
        const scoreDiff = smartGameScore(b, {
          joined: joined.includes(b.id),
          waitlisted: waitlisted.includes(b.id),
          host: b.hostId === user?.id,
        }) - smartGameScore(a, {
          joined: joined.includes(a.id),
          waitlisted: waitlisted.includes(a.id),
          host: a.hostId === user?.id,
        });
        if (Math.abs(scoreDiff) > 0.01) return scoreDiff;
        return gameStartMs(a) - gameStartMs(b);
      }
      if (sortBy === "distance" && a.distance !== null) return a.distance - b.distance;
      if (sortBy === "time") return new Date(`${a.date}T${a.hour||"00:00"}`) - new Date(`${b.date}T${b.hour||"00:00"}`);
      return 0;
    })
  , [games, filter, formatFilter, dateRange, dateRangeActive, search, radiusKm, sortBy, userLocation, user, joined, waitlisted, myCommunities, showFullGames]);

	  const myUpcomingGames = games.filter(g => g.hostId === user?.id || joined.includes(g.id));
	  const myPastGames = pastGames.filter(g => g.hostId === user?.id || joined.includes(g.id));
	  const savedGames = games.filter(g => favs.includes(g.id));
	  const searchPanelOpen = search.trim().length >= 2 && !showNotifs;
  const availabilityMomentumPlayers = myAvailability ? nearbyAvailable : nearbyAvailabilityPreview;
  const availabilityMomentumCount = myAvailability ? nearbyAvailable.length + 1 : nearbyAvailabilityPreview.length;
  const hasAvailabilityMomentum = availabilityMomentumCount >= 2;
  const availabilityMapTitle = myAvailability
    ? `${availabilityLabel(myAvailability)} ${formatAvailabilityTimeRange(myAvailability)}`
    : hasAvailabilityMomentum
      ? t("playersFreeNearbyShort", { n: availabilityMomentumCount })
      : t("imAvailable");
  const availabilityMapSub = !myAvailability && hasAvailabilityMomentum ? t("joinNearbyPlayers") : "";

	  // Skeleton — colours derived from the current theme so dark mode doesn't
  // glow with hard-coded light grays. Subtle shimmer at 1.4s.
  const SkeletonCard = () => {
    const skel0 = C.isDark ? "#1a2540" : "#eef2f5";
    const skel1 = C.isDark ? "#243054" : "#e3e8ed";
    return (
      <div style={{ ...card, padding: SPACE[4], overflow: "hidden" }}>
        <style>{`@keyframes shimmer{0%{background-position:-300px 0}100%{background-position:300px 0}}`}</style>
        <div style={{ display: "flex", gap: SPACE[2], marginBottom: SPACE[3] }}>
          <div style={{ height: 18, width: 56, borderRadius: 999, background: `linear-gradient(90deg, ${skel0} 25%, ${skel1} 50%, ${skel0} 75%)`, backgroundSize: "400px 100%", animation: "shimmer 1.4s infinite linear" }} />
          <div style={{ height: 18, width: 44, borderRadius: 999, background: `linear-gradient(90deg, ${skel0} 25%, ${skel1} 50%, ${skel0} 75%)`, backgroundSize: "400px 100%", animation: "shimmer 1.4s infinite linear" }} />
        </div>
        <div style={{ height: 18, width: "70%", marginBottom: SPACE[3], borderRadius: 6, background: `linear-gradient(90deg, ${skel0} 25%, ${skel1} 50%, ${skel0} 75%)`, backgroundSize: "400px 100%", animation: "shimmer 1.4s infinite linear" }} />
        {[55, 65, 45].map((w, i) => (
          <div key={i} style={{ height: 12, width: `${w}%`, marginBottom: SPACE[1] + 2, borderRadius: 4, background: `linear-gradient(90deg, ${skel0} 25%, ${skel1} 50%, ${skel0} 75%)`, backgroundSize: "400px 100%", animation: "shimmer 1.4s infinite linear" }} />
        ))}
        <div style={{ height: 6, width: "100%", marginTop: SPACE[3], borderRadius: 999, background: skel0 }} />
      </div>
    );
  };

  const renderList = list => (
    <div style={{ display: "flex", flexDirection: "column", gap: SPACE[3] }}>
      {gamesLoading
        ? [1,2,3].map(i => <SkeletonCard key={i} />)
        : list.length === 0
          ? <div style={{ textAlign: "center", padding: `${SPACE[7]}px ${SPACE[5]}px`, background: C.surface, borderRadius: RADIUS.lg, boxShadow: SHADOW.sm }}>
              <div style={{ width: 56, height: 56, borderRadius: "50%", background: C.bg, color: C.inkFaint, display: "flex", alignItems: "center", justifyContent: "center", margin: `0 auto ${SPACE[4]}px` }}>
                <Search size={24} {...ICON} />
              </div>
              {hasActiveDiscoverFilters
                ? <>
                    <div style={{ ...TYPE.h2, color: C.ink, marginBottom: SPACE[1] + 2 }}>No games match your filters</div>
                    <p style={{ ...TYPE.body, color: C.inkMid, marginBottom: SPACE[4] }}>Try adjusting your search or filters.</p>
                    <button onClick={resetDiscoverFilters} style={{ ...btnSecondary, padding: `${SPACE[3]}px ${SPACE[5]}px`, borderRadius: RADIUS.pill }}>Clear filters</button>
                  </>
                : <>
                    <div style={{ ...TYPE.h2, color: C.ink, marginBottom: SPACE[1] + 2 }}>No games yet</div>
                    <p style={{ ...TYPE.body, color: C.inkMid, marginBottom: SPACE[5] }}>Be the first to create a game in Oslo.</p>
                    <button onClick={() => { if (!user) { setShowAuthModal(true); return; } setCreating(true); }} style={{ ...btnPrimary, padding: `${SPACE[3]}px ${SPACE[5] + 4}px`, borderRadius: RADIUS.pill, boxShadow: SHADOW.sm }}>Create a game</button>
                  </>
              }
            </div>
          : list.map(g => <GameCard key={g.id} game={g} joined={joined.includes(g.id)} waitlisted={waitlisted.includes(g.id)} fav={favs.includes(g.id)} isHost={g.hostId === user?.id} onTap={() => setSelectedGame(g)} onFav={() => handleFav(g.id)} onJoin={handleJoin} onWaitlist={handleWaitlist} isJoining={joiningGameId === g.id} />)
      }
    </div>
  );

  // Gates
  if (authLoading) return (
    <ThemeCtx.Provider value={theme}>
      <div style={{ height: "100vh", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", background: C.bg, gap: 16 }}>
        <KickrLoader />
      </div>
    </ThemeCtx.Provider>
  );

  const needsLegalAcceptance = legalSchemaReady && !hasAcceptedCurrentLegal(userProfile);

	  if (user && profileLoaded && (!userProfile?.name || !userProfile?.level || (usernameSchemaReady && !userProfile?.username) || needsLegalAcceptance)) return (
	    <ThemeCtx.Provider value={theme}>
	      <div style={{ height: "100vh", fontFamily: "'Inter', system-ui, sans-serif" }}>
	        <WelcomeFlow user={user} profile={userProfile} requireUsername={usernameSchemaReady} requireLegalAcceptance={needsLegalAcceptance} onDone={data => { setUserProfile(data); }} />
	      </div>
	    </ThemeCtx.Provider>
	  );

  if (user && showAdminDashboard && !profileLoaded) return (
    <ThemeCtx.Provider value={theme}>
      <div style={{ height: "100vh", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", background: C.bg, gap: 16 }}>
        <KickrLoader />
      </div>
    </ThemeCtx.Provider>
  );

  if (user && showAdminDashboard) return (
    <ThemeCtx.Provider value={theme}>
      <AdminDashboard
        user={user}
        userProfile={userProfile}
        showToast={toast_}
        onClose={() => setShowAdminDashboard(false)}
        onSignOut={handleSignOut}
        onVenuesChanged={loadVenues}
      />
      {toastQueue.length > 0 && <div key={toastQueue[0].id} onClick={() => setToastQueue(q => q.slice(1))} style={{ position: "fixed", bottom: "calc(24px + env(safe-area-inset-bottom, 0px))", left: "50%", transform: "translateX(-50%)", background: C.accent, color: C.accentText, padding: "10px 20px", borderRadius: 8, fontSize: 13, fontWeight: 600, zIndex: 400, whiteSpace: "nowrap", boxShadow: `0 4px 20px ${C.accent}44`, animation: "toastIn 0.25s ease", cursor: "pointer" }}>{toastQueue[0].msg}</div>}
    </ThemeCtx.Provider>
  );

	  if (creating) return (
    <ThemeCtx.Provider value={theme}>
      <div style={{ height: "100vh", fontFamily: "'Inter', system-ui, sans-serif" }}>
        <CreateScreen onBack={() => { setCreating(false); setCreatePrefill(null); }} onCreate={handleCreate} onViewGame={g => { setCreating(false); setCreatePrefill(null); setSelectedGame(g); }} prefill={createPrefill} userLocation={userLocation} existingGames={games} venues={venues} myCommunities={myCommunities} currentUser={user} />
      </div>
    </ThemeCtx.Provider>
  );

  return (
    <ThemeCtx.Provider value={theme}>
    {/* Banned/suspended gate. Renders nothing for healthy profiles; renders
        a full-screen blocking screen otherwise. The DB-side is_active_user
        already prevents writes — this is the user-facing explanation. */}
    <AccountStatusGate
      profile={userProfile}
      onSignOut={async () => { await supabase.auth.signOut(); setUser(null); setUserProfile(null); }}
    />
    <div style={{ height: "100vh", display: "flex", flexDirection: "column", background: C.bg, overflow: "hidden", fontFamily: "'Inter', system-ui, sans-serif" }}>
      <style>{`
        /* Inter is loaded once in index.html with full weight range (400-800).
           This block was loading it again at limited weights, which forced
           the browser to synthesize 700/800 from 600 and made the type look
           weak. Removed. */
        @keyframes slideUp{from{transform:translateY(100%)}to{transform:translateY(0)}}
        @keyframes slideRight{from{transform:translateX(100%)}to{transform:translateX(0)}}
        @keyframes fadeIn{from{opacity:0;transform:translateY(4px)}to{opacity:1;transform:translateY(0)}}
        @keyframes toastIn{from{opacity:0;transform:translateX(-50%) translateY(8px)}to{opacity:1;transform:translateX(-50%) translateY(0)}}
        @keyframes pulse{0%,100%{opacity:1}50%{opacity:0.4}}
        @keyframes spin{to{transform:rotate(360deg)}}
        *{box-sizing:border-box;-webkit-tap-highlight-color:transparent}
        button,a,[role="button"]{touch-action:manipulation}
        input:focus,textarea:focus{border-color:${C.ink}!important;outline:none}
        button:focus-visible,a:focus-visible,[role="button"]:focus-visible{outline:2px solid ${C.accent};outline-offset:2px}
        select{appearance:none;-webkit-appearance:none;background-image:url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='6' viewBox='0 0 10 6'%3E%3Cpath d='M1 1l4 4 4-4' stroke='%236b6b6b' stroke-width='1.5' fill='none' stroke-linecap='round'/%3E%3C/svg%3E");background-repeat:no-repeat;background-position:right 10px center;padding-right:28px!important;}
        ::-webkit-scrollbar{width:3px} ::-webkit-scrollbar-thumb{background:${C.border};border-radius:2px}
        .mapboxgl-ctrl-logo{display:none!important}
        button{font-family:inherit}
      `}</style>

      {/* Top bar — wordmark on the left, notification bell on the right. Full
          width so the chrome reads as native-app rather than a phone preview
          centred on a desktop. */}
      <div style={{
        paddingTop: "calc(14px + env(safe-area-inset-top, 44px))",
        paddingBottom: SPACE[3],
        paddingLeft: SPACE[4], paddingRight: SPACE[4],
        display: "flex", alignItems: "center", justifyContent: "space-between",
        flexShrink: 0, background: C.bg,
      }}>
        <KickrWordmark onClick={() => { setTab("discover"); setSelectedGame(null); setSearch(""); }} />
        <button
          aria-label={unread > 0 ? `${unread} unread notification${unread > 1 ? "s" : ""}` : "Notifications"}
          onClick={() => setShowNotifs(v => !v)}
          style={{
            position: "relative",
            width: 40, height: 40, borderRadius: "50%",
            background: showNotifs ? C.surface : "transparent", border: "none",
            cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center",
            color: C.ink, transition: "background 0.15s",
          }}
          onMouseEnter={e => { if (!showNotifs) e.currentTarget.style.background = C.surface; }}
          onMouseLeave={e => { if (!showNotifs) e.currentTarget.style.background = "transparent"; }}
        >
          <Bell size={20} {...ICON} color={showNotifs || unread > 0 ? C.ink : C.inkMid} />
          {unread > 0 && (
            <span style={{
              position: "absolute", top: 6, right: 6,
              minWidth: 16, height: 16, padding: "0 4px", borderRadius: 999,
              background: C.danger, color: "#fff", fontSize: 9, fontWeight: 700,
              display: "flex", alignItems: "center", justifyContent: "center",
              boxShadow: `0 0 0 2px ${C.bg}`,
            }}>{unread > 99 ? "99+" : unread}</span>
          )}
        </button>
      </div>

      {/* Body */}
      <div style={{ flex: 1, overflow: "hidden", display: "flex", flexDirection: "column" }}>
        {tab === "discover" && (
          <div style={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden" }}>
	            {/* Search + filter bar */}
	            {(() => {
	              return (
                <div style={{ padding: `${SPACE[2] + 2}px ${SPACE[4]}px ${SPACE[3]}px`, display: "flex", gap: SPACE[2], alignItems: "center", background: C.bg, position: "relative", zIndex: searchPanelOpen ? 120 : 5 }}>
                  {/* Search */}
                  <div style={{ position: "relative", flex: 1 }}>
                    <Search size={15} {...ICON} color={C.inkFaint} style={{ position: "absolute", left: 14, top: "50%", transform: "translateY(-50%)", pointerEvents: "none" }} />
                    <input value={search} onChange={e => { const v = e.target.value; setSearch(v); if (showNotifs) setShowNotifs(false); clearTimeout(searchDebounceRef.current); if (v.trim().length >= 2) { searchDebounceRef.current = setTimeout(async () => { const { data, error } = await supabasePublic.from("profiles_public").select("id,user_id,name,level,position,avatar_url").ilike("name", `%${v.trim()}%`).limit(4); setSearchPlayerResults(error ? [] : (data || []).map(p => ({ ...p, id: p.user_id || p.id }))); }, 300); } else { setSearchPlayerResults([]); } }} onFocus={() => { if (showNotifs) setShowNotifs(false); }} placeholder={t("searchPlaceholder")} style={{ ...inputBase, paddingLeft: 38, borderRadius: RADIUS.pill, background: C.surface }} />
                    {/* Suggestions dropdown — grouped by type (Spotify/Airbnb
                        pattern). Section headers make flat results scannable
                        once a query matches across games, venues, and people. */}
                    {searchPanelOpen && (() => {
                      const q = search.toLowerCase().trim();
                      const seenGame = new Set(), seenVenue = new Set(), seenPlayer = new Set();
                      const gameSug = [], venueSug = [], playerSug = [];
                      // Games — titles first, then locations (deduped by title)
                      games.filter(g => g.title?.toLowerCase().includes(q) || (g.players || []).some(p => (p.name || "").toLowerCase().includes(q))).slice(0, 3).forEach(g => {
                        if (!seenGame.has(g.title)) { seenGame.add(g.title); gameSug.push({ label: g.title, sub: g.location, action: () => { setSearch(""); setViewMode("map"); if (g.lat && g.lng) { mapRef.current?.flyTo({ center: [+g.lng, +g.lat], zoom: 15, duration: 900 }); } setSelectedGame(g); } }); }
                      });
                      // Venues — named pitches, then raw game-location strings
                      venues.filter(v => v.name.toLowerCase().includes(q)).slice(0, 4).forEach(v => {
                        if (!seenVenue.has(v.name)) { seenVenue.add(v.name); venueSug.push({ label: v.name, sub: v.area || v.address, action: () => { setSearch(""); setViewMode("map"); if (v.lat && v.lng) { mapRef.current?.flyTo({ center: [+v.lng, +v.lat], zoom: 16, duration: 900 }); } setSelectedVenue(v); } }); }
                      });
                      games.filter(g => g.location?.toLowerCase().includes(q)).slice(0, 2).forEach(g => {
                        if (!seenVenue.has(g.location)) { seenVenue.add(g.location); venueSug.push({ label: g.location, action: () => { setSearch(g.location); setViewMode("map"); if (g.lat && g.lng) { mapRef.current?.flyTo({ center: [+g.lng, +g.lat], zoom: 14, duration: 900 }); } } }); }
                      });
                      // Players — from server-side profile lookup
                      searchPlayerResults.forEach(p => {
                        if (!seenPlayer.has(p.id)) { seenPlayer.add(p.id); playerSug.push({ label: p.name, sub: [p.position, p.level].filter(Boolean).join(" · "), action: () => { setSearch(""); setSearchPlayerCardFriendStatus(null); setSearchPlayerCardShowSharedGames(false); setSearchPlayerCard(p); } }); }
                      });
                      const groups = [
                        { key: "games",   title: t("games"),   Icon: CircleDot, items: gameSug },
                        { key: "venues",  title: t("venues"),  Icon: MapPin,     items: venueSug },
                        { key: "players", title: t("players"), Icon: User,       items: playerSug },
                      ].filter(g => g.items.length);
                      if (!groups.length) return null;
                      return (
                        <div style={{ position: "absolute", top: "calc(100% + 6px)", left: 0, right: 0, background: C.surface, borderRadius: 12, zIndex: 200, overflow: "hidden", boxShadow: SHADOW.md }}>
                          {groups.map((grp, gi) => (
                            <div key={grp.key} style={{ borderTop: gi > 0 ? `1px solid ${C.border}` : "none" }}>
                              <div style={{ ...T.label, padding: "10px 16px 6px", background: C.bg }}>{grp.title}</div>
                              {grp.items.slice(0, 4).map((s, i) => (
                                <button key={i} type="button" onMouseDown={e => e.preventDefault()} onClick={s.action} style={{ width: "100%", border: "none", background: "transparent", display: "flex", alignItems: "center", gap: 12, padding: "12px 16px", cursor: "pointer", transition: "background 0.12s", textAlign: "left", font: "inherit" }}
                                  onMouseEnter={e => e.currentTarget.style.background = C.bg}
                                  onMouseLeave={e => e.currentTarget.style.background = "transparent"}
                                >
                                  <div style={{ width: 32, height: 32, borderRadius: "50%", background: C.bg, color: C.inkMid, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                                    <grp.Icon size={15} {...ICON} />
                                  </div>
                                  <div style={{ flex: 1, minWidth: 0 }}>
                                    <div style={{ fontSize: 14, fontWeight: 500, color: C.ink, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{s.label}</div>
                                    {s.sub && <div style={{ fontSize: 12, color: C.inkFaint, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{s.sub}</div>}
                                  </div>
                                </button>
                              ))}
                            </div>
                          ))}
                        </div>
                      );
                    })()}
                  </div>
                  {/* Filter button — same height as the search input, pill
                      shape so the bar reads as a single instrument cluster.
                      Active count moves to a small accent dot rather than
                      a count chip — quieter, faster to scan. */}
	                  <button onClick={() => setShowFilters(true)} style={uiIconControl(ui, activeFilterCount > 0)}
                    onMouseEnter={e => e.currentTarget.style.transform = "translateY(-1px)"}
                    onMouseLeave={e => e.currentTarget.style.transform = ""}
                    aria-label={t("filters")}
                  >
	                    <Filter size={15} {...ICON} strokeWidth={2} color={activeFilterCount > 0 ? C.accent : C.inkMid} />
	                    {activeFilterCount > 0 && (
	                      <span style={uiFloatingBadge(ui, C.bg)}>{activeFilterCount}</span>
                    )}
                  </button>
                  {/* List/Map segmented toggle — iOS-style, smaller and tighter */}
                  <div style={uiSegmentShell(ui)}>
                    {[{v:"map",label:"Map"},{v:"list",label:"List"}].map(m => (
                      <button key={m.v} aria-label={`${m.label} view`} onClick={() => setViewMode(m.v)} style={uiSegmentButton(ui, viewMode === m.v, { padding: `${SPACE[2] - 1}px ${SPACE[3] + 2}px` })}>{m.label}</button>
                    ))}
                  </div>
                </div>
              );
            })()}

	            {/* Filter bottom sheet */}
	            {showFilters && (() => {
	              // Segmented toggle — active state uses brand-green so filter
	              // sheets feel like part of kickr, not a separate settings UI.
              const ToggleGroup = ({ value, onChange, options }) => (
                <div style={{ ...uiSegmentShell(ui, { display: "grid", gridTemplateColumns: `repeat(${options.length}, 1fr)` }), padding: 4 }}>
                  {options.map(({ key, label }) => {
                    const active = value === key;
                    return (
                      <button key={key} onClick={() => onChange(active ? null : key)} style={{ ...uiSegmentButton(ui, active, { minWidth: 0, minHeight: 36, padding: `0 ${SPACE[1]}px`, activeWeight: 800, inactiveWeight: 650 }), whiteSpace: "nowrap" }}>
                        {label}
                      </button>
                    );
                  })}
                </div>
              );
              // FilterRow now expects a Lucide component, not an emoji string.
              // Renders the icon at 14px in the same muted ink colour as the
              // label so the row reads as a single visual unit.
              const FilterRow = ({ Icon, title, children }) => (
                <div>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
                    {Icon && <Icon size={14} {...ICON} color={C.inkMid} />}
                    <span style={{ fontSize: 12, fontWeight: 600, color: C.inkMid, textTransform: "uppercase", letterSpacing: "0.05em" }}>{title}</span>
                  </div>
	                  {children}
	                </div>
	              );
	              const FilterSlider = ({ label, value, min, max, disabled, onChange }) => (
	                <div>
	                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: SPACE[3], marginBottom: 6 }}>
	                    <span style={{ fontSize: 12, fontWeight: 750, color: C.inkMid }}>{label}</span>
	                    <span style={{ fontSize: 12, fontWeight: 800, color: C.ink }}>{`${value} km`}</span>
	                  </div>
	                  <input
	                    type="range"
	                    min={min}
	                    max={max}
	                    value={value}
	                    disabled={disabled}
	                    onChange={e => onChange(Number(e.target.value))}
	                    style={{ width: "100%", accentColor: C.accent, cursor: disabled ? "not-allowed" : "pointer" }}
	                  />
	                </div>
	              );
	              const quickDateRanges = [
	                { key: "all", label: t("anyDate"), range: FILTER_DATE_DEFAULT_RANGE },
	                { label: t("todayLabel"), range: [0, 0] },
	                { label: t("tomorrowLabel"), range: [1, 1] },
	                { label: t("next7Days"), range: [0, 7] },
	                { label: t("weekend"), range: getUpcomingWeekendRange() },
	              ].map(option => ({ key: option.key ?? option.label, ...option }));
	              const activeDateKey = quickDateRanges.find(({ range }) => dateRange[0] === range[0] && dateRange[1] === range[1])?.key ?? "__custom";
	              const radiusValue = radiusKm ?? FILTER_RADIUS_MAX_KM;
	              return (
                <div style={{ position: "absolute", inset: 0, zIndex: 80, display: "flex", flexDirection: "column", justifyContent: "flex-end", background: "rgba(15,22,35,0.35)", animation: "fadeIn 0.18s ease" }} onClick={() => setShowFilters(false)}>
	                  <div onClick={e => e.stopPropagation()} style={{ background: C.bg, borderRadius: "20px 20px 0 0", padding: "0 0 calc(16px + env(safe-area-inset-bottom, 0px))", boxShadow: SHADOW.lg, maxHeight: "86vh", display: "flex", flexDirection: "column", overflow: "hidden" }}>
                    <div style={{ display: "flex", justifyContent: "center", padding: "10px 0 0" }}>
                      <div style={{ width: 36, height: 4, borderRadius: 999, background: C.border }} />
                    </div>
	                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: `${SPACE[4]}px ${SPACE[5]}px ${SPACE[2]}px` }}>
	                      <div style={{ display: "flex", alignItems: "center", gap: SPACE[2] }}>
	                        <div style={{ ...TYPE.h1, color: C.ink }}>{t("filters")}</div>
	                        {activeFilterCount > 0 && <div style={{ minWidth: 22, height: 22, borderRadius: 999, background: C.accent, color: C.accentText, fontSize: 12, fontWeight: 700, display: "flex", alignItems: "center", justifyContent: "center", padding: "0 7px" }}>{activeFilterCount}</div>}
	                      </div>
	                      <div style={{ display: "flex", gap: SPACE[2], alignItems: "center" }}>
	                        <button onClick={() => setShowFilters(false)} aria-label="Close" style={{ width: 32, height: 32, borderRadius: "50%", background: C.surface, border: "none", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", color: C.inkMid, transition: "background 0.12s" }}
                          onMouseEnter={e => e.currentTarget.style.background = C.border}
                          onMouseLeave={e => e.currentTarget.style.background = C.surface}
                        >
                          <X size={16} {...ICON} />
                        </button>
                      </div>
                    </div>
	                    <div style={{ padding: `${SPACE[2]}px ${SPACE[5]}px ${SPACE[4]}px`, display: "flex", flexDirection: "column", gap: SPACE[5] + 4, overflowY: "auto", flex: 1 }}>
                      <FilterRow Icon={Zap} title={t("level")}>
                        <ToggleGroup
                          value={filter === "All" ? null : filter}
                          onChange={v => setFilter(v || "All")}
                          options={[{ key: null, label: t("levelAll") }, { key: "Beginner", label: tLevel("Beginner") }, { key: "Intermediate", label: tLevel("Intermediate") }, { key: "Advanced", label: tLevel("Advanced") }]}
                        />
                      </FilterRow>
                      <FilterRow Icon={CircleDot} title={t("format")}>
                        <ToggleGroup
                          value={formatFilter === "All" ? null : formatFilter}
                          onChange={v => setFormatFilter(v || "All")}
                          options={[{ key: null, label: t("anyFormat") }, ...STANDARD_FORMATS.map(format => ({ key: format, label: format }))]}
                        />
	                      </FilterRow>
	                      <FilterRow Icon={Calendar} title={t("dateFilter")}>
	                        <div style={{ ...uiSegmentShell(ui, { display: "grid", gridTemplateColumns: `repeat(${quickDateRanges.length}, minmax(0, 1fr))` }), padding: 4 }}>
	                          {quickDateRanges.map(({ key, label, range }) => {
	                            const active = activeDateKey === key;
	                            return (
	                              <button
	                                key={label}
	                                onClick={() => setDateRange(active && key !== "all" ? [...FILTER_DATE_DEFAULT_RANGE] : [...range])}
	                                style={{ ...uiSegmentButton(ui, active, { minWidth: 0, minHeight: 36, padding: `0 ${SPACE[1]}px`, activeWeight: 800, inactiveWeight: 650 }), whiteSpace: "nowrap" }}
	                              >
	                                {label}
	                              </button>
	                            );
	                          })}
	                        </div>
	                      </FilterRow>
	                      <FilterRow Icon={MapPin} title={t("distanceFilter")}>
	                        <div style={{ background: C.surface, borderRadius: RADIUS.md, boxShadow: SHADOW.sm, padding: SPACE[3], opacity: userLocation ? 1 : 0.62 }}>
	                          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: SPACE[3], marginBottom: SPACE[3] }}>
	                            <div>
	                              <div style={{ ...T.h3, color: C.ink }}>{radiusKm == null ? t("anyDistance") : t("withinKm", { n: radiusKm })}</div>
	                              <div style={{ ...T.small, color: C.inkFaint, marginTop: 2 }}>{userLocation ? t("distanceRangeHint") : t("useLocationForRadius")}</div>
	                            </div>
	                            {radiusKm != null && (
	                              <button onClick={() => setRadiusKm(null)} style={{ ...btnSecondary, padding: "7px 12px", borderRadius: RADIUS.pill, fontSize: 12, fontWeight: 750 }}>{t("anyDistance")}</button>
	                            )}
	                          </div>
	                          <FilterSlider label={t("distanceRadius")} value={radiusValue} min={FILTER_RADIUS_MIN_KM} max={FILTER_RADIUS_MAX_KM} disabled={!userLocation} onChange={v => setRadiusKm(v)} />
	                          <div style={{ display: "flex", justifyContent: "space-between", marginTop: 2, color: C.inkFaint, fontSize: 11, fontWeight: 700 }}>
	                            <span>{FILTER_RADIUS_MIN_KM} km</span>
	                            <span>{FILTER_RADIUS_MAX_KM} km</span>
	                          </div>
	                        </div>
	                      </FilterRow>
	                      <FilterRow Icon={Users} title={t("availabilityFilter")}>
	                        <button onClick={() => setShowFullGames(v => !v)} aria-pressed={showFullGames} style={{ width: "100%", minHeight: 62, border: "none", borderRadius: RADIUS.md, background: C.surface, boxShadow: SHADOW.sm, padding: SPACE[3], display: "flex", alignItems: "center", justifyContent: "space-between", gap: SPACE[3], cursor: "pointer", textAlign: "left" }}>
	                          <span>
	                            <span style={{ ...T.h3, color: C.ink, display: "block" }}>{t("showFullyBooked")}</span>
	                            <span style={{ ...T.small, color: C.inkFaint, display: "block", marginTop: 2 }}>{t("showFullyBookedHint")}</span>
	                          </span>
	                          <span style={{ width: 48, height: 28, borderRadius: RADIUS.pill, background: showFullGames ? C.accent : C.border, padding: 3, boxSizing: "border-box", display: "flex", alignItems: "center", justifyContent: showFullGames ? "flex-end" : "flex-start", transition: "background 0.15s" }}>
	                            <span style={{ width: 22, height: 22, borderRadius: "50%", background: C.surface, boxShadow: SHADOW.sm }} />
	                          </span>
	                        </button>
	                      </FilterRow>
                      <FilterRow Icon={Filter} title={t("sortBy")}>
                        <ToggleGroup
                          value={sortBy}
                          onChange={v => setSortBy(v || "smart")}
                          options={[{ key: "smart", label: t("bestMatch") }, { key: "distance", label: t("nearestFirst") }, { key: "time", label: t("soonestFirst") }]}
                        />
                      </FilterRow>
                    </div>
		                    <div style={{ padding: `${SPACE[3]}px ${SPACE[5]}px 0`, display: "flex", gap: SPACE[2], background: C.bg, borderTop: `1px solid ${C.border}` }}>
		                      {activeFilterCount > 0 && (
		                        <button onClick={e => { e.stopPropagation(); resetDiscoverFilters(); }} style={{ ...btnSecondary, flex: 1, minHeight: 52, borderRadius: RADIUS.pill, fontSize: 15, fontWeight: 800, background: C.surface }}>
		                          {t("clearFilters")}
		                        </button>
		                      )}
		                      <button onClick={e => { e.stopPropagation(); setShowFilters(false); }} style={{ ...btnPrimary, flex: activeFilterCount > 0 ? 1.45 : 1, minHeight: 52, borderRadius: RADIUS.pill, fontSize: 15, fontWeight: 800, boxShadow: SHADOW.sm }}>
		                        {filtered.length === 1 ? t("showGames", { n: 1 }) : t("showGamesPlural", { n: filtered.length })}
		                      </button>
	                    </div>
                  </div>
                </div>
              );
            })()}

            {/* Content */}
            {viewMode === "list"
              ? <PullToRefresh style={{ flex: 1, padding: SPACE[4] }} onRefresh={() => loadGames(true)}>
                  {/* Urgent games today */}
                  {(() => {
                    const today = new Date(); today.setHours(0,0,0,0);
                    const urgent = filtered.filter(g => {
                      if (!g.date) return false;
                      const [y, mo, d] = g.date.split("-").map(Number);
                      const gDate = new Date(y, mo - 1, d);
                      return gDate.getTime() === today.getTime() && g.spotsFilled < g.spotsTotal;
                    });
                    if (urgent.length === 0 || gamesLoading) return null;
                    return (
                      <div style={{ marginBottom: 16 }}>
                        <div style={{ ...T.label, color: C.danger, marginBottom: 10, display: "flex", alignItems: "center", gap: 6 }}>
                          <span style={{ width: 6, height: 6, borderRadius: "50%", background: C.danger, display: "inline-block", animation: "pulse 1.5s infinite" }} />
                          {t("playingToday")}
                        </div>
                        <div style={{ display: "flex", gap: 10, overflowX: "auto", scrollbarWidth: "none", paddingBottom: 4 }}>
                          {urgent.map(g => {
                            const spotsLeft = g.spotsTotal - g.spotsFilled;
                            return (
                              <div key={g.id} onClick={() => setSelectedGame(g)} style={{ minWidth: 180, ...card, padding: "12px 14px", cursor: "pointer", flexShrink: 0, borderColor: C.danger, background: dangerBg }}
                                onMouseEnter={e => e.currentTarget.style.boxShadow = "0 4px 12px rgba(0,0,0,0.08)"}
                                onMouseLeave={e => e.currentTarget.style.boxShadow = "none"}
                              >
                                <div style={{ fontSize: 12, fontWeight: 600, color: C.danger, marginBottom: 4 }}>{spotsLeft === 1 ? t("spotsLeft", { n: 1 }) : t("spotsLeftPlural", { n: spotsLeft })}</div>
                                <div style={{ fontSize: 13, fontWeight: 600, color: C.ink, marginBottom: 4, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{g.title}</div>
                                <div style={{ fontSize: 12, color: C.inkMid }}>{g.hour} · {g.format}</div>
                              </div>
                            );
                          })}
                        </div>
                        <div style={{ height: 1, background: C.border, marginTop: 16, marginBottom: 4 }} />
                      </div>
                    );
                  })()}
                  {/* I'm Available */}
	                  {user && (
                    <div style={{ marginBottom: 16 }}>
                      {myAvailability ? (
                        <div style={{ ...card, padding: "14px 16px" }}>
                          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: nearbyAvailable.length > 0 ? 10 : 0 }}>
                            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                              <div style={{ width: 8, height: 8, borderRadius: "50%", background: C.accent, flexShrink: 0 }} />
                              <span style={{ fontSize: 13, fontWeight: 600, color: C.ink }}>
                                {availabilityLabel(myAvailability)} · {formatAvailabilityTimeRange(myAvailability)}
                                {myAvailabilities.length > 1 && <span style={{ marginLeft: 6, fontSize: 11, fontWeight: 500, color: C.accent, background: C.accentBg, padding: "1px 6px", borderRadius: 10 }}>+{myAvailabilities.length - 1} more</span>}
                              </span>
                            </div>
                            <button onClick={() => setShowAvailabilitySheet(true)} style={{ background: "none", border: "none", cursor: "pointer", color: C.accent, fontSize: 13, fontWeight: 600 }}>{t("edit")}</button>
                          </div>
                          {nearbyAvailable.length > 0 && (
                            <>
                              <div style={{ ...T.small, color: C.inkFaint, marginBottom: 8 }}>{nearbyAvailable.length === 1 ? t("freePlayers", { n: 1 }) : t("freePlayersPlural", { n: nearbyAvailable.length })}</div>
                              <div style={{ display: "flex", gap: 8, overflowX: "auto", scrollbarWidth: "none", paddingBottom: 2 }}>
                                {nearbyAvailable.slice(0, 7).map(p => {
                                  const name = (p.profiles_public?.name || p.profiles?.name) || "Player";
                                  const initials = name.split(" ").map(w => w[0]).join("").slice(0,2).toUpperCase();
                                  return (
                                    <div key={p.id} style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 4, flexShrink: 0 }}>
                                      <div style={{ width: 36, height: 36, borderRadius: "50%", background: C.accentBg, border: `1.5px solid ${C.accent}44`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, fontWeight: 700, color: C.accent }}>{initials}</div>
                                      <span style={{ fontSize: 10, color: C.inkMid, maxWidth: 44, textAlign: "center", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{name.split(" ")[0]}</span>
                                    </div>
                                  );
                                })}
                                {nearbyAvailable.length > 7 && (
                                  <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 4, flexShrink: 0 }}>
                                    <div style={{ width: 36, height: 36, borderRadius: "50%", background: C.surface, border: `1.5px solid ${C.border}`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, fontWeight: 600, color: C.inkFaint }}>+{nearbyAvailable.length - 7}</div>
                                  </div>
                                )}
                              </div>
                              {nearbyAvailable.length >= 4 && (
                                <button onClick={() => {
                                  const availabilityFormats = normalizeAvailabilityFormats(myAvailability.formats);
                                  const availabilityLevel = normalizeAvailabilityLevel(myAvailability.level);
                                  setCreatePrefill({
                                    source: "availability",
                                    date: myAvailability.date,
                                    hour: getAvailabilityGameStartTime(myAvailability),
                                    format: availabilityFormats[0] || "5v5",
                                    level: availabilityLevel,
                                    autoInvite: nearbyAvailable.map(p => p.user_id),
                                  });
                                  setCreating(true);
                                }} style={{ marginTop: 10, width: "100%", padding: "9px 0", borderRadius: 10, background: C.accent, color: C.accentText, border: "none", cursor: "pointer", fontSize: 13, fontWeight: 600 }}>
                                  {t("playersFreeCta", { n: nearbyAvailable.length + 1 })}
                                </button>
                              )}
                            </>
                          )}
                        </div>
	                      ) : (
	                        <button onClick={() => setShowAvailabilitySheet(true)} style={{ width: "100%", padding: "13px 16px", borderRadius: 12, border: `1.5px ${hasAvailabilityMomentum ? "solid" : "dashed"} ${hasAvailabilityMomentum ? C.accent : C.border}`, background: hasAvailabilityMomentum ? C.accentBg : "transparent", display: "flex", alignItems: "center", gap: 10, cursor: "pointer", textAlign: "left", boxShadow: hasAvailabilityMomentum ? SHADOW.sm : "none" }}>
	                          {hasAvailabilityMomentum ? (
	                            <AvailabilityAvatarStack players={availabilityMomentumPlayers} total={availabilityMomentumCount} size={34} />
	                          ) : (
	                            <div style={{ width: 32, height: 32, borderRadius: "50%", background: C.accentBg, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
	                              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={C.accent} strokeWidth="2.5" strokeLinecap="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
	                            </div>
	                          )}
	                          <div style={{ minWidth: 0, flex: 1 }}>
	                            <div style={{ fontSize: 13, fontWeight: 750, color: hasAvailabilityMomentum ? C.accent : C.ink }}>{hasAvailabilityMomentum ? t("playersFreeNearbyShort", { n: availabilityMomentumCount }) : t("imAvailable")}</div>
	                            <div style={{ fontSize: 12, color: C.inkFaint }}>{hasAvailabilityMomentum ? t("availabilityMomentumHint") : t("imAvailableHint")}</div>
	                          </div>
	                          {hasAvailabilityMomentum && <span style={{ padding: "5px 10px", borderRadius: 999, background: C.accent, color: C.accentText, fontSize: 11, fontWeight: 850, flexShrink: 0 }}>{t("joinNearbyPlayers")}</span>}
	                        </button>
	                      )}
                    </div>
                  )}

                  {renderList(filtered)}
                </PullToRefresh>
              : <div style={{ flex: 1, position: "relative", overflow: "hidden" }}>
                  <GameMap games={filtered} selectedGame={selectedGame} onSelectGame={g => { setSelectedVenue(null); setClusterGames(null); handleSelectGame(g); }} joined={joined} userLocation={userLocation} onVenueClick={v => { setSelectedGame(null); setSelectedVenue(prev => prev?.id === v.id ? null : v); }} selectedVenueId={selectedVenue?.id} onSelectCluster={gs => { setSelectedVenue(null); setClusterGames(gs); }} venues={venues} mapRef={mapRef} />
	                  {user && !selectedVenue && !selectedGame && !clusterGames && !searchPanelOpen && (
	                    <button onClick={() => setShowAvailabilitySheet(true)} style={{ position: "absolute", top: 12, left: 12, zIndex: 50, display: "flex", alignItems: "center", gap: hasAvailabilityMomentum ? 8 : 6, padding: hasAvailabilityMomentum ? "7px 10px 7px 8px" : "7px 14px", borderRadius: 22, background: hasAvailabilityMomentum ? C.accentBg : C.bg, border: `1.5px solid ${myAvailability || hasAvailabilityMomentum ? C.accent : C.border}`, cursor: "pointer", boxShadow: hasAvailabilityMomentum ? `0 6px 18px ${C.accent}33` : "0 2px 8px rgba(0,0,0,0.15)", maxWidth: "calc(100% - 112px)", textAlign: "left" }}>
	                      {hasAvailabilityMomentum ? (
	                        <AvailabilityAvatarStack players={availabilityMomentumPlayers} total={availabilityMomentumCount} size={24} />
	                      ) : (
	                        <div style={{ width: 7, height: 7, borderRadius: "50%", background: myAvailability ? C.accent : C.inkFaint, flexShrink: 0 }} />
	                      )}
	                      <span style={{ display: "flex", flexDirection: "column", minWidth: 0 }}>
	                        <span style={{ fontSize: 12, fontWeight: 800, color: myAvailability || hasAvailabilityMomentum ? C.accent : C.inkMid, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{availabilityMapTitle}</span>
	                        {availabilityMapSub && <span style={{ fontSize: 10, fontWeight: 750, color: C.inkMid, whiteSpace: "nowrap" }}>{availabilityMapSub}</span>}
	                      </span>
	                    </button>
	                  )}
                  {/* Venue info panel */}
                  {selectedVenue && !selectedGame && (() => {
                    const isCage = selectedVenue.type === "Cageball";
                    const vc = isCage ? PALETTE.violet600 : C.success;
                    return (
                      <div style={{ position: "absolute", bottom: 0, left: 0, right: 0, zIndex: 15, background: C.bg, borderTop: `1px solid ${C.border}`, borderRadius: "20px 20px 0 0", padding: "20px 24px 24px", boxShadow: "0 -4px 20px rgba(0,0,0,0.2)", animation: "slideUp 0.22s ease", maxHeight: "72vh", overflowY: "auto" }}>
                        <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: 10 }}>
                          <div style={{ flex: 1, paddingRight: 8 }}>
                            <div style={{ fontSize: 16, fontWeight: 700, color: C.ink, marginBottom: 3 }}>{selectedVenue.name}</div>
                            <div style={{ fontSize: 12, color: C.inkMid }}>{selectedVenue.address}</div>
                          </div>
                          <button onClick={() => setSelectedVenue(null)} style={{ background: "none", border: "none", cursor: "pointer", color: C.inkMid, fontSize: 18, padding: "0 0 0 8px", lineHeight: 1 }}>✕</button>
                        </div>
                        {(() => {
                          const conf = getPitchConfidence(selectedVenue, format(new Date(), "yyyy-MM-dd"), "17:00");
                          const cl = confidenceLabel(conf);
                          return (
                            <div style={{ display: "flex", gap: 6, marginBottom: 14, flexWrap: "wrap", alignItems: "center" }}>
                              <span style={{ padding: "3px 10px", borderRadius: 20, fontSize: 11, fontWeight: 600, background: isCage ? "#2d1a5e" : "#1a3a28", color: vc }}>{selectedVenue.type}</span>
                              {selectedVenue.size && <span style={{ padding: "3px 10px", borderRadius: 20, fontSize: 11, fontWeight: 600, background: C.surface, color: C.inkMid }}>{selectedVenue.size}</span>}
                              {selectedVenue.area && <span style={{ padding: "3px 10px", borderRadius: 20, fontSize: 11, fontWeight: 600, background: C.surface, color: C.inkMid }}>{selectedVenue.area}</span>}
                              <span style={{ padding: "3px 10px", borderRadius: 20, fontSize: 11, fontWeight: 700, background: cl.bg, color: cl.color }}>
                                {cl.label}
                              </span>
                            </div>
                          );
                        })()}
                        {selectedVenue.details && (
                          <div style={{ marginBottom: 14, padding: "10px 12px", borderRadius: 8, background: C.surface, color: C.inkMid, fontSize: 13, lineHeight: 1.45 }}>
                            {selectedVenue.details}
                          </div>
                        )}
                        {/* Multi-axis review breakdown (renders nothing if no reviews yet) */}
                        <VenueReviewBreakdown venueId={selectedVenue.id} />
                        {/* Pitch availability chart */}
                        <VenueAvailabilityChart venue={selectedVenue} />
                        {/* Backup venues */}
                        {(() => {
                          const backups = [...venues]
                            .filter(v => v.id !== selectedVenue.id && v.type === selectedVenue.type)
                            .map(v => ({ ...v, dist: getDistance(selectedVenue.lat, selectedVenue.lng, v.lat, v.lng) }))
                            .sort((a, b) => a.dist - b.dist)
                            .slice(0, 2);
                          if (backups.length === 0) return null;
                          return (
                            <div style={{ marginBottom: 14 }}>
                              <div style={{ fontSize: 11, fontWeight: 600, color: C.inkMid, textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 8 }}>Nearby alternatives</div>
                              {backups.map(v => {
                                const bc = getPitchConfidence(v, format(new Date(), "yyyy-MM-dd"), "17:00");
                                const bcl = confidenceLabel(bc);
                                return (
                                  <button key={v.id} onClick={() => setSelectedVenue(v)} style={{ width: "100%", display: "flex", alignItems: "center", justifyContent: "space-between", padding: "9px 12px", borderRadius: 8, border: `1px solid ${C.border}`, background: C.surface, cursor: "pointer", marginBottom: 4, textAlign: "left" }}>
                                    <div>
                                      <div style={{ fontSize: 13, fontWeight: 500, color: C.ink }}>{v.name}</div>
                                      <div style={{ fontSize: 11, color: C.inkFaint }}>{v.dist < 1 ? `${Math.round(v.dist * 1000)}m` : `${v.dist.toFixed(1)}km`} · {v.area}</div>
                                    </div>
                                    <span style={{ fontSize: 10, fontWeight: 700, color: bcl.color, background: bcl.bg, padding: "2px 7px", borderRadius: 99, flexShrink: 0 }}>{bcl.label}</span>
                                  </button>
                                );
                              })}
                            </div>
                          );
                        })()}
                        <button onClick={() => { if (!user) { setShowAuthModal(true); return; } setSelectedVenue(null); setCreatePrefill({ source: "venue", location: selectedVenue.name, lat: selectedVenue.lat, lng: selectedVenue.lng, price: selectedVenue.price, venueId: selectedVenue.id }); setCreating(true); }} style={{ ...btnPrimary, width: "100%", padding: "12px 0", borderRadius: 10, fontSize: 15 }}>{t("createGameHere")}</button>
                      </div>
                    );
                  })()}
                  {/* Bottom cards strip */}
                  {!selectedVenue && (
                    <div style={{ position: "absolute", bottom: 0, left: 0, right: 0, background: C.bg, borderTop: `1px solid ${C.border}` }}>
                      <div id="map-card-strip" style={{ display: "flex", gap: 12, overflowX: "auto", padding: "14px 16px 18px", scrollbarWidth: "none" }}>
                        {filtered.length === 0 && (
                          <div style={{ padding: "12px 0", color: C.inkFaint, fontSize: 13 }}>No games in this area</div>
                        )}
                        {filtered.map(g => {
                          const isJoined = joined.includes(g.id);
                          const full = g.spotsFilled >= g.spotsTotal;
                          const isSel = selectedGame?.id === g.id;
                          return (
                            <div
                              key={g.id}
                              id={`map-card-${g.id}`}
                              onClick={() => setSelectedGame(g)}
                              style={{
                                minWidth: 220,
                                maxWidth: 260,
                                background: C.surface,
                                border: `${isSel ? 2 : 1}px solid ${isSel ? C.ink : isJoined ? C.accent : C.border}`,
                                borderRadius: 12,
                                padding: "12px 14px",
                                cursor: "pointer",
                                flexShrink: 0,
                                transition: "all 0.15s",
                                boxShadow: isSel ? "0 4px 16px rgba(0,0,0,0.18)" : "none",
                              }}
                              onMouseEnter={e => { e.currentTarget.style.boxShadow = "0 4px 12px rgba(0,0,0,0.12)"; e.currentTarget.style.borderColor = C.inkMid; }}
                              onMouseLeave={e => { e.currentTarget.style.boxShadow = isSel ? "0 4px 16px rgba(0,0,0,0.18)" : "none"; e.currentTarget.style.borderColor = isSel ? C.ink : isJoined ? C.accent : C.border; }}
                            >
                              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 6 }}>
                                <LevelPill level={g.level} small />
                                <span style={{ fontSize: 11, fontWeight: 700, color: full ? C.danger : isJoined ? C.accent : C.inkMid }}>
                                  {full ? "Full" : isJoined ? "Joined" : `${g.spotsFilled}/${g.spotsTotal}`}
                                </span>
                              </div>
                              <div style={{ fontSize: 14, fontWeight: 600, color: C.ink, marginBottom: 3, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{g.title}</div>
                              <div style={{ fontSize: 12, color: C.inkFaint, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", marginBottom: 6 }}>{g.location}</div>
                              <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                                <span style={{ fontSize: 11, fontWeight: 600, color: C.inkMid, background: C.bg, border: `1px solid ${C.border}`, borderRadius: 6, padding: "2px 7px" }}>{g.hour}</span>
                                <span style={{ fontSize: 11, color: C.inkMid }}>{g.time}</span>
                                <span style={{ fontSize: 11, color: C.inkFaint }}>· {g.format}</span>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
            }
          </div>
        )}

        {tab === "my" && (user
	          ? <MyGamesTab upcomingGames={myUpcomingGames} pastGames={myPastGames} savedGames={savedGames} renderList={renderList} onDiscover={() => setTab("discover")} onTap={g => setSelectedGame(g)} currentUser={user} onRefresh={() => loadGames(true)} onRepeatGame={handleCloneNextWeek} view={myGamesView} setView={setMyGamesView} reviewGameIdToOpen={reviewGameIdToOpen} onReviewGameOpened={() => setReviewGameIdToOpen(null)} />
          : <div style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: 32, gap: 16 }}>
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke={C.inkFaint} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>
              <div style={{ ...T.h2, textAlign: "center" }}>{t("yourGamesHere")}</div>
              <div style={{ ...T.body, textAlign: "center", color: C.inkFaint, maxWidth: 260 }}>{t("yourGamesHereDesc")}</div>
              <button onClick={() => setShowAuthModal(true)} style={{ ...btnPrimary, padding: "13px 32px", borderRadius: 10, fontSize: 15, marginTop: 4 }}>{t("signInToJoin")}</button>
            </div>
        )}
        {tab === "communities" && (
          <CommunitiesTab
            user={user}
            userProfile={userProfile}
            myCommunities={myCommunities}
            discoverCommunities={discoverCommunities}
            loading={communitiesLoading}
            onOpenCommunity={c => { setOpenedCommunityInitialView("about"); setOpenedCommunity(c); }}
            onCreate={() => user ? setShowCreateCommunity(true) : setShowAuthModal(true)}
            onRefresh={loadCommunities}
            onOpenFriends={() => {
              if (!user) { setShowAuthModal(true); return; }
              setFriendsInitialChatUserId(null);
              setShowFriendsScreen(true);
            }}
            invitableGames={games.filter(g => (g.hostId === user?.id || joined.includes(g.id)) && g.spotsFilled < g.spotsTotal && !isPastGame(g))}
            friendsReloadKey={friendsVersion}
            onCreateGame={() => setCreating(true)}
            showToast={toast_}
          />
        )}
        {tab === "profile" && (user
          ? <div style={{ flex: 1, overflow: "hidden" }}><ProfileScreen hostedCount={games.filter(g => g.hostId === user?.id).length + pastGames.filter(g => g.hostId === user?.id).length} joinedCount={[...myUpcomingGames, ...myPastGames].filter(g => g.hostId !== user?.id).length} user={user} userProfile={userProfile} usernameEnabled={usernameSchemaReady} onProfileSave={updates => setUserProfile(prev => ({ ...prev, ...updates }))} onOpenSettings={() => setShowSettings(true)} onOpenAllPast={() => { setMyGamesView("past"); setTab("my"); }} recentPastGames={myPastGames} /></div>
          : <div style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: 32, gap: 16 }}>
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke={C.inkFaint} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
              <div style={{ ...T.h2, textAlign: "center" }}>{t("yourProfile")}</div>
              <div style={{ ...T.body, textAlign: "center", color: C.inkFaint, maxWidth: 260 }}>{t("yourProfileDesc")}</div>
              <button onClick={() => setShowAuthModal(true)} style={{ ...btnPrimary, padding: "13px 32px", borderRadius: 10, fontSize: 15, marginTop: 4 }}>{t("signInToJoin")}</button>
            </div>
        )}
      </div>

      {/* Tab bar with centered create button. Single Lucide icon family across
          all five tabs at one stroke weight (1.8). Active state: filled bg
          chip behind the icon + label below. The previous mix of three icon
          styles read as inconsistent — this version reads as one product. */}
      <div style={{ display: "flex", alignItems: "center", borderTop: `1px solid ${C.border}`, background: C.bg, flexShrink: 0, paddingTop: 6, paddingBottom: "calc(8px + env(safe-area-inset-bottom, 34px))" }}>
        {[
          { key: "discover",    Icon: Compass,      label: t("discoverTab")    },
          { key: "my",          Icon: Calendar,     label: t("myGamesTab"),    badge: user && myUpcomingGames.length > 0 ? myUpcomingGames.length : null },
          { key: "create",      Icon: Plus,         label: t("createGame"),    isCenter: true },
          { key: "communities", Icon: Users,        label: t("communitiesTab") },
          { key: "profile",     Icon: UserCircle2,  label: t("profileTab")     },
        ].map(({ key, Icon: NavIcon, label, badge, isCenter }) => {
          if (isCenter) {
            return (
              <button
                key={key}
                onClick={() => { if (!user) { setShowAuthModal(true); return; } setCreating(true); }}
                aria-label={label}
                style={{
                  width: 52, height: 52, borderRadius: "50%",
                  background: C.accent, border: "none", cursor: "pointer",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  boxShadow: `0 6px 18px ${C.accent}55`, flexShrink: 0,
                  margin: `0 ${SPACE[2]}px`,
                  transition: "transform 0.15s ease, box-shadow 0.15s ease",
                }}
                onMouseEnter={e => { e.currentTarget.style.transform = "translateY(-2px)"; e.currentTarget.style.boxShadow = `0 10px 24px ${C.accent}77`; }}
                onMouseLeave={e => { e.currentTarget.style.transform = ""; e.currentTarget.style.boxShadow = `0 6px 18px ${C.accent}55`; }}
                onMouseDown={e => e.currentTarget.style.transform = "scale(0.94)"}
                onMouseUp={e => e.currentTarget.style.transform = ""}
              >
                <Plus size={22} {...ICON} strokeWidth={2.4} color={C.accentText} />
              </button>
            );
          }
          const active = tab === key;
          return (
            <button
              key={key}
              onClick={() => switchTab(key)}
              style={{
                flex: 1, display: "flex", flexDirection: "column", alignItems: "center",
                gap: SPACE[1] / 2, background: "none", border: "none", cursor: "pointer",
                padding: `${SPACE[1] + 2}px 0`, position: "relative", minHeight: 48,
                transition: "transform 0.12s ease",
              }}
              aria-label={label}
              aria-current={active ? "page" : undefined}
            >
              <span style={{
                width: 44, height: 28, borderRadius: RADIUS.pill,
                background: active ? C.accentBg : "transparent",
                boxShadow: active ? SHADOW.sm : "none",
                display: "flex", alignItems: "center", justifyContent: "center",
                transition: "background 0.15s, box-shadow 0.15s",
              }}>
	                {React.createElement(NavIcon, { size: 21, ...ICON, strokeWidth: active ? 2.2 : 1.8, color: active ? C.accent : C.inkFaint })}
              </span>
              <span style={{ fontSize: 10, fontWeight: active ? 700 : 500, color: active ? C.ink : C.inkFaint, letterSpacing: "0.01em" }}>
                {label}
              </span>
              {badge != null && (
                <span style={{
                  position: "absolute", top: 0, right: "calc(50% - 20px)",
                  minWidth: 16, height: 16, padding: "0 4px", borderRadius: 999,
                  background: C.danger, color: "#fff", fontSize: 9, fontWeight: 700,
                  display: "flex", alignItems: "center", justifyContent: "center",
                  boxShadow: `0 0 0 2px ${C.bg}`,
                }}>{badge}</span>
              )}
            </button>
          );
        })}
      </div>

      {/* Overlays */}
      {clusterGames && (
        <div onClick={() => setClusterGames(null)} style={{ position: "fixed", inset: 0, zIndex: 55, background: "rgba(0,0,0,0.4)", display: "flex", alignItems: "flex-end" }}>
          {(() => {
            const firstGame = clusterGames[0];
            const venue = venues.find(v => v.lat?.toFixed(5) === firstGame.lat?.toFixed(5) && v.lng?.toFixed(5) === firstGame.lng?.toFixed(5));
            return (
              <div onClick={e => e.stopPropagation()} style={{ width: "100%", background: C.surface, borderRadius: "20px 20px 0 0", padding: "20px 20px 32px", boxShadow: "0 -4px 24px rgba(0,0,0,0.3)" }}>
                <div style={{ width: 36, height: 4, borderRadius: 2, background: C.border, margin: "0 auto 16px" }} />
                <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", marginBottom: 4 }}>
                  <div style={{ ...T.h3, color: C.ink }}>{venue ? venue.name : firstGame.location || "This location"}</div>
                  {venue && <div style={{ fontSize: 11, color: C.inkFaint }}>{venue.area}</div>}
                </div>
                {/* Create new game here */}
                <button onClick={() => { if (!user) { setClusterGames(null); setShowAuthModal(true); return; } setClusterGames(null); setCreatePrefill(venue ? { source: "venue", location: venue.name, lat: venue.lat, lng: venue.lng, price: venue.price, venueId: venue.id } : { source: "venue", location: firstGame.location, lat: firstGame.lat, lng: firstGame.lng }); setCreating(true); }} style={{ ...btnPrimary, width: "100%", padding: "11px 0", borderRadius: 10, fontSize: 14, marginTop: 10, marginBottom: 14 }}>
                  {t("createNewGameHere")}
                </button>
                {clusterGames.length > 0 && <div style={{ ...T.label, color: C.inkFaint, marginBottom: 8 }}>{t("activeGames", { n: clusterGames.length })}</div>}
                <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                  {clusterGames.map(g => {
                    const isJoined = joined.includes(g.id);
                    const full = g.spotsFilled >= g.spotsTotal;
                    const lc = (levelColor || {})[g.level] || { dot: C.accent };
                    return (
                      <button key={g.id} onClick={() => { setClusterGames(null); handleSelectGame(g); }} style={{ display: "flex", alignItems: "center", gap: 14, padding: "12px 14px", borderRadius: 12, border: `1px solid ${C.border}`, background: C.bg, cursor: "pointer", textAlign: "left" }}>
                        <div style={{ width: 10, height: 10, borderRadius: "50%", background: isJoined ? C.ink : full ? C.inkFaint : lc.dot, flexShrink: 0 }} />
                        <div style={{ flex: 1 }}>
                          <div style={{ fontSize: 14, fontWeight: 600, color: C.ink }}>{g.title}</div>
                          <div style={{ fontSize: 12, color: C.inkMid, marginTop: 2 }}>{g.time} · {g.format} · {g.spotsFilled}/{g.spotsTotal} players</div>
                        </div>
                        {isJoined && <span style={{ fontSize: 11, fontWeight: 600, color: C.accent }}>Joined</span>}
                        {!isJoined && full && <span style={{ fontSize: 11, fontWeight: 600, color: C.inkFaint }}>Full</span>}
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          })()}
        </div>
      )}
      {addFriendPrompt && (
        <div onClick={() => setAddFriendPrompt(null)} style={{ position: "fixed", inset: 0, zIndex: 300, background: "rgba(0,0,0,0.55)", display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }}>
          <div onClick={e => e.stopPropagation()} style={{ background: C.surface, borderRadius: 18, padding: 28, width: "100%", maxWidth: 320, boxShadow: "0 8px 40px rgba(0,0,0,0.4)", display: "flex", flexDirection: "column", alignItems: "center", gap: 12 }}>
            {addFriendPrompt.avatarUrl
              ? <img src={addFriendPrompt.avatarUrl} alt="" style={{ width: 64, height: 64, borderRadius: "50%", objectFit: "cover" }} />
              : <div style={{ width: 64, height: 64, borderRadius: "50%", background: C.accentBg, color: C.accent, display: "flex", alignItems: "center", justifyContent: "center" }}><User size={28} {...ICON} /></div>
            }
            <div style={{ ...T.h2, textAlign: "center" }}>{addFriendPrompt.name}</div>
            <div style={{ ...T.body, textAlign: "center", color: C.inkFaint }}>Send a friend request?</div>
            <div style={{ display: "flex", gap: 10, width: "100%", marginTop: 4 }}>
              <button onClick={() => setAddFriendPrompt(null)} style={{ ...btnSecondary, flex: 1 }}>Cancel</button>
              <button onClick={async () => {
                const { error } = await supabase.from("friendships").insert({ requester_id: user.id, recipient_id: addFriendPrompt.userId, status: "pending" });
                if (error) { toast_(t("couldNotSendRequest")); setAddFriendPrompt(null); return; }
                const senderName = user?.user_metadata?.name || user?.email?.split("@")[0] || "Someone";
                await supabase.from("notifications").insert({ user_id: addFriendPrompt.userId, type: "friend_request", text: `${senderName} sent you a friend request`, sub: user.id, game_id: null, read: false });
                toast_(t("friendRequestSent"));
                setAddFriendPrompt(null);
              }} style={{ ...btnPrimary, flex: 1 }}>Send Request</button>
            </div>
          </div>
        </div>
      )}
      {showFriendsScreen && <FriendsScreen currentUser={user} onClose={() => { setShowFriendsScreen(false); setFriendsInviteGameId(null); setFriendsInitialChatUserId(null); }} showToast={toast_} invitableGames={games.filter(g => (g.hostId === user?.id || joined.includes(g.id)) && g.spotsFilled < g.spotsTotal && !isPastGame(g))} reloadKey={friendsVersion} onCreateGame={() => { setShowFriendsScreen(false); setCreating(true); }} initialGameId={friendsInviteGameId} initialChatFriendId={friendsInitialChatUserId} />}
      {openedCommunity && (
        <CommunityScreen
          community={openedCommunity}
          user={user}
          onClose={() => { setOpenedCommunity(null); setOpenedCommunityInitialView("about"); }}
          onRefresh={loadCommunities}
          showToast={toast_}
          onDeleted={() => { setOpenedCommunity(null); setOpenedCommunityInitialView("about"); loadCommunities(); }}
          onOpenGame={game => setSelectedGame(game)}
          onJoinGame={handleJoin}
          joinedGameIds={joined}
          waitlistedGameIds={waitlisted}
          joiningGameId={joiningGameId}
          allKnownGames={[...games, ...pastGames]}
          initialView={openedCommunityInitialView}
          onCreateGame={(community, extras = null) => {
            // Close the community overlay so CreateScreen lands on top, and
            // pre-fill scope so the new game is bound to this community.
            // `extras` lets accepted challenges seed format/date/hour/location.
              setOpenedCommunity(null);
              setOpenedCommunityInitialView("about");
            setCreatePrefill({
              source: "group",
              visibility: "community",
              communityId: community.id,
              ...(extras || {}),
            });
            setCreating(true);
          }}
        />
      )}
      {showCreateCommunity && user && (
        <CreateCommunityModal
          user={user}
          userProfile={userProfile}
          onClose={() => setShowCreateCommunity(false)}
          onCreated={c => { setShowCreateCommunity(false); loadCommunities(); setOpenedCommunityInitialView("about"); setOpenedCommunity(c); }}
          showToast={toast_}
        />
      )}
      {showSettings && user && <SettingsScreen user={user} userProfile={userProfile} onClose={() => setShowSettings(false)} onSignOut={() => { setShowSettings(false); handleSignOut(); }} onAccountDeleted={() => { setShowSettings(false); handleSignOut({ localOnly: true }); }} onOpenAdminDashboard={() => { setShowSettings(false); setShowAdminDashboard(true); }} />}
      {/* Standalone player card — opened when tapping a player result in the search bar */}
      {searchPlayerCard && (() => {
        const p = searchPlayerCard;
        const playerId = p.id || p.user_id;
        const inviteTargetGame = games.find(g => (g.hostId === user?.id || joined.includes(g.id)) && g.spotsFilled < g.spotsTotal && !isPastGame(g));
        const hasPlayer = (game, id) => !!id && (game.hostId === id || (game.players || []).some(pl => pl.id === id));
        const dedupedGames = Array.from(new Map([...pastGames, ...games].map(game => [game.id, game])).values());
        const sharedGames = user
          ? dedupedGames
              .filter(game => hasPlayer(game, playerId) && hasPlayer(game, user.id))
              .sort((a, b) => new Date(`${b.date || "1970-01-01"}T${b.hour || "00:00"}`) - new Date(`${a.date || "1970-01-01"}T${a.hour || "00:00"}`))
          : [];
        const isSelfPlayer = user && playerId === user.id;
        const isFriend = searchPlayerCardFriendStatus === "accepted";
        const requestSent = searchPlayerCardFriendStatus === "sent";
        const closePlayerCard = () => {
          setSearchPlayerCard(null);
          setSearchPlayerCardFriendStatus(null);
          setSearchPlayerCardShowSharedGames(false);
        };
        const friendActionLabel = isFriend ? "Already friends" : requestSent ? "Request sent" : searchPlayerCardLoading ? "Sending..." : "Send friend request";
        const friendActionDisabled = searchPlayerCardLoading || isFriend || requestSent;
        const sharedGamesLabel = sharedGames.length ? `${sharedGames.length} shared game${sharedGames.length === 1 ? "" : "s"}` : "No shared games yet";
        return (
          <div style={{ position: "fixed", inset: 0, zIndex: 600, background: "rgba(15,22,35,0.45)", display: "flex", alignItems: "flex-end" }} onClick={closePlayerCard}>
            <div style={{ background: C.bg, borderRadius: `${RADIUS.xl}px ${RADIUS.xl}px 0 0`, padding: `${SPACE[2]}px ${SPACE[4]}px calc(${SPACE[4]}px + env(safe-area-inset-bottom, 0px))`, width: "100%", maxWidth: 620, margin: "0 auto", boxSizing: "border-box", animation: "slideUp 0.25s ease", boxShadow: SHADOW.lg }} onClick={e => e.stopPropagation()}>
              <div style={{ width: 42, height: 4, borderRadius: RADIUS.pill, background: C.border, margin: `${SPACE[1]}px auto ${SPACE[3]}px` }} />
              <div style={{ display: "flex", alignItems: "flex-start", gap: SPACE[3], marginBottom: SPACE[4] }}>
                <div style={{ padding: 3, borderRadius: RADIUS.pill, background: C.surface, boxShadow: SHADOW.sm, flexShrink: 0 }}>
                  <Avatar initials={(p.name || "??").slice(0, 2).toUpperCase()} avatarUrl={p.avatar_url} size={58} />
                </div>
                <div style={{ flex: 1, minWidth: 0, paddingTop: 2 }}>
                  <div style={{ ...TYPE.h1, color: C.ink, marginBottom: SPACE[2], overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{p.name}</div>
                  <div style={{ display: "flex", gap: SPACE[2], alignItems: "center", flexWrap: "wrap" }}>
                    <LevelPill level={p.level} />
                    {p.position && <span style={{ fontSize: 12, color: C.inkMid, fontWeight: 700, background: C.surface, borderRadius: RADIUS.pill, padding: "5px 10px" }}>{p.position}</span>}
                    {isFriend && <span style={{ fontSize: 12, color: C.accent, fontWeight: 800, background: C.accentBg, borderRadius: RADIUS.pill, padding: "5px 10px" }}>Friend</span>}
                    {requestSent && <span style={{ fontSize: 12, color: C.inkMid, fontWeight: 800, background: C.surface, borderRadius: RADIUS.pill, padding: "5px 10px" }}>Request sent</span>}
                  </div>
                </div>
                <button aria-label="Close" onClick={closePlayerCard} style={{ width: 38, height: 38, border: "none", borderRadius: RADIUS.pill, background: C.surface, color: C.inkMid, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", boxShadow: SHADOW.sm, flexShrink: 0 }}>
                  <X size={18} {...ICON} />
                </button>
              </div>
              {user && !isSelfPlayer && (
                <div style={{ background: C.surface, borderRadius: RADIUS.md, boxShadow: SHADOW.sm, padding: SPACE[3], marginBottom: SPACE[4] }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: SPACE[3] }}>
                    <div style={{ display: "flex", alignItems: "center", gap: SPACE[3], minWidth: 0 }}>
                      <div style={{ width: 36, height: 36, borderRadius: RADIUS.pill, background: C.accentBg, color: C.accent, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                        <Handshake size={17} {...ICON} />
                      </div>
                      <div style={{ minWidth: 0 }}>
                        <div style={{ ...T.label, marginBottom: 2 }}>Played together</div>
                        <div style={{ ...T.h3, color: C.ink, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{sharedGamesLabel}</div>
                      </div>
                    </div>
                    {sharedGames.length > 0 && (
                      <button
                        onClick={() => setSearchPlayerCardShowSharedGames(v => !v)}
                        style={{ border: `1px solid ${C.border}`, background: C.bg, color: C.inkMid, borderRadius: RADIUS.pill, width: 36, height: 36, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", flexShrink: 0 }}
                        aria-label={searchPlayerCardShowSharedGames ? "Hide shared games" : "Show shared games"}
                      >
                        <ChevronRight size={17} {...ICON} style={{ transform: searchPlayerCardShowSharedGames ? "rotate(90deg)" : "none", transition: "transform 0.15s" }} />
                      </button>
                    )}
                  </div>
                  {searchPlayerCardShowSharedGames && sharedGames.length > 0 && (
                    <div style={{ marginTop: SPACE[3], display: "flex", flexDirection: "column", gap: SPACE[2] }}>
                      {sharedGames.slice(0, 4).map(game => (
                        <button
                          key={game.id}
                          onClick={() => {
                            setSelectedGame(game);
                            closePlayerCard();
                          }}
                          style={{ width: "100%", display: "flex", alignItems: "center", justifyContent: "space-between", gap: SPACE[2], padding: `${SPACE[2]}px ${SPACE[3]}px`, borderRadius: RADIUS.sm, border: `1px solid ${C.border}`, background: C.bg, color: C.ink, cursor: "pointer", textAlign: "left" }}
                        >
                          <span style={{ minWidth: 0 }}>
                            <span style={{ display: "block", fontSize: 13, fontWeight: 750, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{game.title}</span>
                            <span style={{ display: "block", fontSize: 12, color: C.inkFaint, marginTop: 2 }}>{game.date ? format(new Date(`${game.date}T00:00:00`), "d MMM") : ""} · {game.format}</span>
                          </span>
                          <ChevronRight size={15} color={C.inkFaint} {...ICON} />
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
              <div style={{ display: "flex", flexDirection: "column", gap: SPACE[2] }}>
                {user && !isSelfPlayer && !isFriend && (
                  <button
                    disabled={friendActionDisabled}
                    onClick={async () => {
                      setSearchPlayerCardLoading(true);
                      const { data: existing } = await supabase.from("friendships").select("id,status").or(`and(requester_id.eq.${user.id},recipient_id.eq.${playerId}),and(requester_id.eq.${playerId},recipient_id.eq.${user.id})`).maybeSingle();
                      if (existing) {
                        setSearchPlayerCardFriendStatus(existing.status === "accepted" ? "accepted" : "sent");
                      } else {
                        const { error } = await supabase.from("friendships").insert({ requester_id: user.id, recipient_id: playerId, status: "pending" });
                        if (error) { toast_(t("couldNotSendRequest")); }
                        else {
                          setSearchPlayerCardFriendStatus("sent");
                          toast_(t("friendRequestSent"));
                          const senderName = userProfile?.name || user?.user_metadata?.name || "Someone";
                          supabase.from("notifications").insert({ user_id: playerId, type: "friend_request", text: `${senderName} sent you a friend request`, sub: user.id, game_id: null, read: false }).then(() => {}, () => {});
                        }
                      }
                      setSearchPlayerCardLoading(false);
                    }}
                    style={{ ...btnPrimary, width: "100%", minHeight: 48, borderRadius: RADIUS.md, display: "flex", alignItems: "center", justifyContent: "center", gap: SPACE[2], opacity: searchPlayerCardLoading ? 0.65 : 1, background: requestSent ? C.accentBg : C.accent, color: requestSent ? C.accent : C.accentText, cursor: friendActionDisabled ? "default" : "pointer", boxShadow: friendActionDisabled ? "none" : SHADOW.sm }}
                  >
                    <User size={17} {...ICON} />
                    {friendActionLabel}
                  </button>
                )}
                <button
                  disabled={!inviteTargetGame}
	                  onClick={() => {
	                    if (!inviteTargetGame) return;
	                    setFriendsInviteGameId(inviteTargetGame.id);
	                    setFriendsInitialChatUserId(null);
	                    setShowFriendsScreen(true);
	                    closePlayerCard();
	                  }}
                  style={{ ...(isFriend ? btnPrimary : btnSecondary), width: "100%", minHeight: 48, borderRadius: RADIUS.md, display: "flex", alignItems: "center", justifyContent: "center", gap: SPACE[2], opacity: inviteTargetGame ? 1 : 0.45, cursor: inviteTargetGame ? "pointer" : "not-allowed", background: isFriend ? C.accent : C.surface, color: isFriend ? C.accentText : C.ink, boxShadow: isFriend && inviteTargetGame ? SHADOW.sm : "none" }}
                >
                  <Send size={17} {...ICON} />
                  Invite to game
                </button>
              </div>
            </div>
          </div>
        );
      })()}
      {showAvailabilitySheet && <AvailabilitySheet currentUser={user} userLocation={userLocation} onClose={() => setShowAvailabilitySheet(false)} existingAvailabilities={myAvailabilities} onSave={async (cleared) => { setShowAvailabilitySheet(false); if (cleared) { setMyAvailability(null); setMyAvailabilities([]); setNearbyAvailable([]); toast_("Availability cleared"); return; } toast_("You're visible to nearby players"); handleRefreshAvailability(); }} />}
      {selectedGame && <GameSheet game={selectedGame} joined={joined.includes(selectedGame.id)} waitlisted={waitlisted.includes(selectedGame.id)} fav={favs.includes(selectedGame.id)} onClose={() => setSelectedGame(null)} onJoin={handleJoin} onWaitlist={handleWaitlist} onFav={() => handleFav(selectedGame.id)} currentUser={user} onCancel={handleCancel} showToast={toast_} onRefresh={() => { loadGames(true); toast_("Game updated"); setSelectedGame(null); }} onPatchGame={patch => setSelectedGame(g => g ? { ...g, ...patch } : g)} onClone={handleCloneNextWeek} isJoining={joiningGameId === selectedGame.id} isCloning={cloningGameId === selectedGame.id} onInviteFriends={() => { setFriendsInviteGameId(selectedGame?.id); setFriendsInitialChatUserId(null); setShowFriendsScreen(true); }} venues={venues} />}
      {showNotifs && (() => {
        // Honour the user's Settings → Notifications toggles. Filter on display so a
        // future preference change takes effect immediately without DB reshuffling.
        const prefs = getNotifPrefs();
        const FRIEND_TYPES = new Set(["friend_request", "friend_accepted"]);
	        const CHAT_TYPES = new Set(["chat_message", "friend_message", "mention"]);
        const filtered = notifs.filter(n => {
          if (isLaunchQaNotification(n)) return false;
          if (!prefs.friends && FRIEND_TYPES.has(n.type)) return false;
          if (!prefs.chat && CHAT_TYPES.has(n.type)) return false;
          return true;
        });
        return <NotifPanel notifs={filtered} currentUserId={user?.id} onClose={() => setShowNotifs(false)} onTap={handleNotifTap} onReplyNotif={handleReplyNotif} onMarkAllRead={handleMarkAllRead} onAcceptFriendNotif={handleAcceptFriendNotif} onDeclineFriendNotif={handleDeclineFriendNotif} onDeleteNotif={handleDeleteNotif} onClearAll={handleClearAllNotifs} onAcceptGameInvite={handleAcceptGameInvite} onDeclineGameInvite={handleDeclineGameInvite} />;
      })()}
      {toastQueue.length > 0 && <div key={toastQueue[0].id} onClick={() => setToastQueue(q => q.slice(1))} style={{ position: "fixed", bottom: "calc(80px + env(safe-area-inset-bottom, 0px))", left: "50%", transform: "translateX(-50%)", background: C.accent, color: C.accentText, padding: "10px 20px", borderRadius: 8, fontSize: 13, fontWeight: 600, zIndex: 200, whiteSpace: "nowrap", boxShadow: `0 4px 20px ${C.accent}44`, animation: "toastIn 0.25s ease", cursor: "pointer" }}>{toastQueue[0].msg}</div>}
      {showAuthModal && (
        <div onClick={() => setShowAuthModal(false)} style={{ position: "fixed", inset: 0, zIndex: 500, background: "rgba(0,0,0,0.55)", display: "flex", alignItems: "flex-end" }}>
          <div onClick={e => e.stopPropagation()} style={{ width: "100%", background: C.bg, borderRadius: "20px 20px 0 0", maxHeight: "92vh", overflowY: "auto" }}>
            <div style={{ display: "flex", justifyContent: "center", padding: "12px 0 0" }}>
              <div style={{ width: 36, height: 4, borderRadius: 2, background: C.border }} />
            </div>
            <button onClick={() => setShowAuthModal(false)} aria-label="Close" style={{ position: "absolute", top: 16, right: 16, background: "transparent", border: "none", cursor: "pointer", color: C.inkMid, padding: 8, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", transition: "background 0.12s" }}
              onMouseEnter={e => e.currentTarget.style.background = C.bg}
              onMouseLeave={e => e.currentTarget.style.background = "transparent"}
            ><X size={18} {...ICON} /></button>
	            <AuthScreen onAuth={(u, options = {}) => { setUser(u); setShowAuthModal(false); if (options.openAdminDashboard) setShowAdminDashboard(true); }} />
          </div>
        </div>
      )}
    </div>
    </ThemeCtx.Provider>
  );
}
